import React, { useState, useEffect, useRef, useMemo, useCallback, useContext } from 'react';
import { useForm } from 'react-hook-form';
import { emailRegexPattern, roundToFixed, formatNumberWithCommas } from '../../helper';
import {
  formatDbEventDate,
  formatDbEventTime,
  formatDbEventDateYmd,
} from '../../utils/eventDbDateTime';
import styles from './ManageTickets.module.css';
import {
  ChevronDown,
  Download,
  MoreVertical,
  Clock,
  AlertCircle,
  Package,
  Calendar,
  MapPinned,
  CheckSquare,
  Square,
  DollarSign,
  Eye,
  Edit,
  X,
  Search as SearchIcon,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  FileText,
  ArrowRight,
  ExternalLink,
  RefreshCcw,
  Share2,
  Users,
  Mail,
  Filter,
  Split,
  Combine,
  ScanBarcode,
  ClipboardCheck,
  TrendingUp,
  TrendingDown,
  History,
  Pencil,
  Moon,
  Sun,
  Ban,
} from 'lucide-react';
import { useQuery, useMutation, useLazyQuery, useApolloClient } from '@apollo/client';
import { getStatusIcon } from './getStatusIcon';
import {
  sortManageTickets,
  sortDelistedTabTickets,
  MANAGE_TICKETS_ORDER_BY,
} from './manageTicketSort';
import {
  getManageTicketsTrackingVariables,
  getCustomerSearchPattern,
  parseAndSortSeatNumbers,
  compareSeatNumbers,
} from './manageTicketSort';
import {
  MANAGE_TICKET_COLUMN_IDS,
  type ManageTicketColumnId,
  isManageTicketColumnId,
  readManageTicketColumnOrder,
  reorderVisibleManageTicketColumns,
  visibleManageTicketColumnOrder,
  writeManageTicketColumnOrder,
} from './manageTicketColumnOrder';
import {
  isManageTicketTableSortEnabled,
  isManageTicketSortRequestPending,
  shouldShowManageTicketQueryLoader,
  sortManageTicketDisplayRows,
  type ManageTicketSortColumn,
  type ManageTicketTableSort,
} from './manageTicketTableSort';
import {
  buildManageTicketDisplayGroups,
  expandTicketsToDisplayGroups,
  expandTicketsToSameDisplayGroup,
  getGroupedPageSeatFetchSize,
  getGroupedSeatWindowIndex,
  getGroupedWindowLocalOffset,
  getNextGroupedPageOffset,
  getManageTicketDisplayTickets,
  getEventGroupKey,
  getGroupSeatMode,
  hasAnotherGroupedPage,
  resolveLoadedGroupedRowTotal,
  resolveSelectedTicketIdsFromSeatSelection,
  toggleExpandedGroupKeys,
} from './manageTicketGrouping';
import { isFailToTransferTicket } from './utils/failToTransfer';
import { renderEventStatusBadge, isEventDateTbdForVerification } from './eventStatusBadge';
import { excludeUndatedTicketsForDateFilter } from './eventDateFilter';
import {
  GET_LIST_TICKETS,
  GET_LISTED_TICKET_COSTS,
  UPDATE_LISTED_TICKET_COST,
  GET_MANAGE_TICKETS,
  GET_SOLD_TICKETS,
  GET_DELIST_AND_RETURN_TICKETS,
  GET_DELIST_AND_UNSOLD_TICKETS,
  GET_LISTING_REQUEST_CANCELLED_TICKETS,
  GET_DELETED_TICKETS,

} from './api/TicketsQuery';
import {
  GET_LEAGUES_LIST,
  VALIDATE_UPDATE,
  EDIT_TICKET_PRICE_BY_SUPER_USER,
  PUBLISH_TICKETS,
  UPDATE_TICKET_DETAILS,
  UPDATE_TICKET_STATUS,
  DELETE_TICKET_STATUS,
  RESTORE_CANCELLED_LISTING_TO_VERIFICATION,
  RESTORE_DELETED_TO_VERIFICATION,
  GET_EXPIRED_TICKETS,
  GET_FAILED_TRANSFER_TICKETS,
  GET_TICKETS_TO_AUTO_EXPIRE,
  BULK_EXPIRE_TICKETS,
  PUBLISH_NON_VL_LISTING,
  GET_MARKETPLACES_FOR_TICKETS,
  GET_TICKET_MARKETPLACES_BY_TP_IDS,
  GET_TICKET_GROUPING_BOUNDARIES,
  GET_VL_LISTING_IDS_BY_TP_IDS,
  GET_PLACEMENT_VL_PRODUCTION_IDS,
  GET_VL_SUPPORT_REQUESTS_BY_TP_IDS,
  GET_SIBLING_TAB_COUNTS,
  UPDATE_TICKET_COMMISSION_OVERRIDE,
  UPDATE_TICKET_BARCODE_STATUS,

  
} from './api/TicketsQuery';
import {
  EVENT_VIEW_GET_MANAGE_TICKETS,
  EVENT_VIEW_GET_LIST_TICKETS,
  EVENT_VIEW_GET_SOLD_TICKETS,
  EVENT_VIEW_GET_DELIST_AND_RETURN_TICKETS,
  EVENT_VIEW_GET_LISTING_REQUEST_CANCELLED_TICKETS,
  EVENT_VIEW_GET_DELETED_TICKETS,
} from './api/EventViewTicketsQuery';
import { MARK_AS_SOLD } from '../../components/shared-components/mark-as-sold/api/MarkAsSoldQuery';
import {
  ADD_USER_NOTE,
  GET_USER_NOTES,
  GET_LEAGUE_CATEGORIES_BY_IDS,
  GET_USER_VICTORY_LIVE_SEAT_NOTES,
  INSERT_USER_VICTORY_LIVE_SEAT_NOTE,
  UPDATE_USER_VICTORY_LIVE_SEAT_NOTE,
} from '../Userdetails/api/UserdetailsQuery';
import ListingNoteCombobox from '../Userdetails/ListingNoteCombobox';
import { composeListingNoteDraft, expandListingSeatNumbers, findMatchingSeatLevelNote, findSeatLevelNoteForListing, formatListingPopupNotes, isParkingPassNoteGroup, isParkingPassRow, listingNoteChoices, PARKING_EXTERNAL_NOTE, PARKING_INTERNAL_NOTE, seedListingNoteFields, withListingAmenityNotes, withSeatNoteTeamNames, type ListingNoteLocation, type SeatLevelNote } from '../Userdetails/seatLevelNotes';
import { GET_SUPER_USER_BY_UID } from '../PriceSetUp/api/PriceSetUp';
import { config } from '../../config/config';
import { fetchUserFeePercentages } from './api/userFeePercentages';
import { Ticket, League, Seat, type VlSupportRequest } from '../../types/ticket.types';
import { getPidStatusFilterForQuery, type PidStatusFilter } from './pidStatusFilter';
import { BARCODE_STATUS_OPTIONS, getAutomaticBarcodeStatus } from '../../utils/noBarcodeRules';
import BarcodeStatusDropdown, { type BarcodeStatusValue } from './BarcodeStatusDropdown';
import {
  normalizePricingMethod,
  resolvePricingMethodSelection,
} from '../Userdetails/userdetailsTicketTransform';
import { useNavigate } from 'react-router-dom';
import ToasterWithMessage from "../../components/shared-components/toaster";
import { GET_NOTIFICATIONS } from "../../components/shared-components/notification/api/NotificationQuery";
import { getNotificationQueryVariables, mergeNotificationQueryIntoGlobalState, scheduleNotificationSyncRetries } from "../../utils/notification-query-variables";
import { globalContext } from "../../Provider/AppProvider";
import { getStoredEmailFromIdToken } from '../../authToken';
import { GET_USER_PROFILE } from "../Auth/api/SignInQuery";
import { getMailParserErrorTpIds, getManageTicketsTabFromErrorStatus, isStaffAdminEquivalent, roles } from "../../helper";
import { GET_MANAGED_USERS_BY_SALES_REP, GET_SALES_REPS_BY_EMAIL } from "../Salesrep/api/SalesrepQuery";
import { GET_USERS_LIST } from "../Users/api/UsersQuery";
import { completeVlSupportRequest, submitListingsToVl, syncVlListingStatus, startVlAccountCrawl, getVlAccountCrawlStatus, recommendBarcodeSyncAccounts, checkVlBarcodes, syncConciergeVlPrices, shareVlListings, markWrongPid, searchVlProductions, confirmVlProduction, groupVlListings, splitVlListings, verifyTicketPlacements, delistTicketsRequest, delistTicketByAdminRequest, DelistJobError, VL_MARKETPLACE_OPTIONS, type ProductionMismatchDetails, type BarcodeSyncRecommendResult, type CheckBarcodesResult } from './api/vlListing.api';
import { WrongPidRetryButton } from './WrongPidRetryButton';
import {
  formatVlUnknownMessage,
  isVlUnknownStatus,
  summarizeVlActionToast,
  successfulVlTpIds,
  toastForVlCaughtError,
  type VlActionToast,
} from './vlActionOutcome';
import BarcodeSyncConfirmModal from './components/BarcodeSyncConfirmModal';
import SyncFromVlScopeModal, {
  type SyncFromVlScope,
} from './components/SyncFromVlScopeModal';
import CheckBarcodesModal from './components/CheckBarcodesModal';
import { EventMarketplaceDeepLinks, MarketplaceBrandMark } from './EventMarketplaceDeepLinks';
import { ListedVlRowControls } from './ListedVlRowControls';
import { VlSupportControl, VlSupportPanel } from './VlSupportPanel';
import { SeatModeBadge, useSeatModeDialog } from './SeatModeDialog';
import { VlSupportThread } from '../SupportTickets/components/VlSupportThread';
import { subscribeVlSupportUpdates } from '../SupportTickets/hooks/useVlSupportAlerts';
import {
  bulkSupportScope,
  groupListings,
  vlSupportFilterForQuery,
  deriveOwnerInitials,
  VL_SUPPORT_OWNERS,
  type VlSupportFilter,
  type VlSupportOwnerFilter,
  type VlSupportBulkDetails,
  type VlSupportEventDetails,
} from './vlSupportEmail';
import { ListedSharedToggle } from './ListedSharedToggle';
import { ListedDelistConfirmModal } from './ListedDelistConfirmModal';
import type { DelistJobProgress, DelistUiStep } from './delistStepStatus';
import { ListedShareConfirmModal } from './ListedShareConfirmModal';
import { SelfPricingPriceConfirmModal } from './SelfPricingPriceConfirmModal';
import {
  hidesSuperUserEventCardDetails,
  hidesSuperUserTicketDetails,
  isSuperUserDelistedTab,
  isSuperUserListedStatusReadOnly,
  isSuperUserSoldTab,
  isSuperUserUnsoldTab,
} from './soldTabVisibility';
import { NonVlStatusConfirmModal } from './NonVlStatusConfirmModal';
import {
  isNonVlStatusChange,
  buildNonVlStatusConfirmSummary,
} from './nonVlStatusChange';
import {
  getVlMarketplaceIdsForTicket,
  getVlMarketplaceLabels,
  setCachedEnabledMarketplaceIds,
  setCachedEnabledMarketplaceIdsForMany,
  getVlSharedStatusForTicket,
  setCachedVlSharedStatus,
  setCachedVlSharedStatusForMany,
  ticketHasVlListingMarkers,
  isVlListedTicketStatus,
  LISTED_DELIST_BUSY_MESSAGE,
  formatUnshareDelistSuccessMessage,
  formatDelistCleanupFailureMessage,
} from './vlMarketplaceSelection';
import {
  useConciergePriceSocket,
  type ConciergePricesUpdatedPayload,
  type TicketHoldsUpdatedPayload,
} from './hooks/useConciergePriceSocket';
import PriceChangeLogsPanel from '../PriceChangeLogs/PriceChangeLogsPanel';
import {
  LAST_PRICED_BUCKET_OPTIONS,
  getLastPricedBucketQueryRange,
  getLastPricedCustomRange,
  getLastPricedRange,
  matchesLastPricedRange,
  getLastPricedDisplayValue,
  getLastPricedBadge,
  matchesLastPricedBucketFilter,
  describeLastPricedBucket,
} from './staleness';
import { isVictoryLivePriceUpdateSuccess, priceUpdateFailureMessage, VL_ID_MISMATCH_PRICE_ERROR } from './priceUpdateResult';

type MarketplaceOption = {
  id: string;
  name: string;
  vl_fee_percentage: number;
  active?: boolean;
};

type BarcodeSyncSummary = {
  phase: 'crawling' | 'verifying' | 'success' | 'failure';
  accountIds: string[];
  crawlMessage?: string;
  crawlResults: Array<{
    accountId: string;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    crawlStatus?: string | null;
    message?: string;
    username?: string | null;
    marketplaceName?: string | null;
  }>;
  verificationResult?: CheckBarcodesResult | null;
  failures?: string[];
  verificationMessage?: string;
};

type SeatLevelNoteSaveTarget = {
  userId: string;
  teamId: string;
  section: string;
  row: string;
  seatStart: number;
  seatEnd: number;
  internalNote: string;
  externalNote: string;
};

const DEFAULT_CONCIERGE_PERCENT = 20;
const SELF_PRICING_FEE_FALLBACK = 15;

/** Same fee logic as `MarkAsSold.tsx` / backend `markAsSold` (QuickAsyst % of list). */
type SoldFeeConfig = {
  feeMode: 'super' | 'default';
  superClientPercent: number | null;
};

const DEFAULT_SOLD_FEE_CONFIG: SoldFeeConfig = { feeMode: 'default', superClientPercent: null };

/** VL production names often include extras after `;` — show only the short name. */
function displayVlEventName(name?: string | null): string {
  const full = String(name || '').trim();
  if (!full) return '';
  const cut = full.indexOf(';');
  return (cut >= 0 ? full.slice(0, cut) : full).trim();
}

function normalizeEventIdValue(raw: unknown): string {
  if (raw == null) return '';
  // GraphQL alias `e_id: event { e_id }` can leave a nested object on the ticket.
  if (typeof raw === 'object') {
    return String((raw as { e_id?: unknown }).e_id || '').trim();
  }
  const value = String(raw).trim();
  if (!value || value === '[object Object]') return '';
  return value;
}

function getTicketEventId(ticket: Ticket | null | undefined): string {
  if (!ticket) return '';
  return (
    normalizeEventIdValue(ticket.e_id) ||
    normalizeEventIdValue((ticket as any).ticket?.event?.e_id) ||
    normalizeEventIdValue((ticket as any).ticket?.e_id)
  );
}

function getTicketVlProductionId(
  ticket: Ticket | null | undefined,
  _productionIdByEventId?: Record<string, string>,
): string {
  if (!ticket) return '';
  return String(ticket.tp_vl_production_id || '').trim();
}

function isAwaitingVlPidConfirmation(ticket: Ticket | null | undefined): boolean {
  const status = String(ticket?.tp_status || '')
    .toLowerCase()
    .replace(/[^a-z]/g, '');
  return status === 'tobeverified';
}

/** Show a PID saved on this seat. Event-level PIDs stay hidden on To Be Verified
 *  so another group's mapping is not shown on seats that were not selected. */
function getDisplayedTicketVlProductionId(
  ticket: Ticket | null | undefined,
  productionIdByEventId?: Record<string, string>,
): string {
  const fromSeat = String(ticket?.tp_vl_production_id || '').trim();
  if (fromSeat) return fromSeat;
  if (isAwaitingVlPidConfirmation(ticket)) return '';
  return getTicketVlProductionId(ticket, productionIdByEventId);
}

function getTicketMarketplaceIds(
  ticket: Ticket | null | undefined,
  linksByProductionId?: Record<string, { stubhubId: string | null; seatgeekId: string | null }>,
  productionIdByEventId?: Record<string, string>,
): { stubhubId: string | null; seatgeekId: string | null } {
  const seatStubhub = String(ticket?.tp_stubhub_id || '').trim() || null;
  const seatSeatgeek = String(ticket?.tp_seatgeek_id || '').trim() || null;
  if (seatStubhub || seatSeatgeek) {
    return { stubhubId: seatStubhub, seatgeekId: seatSeatgeek };
  }
  if (isAwaitingVlPidConfirmation(ticket)) {
    return { stubhubId: null, seatgeekId: null };
  }
  const productionId = getTicketVlProductionId(ticket, productionIdByEventId);
  const fromCache = productionId ? linksByProductionId?.[productionId] : undefined;
  const stubhubId =
    fromCache?.stubhubId ??
    (ticket?.e_stubhub_id != null && String(ticket.e_stubhub_id).trim() !== ''
      ? String(ticket.e_stubhub_id).trim()
      : null);
  const seatgeekId =
    fromCache?.seatgeekId ??
    (ticket?.e_seatgeek_id != null && String(ticket.e_seatgeek_id).trim() !== ''
      ? String(ticket.e_seatgeek_id).trim()
      : null);
  return { stubhubId, seatgeekId };
}

const EVENT_MISSING_VALUE = '–';
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function getTicketBarcodeValue(ticket: Ticket | null | undefined): string {
  if (!ticket) return '';
  return String(
    ticket.tp_barcode ||
      ticket.barcode ||
      ticket.vl_barcode ||
      '',
  ).trim();
}

function ticketHasBarcodeAttached(ticket: Ticket | null | undefined): boolean {
  if (!ticket) return false;
  if (ticket.tp_barcode_attached === true) return true;
  return Boolean(getTicketBarcodeValue(ticket));
}

function getTicketBarcodeStatus(ticket: Ticket | null | undefined): BarcodeStatusValue {
  const storedStatus = String(ticket?.tp_barcode_status || '').trim();
  if (BARCODE_STATUS_OPTIONS.some((option) => option.value === storedStatus)) {
    return storedStatus as BarcodeStatusValue;
  }
  const automaticStatus = getAutomaticBarcodeStatus(
    ticket?.e_name || ticket?.event?.e_name,
    ticket?.tp_row,
    ticket?.event?.e_platform_name,
  );
  if (automaticStatus) return automaticStatus;
  return ticketHasBarcodeAttached(ticket) ? 'Barcode Available' : 'Barcode Pending';
}

function getTicketsBarcodeStatus(tickets: Array<Ticket | null | undefined>): {
  allAttached: boolean;
  missingCount: number;
  noBarcodeCount: number;
  total: number;
} {
  const list = tickets.filter((candidate): candidate is Ticket => Boolean(candidate));
  const statuses = list.map(getTicketBarcodeStatus);
  const missingCount = statuses.filter((status) => status === 'Barcode Pending').length;
  return {
    allAttached: list.length > 0 && statuses.every((status) => status === 'Barcode Available'),
    missingCount,
    noBarcodeCount: statuses.filter((status) => status === 'Null').length,
    total: list.length,
  };
}

function getBarcodeMissingSummary(tickets: Array<Ticket | null | undefined>): string {
  const { allAttached, missingCount, noBarcodeCount, total } = getTicketsBarcodeStatus(tickets);
  if (allAttached) return 'Barcode attached to every seat';
  if (total > 0 && noBarcodeCount === total) return 'No barcode for every seat';
  if (total === 0 || (missingCount === total && noBarcodeCount === 0)) return 'Barcode missing from all seats';
  return `Missing for ${missingCount} of ${total} seats${noBarcodeCount ? `; No barcode for ${noBarcodeCount}` : ''}`;
}

function shouldShowBarcodeStatus(rowTab: string, isSuperUser = false): boolean {
  return (rowTab === 'Listed' || rowTab === 'Sold') &&
    !(isSuperUser && rowTab === 'Sold');
}

function getGroupBarcodeStatus(tickets: Array<Ticket | null | undefined>): BarcodeStatusValue | 'Mixed' {
  const statuses = new Set(tickets.filter(Boolean).map(getTicketBarcodeStatus));
  return statuses.size === 1 ? [...statuses][0] : 'Mixed';
}

function renderBarcodeStatusGlyph(status: string, label: string) {
  if (status === 'Null') {
    return (
      <span className={`${styles.barcodeStatusIcon} ${styles.barcodeStatusNull}`} title={label} aria-label={label}>
        <Ban size={14} aria-hidden />
      </span>
    );
  }
  if (status === 'Barcode Available') {
    return (
      <span className={`${styles.barcodeStatusIcon} ${styles.barcodeStatusOk}`} title={label} aria-label={label}>
        <CheckCircle size={14} aria-hidden />
      </span>
    );
  }
  return (
    <span className={`${styles.barcodeStatusIcon} ${styles.barcodeStatusMissing}`} title={label} aria-label={label}>
      <AlertCircle size={14} aria-hidden />
    </span>
  );
}

function renderBarcodeStatusIcon(
  tickets: Array<Ticket | null | undefined>,
  onSelect?: (status: BarcodeStatusValue) => void,
) {
  const status = getGroupBarcodeStatus(tickets);
  const label = getBarcodeMissingSummary(tickets);
  return onSelect
    ? <BarcodeStatusDropdown value={status} onSelect={onSelect} />
    : renderBarcodeStatusGlyph(status, label);
}

function renderSeatBarcodeStatus(ticket: Ticket | null | undefined) {
  const barcode = getTicketBarcodeValue(ticket);
  const status = getTicketBarcodeStatus(ticket);
  if (status === 'Null') {
    return (
      <div className={`${styles.seatBarcodeStatus} ${styles.barcodeStatusNull}`}>
        <Ban size={12} aria-hidden />
        <span>No Barcode</span>
      </div>
    );
  }
  if (status === 'Barcode Available') {
    return (
      <div className={`${styles.seatBarcodeStatus} ${styles.barcodeStatusOk}`}>
        <CheckCircle size={12} aria-hidden />
        {barcode ? (
          <span className={styles.seatBarcodeValue} title={barcode}>
            Barcode: {barcode}
          </span>
        ) : null}
      </div>
    );
  }
  return (
    <div className={`${styles.seatBarcodeStatus} ${styles.barcodeStatusMissing}`}>
      <AlertCircle size={12} aria-hidden />
      <span>Barcode missing</span>
    </div>
  );
}

function renderManageTicketsEventCell(options: {
  ticket: Ticket;
  eventName: string;
  datePrefix?: React.ReactNode;
  formatDate: (
    dateString: string,
    timeZone?: string | null,
    dateTimeZone?: string | null,
    eTime?: string | null,
  ) => string;
  formatTime: (
    dateString: string,
    timeZone?: string | null,
    dateTimeZone?: string | null,
    eTime?: string | null,
  ) => string;
  productionIdByEventId?: Record<string, string>;
  marketplaceLinksByProductionId?: Record<
    string,
    { stubhubId: string | null; seatgeekId: string | null }
  >;
  showBarcodeStatusIcon?: boolean;
  hidePidBarcodeDetails?: boolean;
  groupTickets?: Ticket[];
  onBarcodeStatusSelect?: (tickets: Ticket[], status: BarcodeStatusValue) => void;
  sticky?: boolean;
  extraContent?: React.ReactNode;
}) {
  const {
    ticket,
    eventName,
    datePrefix,
    formatDate,
    formatTime,
    productionIdByEventId,
    marketplaceLinksByProductionId,
    showBarcodeStatusIcon,
    hidePidBarcodeDetails = false,
    groupTickets,
    onBarcodeStatusSelect,
    sticky = false,
    extraContent,
  } = options;
  const seats = groupTickets?.length ? groupTickets : [ticket];
  const pid =
    seats
      .map((seat) => String(seat?.tp_vl_production_id || '').trim())
      .find(Boolean) ||
    getDisplayedTicketVlProductionId(ticket, productionIdByEventId);
  const marketplaceIds =
    seats
      .map((seat) =>
        getTicketMarketplaceIds(seat, marketplaceLinksByProductionId, productionIdByEventId),
      )
      .find((ids) => ids.stubhubId || ids.seatgeekId) ||
    getTicketMarketplaceIds(ticket, marketplaceLinksByProductionId, productionIdByEventId);
  const eventTime = formatTime(ticket.e_date, ticket.e_time_zone, ticket.e_date_time_zone, ticket.e_time);
  const barcodeTickets = groupTickets?.length ? groupTickets : [ticket];

  return (
    <td className={`${styles.tdEvent}${sticky ? ` ${styles.stickyEvent}` : ''}`}>
      <div className={styles.eventName}>
      
        <span className={styles.eventNameText}>{eventName || EVENT_MISSING_VALUE}</span>
      </div>
      <div className={styles.eventDate}>
        {datePrefix}
        <Calendar size={10} />
        {formatDate(ticket.e_date, ticket.e_time_zone, ticket.e_date_time_zone, ticket.e_time)}
        {eventTime ? ` • ${eventTime}` : ''}

      </div>
      {!hidePidBarcodeDetails && (
        <>
          <div className={styles.eventPidBarcode}>
            {showBarcodeStatusIcon ? (
              <>
                PID: {pid || EVENT_MISSING_VALUE} and Barcode:{' '}
                {renderBarcodeStatusIcon(
                  barcodeTickets,
                  onBarcodeStatusSelect ? (status) => onBarcodeStatusSelect(barcodeTickets, status) : undefined,
                )}
              </>
            ) : (
              <>PID: {pid || EVENT_MISSING_VALUE}</>
            )}
          </div>
          <div>TM Status : {renderEventStatusBadge(ticket)}</div>
        </>
      )}
      <EventMarketplaceDeepLinks
        stubhubId={marketplaceIds.stubhubId}
        seatgeekId={marketplaceIds.seatgeekId}
        eventName={eventName}
        eventDate={formatVenueEventDateYmd(
          ticket.e_date,
          ticket.e_time_zone,
          ticket.e_date_time_zone,
          ticket.e_time,
        )}
        className={styles.marketplaceDeepLinks}
        linkClassName={styles.marketplaceDeepLink}
        disabledClassName={styles.marketplaceDeepLinkDisabled}
        stubhubClassName={styles.marketplaceDeepLinkStubhub}
        seatgeekClassName={styles.marketplaceDeepLinkSeatgeek}
          ticketmasterClassName={styles.marketplaceDeepLinkTicketmaster}
          vividseatsClassName={styles.marketplaceDeepLinkVivid}
      />
      {extraContent}
    </td>
  );
}

function effectiveSoldQuickCutPercent(
  marketplaceFeePercent: number,
  feeMode: 'super' | 'default',
  superClientPercent: number | null,
): number {
  const clientPercent =
    feeMode === 'super' && superClientPercent != null
      ? superClientPercent
      : DEFAULT_CONCIERGE_PERCENT;
  return Math.max(0, clientPercent - marketplaceFeePercent);
}

type VlProgressStep = 'confirm' | 'submit' | 'share';

const VL_PROGRESS_BY_STATUS: Record<
  string,
  { step: VlProgressStep; label: string; title: string; busyMessage: string }
> = {
  PidPending: {
    step: 'confirm',
    label: 'Confirm Production ID',
    title: 'Review and confirm the Victory Live event before submitting inventory',
    busyMessage: 'Searching Victory Live productions…',
  },
  PidConfirmed: {
    step: 'submit',
    label: 'Submit to VL',
    title: 'Create Victory Live inventory using the confirmed Production ID',
    busyMessage: 'Submitting to Victory Live…',
  },
  Verified: {
    step: 'share',
    label: 'List on Marketplace',
    title: 'Choose VL marketplaces and mark Listed',
    busyMessage: 'Sharing listings on Victory Live…',
  },
};

/** Normalize status labels for VL progress button (module-level; not the component helper). */
function normalizeVlProgressStatus(value: string): string {
  const key = String(value || '')
    .toLowerCase()
    .replace(/[^a-z]/g, '');
  if (key === 'tobeverified') return 'ToBeVerified';
  if (key === 'verified') return 'Verified';
  if (key === 'list' || key === 'listed') return 'List';
  return String(value || '');
}

function getVlProgressForTickets(
  tickets: Array<{
    tp_status?: string | null;
    tp_vl_listing_id?: string | null;
    tp_vl_item_id?: string | null;
  }>,
) {
  if (!tickets.length) return null;
  const statuses = tickets.map((t) => normalizeVlProgressStatus(String(t.tp_status || '')));
  const unique = [...new Set(statuses)];
  if (unique.length !== 1) return null;
  const status = unique[0];
  if (status === 'ToBeVerified') return null;
  // Verified stays Verified through PID confirmation and ingestion.
  const allMissingVlIds = tickets.every((t) => !t.tp_vl_listing_id && !t.tp_vl_item_id);
  if (allMissingVlIds && status === 'Verified') {
    // PID confirmed = this seat has a production id (legacy rows still fall back to the event).
    const allPidConfirmed = tickets.every((ticket: any) => {
      const pid = String(getTicketVlProductionId(ticket) || '').trim();
      return Boolean(pid && /^\d+$/.test(pid));
    });
    return allPidConfirmed
      ? VL_PROGRESS_BY_STATUS.PidConfirmed
      : VL_PROGRESS_BY_STATUS.PidPending;
  }
  // Orphan after failed split: Listed but no VL ids → legacy submit recovery.
  if (allMissingVlIds && status === 'List') {
    return VL_PROGRESS_BY_STATUS.PidConfirmed;
  }
  return VL_PROGRESS_BY_STATUS[status] ?? null;
}

function getVlProgressForStatuses(statuses: string[]) {
  if (!statuses.length) return null;
  const unique = [...new Set(statuses.map((s) => normalizeVlProgressStatus(String(s))))];
  if (unique.length !== 1) return null;
  return VL_PROGRESS_BY_STATUS[unique[0]] ?? null;
}

// ==================== STATUS TO TAB MAPPING ====================

function getTabForStatus(status: string): string {
  if (!status) return 'Verification';
  const normalizedStatus = (status || '').toLowerCase().replace(/\s/g, '');
  
  if (normalizedStatus === 'tobeverified' || normalizedStatus === 'verified') {
    return 'Verification';
  }
  
  
  if (normalizedStatus === 'list') {
    return 'Listed';
  }
  
  // Sold Tab: include QA-related and review statuses here so they surface under Sold
  if (
    normalizedStatus === 'sold' ||
    normalizedStatus === 'paidmanuallyviaqa' ||
    normalizedStatus === 'qadeniedpayout' ||
    normalizedStatus === 'underreview' ||
    normalizedStatus === 'voa'
  ) {
    return 'Sold';
  }
  
  if (normalizedStatus === 'invalid') {
    return 'Verification';
  }

  if (normalizedStatus === 'listingrequestcancelled') {
    return 'Listing Request Cancelled';
  }

  
  if (
    normalizedStatus === 'delist' ||
    normalizedStatus === 'delistinprogress' ||
    normalizedStatus === 'delistrequested' ||
    normalizedStatus === 'return'
  ) {
    return 'Delisted';
  }
  
  if (normalizedStatus === 'unsold') {
    return 'Unsold';
  }

  if (normalizedStatus === 'closed') {
    return 'Listing Expired';
  }

  // Deleted Tab
  if (normalizedStatus === 'deleted') {
    return 'Deleted';
  }
  
  return 'Verification';
}

/** Step 1 listings land as ToBeVerified; Verified is a later human step. VL submit is not Step 1. */
const VERIFICATION_STATUSES = new Set(['ToBeVerified', 'Verified']);

/** Higher rank = further along the listing lifecycle; server wins when ahead of a stale override. */
const STATUS_PROGRESS_RANK: Record<string, number> = {
  ListingRequestCancelled: -1,
  Invalid: -1,
  ToBeVerified: 0,
  Verified: 1,
  List: 2,
  Unsold: 2,
  DelistInProgress: 2,
  DelistRequested: 2,
  Delist: 2,
  Return: 2,
  Sold: 3,
  UnderReview: 3,
  PaidManuallyViaQA: 3,
  QADeniedPayout: 3,
  Closed: 4,
};

const getStatusProgressRank = (status: string): number =>
  STATUS_PROGRESS_RANK[status] ?? -999;

const filterVerificationTickets = (tickets: Ticket[]): Ticket[] =>
  tickets.filter(
    (ticket) =>
      VERIFICATION_STATUSES.has(ticket.tp_status || '') &&
      !isFailToTransferTicket(ticket),
  );

/**
 * Keep optimistic status only while the server is still behind (e.g. List → Sold).
 * Same-rank mismatches (List vs Delist) trust the server so a failed delist does not stay painted.
 */
const applyStatusOverrides = (tickets: Ticket[], overrides: Record<string, string>): Ticket[] =>
  tickets.map(ticket => {
    const overrideStatus = overrides[ticket.tp_id];
    if (!overrideStatus) return ticket;
    const serverStatus = ticket.tp_status || '';
    if (serverStatus === overrideStatus) return ticket;
    if (getStatusProgressRank(serverStatus) >= getStatusProgressRank(overrideStatus)) {
      return ticket;
    }
    return {
      ...ticket,
      tp_status: overrideStatus,
      tp_updated_at: new Date().toISOString(),
    };
  });

const reconcileStatusOverrides = (
  overrides: Record<string, string>,
  serverTickets: Ticket[],
): void => {
  Object.keys(overrides).forEach(tpId => {
    const override = overrides[tpId];
    const serverTicket = serverTickets.find(t => t.tp_id === tpId);
    if (!serverTicket) return;
    const serverStatus = serverTicket.tp_status || '';
    if (
      serverStatus === override ||
      getStatusProgressRank(serverStatus) >= getStatusProgressRank(override)
    ) {
      delete overrides[tpId];
    }
  });
};

/** Don't paint the seat dropdown until confirm/API commits — Delist can fail or be cancelled. */
const DEFERRED_SEAT_STATUS_PAINT = new Set([
  'Verified',
  'Verified_NonVL',
  'Delist',
  'Delist_NonVL',
  'Sold',
  'Deleted',
  'List',
  'List_NonVL',
  'DelistInProgress',
  'DelistInProgress_NonVL',
  'DelistRequested',
  'Return',
  'Return_NonVL',
  'Unsold',
  'Unsold_NonVL',
  'ListingRequestCancelled',
]);

const shouldDeferSeatStatusPaint = (nextStatus: string): boolean =>
  DEFERRED_SEAT_STATUS_PAINT.has(nextStatus);

const readDelistSuccessTpIds = (data: any): string[] => {
  if (!Array.isArray(data?.successTpIds)) return [];
  return data.successTpIds
    .map((id: unknown) => String(id || '').trim())
    .filter(Boolean);
};

type LocalPriceOverride = { price: number; lastPricedAt: string };

const pricesMatchToCent = (a: number, b: number): boolean =>
  Math.round(Number(a) * 100) === Math.round(Number(b) * 100);

const serverLastPricedCaughtUp = (serverLastPricedAt: string | null | undefined, overrideLastPricedAt: string): boolean => {
  const overrideAt = Date.parse(overrideLastPricedAt);
  if (!Number.isFinite(overrideAt)) return true;
  const serverAt = Date.parse(String(serverLastPricedAt || ''));
  return Number.isFinite(serverAt) && serverAt >= overrideAt - 1000;
};

/** Price reads use the ticket query. Do not overlay the optimistic list-price cache. */
const applyPriceOverrides = (
  tickets: Ticket[],
  _overrides: Record<string, LocalPriceOverride>,
): Ticket[] => tickets;

const reconcilePriceOverrides = (
  overrides: Record<string, LocalPriceOverride>,
  serverTickets: Ticket[],
): void => {
  Object.keys(overrides).forEach((tpId) => {
    const override = overrides[tpId];
    const serverTicket = serverTickets.find((t) => t.tp_id === tpId);
    if (!serverTicket) return;
    const serverPrice = Number(serverTicket.tp_list_price);
    if (
      Number.isFinite(serverPrice) &&
      pricesMatchToCent(serverPrice, override.price) &&
      serverLastPricedCaughtUp(serverTicket.last_priced_at, override.lastPricedAt)
    ) {
      delete overrides[tpId];
    }
  });
};

type EventTicketGroupKey = 'pending' | 'listed' | 'sold' | 'delisted' | 'deleted';

const EVENT_TICKET_VIEW_SECTIONS: { key: EventTicketGroupKey; label: string; tab: string }[] = [
  { key: 'pending', label: 'Pending', tab: 'Verification' },
  { key: 'listed', label: 'Listed', tab: 'Listed' },
  { key: 'sold', label: 'Sold', tab: 'Sold' },
  { key: 'delisted', label: 'Delisted', tab: 'Delisted' },
  { key: 'deleted', label: 'Deleted', tab: 'Deleted' },
];

const createEmptyEventGroupedTickets = (): Record<EventTicketGroupKey, Ticket[]> => ({
  pending: [] as Ticket[],
  listed: [] as Ticket[],
  sold: [] as Ticket[],
  delisted: [] as Ticket[],
  deleted: [] as Ticket[],
});

const mapDeletedPlacementRows = (rows: any[]): Ticket[] =>
  (Array.isArray(rows) ? rows : []).map((row: any) => ({
    ...row,
    e_name: row?.ticket?.event?.e_name,
    e_date: row?.ticket?.event?.e_date,
    e_address: row?.ticket?.event?.e_address,
    e_brand_name: row?.ticket?.event?.e_brand_name,
    e_status: row?.ticket?.event?.e_status,
    e_time_zone: row?.ticket?.event?.e_time_zone,
    e_date_time_zone: row?.ticket?.event?.e_date_time_zone,
    e_vl_production_id: row?.ticket?.event?.e_vl_production_id,
    e_id: row?.ticket?.event?.e_id ?? row?.ticket?.e_id?.e_id,
    t_id: row?.ticket?.t_id,
    u_id: row?.ticket?.user?.u_id,
    u_first_name: row?.ticket?.user?.u_first_name,
    u_last_name: row?.ticket?.user?.u_last_name,
    u_full_name: row?.ticket?.user?.u_full_name,
    u_email_id: row?.ticket?.user?.u_email_id,
    u_original_email: row?.ticket?.user?.u_original_email,
    u_pricing_mode: row?.ticket?.user?.u_pricing_mode,
    u_fee_percentage: row?.ticket?.user?.u_fee_percentage,
    tp_status: row?.tp_status || 'Deleted',
  }));

const getEventViewGroupKeyForStatus = (status: string): EventTicketGroupKey | null => {
  const normalizedStatus = (status || '').toLowerCase().replace(/\s/g, '');
  if (normalizedStatus === 'listingrequestcancelled') return 'delisted';
  const tab = getTabForStatus(status);
  if (tab === 'Verification') return 'pending';
  if (tab === 'Listed') return 'listed';
  if (tab === 'Sold') return 'sold';
  if (tab === 'Delisted') return 'delisted';
  if (tab === 'Deleted') return 'deleted';
  return null;
};

const rebucketEventGroupedTickets = (
  prev: Record<EventTicketGroupKey, Ticket[]>,
  tpIds: string[],
  nextStatus: string,
  nextListPrice?: number,
  nextFields: Partial<Ticket> = {},
): Record<EventTicketGroupKey, Ticket[]> => {
  const targetKey = getEventViewGroupKeyForStatus(nextStatus);
  if (!targetKey) return prev;

  const moving = new Set(tpIds);
  const next = createEmptyEventGroupedTickets();

  for (const section of EVENT_TICKET_VIEW_SECTIONS) {
    for (const ticket of prev[section.key] ?? []) {
      if (!moving.has(ticket.tp_id)) {
        next[section.key].push(ticket);
        continue;
      }
      next[targetKey].push({
        ...ticket,
        ...nextFields,
        tp_status: nextStatus,
        tp_list_price: nextListPrice !== undefined ? nextListPrice : ticket.tp_list_price,
        tp_updated_at: new Date().toISOString(),
      });
    }
  }

  for (const key of EVENT_TICKET_VIEW_SECTIONS.map(s => s.key)) {
    next[key] = sortManageTickets(next[key]);
  }
  return next;
};

const updateEventGroupedTickets = (
  prev: Record<EventTicketGroupKey, Ticket[]>,
  tpIds: string[],
  nextStatus: string,
  priceMap: Record<string, number>,
): Record<EventTicketGroupKey, Ticket[]> => {
  const moving = new Set(tpIds);
  const next = createEmptyEventGroupedTickets();
  const now = new Date().toISOString();

  for (const section of EVENT_TICKET_VIEW_SECTIONS) {
    for (const ticket of prev[section.key] ?? []) {
      const updatedTicket = moving.has(ticket.tp_id)
        ? {
            ...ticket,
            tp_status: nextStatus,
            tp_list_price: priceMap[ticket.tp_id] ?? ticket.tp_list_price,
            tp_updated_at: now,
          }
        : ticket;
      const targetKey = getEventViewGroupKeyForStatus(updatedTicket.tp_status || '');
      if (targetKey) {
        next[targetKey].push(updatedTicket);
      }
    }
  }

  for (const key of EVENT_TICKET_VIEW_SECTIONS.map(s => s.key)) {
    next[key] = sortManageTickets(next[key]);
  }
  return next;
};

const distributeEventGroupedTickets = (tickets: Ticket[]): Record<EventTicketGroupKey, Ticket[]> => {
  const byId = new Map<string, Ticket>();
  for (const ticket of tickets) {
    byId.set(ticket.tp_id, ticket);
  }
  const next = createEmptyEventGroupedTickets();
  for (const ticket of byId.values()) {
    const key = getEventViewGroupKeyForStatus(ticket.tp_status || '');
    if (key) next[key].push(ticket);
  }
  for (const section of EVENT_TICKET_VIEW_SECTIONS) {
    next[section.key] = sortManageTickets(next[section.key]);
  }
  return next;
};

const hasActiveEventSearch = (search: string) => search.trim().length >= 2;

const isLocalStatusFilterTab = (tab: string) =>
  tab === 'Sold' || tab === 'Delisted' || tab === 'Verification' || tab === 'Listed';

const isDefaultLocalStatusFilter = (tab: string, filterStatus: string): boolean => {
  switch (tab) {
    case 'Verification':
      return !filterStatus || filterStatus === 'All Verification';
    case 'Listed':
      return !filterStatus || filterStatus === 'All Listed';
    case 'Sold':
      return !filterStatus || filterStatus === 'All Sold';
    case 'Delisted':
      return !filterStatus || filterStatus === 'All Delisted';
    default:
      return true;
  }
};

type BarcodeAttachedFilter = 'all' | 'attached' | 'missing' | 'none';

const BARCODE_ATTACHED_FILTER_OPTIONS: Array<{
  value: BarcodeAttachedFilter;
  label: string;
  icon?: 'check' | 'exclamation' | 'ban';
}> = [
  { value: 'all', label: 'All barcodes' },
  { value: 'attached', label: 'Attached', icon: 'check' },
  { value: 'missing', label: 'Missing', icon: 'exclamation' },
  { value: 'none', label: 'No Barcode', icon: 'ban' },
];

const barcodeStatusFilterForQuery = (filter: BarcodeAttachedFilter): Record<string, unknown> => {
  if (filter === 'attached') return { tp_barcode_status: { _eq: 'Barcode Available' } };
  if (filter === 'none') return { tp_barcode_status: { _eq: 'Null' } };
  if (filter === 'missing') {
    return {
      _and: [
        { tp_barcode_status: { _eq: 'Barcode Pending' } },
        { _not: { tp_barcode_attached: { _eq: true } } },
        { _or: [{ tp_barcode: { _is_null: true } }, { tp_barcode: { _eq: '' } }] },
      ],
    };
  }
  return {};
};

type VlShareFilter = 'all' | 'shared' | 'unshared';

const vlShareFilterForQuery = (filter: VlShareFilter): Record<string, unknown> => {
  if (filter === 'shared') return { tp_vl_share: { _eq: true } };
  if (filter === 'unshared') return { tp_vl_share: { _eq: false } };
  return {};
};

const barcodeFilterButtonLabel = (filter: BarcodeAttachedFilter): string => {
  if (filter === 'attached') return 'Attached';
  if (filter === 'missing') return 'Missing';
  if (filter === 'none') return 'No Barcode';
  return 'Barcode Status';
};

const ticketMatchesBarcodeFilter = (
  ticket: Ticket | null | undefined,
  filter: BarcodeAttachedFilter,
): boolean => {
  if (filter === 'all') return true;
  const status = getTicketBarcodeStatus(ticket);
  if (filter === 'attached') return status === 'Barcode Available';
  if (filter === 'none') return status === 'Null';
  return status === 'Barcode Pending' && !ticketHasBarcodeAttached(ticket);
};

const filterTicketsByBarcodeStatus = (
  tickets: Ticket[],
  filter: BarcodeAttachedFilter,
): Ticket[] => {
  if (filter === 'all') return tickets;
  return tickets.filter((ticket) => ticketMatchesBarcodeFilter(ticket, filter));
};

const filterTicketsByOnHold = (tickets: Ticket[], onHoldOnly: boolean): Ticket[] => {
  if (!onHoldOnly) return tickets;
  return tickets.filter((ticket) => ticket?.on_hold === true);
};

const renderBarcodeFilterIcon = (icon?: 'check' | 'exclamation' | 'ban', size = 13) => {
  if (icon === 'check') {
    return <CheckCircle size={size} className={styles.barcodeStatusOk} aria-hidden />;
  }
  if (icon === 'exclamation') {
    return <AlertCircle size={size} className={styles.barcodeStatusMissing} aria-hidden />;
  }
  if (icon === 'ban') {
    return <Ban size={size} className={styles.barcodeStatusNull} aria-hidden />;
  }
  return null;
};

const EVENT_GROUP_TABS = new Set([
  'Verification',
  'Listed',
  'Sold',
  'Delisted',
  'Listing Request Cancelled',
  'Deleted',
]);
const MANAGE_TICKETS_TAB_STORAGE_KEY = 'manageTicketsActiveTab';

const MANAGE_TICKETS_TABS = [
  'Verification',
  'Listed',
  'Sold',
  'Delisted',
  'Listing Request Cancelled',
  'Listing Expired',
  'Unsold',
  'Deleted',
] as const;
const SUPER_USER_MANAGE_TABS = ['Listed', 'Sold', 'Delisted', 'Unsold'] as const;

const isEventGroupedTab = (tab: string): boolean => EVENT_GROUP_TABS.has(tab);

const getEventGroupedTicketsForTab = (
  tab: string,
  grouped: Record<EventTicketGroupKey, Ticket[]>,
): Ticket[] => {
  switch (tab) {
    case 'Verification':
      return grouped.pending;
    case 'Listed':
      return grouped.listed;
    case 'Sold':
      return grouped.sold;
    case 'Delisted':
      return grouped.delisted.filter(
        (ticket) => ticket.tp_status !== 'ListingRequestCancelled',
      );
    case 'Listing Request Cancelled':
      return grouped.delisted.filter(
        (ticket) => ticket.tp_status === 'ListingRequestCancelled',
      );
    case 'Deleted':
      return grouped.deleted;
    default:
      return [];
  }
};

/** Read tab-badge counts from aggregate fields bundled in any ticket-tab GraphQL response. */
const extractSiblingTabCountsFromResponse = (responseData: any): Record<string, number> => {
  const counts: Record<string, number> = {};
  const assign = (tab: string, value: unknown) => {
    if (typeof value === 'number' && !Number.isNaN(value)) {
      counts[tab] = value;
    }
  };

  // Verification counts are filtered client-side (fail-to-transfer rows are excluded),
  // so do not populate them from sibling tab responses where the aggregate count may be stale.
  assign(
    'Listed',
    responseData?.FilterListed_allTab_aggregate?.aggregate?.count ??
      responseData?.filterlisttickets_aggregate?.aggregate?.count,
  );
  assign('Sold', responseData?.FilterSoldTickets_aggregate?.aggregate?.count);
  assign('Delisted', responseData?.FilterDelisted_allTab_aggregate?.aggregate?.count ?? responseData?.filterreturntickets_aggregate?.aggregate?.count);
  assign(
    'Unsold',
    responseData?.filterUnsoldTickets_aggreagate?.aggregate?.count ??
      responseData?.filterUnsoldTickets?.aggregate?.count,
  );
  assign(
    'Listing Request Cancelled',
    responseData?.filterListingRequestCancelledTickets_aggregate?.aggregate?.count,
  );

  const expiredCount =
    responseData?.closed_placement_aggregate?.aggregate?.count ??
    (responseData?.filterreturntickets != null && responseData?.closed_orphan_placements != null
      ? responseData?.filterreturntickets_aggregate?.aggregate?.count
      : undefined);
  assign('Listing Expired', expiredCount);

  // Deleted tab count - from GET_DELETED_TICKETS query or GET_MANAGE_TICKETS deletedCount
  assign('Deleted', 
    responseData?.ticket_placement_aggregate?.aggregate?.count ?? 
    responseData?.deletedCount?.aggregate?.count
  );

  return counts;
};

/** Sold sub-filter counts from DB aggregates (no need to load all rows). */
const extractSoldStatusCountsFromResponse = (responseData: any): Record<string, number> => {
  const sold = responseData?.FilterSoldTickets_soldOpen_aggregate?.aggregate?.count ?? 0;
  const settled = responseData?.FilterSoldTickets_settled_aggregate?.aggregate?.count ?? 0;
  const inProgress = responseData?.FilterSoldTickets_inProgress_aggregate?.aggregate?.count ?? 0;
  const failed = responseData?.FilterSoldTickets_failed_aggregate?.aggregate?.count ?? 0;
  const voidedPayout = responseData?.FilterSoldTickets_voidedPayout_aggregate?.aggregate?.count ?? 0;
  const paidManuallyViaQA = responseData?.FilterSoldTickets_paidManuallyViaQA_aggregate?.aggregate?.count ?? 0;
  const qaDeniedPayout = responseData?.FilterSoldTickets_qaDeniedPayout_aggregate?.aggregate?.count ?? 0;
  const underReview = responseData?.FilterSoldTickets_underReview_aggregate?.aggregate?.count ?? 0;
  const allFromParts = sold + settled + inProgress + failed + paidManuallyViaQA + qaDeniedPayout + underReview;
  const allFromAggregate = responseData?.FilterSoldTickets_aggregate?.aggregate?.count;
  return {
    'All Sold': allFromAggregate ?? allFromParts,
    Sold: sold,
    Settled: settled,
    SettlementInProgress: inProgress,
    Failed: failed,
    Voided_Payout: voidedPayout,
    PaidManuallyViaQA: paidManuallyViaQA,
    QADeniedPayout: qaDeniedPayout,
    UnderReview: underReview,
  };
};

const extractVerificationStatusCountsFromResponse = (responseData: any): Record<string, number> => {
  const toBeVerified = responseData?.FilterManageTickets_toBeVerified_aggregate?.aggregate?.count ?? 0;
  const verified = responseData?.FilterManageTickets_verified_aggregate?.aggregate?.count ?? 0;
  const allFromParts = toBeVerified + verified;
  const allFromAggregate = responseData?.filtermanagetickets_aggregate?.aggregate?.count;
  return {
    'All Verification': allFromParts > 0 ? allFromParts : (allFromAggregate ?? 0),
    ToBeVerified: toBeVerified,
    Verified: verified,
  };
};
const extractDelistedStatusCountsFromResponse = (responseData: any): Record<string, number> => {
  const delist = responseData?.FilterDelisted_delist_aggregate?.aggregate?.count ?? 0;
  const inProgress = responseData?.FilterDelisted_delistInProgress_aggregate?.aggregate?.count ?? 0;
  const returned = responseData?.FilterDelisted_return_aggregate?.aggregate?.count ?? 0;
  const allFromParts = delist + inProgress + returned;
  const allFromAggregate =
    responseData?.FilterDelisted_allTab_aggregate?.aggregate?.count ??
    responseData?.filterreturntickets_aggregate?.aggregate?.count;
  return {
    'All Delisted': allFromParts > 0 ? allFromParts : (allFromAggregate ?? 0),
    Delist: delist,
    DelistInProgress: inProgress,
    Return: returned,
  };
};

const ALL_DELISTED_STATUSES = ['Delist', 'DelistInProgress', 'DelistRequested', 'Return'];

const getDelistedStatusesForQuery = (filterStatus: string): string[] => {
  switch (filterStatus) {
    case 'Delist':
      return ['Delist'];
    case 'DelistInProgress':
      return ['DelistInProgress', 'DelistRequested'];
    case 'Return':
      return ['Return'];
    default:
      return ALL_DELISTED_STATUSES;
  }
};

/** Cap for tabs that must load a wider set client-side (Verification fail-to-transfer filter, marketing scope). */
const FULL_TAB_CLIENT_FETCH_LIMIT = 1500;

const getManageTicketServerOrderBy = (
  sort: ManageTicketTableSort | null,
  tab?: string,
): Array<Record<string, string | undefined>> => {
  if (!sort && tab === 'Delisted') {
    return [{ tp_status: 'asc' }, ...MANAGE_TICKETS_ORDER_BY];
  }
  if (!sort) return MANAGE_TICKETS_ORDER_BY;

  const fields: Record<ManageTicketSortColumn, string> = {
    event: 'e_date',
    section: 'tp_section',
    row: 'tp_row',
    price: tab === 'Sold' ? 'tp_sold_amount' : 'tp_list_price',
    lastPriced: 'last_priced_at',
  };
  return [{ [fields[sort.column]]: sort.direction }, { tp_id: 'asc' }];
};

/** When true, the active tab total comes from the database count for that page query. */
const shouldPreferServerPagination = (ctx: {
  isEventGroupedView: boolean;
  isMailParserTpLookupView: boolean;
  isMailParserErrorView: boolean;
  isMarketingUser: boolean;
  effectiveTab: string;
}): boolean => {
  if (ctx.isEventGroupedView || ctx.isMailParserTpLookupView || ctx.isMailParserErrorView) return false;
  if (ctx.isMarketingUser) return false;
  return Boolean(ctx.effectiveTab);
};

/**
 * Manage Tickets pages by ticket group. One group is one row even when it
 * holds several seats, so the database page is a seat window and the footer
 * counts groups.
 */
const usesDirectSeatServerPagination = (ctx: {
  isEventGroupedView: boolean;
  isMailParserTpLookupView: boolean;
  isMailParserErrorView: boolean;
  isMarketingUser: boolean;
  effectiveTab: string;
}): boolean => {
  if (ctx.isEventGroupedView || ctx.isMailParserTpLookupView || ctx.isMailParserErrorView) return false;
  if (ctx.isMarketingUser) return false;
  return false;
};

/** Tabs that load a capped result set and paginate grouped display rows in the browser. */
const usesClientSidePaginationForTab = (
  _tab: string,
  ctx: { isMailParserErrorView: boolean; isMarketingUser: boolean },
) => {
  if (ctx.isMailParserErrorView || ctx.isMarketingUser) return true;
  return false;
};

/**
 * Server tabs over-fetch seats then trim to `pageSize` grouped rows.
 * When the seat aggregate fits in one oversample request, that trim would hide
 * remaining groups and report "page 1 of 1". Paginate those groups client-side instead.
 */
const serverSeatResultFitsOneFetch = (seatTotal: number, pageSize: number): boolean => {
  if (seatTotal <= 0) return false;
  return seatTotal <= getGroupedPageSeatFetchSize(pageSize);
};

const getSoldPaymentStatusForQuery = (filterStatus: string): string | null => {
  switch (filterStatus) {
    case 'Sold':
      return 'NotInitiated';
    case 'Settled':
      return 'Success';
    case 'SettlementInProgress':
      return 'Inprogress';
    case 'Failed':
      return 'Failed';
    default:
      return null;
  }
};

const getSoldPayoutTypeForQuery = (filterStatus: string): string | null => {
  if (filterStatus === 'Voided_Payout') return 'Voided_Payout';
  if (filterStatus === 'All Sold' || filterStatus === 'Sold' || filterStatus === 'Settled' || filterStatus === 'SettlementInProgress' || filterStatus === 'Failed') {
    return 'Unvoided_Payout';
  }
  return null;
};

type StripeAccountFilter = 'all' | 'due' | 'connected';

const STRIPE_ACCOUNT_FILTER_OPTIONS: Array<{ value: StripeAccountFilter; label: string }> = [
  { value: 'due', label: 'Stripe Due' },
  { value: 'connected', label: 'Stripe Connected' },
  { value: 'all', label: 'All' },
];

const getStripeAccountFilterForQuery = (filter: StripeAccountFilter) => {
  if (filter === 'due') {
    return {
      _or: [
        { u_stripe_account_is_due: { _eq: true } },
        { u_stripe_account_id: { _is_null: true } },
      ],
    };
  }
  if (filter === 'connected') {
    return {
      u_stripe_account_id: { _is_null: false },
      u_stripe_account_is_due: { _eq: false },
      u_stripe_transfers_status: { _eq: 'active' },
    };
  }
  return {};
};

const getStripeAccountFilterButtonLabel = (filter: StripeAccountFilter): string =>
  STRIPE_ACCOUNT_FILTER_OPTIONS.find((option) => option.value === filter)?.label || 'Stripe';

const STATUS_FILTER_LABELS: Record<string, string> = {
  Sold: 'Sold',
  Settled: 'Settled',
  SettlementInProgress: 'Settlement In Progress',
  Failed: 'Failed',
  DelistInProgress: 'Delist Requested',
  Delist: 'Delisted',
  Return: 'Returned',
};


// ==================== CSV EXPORT UTILITIES ====================

const escapeCSV = (field: any): string => {
  if (field === null || field === undefined) return '""';
  const stringField = String(field);
  if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
    return `"${stringField.replace(/"/g, '""')}"`;
  }
  return stringField;
};
const formatDateForCSV = (
  dateString: string,
  format: 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD' = 'MM/DD/YYYY',
  timeZone?: string | null,
): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';

    const tz = timeZone || 'America/Chicago';
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const year = parts.find((p) => p.type === 'year')?.value || '';
    const month = parts.find((p) => p.type === 'month')?.value || '';
    const day = parts.find((p) => p.type === 'day')?.value || '';

    let formatted = '';
    switch (format) {
      case 'DD/MM/YYYY':
        formatted = `${day}/${month}/${year}`;
        break;
      case 'YYYY-MM-DD':
        formatted = `${year}-${month}-${day}`;
        break;
      case 'MM/DD/YYYY':
      default:
        formatted = `${month}/${day}/${year}`;
    }

    // Force Excel to treat it as text to avoid ##### and auto-conversion
    return `="${formatted}"`;
  } catch {
    return 'N/A';
  }
};

const normalizeClientTypeLabel = (ticket: any): string => {
  const rawType = String(
    ticket?.u_pricing_mode || ticket?.user?.u_pricing_mode || ticket?.u_client_type || ''
  ).trim().toLowerCase();
  if (rawType === 'self-pricing') return 'Self Pricing';
  if (rawType === 'concierge') return 'Concierge';
  return '';
};

/** Default VL internal notes when the client has none — same Custom:N rules as CSV listing. */
const buildDefaultInternalNotes = (quantity: number | string | null | undefined): string => {
  const parsedQuantity = Number(quantity);
  if (!Number.isInteger(parsedQuantity) || parsedQuantity <= 0) {
    return '';
  }
  const start = parsedQuantity % 2 === 0 ? 2 : 1;
  const values: number[] = [];
  for (let value = start; value <= parsedQuantity; value += 2) {
    values.push(value);
  }
  return values.length > 0 ? `Custom:${values.join(',')}` : '';
};

const getTicketCsvNotes = (ticket: any, noteType: 'internal' | 'external'): string => {
  const placementField = noteType === 'internal' ? 'tp_internal_notes' : 'tp_external_notes';
  const fieldName = noteType === 'internal' ? 'internalNotes' : 'externalNotes';
  const directValue = ticket?.[placementField] ?? ticket?.[fieldName] ?? ticket?.[`${fieldName.toLowerCase()}`];
  if (directValue == null) return '';
  if (typeof directValue === 'string') return directValue.trim();
  return String(directValue).trim();
};

const notesFromUserNotesRows = (
  rows: Array<{ n_type?: string; n_content?: string }> | null | undefined,
  noteType: 'internal' | 'external',
): string => {
  if (!Array.isArray(rows) || !rows.length) return '';
  return rows
    .filter((note) => String(note?.n_type || '').toLowerCase() === noteType)
    .map((note) => String(note?.n_content || '').trim())
    .filter(Boolean)
    .join(' | ');
};

const toSeatLevelNote = (row: any): SeatLevelNote => ({
  id: String(row?.vlsn_id || ''),
  userId: String(row?.u_id || ''),
  teamId: String(row?.lc_id || ''),
  section: String(row?.section || ''),
  row: String(row?.seat_row || ''),
  seatStart: Number(row?.seat_start),
  seatEnd: Number(row?.seat_end),
  internalNote: String(row?.internal_note || ''),
  externalNote: String(row?.external_note || ''),
});
const getSeatBounds = (seat: string | number | null | undefined): { start: number; end: number } | null => {
  const normalizedSeat = String(seat ?? '')
    .trim()
    .replace(/[–—]/g, '-')
    .replace(/\s+/g, '');
  
  // Try to parse as a range first (e.g., "1-10")
  const rangeMatch = normalizedSeat.match(/^(\d+)-(\d+)$/);
  if (rangeMatch) {
    const start = Number(rangeMatch[1]);
    const end = Number(rangeMatch[2]);
    if (Number.isFinite(start) && Number.isFinite(end) && start > 0 && end > 0) {
      return { start: Math.min(start, end), end: Math.max(start, end) };
    }
  }
  
  // Try to parse as individual seats or comma-separated values
  const seats = normalizedSeat
    .split(',')
    .map(part => Number(part.trim()))
    .filter(n => Number.isFinite(n) && n > 0);
  
  if (seats.length === 0) return null;
  return { start: Math.min(...seats), end: Math.max(...seats) };
};

const parseSeatNumbersForExport = (seatNo: string | number | null | undefined): string[] => {
  if (!seatNo) return ['N/A'];
  return parseAndSortSeatNumbers(seatNo);
};

/** Manual line override wins; otherwise preserve the legacy quick-cut formula. */
const getSalesRepCommission = (ticket: Ticket): number => {
  const manualOverride = Number(ticket.tp_sales_rep_commission_override);
  if (ticket.tp_sales_rep_commission_override != null && Number.isFinite(manualOverride) && manualOverride >= 0) {
    return Math.round(manualOverride * 100) / 100;
  }
  const quickCut = Number(ticket.tp_quick_cut_amount ?? 0);
  const rate = Number(ticket.ticket_user?.sales_reps?.sr_commission_rate ?? 0);
  if (!Number.isFinite(quickCut) || quickCut <= 0 || !Number.isFinite(rate) || rate <= 0) {
    return 0;
  }
  return Math.round(((quickCut * rate) / 100) * 100) / 100;
};

const exportToCSV = (
  tickets: Ticket[],
  options: {
    filename?: string;
    includeHeaders?: boolean;
    includeSeatDetails?: boolean;
    includeReturnEmail?: boolean;
    includeCommission?: boolean;
    dateFormat?: 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD';
    delimiter?: ',' | ';' | '\t';
  } = {}
) => {
  if (!tickets || tickets.length === 0) {
    const { error } = require('../../components/shared-components/toaster').default();
    error('No tickets to export');
    return;
  }

  const {
    filename = 'tickets_export',
    includeHeaders = true,
    includeSeatDetails = false,
    includeReturnEmail = false,
    includeCommission = false,
    dateFormat = 'MM/DD/YYYY',
    delimiter = ','
  } = options;

  const rows: string[] = [];
  const baseHeaders = [
    'Event Name', 'Event Date', 'Event Time', 'Venue', 'Section', 'Row', 'Seat Numbers',
    'Status', 'Client Name', 'Client Email',
    ...(includeReturnEmail ? ['Return Email'] as const : []),
    'Client Type', 'Internal Notes', 'External Notes',
    'Price',
    ...(includeCommission ? ['Commission'] as const : []),
    'Listed Date', 'Ticket ID', 'League'
  ];
  const headers = includeSeatDetails ? [...baseHeaders, 'Individual Seats'] : baseHeaders;

  if (includeHeaders) {
    rows.push(headers.join(delimiter));
  }

  // Debug: log first ticket notes structure to help identify missing note values during export
  if (tickets && tickets.length > 0) {
    const t0: any = tickets[0];
    // eslint-disable-next-line no-console
    console.log('CSV export debug - ticket[0] user:', t0?.user, 'user_notes:', t0?.user?.user_notes, 'internalNotes:', t0?.internalNotes, 'externalNotes:', t0?.externalNotes);
  }

  tickets.forEach(ticket => {
    const clientTypeLabel = normalizeClientTypeLabel(ticket);
    const internalNotes = getTicketCsvNotes(ticket, 'internal');
    const externalNotes = getTicketCsvNotes(ticket, 'external');
    const eventTime = formatEventTimeForCsv(ticket);

    if (includeSeatDetails) {
      const seatNumbers = parseSeatNumbersForExport(ticket.tp_seat_no);
      seatNumbers.forEach(seatNum => {
        const row = [
          escapeCSV(ticket.e_name || 'N/A'),
          escapeCSV(formatDateForCSV(ticket.e_date, dateFormat, ticket.e_time_zone)),
          escapeCSV(eventTime),
          escapeCSV(ticket.e_address || 'N/A'),
          escapeCSV(ticket.tp_section || 'N/A'),
          escapeCSV(ticket.tp_row || 'N/A'),
          escapeCSV(seatNum),
          escapeCSV(ticket.tp_status || 'Unknown'),
          escapeCSV(ticket.u_full_name || `${ticket.u_first_name || ''} ${ticket.u_last_name || ''}`.trim() || 'N/A'),
          escapeCSV(ticket.u_email_id || 'N/A'),
          ...(includeReturnEmail
            ? [escapeCSV(ticket.tp_delist_requested_email?.trim() || '')]
            : []),
          escapeCSV(clientTypeLabel || ''),
          escapeCSV(internalNotes),
          escapeCSV(externalNotes),
          ticket.tp_list_price || 0,
          ...(includeCommission ? [getSalesRepCommission(ticket)] : []),
          escapeCSV(formatDateForCSV(ticket.e_date, dateFormat, ticket.e_time_zone)),
          escapeCSV(ticket.tp_id || 'N/A'),
          escapeCSV((ticket as any).league_name || 'N/A'),
          escapeCSV(seatNum)
        ];
        rows.push(row.join(delimiter));
      });
    } else {
      const row = [
        escapeCSV(ticket.e_name || 'N/A'),
        escapeCSV(formatDateForCSV(ticket.e_date, dateFormat, ticket.e_time_zone)),
        escapeCSV(eventTime),
        escapeCSV(ticket.e_address || 'N/A'),
        escapeCSV(ticket.tp_section || 'N/A'),
        escapeCSV(ticket.tp_row || 'N/A'),
        escapeCSV(ticket.tp_seat_no || 'N/A'),
        escapeCSV(ticket.tp_status || 'Unknown'),
        escapeCSV(ticket.u_full_name || `${ticket.u_first_name || ''} ${ticket.u_last_name || ''}`.trim() || 'N/A'),
        escapeCSV(ticket.u_email_id || 'N/A'),
        ...(includeReturnEmail
          ? [escapeCSV(ticket.tp_delist_requested_email?.trim() || '')]
          : []),
        escapeCSV(clientTypeLabel || ''),
        escapeCSV(internalNotes),
        escapeCSV(externalNotes),
        ticket.tp_list_price || 0,
        ...(includeCommission ? [getSalesRepCommission(ticket)] : []),
        escapeCSV(formatDateForCSV(ticket.e_date, dateFormat, ticket.e_time_zone)),
        escapeCSV(ticket.tp_id || 'N/A'),
        escapeCSV((ticket as any).league_name || 'N/A')
      ];
      rows.push(row.join(delimiter));
    }
  });

  const csvString = rows.join('\n');
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  const extension = delimiter === '\t' ? 'tsv' : 'csv';
  const timestamp = new Date().toISOString().split('T')[0];
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${timestamp}.${extension}`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const shouldIncludeReturnEmailInExport = (tab: string) =>
  tab === 'Delisted' || tab === 'Listing Request Cancelled';

const shouldIncludeCommissionInExport = (tab: string) => tab === 'Sold';

const formatEventTimeForCsv = (ticket: any): string => {
  if (!ticket?.e_date) return '';

  try {
    const formattedDate = String(ticket.e_date)
      .replace(' ', 'T')
      .replace(' +', '+');

    const parsed = new Date(formattedDate);

    if (isNaN(parsed.getTime())) return '';

    return parsed.toLocaleTimeString('en-US', {
      timeZone: ticket.e_time_zone || 'America/Chicago',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch (error) {
    return '';
  }
};

/** Venue-local YYYY-MM-DD from DB fields (not browser TZ). */
const formatVenueEventDateYmd = (
  eDate?: string | null,
  eTimeZone?: string | null,
  eDateTimeZone?: string | null,
  eTime?: string | null,
): string => {
  return formatDbEventDateYmd({
    e_date: eDate,
    e_time_zone: eTimeZone,
    e_date_time_zone: eDateTimeZone,
    e_time: eTime,
  });
};

/** Section/row/seat (+ event name/date unless Date TBD) required before Verify / PID. */
const isMissingVerifyRequiredFields = (ticket: {
  e_name?: string | null;
  e_brand_name?: string | null;
  e_date?: string | null;
  e_status?: string | null;
  e_date_time_zone?: string | null;
  eventDateRaw?: string | null;
  eventStatus?: string | null;
  event?: { e_name?: string | null; e_date?: string | null; e_status?: string | null } | null;
  ticket?: { event?: { e_name?: string | null; e_date?: string | null; e_status?: string | null } | null } | null;
  tp_section?: string | null;
  tp_row?: string | null;
  tp_seat_no?: string | number | null;
}): boolean => {
  const eventName = String(
    ticket.e_name || ticket.e_brand_name || ticket.event?.e_name || ticket.ticket?.event?.e_name || '',
  ).trim();
  const dateTbd = isEventDateTbdForVerification(ticket);
  return (
    !eventName ||
    (!dateTbd && !ticket.e_date && !ticket.eventDateRaw && !ticket.event?.e_date) ||
    !ticket.tp_section ||
    !ticket.tp_row ||
    ticket.tp_seat_no == null ||
    !String(ticket.tp_seat_no).trim()
  );
};

/**
 * VL production timestamps are wall-clock values from the catalog.
 * Never convert via browser TZ or America/Chicago (that turned 1:40 PM into 3:10 AM).
 */
const formatVlProductionDate = (value?: string | null): string => {
  const raw = String(value || '').trim();
  if (!raw) return '—';
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[2]}/${iso[3]}/${iso[1]}`;
  const us = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (us) {
    return `${us[1].padStart(2, '0')}/${us[2].padStart(2, '0')}/${us[3]}`;
  }
  return raw;
};

const formatVlProductionTime = (eventDate?: string | null, eventTime?: string | null): string => {
  if (eventTime && String(eventTime).trim()) return String(eventTime).trim();
  const raw = String(eventDate || '').trim();
  const iso = raw.match(/[T\s](\d{1,2}):(\d{2})(?::\d{2})?/);
  if (!iso) return '—';
  const hour24 = Number(iso[1]);
  const minute = iso[2];
  const ampm = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${String(hour12).padStart(2, '0')}:${minute} ${ampm}`;
};

// ==================== MAIN COMPONENT ====================

interface ManageTicketsProps {
  onPriceChange?: (tpIds: string[], price: number) => Promise<void>;
}

interface SalesRepLookupResponse {
  sales_reps: Array<{
    sr_id: string;
    sr_email: string;
  }>;
}

const ManageTickets: React.FC<ManageTicketsProps> = () => {
  // Status dropdown state for table rows
  const [openStatusDropdown, setOpenStatusDropdown] = useState<string | null>(null);
  const [seatCardStatusDropdownOpen, setSeatCardStatusDropdownOpen] = useState<Record<string, boolean>>({});
  const [seatCardStatusBySeatId, setSeatCardStatusBySeatId] = useState<Record<string, string>>({});

  // Allow only numbers in normal and decimal format for price/amount inputs
  const sanitizePriceInput = (value: string): string => {
    let sanitized = value.replace(/[^0-9.]/g, '');
    const firstDecimalIndex = sanitized.indexOf('.');
    if (firstDecimalIndex !== -1) {
      sanitized =
        sanitized.slice(0, firstDecimalIndex + 1) +
        sanitized.slice(firstDecimalIndex + 1).replace(/\./g, '');
    }
    return sanitized;
  };

  // Extracted status transitions for reuse
const getStatusTransitions = (currentStatus: string) => {
  // Normalize status value first to ensure consistent matching
  const normalizedStatus = currentStatus 
    ? ((currentStatus || '').toLowerCase().replace(/\s/g, ''))
    : '';
  
  const statusCanonicalMapLocal: Record<string, string> = {
    closed: 'Closed',
    delist: 'Delist',
    delistinprogress: 'DelistInProgress',
    delistrequested: 'DelistInProgress',
    deleted: 'Deleted',
    invalid: 'Invalid',
    list: 'List',
    listed: 'List',
    listingrequestcancelled: 'ListingRequestCancelled',
    paidmanuallyviaqa: 'PaidManuallyViaQA',
    qadeniedpayout: 'QADeniedPayout',
    underreview: 'UnderReview',
    return: 'Return',
    sold: 'Sold',
    voidedpayout: 'Voided_Payout',
    settled: 'Settled',
    settlementinprogress: 'SettlementInProgress',
    tobeverified: 'ToBeVerified',
    unsold: 'Unsold',
    verified: 'Verified'
  };
  
  const canonicalStatus = statusCanonicalMapLocal[normalizedStatus] || currentStatus;
  
  const statusTransitions: { [key: string]: { value: string, label: string, color?: string, bg?: string }[] } = {
    ToBeVerified: [
      { value: 'Verified', label: 'Verified' },
      // Non-VL: QA-only verify — no PID / Submit to VL
      { value: 'Verified_NonVL', label: 'Verified (Non-VL)' },
      { value: 'ListingRequestCancelled', label: 'Listing Request Cancelled' },
       { value: 'Deleted', label: 'Delete' } 
    ],
    Verified: [
      { value: 'List', label: 'Listed' },
      // Non-VL: publish locally — no List on Marketplace / VL share
      { value: 'List_NonVL', label: 'Listed (Non-VL)' },
      { value: 'DelistInProgress', label: 'Delist Requested' },
      { value: 'DelistInProgress_NonVL', label: 'Delist Requested (Non-VL)' },
      { value: 'Deleted', label: 'Delete' } 
    ],
    List: [
      // { value: 'DelistInProgress', label: 'Delist Requested' },
       { value: 'Delist', label: 'Delisted' },
      { value: 'Delist_NonVL', label: 'Delisted (Non-VL)' },
      { value: 'Sold', label: 'Sold' },
      { value: 'Unsold', label: 'Unsold' },
      { value: 'Unsold_NonVL', label: 'Unsold (Non-VL)' },
      { value: 'Deleted', label: 'Delete' }
            
    ],
    UnderReview: [
        { value: 'Sold', label: 'Sold' }, 
    ],
    PaidManuallyViaQA: [],
    QADeniedPayout: [
      { value: 'PaidManuallyViaQA', label: 'Paid Manually via QA' },
    ],
    DelistInProgress: [
      { value: 'Delist', label: 'Delist' },
      { value: 'Delist_NonVL', label: 'Delisted (Non-VL)' },
      { value: 'List', label: 'Listed (Undo)' },
      { value: 'List_NonVL', label: 'Listed (Non-VL Undo)' },
      { value: 'Return', label: 'Returned' },
      { value: 'Return_NonVL', label: 'Returned (Non-VL)' },
    ],
    Delist: [
      { value: 'List', label: 'Listed (Undo)' },
      { value: 'List_NonVL', label: 'Listed (Non-VL Undo)' },
      { value: 'DelistInProgress', label: 'Delist Requested' },
      { value: 'DelistInProgress_NonVL', label: 'Delist Requested (Non-VL)' },
      { value: 'Return', label: 'Returned' },
      { value: 'Return_NonVL', label: 'Returned (Non-VL)' },
      { value: 'Deleted', label: 'Delete' }
    ],
    Sold: [
      { value: 'Settled', label: 'Settled' },
      { value: 'SettlementInProgress', label: 'Settlement In Progress' },
        { value: 'PaidManuallyViaQA', label: 'Paid Manually via QA' },
      { value: 'QADeniedPayout', label: 'QA Denied Payout' },
      { value: 'UnderReview', label: 'Under Review' }
    ],
    Voided_Payout: [
      { value: 'Sold', label: 'Sold' },
      { value: 'PaidManuallyViaQA', label: 'Paid Manually via QA' },
      { value: 'Settled', label: 'Settled' },
      { value: 'SettlementInProgress', label: 'Settlement In Progress' },
    ],
    Settled: [
      { value: 'Sold', label: 'Sold' },
      { value: 'SettlementInProgress', label: 'Settlement In Progress' }
    ],
    SettlementInProgress: [
      { value: 'Sold', label: 'Sold' },
      { value: 'Settled', label: 'Settled' },
      { value: 'PaidManuallyViaQA', label: 'Paid Manually via QA' } 
    ],
    Unsold: [
      { value: 'Sold', label: 'Sold' }
    ],
    Invalid: [
      { value: 'ToBeVerified', label: 'To Be Verified' }
    ],
    ListingRequestCancelled: [
      { value: 'ToBeVerified', label: 'To Be Verified' }
    ],
    Return: [
      
    ],
    Closed: [],
    Deleted: [
      { value: 'ToBeVerified', label: 'To Be Verified' },
      { value: 'List', label: 'Listed' },
    ]
  };
  // Allow super users to delist directly from Listed (skip Delist Requested workflow)
  try {
    if (canonicalStatus === 'List' && (typeof isSuperUser !== 'undefined') && isSuperUser) {
      const existing = statusTransitions['List'] || [];
      if (!existing.some(s => s.value === 'Delist')) {
        statusTransitions['List'] = [{ value: 'Delist', label: 'Delist' }, ...existing];
      }
    }
  } catch (e) {
    // defensive: if isSuperUser isn't available at call-time, just ignore
  }
  return statusTransitions[canonicalStatus] || [];
};
  const soldPaymentStartedMessage =
    'It will update automatically when payment completes.';
  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = () => setOpenStatusDropdown(null);
    if (openStatusDropdown) {
      window.addEventListener('click', handleClick);
      return () => window.removeEventListener('click', handleClick);
    }
  }, [openStatusDropdown]);

  useEffect(() => {
    const hasOpenSeatCardDropdown = Object.values(seatCardStatusDropdownOpen).some(Boolean);
    if (!hasOpenSeatCardDropdown) return undefined;

    const handleSeatCardDropdownClick = () => {
      setSeatCardStatusDropdownOpen({});
    };

    window.addEventListener('click', handleSeatCardDropdownClick);
    return () => window.removeEventListener('click', handleSeatCardDropdownClick);
  }, [seatCardStatusDropdownOpen]);

  // --- Custom Modal States for Status Transitions ---
  const [showListedModal, setShowListedModal] = useState(false);
  const [listedModalLoading, setListedModalLoading] = useState(false);
  const [showSoldModal, setShowSoldModal] = useState(false);
  const [listedModalTicket, setListedModalTicket] = useState<Ticket | null>(null);
  const [listedPrice, setListedPrice] = useState('');
  const [soldModalTicket, setSoldModalTicket] = useState<Ticket | null>(null);
  const [soldMarketplaceId, setSoldMarketplaceId] = useState('');
  const [soldLogitixAmount, setSoldLogitixAmount] = useState('0');
  const [soldFeeMode, setSoldFeeMode] = useState<'super' | 'default'>('default');
  const [soldSuperClientPercent, setSoldSuperClientPercent] = useState<number | null>(null);
  const [soldFeeConfigReady, setSoldFeeConfigReady] = useState(false);
  const [showBulkListModal, setShowBulkListModal] = useState(false);
  const [bulkListLoading, setBulkListLoading] = useState(false);
  const [bulkListedPrices, setBulkListedPrices] = useState<Record<string, string>>({});
  const [bulkListCommonPrice, setBulkListCommonPrice] = useState('');
  const [showBulkSoldModal, setShowBulkSoldModal] = useState(false);
  const [bulkSoldLoading, setBulkSoldLoading] = useState(false);
  const [bulkSoldMarketplaceId, setBulkSoldMarketplaceId] = useState('');
  const [bulkSoldLogitixAmounts, setBulkSoldLogitixAmounts] = useState<Record<string, string>>({});
  const [bulkSoldCommonLogitixAmount, setBulkSoldCommonLogitixAmount] = useState('');
  const [bulkSoldFeeConfigByTicketId, setBulkSoldFeeConfigByTicketId] = useState<Record<string, SoldFeeConfig>>({});
  const [bulkSoldFeeConfigReady, setBulkSoldFeeConfigReady] = useState(false);
  
  const navigate = useNavigate();
  const { error: showError, success, info } = ToasterWithMessage();
  const { globalState, handleGlobalState } = useContext<any>(globalContext);
  const apolloClient = useApolloClient();
  const { data: authenticatedProfileData } = useQuery(GET_USER_PROFILE, {
    fetchPolicy: 'network-only',
  });
  const authenticatedProfileEmail = String(
    authenticatedProfileData?.get_user_profile?.[0]?.u_email_id ||
      getStoredEmailFromIdToken() ||
      globalState?.profile?.mailId ||
      '',
  ).trim().toLowerCase();
  const userRole = globalState?.profile?.userRole || localStorage.getItem("x-hasura-user-role") || "";

  const isMarketingUser = false;
  const isSuperUser = globalState?.profile?.userRole === roles.SUPER_USER;
  const canEditSoldCommission = String(userRole).toLowerCase() === roles.ADMIN;
  const isQADashboardUser = isStaffAdminEquivalent(String(userRole).toLowerCase());
  const canOverrideSettlementStatus = isStaffAdminEquivalent(
    String(localStorage.getItem("x-hasura-user-role") || userRole).toLowerCase(),
  );
  const loggedInEmail = (
    globalState?.profile?.mailId ||
    localStorage.getItem("adminMailId") ||
    ""
  ).toLowerCase().trim();
  const statusCanonicalMap: Record<string, string> = {
    closed: 'Closed',
    delist: 'Delist',
    delistinprogress: 'DelistInProgress',
    // Legacy/older records may still come with this value; treat it as DelistInProgress in UI logic.
    delistrequested: 'DelistInProgress',
    deleted: 'Deleted',
    invalid: 'Invalid',
    list: 'List',
    listed: 'List',
    listingrequestcancelled: 'ListingRequestCancelled',
    paidmanuallyviaqa: 'PaidManuallyViaQA',
    qadeniedpayout: 'QADeniedPayout',
    underreview: 'UnderReview',
    return: 'Return',
    settled: 'Settled',
    settlementinprogress: 'SettlementInProgress',
    sold: 'Sold',
    tobeverified: 'ToBeVerified',
    unsold: 'Unsold',
    verified: 'Verified',
    // UI-only sentinels: direct status without Victory Live
    verifiednonvl: 'Verified_NonVL',
    listnonvl: 'List_NonVL',
    delistnonvl: 'Delist_NonVL',
    delistinprogressnonvl: 'DelistInProgress_NonVL',
    unsoldnonvl: 'Unsold_NonVL',
    returnnonvl: 'Return_NonVL',
  };

  const normalizeStatusValue = (value: string): string => {
    const raw = (value || '').trim();
    if (!raw) return '';
    const alphaOnlyKey = raw.toLowerCase().replace(/[^a-z]/g, '');
    return statusCanonicalMap[alphaOnlyKey] || '';
  };

  const getErrorMessage = (error: any, fallback: string): string => {
    const candidateMessages = [
      error?.graphQLErrors?.[0]?.message,
      error?.networkError?.result?.errors?.[0]?.message,
      error?.networkError?.result?.errors?.[0]?.extensions?.internal?.error?.message,
      error?.networkError?.result?.error,
      error?.networkError?.result?.message,
      error?.networkError?.message,
      error?.message,
    ];
    const presentMessages = candidateMessages
      .filter((msg): msg is string => typeof msg === 'string' && msg.trim().length > 0)
      .map((msg) => msg.trim());
    if (presentMessages.some((msg) => /vl ids are not matched|vl id'?s? are mismatch/i.test(msg))) {
      return VL_ID_MISMATCH_PRICE_ERROR;
    }
    const raw = (presentMessages[0] || fallback || '').toString().trim();
    if (!raw) return fallback;
    if (/timeout of \d+ms exceeded/i.test(raw) || /aborted|timed out/i.test(raw) || /result is unknown/i.test(raw)) {
      return formatVlUnknownMessage('this action', raw.includes('UNKNOWN') ? raw : undefined);
    }

    const normalized = raw.toLowerCase();
    const isLocalDevHost =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    if (normalized.includes('failed to fetch') || normalized.includes('networkerror') || normalized.includes('load failed')) {
      // Never show localhost:4000 instructions on deployed QA/staging/prod admin.
      if (isLocalDevHost) {
        return 'Backend API unreachable (Failed to fetch). Ensure Node is running on port 4000 (npm run start:local) and REACT_APP_NODE_API_URL=http://localhost:4000.';
      }
      return 'Unable to reach the server. Please wait a moment and try again, then refresh if the change did not apply.';
    }
    if (normalized.includes('no matching ticket found in logitix')) {
      return 'No matching ticket found in Logitix for selected section/row/seat. Please verify ticket details.';
    }
    if (normalized.includes('no matching tickets found to update price')) {
      return 'No matching ticket found in Logitix for selected section/row/seat. Please verify ticket details.';
    }
    if (/vl ids are not matched|vl id'?s? are mismatch/i.test(normalized)) {
      return VL_ID_MISMATCH_PRICE_ERROR;
    }
    if (normalized.includes("we couldn't complete the delist request")) {
      return "We couldn't complete the delist request. Please verify section/row/seat or try again.";
    }
    if (normalized.includes('forbidden')) {
      return 'Operation not allowed for selected ticket(s). Please verify status/ownership and try again.';
    }
    if (normalized.includes('http exception when calling webhook')) {
      if (isLocalDevHost) {
        return 'Backend action timed out (Hasura → Node). The price may still have updated — wait a moment and refresh.';
      }
      return 'The server took too long to respond. The change may still complete — wait a moment and refresh.';
    }
    if (normalized.includes('invalid ticket')) {
      return 'Invalid ticket selection. Please refresh and try again.';
    }
    if (normalized.includes('already exist') || normalized.includes('already exists')) {
      return 'Another ticket already exists with the same section, row, and seat for this event.';
    }
    return raw;
  };

  const isBackendActionTimeoutError = (error: unknown): boolean => {
    const msg = getErrorMessage(error, '').toLowerCase();
    return (
      msg.includes('result is unknown') ||
      msg.includes('timed out') ||
      msg.includes('http exception when calling webhook') ||
      msg.includes('took too long') ||
      msg.includes('taking too long') ||
      /timeout of \d+ms exceeded/i.test(msg)
    );
  };

  const PRICE_UPDATE_TIMEOUT_INFO =
    'Victory Live result is UNKNOWN for this price update. We refreshed the list — if the new price shows, you are all set. Do not retry yet; use Sync from VL if it does not match.';

  const showVlToast = (toast: VlActionToast) => {
    if (toast.type === 'success') success(toast.message);
    else if (toast.type === 'info') info(toast.message);
    else showError(toast.message);
  };

 const resolveActionMessageType = (message: string): 'success' | 'info' | 'error' => {
  const normalized = (message || '').toLowerCase();
  if (!normalized) return 'info';

  if (normalized.includes('unknown') && normalized.includes('sync from vl')) return 'info';

  const hasSuccess = normalized.includes('success') || normalized.includes('successfully');
  const hasUndoSuccess = normalized.includes('undo delist successful');
  const hasFailure = normalized.includes('fail') ||
                     normalized.includes('error') ||
                     normalized.includes("couldn't") ||
                     normalized.includes('forbidden') ||
                     normalized.includes('no matching');
  const hasNotFound = normalized.includes('not found');
  const hasZeroUpdated = normalized.includes('0 ticket') || normalized.includes('0 tickets');

  // Specific check for undo delist success
  if (hasUndoSuccess) return 'success';
  
  // Success without any failure indicators
  if (hasSuccess && !hasFailure && !hasZeroUpdated) return 'success';
  
  // Partial success
  if (hasSuccess && hasFailure) return 'info';
  
  // Pure failure
  if (hasFailure) return 'error';
  
  // Success with not found but still worked
  if (hasSuccess && hasNotFound) return 'info';
  
  if (hasNotFound) return 'error';
  
  return 'info';
}; 
  // UI States
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return document.documentElement.getAttribute('data-theme') === 'dark';
  });
  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);
  const [filteredTickets, setFilteredTickets] = useState<Ticket[]>([]);
  const [listedTicketCosts, setListedTicketCosts] = useState<Record<string, number | null>>({});
  const [listedTicketFees, setListedTicketFees] = useState<Record<string, { fee: number | null; pricingMode: string }>>({});
  const [listedCostDrafts, setListedCostDrafts] = useState<Record<string, string>>({});
  const [savingListedCostId, setSavingListedCostId] = useState<string | null>(null);
  const [statusFilterSourceTickets, setStatusFilterSourceTickets] = useState<Ticket[]>([]);
  const [expandedGroupKeys, setExpandedGroupKeys] = useState<string[]>([]);
  const localStatusOverridesRef = useRef<Record<string, string>>({});
  const localPriceOverridesRef = useRef<Record<string, LocalPriceOverride>>({});
  /** Production ids saved in this session, keyed by event id. */
  const localVlProductionIdsByEventRef = useRef<Record<string, string>>({});
  /** Marketplace deep-link ids by VL production id (from VL catalog; not stored in DB). */
  const marketplaceLinksByProductionIdRef = useRef<
    Record<string, { stubhubId: string | null; seatgeekId: string | null }>
  >((() => {
    try {
      const raw = sessionStorage.getItem('qa_vl_marketplace_links');
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  })());
  /** Triggers re-render when marketplace links resolve (refs alone do not). */
  const [marketplaceLinksByProductionId, setMarketplaceLinksByProductionId] = useState<
    Record<string, { stubhubId: string | null; seatgeekId: string | null }>
  >(() => ({ ...marketplaceLinksByProductionIdRef.current }));
  const [vlProductionIdByEventId, setVlProductionIdByEventId] = useState<Record<string, string>>(
    {},
  );

  const persistMarketplaceLinks = useCallback(
    (next: Record<string, { stubhubId: string | null; seatgeekId: string | null }>) => {
      marketplaceLinksByProductionIdRef.current = next;
      try {
        sessionStorage.setItem('qa_vl_marketplace_links', JSON.stringify(next));
      } catch {
        // ignore quota / private mode
      }
      setMarketplaceLinksByProductionId(next);
    },
    [],
  );

  /** Re-attach PID + SH/SG from caches so a later unenriched paint cannot wipe icons. */
  const attachCachedMarketplaceFields = useCallback((tickets: Ticket[]): Ticket[] => {
    return tickets.map((ticket) => {
      const eventId = getTicketEventId(ticket);
      const productionId =
        getTicketVlProductionId(ticket, {
          ...localVlProductionIdsByEventRef.current,
          ...vlProductionIdByEventId,
        }) ||
        (eventId ? localVlProductionIdsByEventRef.current[eventId] : '') ||
        '';
      const links = productionId
        ? marketplaceLinksByProductionIdRef.current[productionId]
        : undefined;
      if (!productionId && !links) return ticket;
      return {
        ...ticket,
        ...(productionId ? { e_vl_production_id: productionId } : {}),
        ...(links
          ? {
              e_stubhub_id: links.stubhubId,
              e_seatgeek_id: links.seatgeekId,
            }
          : {}),
      };
    });
  }, [vlProductionIdByEventId]);

  const updateLocalTicketStatus = (
    tpIds: string[],
    nextStatus: string,
    nextListPrice?: number,
    targetTabOverride?: string,
    nextFields: Partial<Ticket> = {},
  ) => {
    const destinationTab = targetTabOverride || getTabForStatus(nextStatus) || activeTab;
    const keepInActiveTab = (ticket: Ticket) =>
      !tpIds.includes(ticket.tp_id) ||
      hasActiveEventSearch(eventVenueSearch) ||
      destinationTab === activeTab ||
      getTabForStatus(nextStatus) === activeTab ||
      destinationTab === getTabForStatus(ticket.tp_status || '');

    tpIds.forEach(id => {
      localStatusOverridesRef.current[id] = nextStatus;
      if (nextListPrice !== undefined && Number.isFinite(Number(nextListPrice))) {
        localPriceOverridesRef.current[id] = {
          price: Number(nextListPrice),
          lastPricedAt: new Date().toISOString(),
        };
      }
    });
    clearSeatCardStatusForTpIds(tpIds);
    setStatusFilterSourceTickets(prevTickets =>
      sortManageTickets(
        prevTickets
          .map(ticket =>
            tpIds.includes(ticket.tp_id)
              ? {
                  ...ticket,
                  ...nextFields,
                  tp_status: nextStatus,
                  tp_list_price: nextListPrice ?? ticket.tp_list_price,
                  tp_updated_at: new Date().toISOString(),
                }
              : ticket
          )
          .filter(keepInActiveTab)
      )
    );
    setFilteredTickets(prevTickets =>
      sortManageTickets(
        prevTickets
          .map(ticket =>
            tpIds.includes(ticket.tp_id)
              ? {
                  ...ticket,
                  ...nextFields,
                  tp_status: nextStatus,
                  tp_list_price: nextListPrice ?? ticket.tp_list_price,
                  tp_updated_at: new Date().toISOString()
                }
              : ticket
          )
          .filter(keepInActiveTab)
      )
    );
    if (hasActiveEventSearch(eventVenueSearch)) {
      setEventGroupedByStatus(prev =>
        rebucketEventGroupedTickets(prev, tpIds, nextStatus, nextListPrice, nextFields),
      );
    }
  };

  const updateLocalTicketStatusWithPriceMap = (
    tpIds: string[],
    nextStatus: string,
    priceMap: Record<string, number>,
    targetTabOverride?: string,
  ) => {
    const now = new Date().toISOString();
    const destinationTab = targetTabOverride || getTabForStatus(nextStatus) || activeTab;
    const keepInActiveTab = (ticket: Ticket) =>
      !tpIds.includes(ticket.tp_id) ||
      hasActiveEventSearch(eventVenueSearch) ||
      destinationTab === activeTab ||
      getTabForStatus(nextStatus) === activeTab ||
      destinationTab === getTabForStatus(ticket.tp_status || '');

    tpIds.forEach(id => {
      localStatusOverridesRef.current[id] = nextStatus;
      const mappedPrice = priceMap[id];
      if (mappedPrice !== undefined && Number.isFinite(Number(mappedPrice))) {
        localPriceOverridesRef.current[id] = {
          price: Number(mappedPrice),
          lastPricedAt: now,
        };
      }
    });
    clearSeatCardStatusForTpIds(tpIds);

    const updateTicket = (ticket: Ticket) =>
      tpIds.includes(ticket.tp_id)
        ? {
            ...ticket,
            tp_status: nextStatus,
            tp_list_price: priceMap[ticket.tp_id] ?? ticket.tp_list_price,
            tp_updated_at: now,
          }
        : ticket;

    setStatusFilterSourceTickets(prevTickets =>
      sortManageTickets(prevTickets.map(updateTicket).filter(keepInActiveTab))
    );

    setFilteredTickets(prevTickets =>
      sortManageTickets(prevTickets.map(updateTicket).filter(keepInActiveTab))
    );

    if (hasActiveEventSearch(eventVenueSearch)) {
      setEventGroupedByStatus(prev => updateEventGroupedTickets(prev, tpIds, nextStatus, priceMap));
    }
  };

  const updateLocalTicketDetails = (tpIds: string[], updates: Partial<Ticket>) => {
    if (updates.tp_status) {
      const nextListPrice =
        typeof updates.tp_list_price === 'number' ? updates.tp_list_price : undefined;
      updateLocalTicketStatus(tpIds, updates.tp_status, nextListPrice, undefined, updates);
      return;
    }

    const now = new Date().toISOString();
    const applyUpdate = (ticket: Ticket) =>
      tpIds.includes(ticket.tp_id)
        ? {
            ...ticket,
            ...updates,
            tp_updated_at: now,
          }
        : ticket;

    setStatusFilterSourceTickets(prevTickets =>
      sortManageTickets(prevTickets.map(applyUpdate))
    );

    setFilteredTickets(prevTickets =>
      sortManageTickets(prevTickets.map(applyUpdate))
    );

    if (hasActiveEventSearch(eventVenueSearch)) {
      setEventGroupedByStatus(prev => {
        const next = createEmptyEventGroupedTickets();
        for (const section of EVENT_TICKET_VIEW_SECTIONS) {
          next[section.key] = sortManageTickets(
            (prev[section.key] ?? []).map(applyUpdate)
          );
        }
        return next;
      });
    }
  };

  const updateLocalTicketPaymentStatus = (tpIds: string[], paymentStatus: string | null) => {
    const payoutStatus = 'Unvoided_Payout';
    clearSeatCardStatusForTpIds(tpIds);
    setStatusFilterSourceTickets(prevTickets =>
      sortManageTickets(
        prevTickets.map(ticket =>
          tpIds.includes(ticket.tp_id)
            ? { ...ticket, tp_payment_status: paymentStatus, ...(payoutStatus ? { tp_payout_status: payoutStatus } : {}), tp_updated_at: new Date().toISOString() }
            : ticket
        )
      )
    );
    setFilteredTickets(prevTickets =>
      sortManageTickets(
        prevTickets.map(ticket =>
          tpIds.includes(ticket.tp_id)
            ? { ...ticket, tp_payment_status: paymentStatus, ...(payoutStatus ? { tp_payout_status: payoutStatus } : {}), tp_updated_at: new Date().toISOString() }
            : ticket
        )
      )
    );
  };

  // Filter states
  const [filterStatus, setFilterStatus] = useState(() =>
    typeof window !== "undefined" && localStorage.getItem("x-hasura-user-role") === roles.SUPER_USER
      ? "All Listed"
      : "All Verification",
  );
  const [stripeAccountFilter, setStripeAccountFilter] = useState<StripeAccountFilter>('all');
  const [barcodeFilter, setBarcodeFilter] = useState<BarcodeAttachedFilter>('all');
  const [pidStatusFilter, setPidStatusFilter] = useState<PidStatusFilter>('all');
  const [vlShareFilter, setVlShareFilter] = useState<VlShareFilter>('all');
  const [vlSupportFilter, setVlSupportFilter] = useState<VlSupportFilter>('all');
  const [vlSupportOwner, setVlSupportOwner] = useState<VlSupportOwnerFilter>('company');
  const [showVlSupportOwnerDropdown, setShowVlSupportOwnerDropdown] = useState(false);
  /** Initials this admin's support tickets are tagged with, from their saved name. */
  const myVlSupportInitials = deriveOwnerInitials(
    globalState?.profile?.firstName,
    globalState?.profile?.lastName,
  );
  /** Initials to filter by; null means the whole company. */
  const resolvedVlSupportOwner: string | null =
    vlSupportOwner === 'company' ? null : vlSupportOwner === 'mine' ? myVlSupportInitials || '__none__' : vlSupportOwner;
  const [vlSupportOpenKey, setVlSupportOpenKey] = useState<string | null>(null);
  /** Listed row whose Victory Live thread is expanded, and that thread's ticket. */
  const [vlSupportThread, setVlSupportThread] = useState<{ rowKey: string; ticketId: string } | null>(null);
  /** Bulk "Contact Victory Live" for the checkbox selection (one event or one user). */
  const [vlSupportBulk, setVlSupportBulk] = useState<{ tpIds: string[]; bulk: VlSupportBulkDetails } | null>(null);
  const [vlSupportCompletingKey, setVlSupportCompletingKey] = useState<string | null>(null);
  const [vlSupportByTpId, setVlSupportByTpId] = useState<Record<string, VlSupportRequest[]>>({});
  /** False when this Hasura has no vl_support_request table yet; hides the feature instead of erroring. */
  const [vlSupportAvailable, setVlSupportAvailable] = useState(true);
  const [vlSupportReloadToken, setVlSupportReloadToken] = useState(0);

  const [pricingMethodFilter, setPricingMethodFilter] = useState<'all' | 'MANUAL' | 'FOLLOW_MARKET' | 'NONE'>('all');
  const [lastPricedFilter, setLastPricedFilter] = useState<string[]>([]);
  const [lastPricedFrom, setLastPricedFrom] = useState('');
  const [lastPricedTo, setLastPricedTo] = useState('');
  const [lastPricedCustomValue, setLastPricedCustomValue] = useState('');
  const [lastPricedCustomUnit, setLastPricedCustomUnit] = useState<'Hours' | 'Minutes' | 'Days'>('Hours');
  const [lastPricedCustomWindow, setLastPricedCustomWindow] = useState<'within' | 'outside'>('within');
  const [lastPricedCustomFilter, setLastPricedCustomFilter] = useState<{
    value: number;
    unit: 'Hours' | 'Minutes' | 'Days';
    range: { from: string; to: string };
  } | null>(null);
  const [lastPricedCustomError, setLastPricedCustomError] = useState('');
  const [onHoldFilter, setOnHoldFilter] = useState(false);
  const [onHoldCount, setOnHoldCount] = useState(0);
  const [clientType, setClientType] = useState('All');
  const userFeePercentByIdRef = useRef<Record<string, number>>({});
  const [clientTypeCounts, setClientTypeCounts] = useState({ 'All': 0, 'Self Pricing': 0, 'Concierge': 0 });
  const [clientTypeCountsLoading, setClientTypeCountsLoading] = useState(false);
  const lastClientTypeCountsQueryKeyRef = useRef('');
  const [shouldLoadUsersList, setShouldLoadUsersList] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState<string>('');
  const [activeTab, setActiveTab] = useState(() => {
    const storedTab = typeof window !== 'undefined'
      ? localStorage.getItem(MANAGE_TICKETS_TAB_STORAGE_KEY)
      : null;
    const storedRole = typeof window !== 'undefined'
      ? localStorage.getItem('x-hasura-user-role')
      : null;
    const isStoredTabAllowed = storedRole === roles.SUPER_USER
      ? SUPER_USER_MANAGE_TABS.includes(storedTab as (typeof SUPER_USER_MANAGE_TABS)[number])
      : MANAGE_TICKETS_TABS.includes(storedTab as (typeof MANAGE_TICKETS_TABS)[number]);

    if (storedTab && isStoredTabAllowed) return storedTab;
    return storedRole === roles.SUPER_USER ? 'Listed' : 'Verification';
  });
  const isSelfPricingListedView = isSuperUser && activeTab === 'Listed';
  const isSuperUserSoldView = isSuperUserSoldTab(isSuperUser, activeTab);
  const isSuperUserDelistedView = isSuperUserDelistedTab(isSuperUser, activeTab);
  const isSuperUserUnsoldView = isSuperUserUnsoldTab(isSuperUser, activeTab);
  const hideSuperUserClientColumns =
    isSuperUserSoldView || isSuperUserDelistedView || isSuperUserUnsoldView;
  const hideSuperUserClientToolbar = hideSuperUserClientColumns;
  // Separate search fields
  const [eventVenueSearch, setEventVenueSearch] = useState("");
  const [debouncedEventVenueSearch, setDebouncedEventVenueSearch] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [debouncedClientSearch, setDebouncedClientSearch] = useState("");
  // Date filter states (based on event date)
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  // Preset date filter: 'today' | 'tomorrow' | 'next7days' | null
  const [datePreset, setDatePreset] = useState<'today' | 'tomorrow' | 'next7days' | null>(null);
  const [customRangeStart, setCustomRangeStart] = useState<string>('');
  const [customRangeEnd, setCustomRangeEnd] = useState<string>('');
  const [showCustomRangeModal, setShowCustomRangeModal] = useState(false);

  // Pagination states
  const [pageOffset, setPageOffset] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [tableSort, setTableSort] = useState<ManageTicketTableSort | null>(null);
  const [sortRequestPendingKey, setSortRequestPendingKey] = useState<string | null>(null);
  const sortRequestPendingKeyRef = useRef<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  // Display groups observed in one full seat-window fetch. Drives the next
  // seat offset so paging does not skip the groups inside that window.
  const [groupsPerFullWindow, setGroupsPerFullWindow] = useState<number | null>(null);
  const [loadedSeatWindowIndex, setLoadedSeatWindowIndex] = useState(0);
  const pendingSeatWindowIndexRef = useRef(0);
  const [isLoading, setIsLoading] = useState(false);
  const [eventGroupedByStatus, setEventGroupedByStatus] = useState<Record<EventTicketGroupKey, Ticket[]>>(
    () => createEmptyEventGroupedTickets(),
  );
  const [eventGroupedLoading, setEventGroupedLoading] = useState(false);
  const [eventGroupedUnsoldCount, setEventGroupedUnsoldCount] = useState(0);
  const [eventGroupedListingExpiredCount, setEventGroupedListingExpiredCount] = useState(0);
  const [mailParserErrorLoading, setMailParserErrorLoading] = useState(false);

  // Dropdown states
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showBarcodeFilterDropdown, setShowBarcodeFilterDropdown] = useState(false);
  const [showPidStatusFilterDropdown, setShowPidStatusFilterDropdown] = useState(false);
  const [showVlShareFilterDropdown, setShowVlShareFilterDropdown] = useState(false);
  const [showVlSupportFilterDropdown, setShowVlSupportFilterDropdown] = useState(false);
  const [showStripeAccountFilterDropdown, setShowStripeAccountFilterDropdown] = useState(false);
  const [showPricingMethodFilterDropdown, setShowPricingMethodFilterDropdown] = useState(false);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showLeagueDropdown, setShowLeagueDropdown] = useState(false);
  const [showLastPricedFilterDropdown, setShowLastPricedFilterDropdown] = useState(false);
  const [showLastPricedRangePicker, setShowLastPricedRangePicker] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);

  // Seat selection states
  const [selectedSeats, setSelectedSeats] = useState<{ [key: string]: boolean }>({});
  const [selectAllSeats, setSelectAllSeats] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [wrongPidConfirm, setWrongPidConfirm] = useState<{ tpIds: string[]; tickets: Ticket[] } | null>(null);
  const [wrongPidLoading, setWrongPidLoading] = useState(false);
  const wrongPidFlowRef = useRef<'resubmit' | 'relist' | null>(null);
  const [showVlShareModal, setShowVlShareModal] = useState(false);
  const [pendingSeatNotesSave, setPendingSeatNotesSave] = useState<SeatLevelNoteSaveTarget[] | null>(null);
  const [seatNotesSaveLoading, setSeatNotesSaveLoading] = useState(false);
  const [vlShareLoading, setVlShareLoading] = useState(false);
  const [showVlSplitModal, setShowVlSplitModal] = useState(false);
  const [vlSplitLoading, setVlSplitLoading] = useState(false);
  const [vlSplitQuantity, setVlSplitQuantity] = useState('1');
  const [vlSplitGroupSize, setVlSplitGroupSize] = useState(0);
  const [vlSplitSeatPreview, setVlSplitSeatPreview] = useState('');
  const [vlSplitTpIds, setVlSplitTpIds] = useState<string[]>([]);
  const [barcodeSyncSummary, setBarcodeSyncSummary] = useState<BarcodeSyncSummary | null>(null);
  const [showBarcodeSyncSummaryModal, setShowBarcodeSyncSummaryModal] = useState(false);
  const [barcodeSyncTargetTpIds, setBarcodeSyncTargetTpIds] = useState<string[]>([]);
  const [showBarcodeSyncConfirmModal, setShowBarcodeSyncConfirmModal] = useState(false);
  const [barcodeSyncRecommendLoading, setBarcodeSyncRecommendLoading] = useState(false);
  const [barcodeSyncConfirming, setBarcodeSyncConfirming] = useState(false);
  const [showSyncFromVlScopeModal, setShowSyncFromVlScopeModal] = useState(false);
  const [syncFromVlScopeTpIds, setSyncFromVlScopeTpIds] = useState<string[]>([]);
  const [syncFromVlConfirming, setSyncFromVlConfirming] = useState(false);
  const [barcodeSyncRecommendation, setBarcodeSyncRecommendation] =
    useState<BarcodeSyncRecommendResult | null>(null);
  const [barcodeSyncRecommendError, setBarcodeSyncRecommendError] = useState<string | null>(null);
  const [showCheckBarcodesModal, setShowCheckBarcodesModal] = useState(false);
  const [checkBarcodesLoading, setCheckBarcodesLoading] = useState(false);
  const [checkBarcodesError, setCheckBarcodesError] = useState<string | null>(null);
  const [checkBarcodesResult, setCheckBarcodesResult] = useState<CheckBarcodesResult | null>(null);
  const [showConciergePriceModal, setShowConciergePriceModal] = useState(false);
  const [conciergePriceLoading, setConciergePriceLoading] = useState(false);
  const [conciergePriceResults, setConciergePriceResults] = useState<
    Array<{
      tpId: string;
      status: string;
      message?: string;
      oldPrice?: number | null;
      newPrice?: number | null;
      direction?: string;
      section?: string | null;
      row?: string | null;
      seat?: string | number | null;
      eventName?: string | null;
    }>
  >([]);
  const [conciergeRecentChanges, setConciergeRecentChanges] = useState<
    Array<{
      lpId: string;
      tpId: string;
      oldPrice: number | null;
      newPrice: number | null;
      direction: string;
      editType: string | null;
      createdAt: string | null;
      section?: string | null;
      row?: string | null;
      seat?: string | number | null;
      eventName?: string | null;
    }>
  >([]);
  const [vlEnabledMarketplaces, setVlEnabledMarketplaces] = useState<string[]>(
    () => VL_MARKETPLACE_OPTIONS.map((m) => m.id),
  );
  const [vlSharePrices, setVlSharePrices] = useState<Record<string, string>>({});
  const [vlShareSections, setVlShareSections] = useState<Record<string, string>>({});
  const [vlShareRows, setVlShareRows] = useState<Record<string, string>>({});
  const [vlShareSeats, setVlShareSeats] = useState<Record<string, string>>({});
  const [vlShareInternalNotes, setVlShareInternalNotes] = useState('');
  const [vlSharePricingMethod, setVlSharePricingMethod] = useState<'MANUAL' | 'FOLLOW_MARKET' | 'NONE' | ''>('');
  const [vlShareExternalNotes, setVlShareExternalNotes] = useState('');
  const [vlShareQaNotes, setVlShareQaNotes] = useState('');
  const [vlShareInternalNoteQuery, setVlShareInternalNoteQuery] = useState('');
  const [vlShareExternalNoteQuery, setVlShareExternalNoteQuery] = useState('');
  const [vlShareInternalNoteChoices, setVlShareInternalNoteChoices] = useState<string[]>([]);
  const [vlShareExternalNoteChoices, setVlShareExternalNoteChoices] = useState<string[]>([]);
  const [vlShareListingCustom, setVlShareListingCustom] = useState('');
  const vlShareNotesRequestRef = useRef(0);
  const vlShareNotesEditedRef = useRef(false);
  const vlShareWasParkingRef = useRef(false);
  useEffect(() => {
    const parking = isParkingPassNoteGroup(Object.values(vlShareRows));
    if (parking && !vlShareWasParkingRef.current) {
      setVlShareInternalNoteQuery(PARKING_INTERNAL_NOTE);
      setVlShareExternalNoteQuery(PARKING_EXTERNAL_NOTE);
      setVlShareInternalNotes(PARKING_INTERNAL_NOTE);
      setVlShareExternalNotes(PARKING_EXTERNAL_NOTE);
    }
    vlShareWasParkingRef.current = parking;
  }, [vlShareRows]);
  const [showListedDelistConfirm, setShowListedDelistConfirm] = useState(false);
  const [listedDelistTpIds, setListedDelistTpIds] = useState<string[]>([]);
  const [listedDelistStoredEmail, setListedDelistStoredEmail] = useState('');
  const [listedDelistProgress, setListedDelistProgress] = useState<DelistJobProgress | null>(null);
  const [showListedMarketplaceEdit, setShowListedMarketplaceEdit] = useState(false);
  const [listedMarketplaceEditTpIds, setListedMarketplaceEditTpIds] = useState<string[]>([]);
  const [listedMarketplaceEditTickets, setListedMarketplaceEditTickets] = useState<Ticket[]>([]);
  const [listedEditMarketplaces, setListedEditMarketplaces] = useState<string[]>(() =>
    VL_MARKETPLACE_OPTIONS.map((m) => m.id),
  );
  const [listedMarketplaceEditLoading, setListedMarketplaceEditLoading] = useState(false);
  const [listedSharedToggleLoading, setListedSharedToggleLoading] = useState(false);
  const [listedShareConfirm, setListedShareConfirm] = useState<{
    tpIds: string[];
    tickets: Ticket[];
    targetShared: boolean;
  } | null>(null);
  const [showSelfPricingPriceConfirm, setShowSelfPricingPriceConfirm] = useState(false);
  const [selfPricingPriceConfirmSummary, setSelfPricingPriceConfirmSummary] = useState('');
  const [selfPricingPriceConfirmCount, setSelfPricingPriceConfirmCount] = useState(1);
  const selfPricingPriceConfirmActionRef = useRef<(() => Promise<void>) | null>(null);
  const [showNonVlStatusConfirm, setShowNonVlStatusConfirm] = useState(false);
  const [nonVlStatusConfirmSummary, setNonVlStatusConfirmSummary] = useState('');
  const nonVlStatusConfirmActionRef = useRef<(() => Promise<void>) | null>(null);
  const skipNonVlStatusConfirmRef = useRef(false);
  const [showVlProductionMapModal, setShowVlProductionMapModal] = useState(false);
  const [vlProductionMapLoading, setVlProductionMapLoading] = useState(false);
  const [vlProductionSearchLoading, setVlProductionSearchLoading] = useState(false);
  const [vlProductionMismatch, setVlProductionMismatch] = useState<ProductionMismatchDetails | null>(null);
  const [vlProductionSearchQuery, setVlProductionSearchQuery] = useState('');
  const [vlProductionSearchResults, setVlProductionSearchResults] = useState<
    Array<{
      productionId: string;
      eventName: string;
      venueName: string;
      eventDate: string;
      eventTime?: string;
      stubhubId?: string | null;
      seatgeekId?: string | null;
      score: number;
      acceptable?: boolean;
      isSuggested?: boolean;
    }>
  >([]);
  const [vlSelectedProductionId, setVlSelectedProductionId] = useState('');
  const [vlEnteredProductionId, setVlEnteredProductionId] = useState('');
  const [vlAddPidTab, setVlAddPidTab] = useState<'search' | 'enter'>('search');
  const [vlPendingSubmitTpIds, setVlPendingSubmitTpIds] = useState<string[]>([]);
  const [vlProductionModalMode, setVlProductionModalMode] = useState<
    'confirm' | 'retry-submit' | 'add-pid'
  >('retry-submit');
  // Action menu states
  const [openActionMenu, setOpenActionMenu] = useState<string | null>(null);
  const [openSeatActionMenu, setOpenSeatActionMenu] = useState<string | null>(null);

  // Price modal states
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [selectedTicketForPrice, setSelectedTicketForPrice] = useState<Ticket | null>(null);
  const [newPrice, setNewPrice] = useState<string>('');
  const [selectedTpIds, setSelectedTpIds] = useState<string[]>([]);
  const [cardFilter, setCardFilter] = useState<string | null>(null);
  const [selectedMarketplaceId, setSelectedMarketplaceId] = useState<string>('');
  const [priceConfirmationFlow, setPriceConfirmationFlow] = useState<
    'single' | 'listed' | 'bulkPrice' | 'bulkList' | 'bulkStatus' | null
  >(null);
  const pendingFinalPriceRef = useRef<number>(0);
  const pendingPriceTpIdsRef = useRef<string[]>([]);
  const [priceUpdatePendingIds, setPriceUpdatePendingIds] = useState<ReadonlySet<string>>(() => new Set());
  const markPriceUpdatePending = useCallback((ids: string[], pending: boolean) => {
    if (!ids.length) return;
    setPriceUpdatePendingIds((current) => {
      const next = new Set(current);
      ids.forEach((id) => {
        if (!id) return;
        if (pending) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  }, []);
  /** When true, mutation onCompleted/onError hooks skip per-item toasts (bulk flows show one summary toast). */
  const suppressListPublishChainToastsRef = useRef(false);
  const withSuppressedBulkMutationToasts = async <T,>(operation: () => Promise<T>): Promise<T> => {
    suppressListPublishChainToastsRef.current = true;
    try {
      return await operation();
    } finally {
      // Apollo onCompleted/onError can run just after the mutate promise settles — keep suppress for a tick.
      await new Promise((resolve) => setTimeout(resolve, 0));
      suppressListPublishChainToastsRef.current = false;
    }
  };
  /** `markAsSold`: tp_id for onCompleted (set before mutate; cleared after UI work). */
  const markAsSoldPendingTpIdRef = useRef<string | null>(null);
  /** Prevents duplicate success UI if Apollo invokes `onCompleted` more than once. */
  const markAsSoldUiHandledRef = useRef(false);
  const refreshTicketsTableRef = useRef<() => void | Promise<unknown>>(() => undefined);
  const processQueryDataForTableRef = useRef<(responseData: any, tab: string) => void>(() => undefined);
  const queryFetchIdRef = useRef(0);
  const prevTicketsQueryKeyRef = useRef('');
  const effectiveTabRef = useRef(activeTab);
  const hasAppliedActiveTabDataRef = useRef(false);
  const eventGroupedFetchIdRef = useRef(0);
  const refetch = useCallback(() => {
    refreshTicketsTableRef.current?.();
  }, []);

  const refreshListedTicketsAfterPriceChange = () => {
    const filterActive =
      lastPricedFilter.length > 0 ||
      Boolean(lastPricedFrom || lastPricedTo) ||
      Boolean(lastPricedCustomFilter);
    if (!filterActive) return;
    if (lastPricedCustomFilter) {
      const range = getLastPricedCustomRange(
        lastPricedCustomFilter.value,
        lastPricedCustomFilter.unit,
        Date.now(),
        lastPricedCustomWindow,
      );
      if (range) {
        setLastPricedCustomFilter((current) => (current ? { ...current, range } : current));
      }
    }
    void refetch();
  };

  // Card detail modal states
  const [showCardDetailModal, setShowCardDetailModal] = useState<string | null>(null);
  const showCardDetailModalRef = useRef<string | null>(null);
  const [cardDetailTickets, setCardDetailTickets] = useState<Ticket[]>([]);
  const [cardDetailLoading, setCardDetailLoading] = useState(false);
  const cardDetailRequestIdRef = useRef(0);
// Add these with your other state declarations (around line 300)
const [bulkSelectedTicketIds, setBulkSelectedTicketIds] = useState<Set<string>>(new Set());
const [bulkActionType, setBulkActionType] = useState<string>('');
const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
const [bulkSelectedTickets, setBulkSelectedTickets] = useState<Ticket[]>([]);
const [bulkNewStatus, setBulkNewStatus] = useState('');
const [bulkStatusListingPrices, setBulkStatusListingPrices] = useState<Record<string, string>>({});
const [bulkStatusListingCommonPrice, setBulkStatusListingCommonPrice] = useState('');
const [bulkStatusChangeLoading, setBulkStatusChangeLoading] = useState(false);
// Bulk price change states
const [showBulkPriceModal, setShowBulkPriceModal] = useState(false);
const [bulkPriceCommon, setBulkPriceCommon] = useState('');
const [bulkPriceIndividual, setBulkPriceIndividual] = useState<Record<string, string>>({});
const [bulkPriceLoading, setBulkPriceLoading] = useState(false);
/** Table overlay + blocks interaction while any status API is in flight. */
const [statusTableBusyCount, setStatusTableBusyCount] = useState(0);
const [statusTableBusyMessage, setStatusTableBusyMessage] = useState('Updating ticket status…');

const beginStatusTableBusy = (message = 'Updating ticket status…') => {
  setStatusTableBusyMessage(message);
  setStatusTableBusyCount((count) => count + 1);
};

const endStatusTableBusy = () => {
  setStatusTableBusyCount((count) => Math.max(0, count - 1));
};

const bumpTabCountsRefreshRef = useRef<() => void>(() => {});

const syncTicketsAfterStatusChange = async () => {
  const refresh = refreshTicketsTableRef.current;
  const tabForRefresh = effectiveTabRef.current;
  if (refresh) {
    const result = await refresh();
    if (result && typeof result === 'object' && 'data' in result && result.data) {
      processQueryDataForTableRef.current?.(result.data, tabForRefresh);
    }

  }
  await fetchTrackingCounts();
  bumpTabCountsRefreshRef.current();
  await fetchDeletedCount();
};

const actionInFlightRef = useRef(false);

const beginManagedAction = () => {
  if (actionInFlightRef.current) return false;
  actionInFlightRef.current = true;
  return true;
};

const endManagedAction = () => {
  actionInFlightRef.current = false;
};

const clearSelectionState = useCallback(() => {
  setSelectedSeats({});
  setSelectAllSeats(false);
  setShowBulkActions(false);
  setSelectedTpIds([]);
  setBulkSelectedTicketIds(new Set());
  setBulkSelectedTickets([]);
  setOpenActionMenu(null);
  setOpenStatusDropdown(null);
}, []);

useEffect(() => {
  showCardDetailModalRef.current = showCardDetailModal;
}, [showCardDetailModal]);

  /** Fail-to-transfer modal: remove row + count immediately so UI does not wait on fetchTrackingCounts (auto-expire, etc.). */
  const removeTicketFromFailTransferModal = (tpId: string) => {
    if (showCardDetailModalRef.current !== 'FAILED_TRANSFER') return;
    setCardDetailTickets(prev => prev.filter(t => t.tp_id !== tpId));
    setTrackingCounts(prev => ({
      ...prev,
      failedTransfer: Math.max(0, (prev.failedTransfer ?? 0) - 1),
    }));
    setFailedTransferTicketIds(prev => {
      const next = new Set(prev);
      next.delete(tpId);
      return next;
    });
  };

  /** Optimistic row patch while Fail-to-transfer modal is open (revert on API error). */
  const patchFailTransferModalRow = (tpId: string, patch: Partial<Ticket>) => {
    if (showCardDetailModalRef.current !== 'FAILED_TRANSFER') return;
    setCardDetailTickets(prev =>
      prev.map(t => (t.tp_id === tpId ? { ...t, ...patch } : t))
    );
  };

  // Edit ticket modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTicketForEdit, setSelectedTicketForEdit] = useState<Ticket | null>(null);
  const [editDetailsTpIds, setEditDetailsTpIds] = useState<string[]>([]);
  const [editDetailsTickets, setEditDetailsTickets] = useState<Ticket[]>([]);
  const [editDetailsLoading, setEditDetailsLoading] = useState(false);
  const [editFormData, setEditFormData] = useState({
    section: '',
    row: '',
    setDetails: '',
    seatStart: '',
    seatEnd: '',
    isGroupEdit: false,
    internalNotes: '',
    externalNotes: '',
  });
  const [editInternalNoteChoices, setEditInternalNoteChoices] = useState<string[]>([]);
  const [editExternalNoteChoices, setEditExternalNoteChoices] = useState<string[]>([]);
  const editDetailsNotesRequestRef = useRef(0);
  const editDetailsNotesEditedRef = useRef(false);
  const editDetailsNotesBaselineRef = useRef({ internalNotes: '', externalNotes: '' });

  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [selectedTicketForCommission, setSelectedTicketForCommission] = useState<Ticket | null>(null);
  const [commissionOverrideInput, setCommissionOverrideInput] = useState('');
  const [commissionOverrideLoading, setCommissionOverrideLoading] = useState(false);

  // Pricing method modal states
  const [showPricingMethodModal, setShowPricingMethodModal] = useState(false);
  const [selectedTicketForPricingMethod, setSelectedTicketForPricingMethod] = useState<Ticket | null>(null);
  const [pricingMethodTpIds, setPricingMethodTpIds] = useState<string[]>([]);
  const [pricingMethodLoading, setPricingMethodLoading] = useState(false);
  const [pricingMethodFormData, setPricingMethodFormData] = useState<'MANUAL' | 'FOLLOW_MARKET' | 'NONE' | ''>('');

  const closeEditModal = () => {
    if (editDetailsLoading) return;
    setShowEditModal(false);
    setSelectedTicketForEdit(null);
    setEditDetailsTpIds([]);
    setEditDetailsTickets([]);
    setEditFormData({
      section: '',
      row: '',
      setDetails: '',
      seatStart: '',
      seatEnd: '',
      isGroupEdit: false,
      internalNotes: '',
      externalNotes: '',
    });
    setEditInternalNoteChoices([]);
    setEditExternalNoteChoices([]);
    setEditDetailsLoading(false);
  };

  const closeCommissionModal = () => {
    if (commissionOverrideLoading) return;
    setShowCommissionModal(false);
    setSelectedTicketForCommission(null);
    setCommissionOverrideInput('');
  };

  const closePricingMethodModal = () => {
    if (pricingMethodLoading) return;
    setShowPricingMethodModal(false);
    setSelectedTicketForPricingMethod(null);
    setPricingMethodTpIds([]);
    setPricingMethodFormData('');
    setPricingMethodLoading(false);
  };

  // Status change modal states
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);
  const [selectedTicketForStatus, setSelectedTicketForStatus] = useState<Ticket | null>(null);
  const [newStatus, setNewStatus] = useState('');

  // Delete confirmation modal state
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedTicketForDelete, setSelectedTicketForDelete] = useState<Ticket | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const closeStatusModal = () => {
    setShowStatusModal(false);
    setSelectedTicketForStatus(null);
    setNewStatus('');
    setStatusChangeLoading(false);
  };

  // View details modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTicketForView, setSelectedTicketForView] = useState<Ticket | null>(null);

  // Price change logs modal states
  const [showPriceLogsModal, setShowPriceLogsModal] = useState(false);
  const [selectedTicketForPriceLogs, setSelectedTicketForPriceLogs] = useState<Ticket | null>(null);
  const [priceLogsTpIds, setPriceLogsTpIds] = useState<string[]>([]);


  // Export modal states
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    includeHeaders: true,
    includeSeatDetails: false,
    dateFormat: 'MM/DD/YYYY' as 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD',
    delimiter: ',' as ',' | ';' | '\t'
  });

  // Tracking card counts
  const [trackingCounts, setTrackingCounts] = useState({
    listingExpired: 0,
    failedTransfer: 0
  });

  // Set of tp_ids that are in "failed transfer" state (ToBeVerified 48+ hrs)
  const [failedTransferTicketIds, setFailedTransferTicketIds] = useState<Set<string>>(new Set<string>());

  // Tab counts — active tab from row query; inactive badges from GET_SIBLING_TAB_COUNTS
  const [tabCounts, setTabCounts] = useState<Record<string, number>>({
    Verification: 0,
    Verified: 0,
    Listed: 0,
    Sold: 0,
    Delisted: 0,
    'Listing Request Cancelled': 0,
    'Listing Expired': 0,
    Unsold: 0,
    Deleted: 0,
  });
  const [tabCountsRefreshNonce, setTabCountsRefreshNonce] = useState(0);

  useEffect(() => {
    bumpTabCountsRefreshRef.current = () => setTabCountsRefreshNonce((nonce) => nonce + 1);
  }, []);
// Add these helper functions after your existing helper functions (around line 800)

const getSoldStatusFilterValue = (ticket: Ticket): 'Sold' | 'Settled' | 'SettlementInProgress' | 'Failed' | 'Voided_Payout' | 'PaidManuallyViaQA' | 'QADeniedPayout' | 'UnderReview' | null => {
  const ticketStatus = normalizeStatusValue(ticket.tp_status || '');
  if (ticketStatus === 'PaidManuallyViaQA' || ticketStatus === 'QADeniedPayout' || ticketStatus === 'UnderReview') {
    return ticketStatus;
  }
  if (ticketStatus !== 'Sold') return null;
  if (ticket.tp_payout_status === 'Voided_Payout') return 'Voided_Payout';
  if (ticket.tp_payment_status === 'Success') return 'Settled';
  if (ticket.tp_payment_status === 'Inprogress') return 'SettlementInProgress';
  if (ticket.tp_payment_status === 'Failed') return 'Failed';
  return 'Sold';
};

const getTicketTransitionStatus = (ticket: Ticket): string => {
  if (normalizeStatusValue(ticket.tp_status || '') !== 'Sold') {
    return ticket.tp_status || '';
  }
  const soldFilterValue = getSoldStatusFilterValue(ticket);
  if (soldFilterValue === 'Failed') return 'Sold';
  return soldFilterValue ?? 'Sold';
};

const getTicketStatusTransitions = (ticket: Ticket): { value: string; label: string }[] =>
  getStatusTransitions(getTicketTransitionStatus(ticket));

const getPaymentStatusForSettlementOverride = (status: string): string | null | undefined => {
  const normalized = normalizeStatusValue(status);
  if (normalized === 'SettlementInProgress') return 'Inprogress';
  if (normalized === 'Settled') return 'Success';
  if (normalized === 'Sold') return null;
  return undefined;
};

const getDelistedStatusFilterValue = (ticket: Ticket): 'DelistInProgress' | 'Delist' | 'Return' | null => {
  const status = ticket.tp_status || '';
  if (status === 'Delist' || status === 'Return') return status;
  if (status === 'DelistInProgress' || status === 'DelistRequested') return 'DelistInProgress';
  return null;
};

const getLocalStatusFilterValue = (ticket: Ticket, tab: string): string | null => {
  if (tab === 'Verification') {
    if (ticket.tp_status === 'ToBeVerified') return 'ToBeVerified';
    if (ticket.tp_status === 'Verified') return 'Verified';
    return null;
  }
  if (tab === 'Listed') {
    return ticket.tp_status === 'List' ? 'List' : null;
  }
  if (tab === 'Sold') return getSoldStatusFilterValue(ticket);
  if (tab === 'Delisted') return getDelistedStatusFilterValue(ticket);
  return null;
};

const applyLocalStatusFilters = (tickets: Ticket[], tab: string, selectedFilters: string[]): Ticket[] => {
  if (!isLocalStatusFilterTab(tab) || !selectedFilters.length) return tickets;

  const selected = new Set(selectedFilters);
  return tickets.filter((ticket) => {
    const filterValue = getLocalStatusFilterValue(ticket, tab);
    return filterValue ? selected.has(filterValue) : false;
  });
};

const getDefaultLocalStatusFilterByTab = (tab: string): string => {
  switch (tab) {
    case 'Verification': return 'All Verification';
    case 'Listed': return 'All Listed';
    case 'Sold': return 'All Sold';
    case 'Delisted': return 'All Delisted';
    default: return '';
  }
};

const getLocalStatusFiltersFromFilterStatus = (tab: string, filterStatus: string): string[] => {
  if (!isLocalStatusFilterTab(tab)) return [];
  if (!filterStatus || filterStatus === getDefaultLocalStatusFilterByTab(tab)) return [];
  return [filterStatus];
};

/** Server-paginated tabs only load one page in memory; dropdown counts must use aggregate totals. */
const applyAggregateStatusCounts = (
  counts: Record<string, number>,
  tab: string,
  totalCount: number,
): Record<string, number> => {
  const next = { ...counts };
  switch (tab) {
    case 'Verification':
      // Sub-status counts stay from loaded rows; Verification is client-paginated.
      next['All Verification'] = totalCount;
      break;
    case 'Listed':
      // Listed has a single status ("List"); both dropdown rows must match the aggregate total.
      next['All Listed'] = totalCount;
      next['List'] = totalCount;
      break;
    case 'Sold':
      next['All Sold'] = totalCount;
      break;
    case 'Delisted':
      next['All Delisted'] = totalCount;
      break;
    case 'Unsold':
      next['All Unsold'] = totalCount;
      next['Unsold'] = totalCount;
      break;
    case 'Listing Request Cancelled':
      next['All Listing Request Cancelled'] = totalCount;
      next['ListingRequestCancelled'] = totalCount;
      break;
    case 'Listing Expired':
      next['All Expired'] = totalCount;
      next['Closed'] = totalCount;
      break;
    case 'Deleted':
      next['All Deleted'] = totalCount;
      next['Deleted'] = totalCount;
      break;
    default:
      break;
  }
  return next;
};

// Check if tickets can be bulk updated (same event, section, row)
const canBulkUpdateTickets = (ticketIds: string[]): { allowed: boolean; reason?: string; groupedTickets?: Map<string, Ticket[]> } => {
  if (ticketIds.length === 0) {
    return { allowed: false, reason: 'No tickets selected' };
  }
  
  const selectedTicketsList = ticketIds
    .map((id) =>
      filteredTickets.find((ticket) => ticket.tp_id === id) ??
      statusFilterSourceTickets.find((ticket) => ticket.tp_id === id),
    )
    .filter((ticket): ticket is Ticket => Boolean(ticket));
  
  // Group by event_id, section, row, and client — never merge two users' seats.
  const groupKey = (ticket: Ticket) =>
    `${ticket.e_id}|${(ticket.tp_section || '').toLowerCase().trim()}|${(ticket.tp_row || '').toLowerCase().trim()}|${ticket.u_id || ''}`;
  const groups = new Map<string, Ticket[]>();
  
  for (const ticket of selectedTicketsList) {
    const key = groupKey(ticket);
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(ticket);
  }
  
  if (groups.size > 1) {
    return { 
      allowed: false, 
      reason: `Tickets must be from the same client, event, section, and row. Found ${groups.size} different groups.`,
      groupedTickets: groups
    };
  }
  
  return { allowed: true, groupedTickets: groups };
};

// Get available status transitions for bulk action
const getBulkStatusTransitions = (tickets: Ticket[]): { value: string; label: string }[] => {
  if (tickets.length === 0) return [];
  
  // Get all possible statuses from all tickets
  const allTransitions = new Map<string, { value: string; label: string }>();
  
  for (const ticket of tickets) {
    const transitions = getTicketStatusTransitions(ticket);
    for (const trans of transitions) {
      if (!allTransitions.has(trans.value)) {
        allTransitions.set(trans.value, trans);
      }
    }
  }
  
  // Remove transitions that are not common to all tickets
  const result: { value: string; label: string }[] = [];
  for (const [value, trans] of allTransitions) {
    let isCommon = true;
    for (const ticket of tickets) {
      const ticketTransitions = getTicketStatusTransitions(ticket);
      const hasTransition = ticketTransitions.some(t => t.value === value);
      if (!hasTransition) {
        isCommon = false;
        break;
      }
    }
    if (isCommon) {
      result.push(trans);
    }
  }
  
  return result;
};

const getDefaultBulkStatusListingPrices = (tickets: Ticket[]): Record<string, string> =>
  Object.fromEntries(
    tickets.map((t) => {
      const price = Number(t.tp_list_price);
      return [
        t.tp_id,
        Number.isFinite(price) && price > 0 ? String(roundToFixed(price, 2)) : '',
      ];
    }),
  );

const areAllBulkStatusListingPricesValid = (tickets: Ticket[], prices: Record<string, string>): boolean =>
  tickets.every(ticket => {
    const value = String(prices[ticket.tp_id] || '').trim();
    const num = parseFloat(value);
    return !!value && !isNaN(num) && num > 0;
  });

const resetBulkSoldState = () => {
  setBulkSoldMarketplaceId('');
  setBulkSoldLogitixAmounts({});
  setBulkSoldCommonLogitixAmount('');
  setBulkSoldFeeConfigReady(false);
  setBulkSoldFeeConfigByTicketId({});
  setBulkSoldLoading(false);
};

const closeBulkStatusModal = () => {
  setShowBulkStatusModal(false);
  setBulkNewStatus('');
  setBulkStatusListingPrices({});
  setBulkStatusListingCommonPrice('');
  resetBulkSoldState();
};

const openBulkPriceModal = (ticketIds = Array.from(bulkSelectedTicketIds)) => {
  if (!ticketIds.length) {
    showError('No tickets selected');
    return;
  }

  const uniqueIds = [...new Set(ticketIds.map((id) => String(id || '').trim()).filter(Boolean))];
  const validation = canBulkUpdateTickets(uniqueIds);
  if (!validation.allowed) {
    showError(validation.reason || 'Cannot change price for selected tickets');
    return;
  }

  const displayTickets = getManageTicketDisplayTickets(manageTicketTableRows);
  const selectedTickets = uniqueIds
    .map(
      (id) =>
        filteredTickets.find((ticket) => ticket.tp_id === id) ??
        statusFilterSourceTickets.find((ticket) => ticket.tp_id === id) ??
        displayTickets.find((ticket) => ticket.tp_id === id),
    )
    .filter((ticket): ticket is Ticket => Boolean(ticket));

  if (!selectedTickets.length) {
    showError('Selected tickets are not available for bulk pricing');
    return;
  }

  setBulkSelectedTickets(selectedTickets);
  setBulkPriceIndividual(
    Object.fromEntries(
      selectedTickets.map(t => [t.tp_id, t.tp_list_price != null ? String(t.tp_list_price) : ''])
    )
  );
  setBulkPriceCommon('');
  setShowBulkPriceModal(true);
};

const closeBulkPriceModal = () => {
  if (bulkPriceLoading) return;
  setShowBulkPriceModal(false);
  setBulkPriceCommon('');
  setBulkPriceIndividual({});
  setBulkPriceLoading(false);
};

const requiresPriceChangeConfirmation = (currentPrice: unknown, proposedPrice: number): boolean => {
  const current = Number(currentPrice);
  return current > 0 && Math.abs(proposedPrice - current) > current * 0.2;
};

const getLargePriceChangeDirection = (
  currentPrice: unknown,
  proposedPrice: number,
): 'increase' | 'decrease' | null => {
  if (!requiresPriceChangeConfirmation(currentPrice, proposedPrice)) return null;
  return Number(proposedPrice) > Number(currentPrice) ? 'increase' : 'decrease';
};

const getPriceChangeWarningNote = (
  changes: Array<{ currentPrice: unknown; proposedPrice: number }>,
): string => {
  const directions = new Set(
    changes
      .map(({ currentPrice, proposedPrice }) => getLargePriceChangeDirection(currentPrice, proposedPrice))
      .filter((direction): direction is 'increase' | 'decrease' => direction != null),
  );
  if (directions.has('increase') && directions.has('decrease')) {
    return 'You are changing the price for one or more tickets by more than 20% and more than 20% less. Review the details before proceeding.';
  }
  if (directions.has('decrease')) {
    return 'You are changing the price for one or more tickets by less than 20% . Review the details before proceeding.';
  }
  return 'You are changing the price for one or more tickets by more than 20%. Review the details before proceeding.';
};

const handleApplyCommonBulkPrice = () => {
  const value = String(bulkPriceCommon).trim();
  const priceNum = parseFloat(value);
  if (!value || isNaN(priceNum) || priceNum < 0) {
    showError('Enter a valid price to apply to all tickets');
    return;
  }
  setBulkPriceIndividual(
    Object.fromEntries(bulkSelectedTickets.map(ticket => [ticket.tp_id, String(priceNum)]))
  );
};

const executeBulkPriceConfirm = async (confirmed = false) => {
  if (bulkPriceLoading) return;
  if (!bulkSelectedTickets.length) {
    showError('No tickets selected');
    return;
  }

  const validation = canBulkUpdateTickets(bulkSelectedTickets.map((ticket) => ticket.tp_id));
  if (!validation.allowed) {
    showError(validation.reason || 'Cannot change price for selected tickets');
    return;
  }

  const invalidTicket = bulkSelectedTickets.find(ticket => {
    const value = String(bulkPriceIndividual[ticket.tp_id] || '').trim();
    const num = parseFloat(value);
    return !value || isNaN(num) || num < 0;
  });

  if (invalidTicket) {
    showError('All selected tickets must have a valid price.');
    return;
  }

  const hasLargePriceChange = bulkSelectedTickets.some(ticket =>
    requiresPriceChangeConfirmation(
      ticket.tp_list_price,
      Number(parseFloat(String(bulkPriceIndividual[ticket.tp_id] || '').trim())),
    ),
  );
  if (hasLargePriceChange && !confirmed) {
    setPriceConfirmationFlow('bulkPrice');
    return;
  }

  beginStatusTableBusy('Updating ticket prices…');
  setBulkPriceLoading(true);
  const bulkPriceTpIds = bulkSelectedTickets.map((ticket) => ticket.tp_id);
  markPriceUpdatePending(bulkPriceTpIds, true);

  try {
    await withSuppressedBulkMutationToasts(async () => {
      const tpIdsByPrice = new Map<number, string[]>();

      bulkSelectedTickets.forEach((ticket) => {
        const priceNum = Number(parseFloat(String(bulkPriceIndividual[ticket.tp_id] || '').trim()));
        const existing = tpIdsByPrice.get(priceNum) || [];
        existing.push(ticket.tp_id);
        tpIdsByPrice.set(priceNum, existing);
      });

      const results: Array<{
        status: 'fulfilled' | 'rejected';
        tp_ids: string[];
        price: number;
        reason?: unknown;
        message?: string;
      }> = [];

      // One Logitix BulkUpdate per price group — parallel per-ticket calls split VL seat groups.
      for (const [priceNum, tp_ids] of tpIdsByPrice.entries()) {
        try {
          const res = await editTicketPrice({
            variables: {
              tp_ids,
              tp_list_price: priceNum,
            },
          });
          const message = String(res?.data?.editTicketPriceBySuperUser?.message || '').trim();
          if (!isVictoryLivePriceUpdateSuccess(message)) {
            markPriceUpdatePending(tp_ids, false);
            results.push({
              status: 'rejected',
              tp_ids,
              price: priceNum,
              reason: new Error(priceUpdateFailureMessage(message)),
            });
          } else {
            results.push({ status: 'fulfilled', tp_ids, price: priceNum, message });
          }
        } catch (error) {
          markPriceUpdatePending(tp_ids, false);
          results.push({ status: 'rejected', tp_ids, price: priceNum, reason: error });
        }
      }

      const successIds = results
        .filter((result) => result.status === 'fulfilled')
        .flatMap((result) => result.tp_ids.map((tp_id) => ({ tp_id, price: result.price })));
      const failedResults = results.filter((result) => result.status === 'rejected');
      const failedCount = failedResults.reduce((sum, result) => sum + result.tp_ids.length, 0);

      if (successIds.length > 0) {
        const now = new Date().toISOString();
        successIds.forEach(({ tp_id, price }) => {
          localPriceOverridesRef.current[tp_id] = { price, lastPricedAt: now };
        });
        const updatedPriceByTpId = new Map(successIds.map(({ tp_id, price }) => [tp_id, price]));
        const updateTicketPrice = (ticket: Ticket): Ticket => {
          const price = updatedPriceByTpId.get(ticket.tp_id);
          if (price === undefined) return ticket;
          return {
            ...ticket,
            tp_list_price: price,
            last_priced_at: now,
          };
        };
        setFilteredTickets(prevTickets =>
          prevTickets.map(updateTicketPrice)
        );
        setStatusFilterSourceTickets(prevTickets =>
          prevTickets.map(updateTicketPrice)
        );
        if (hasActiveEventSearch(eventVenueSearch)) {
          setEventGroupedByStatus((prev) => {
            const next = createEmptyEventGroupedTickets();
            for (const section of EVENT_TICKET_VIEW_SECTIONS) {
              next[section.key] = (prev[section.key] ?? []).map(updateTicketPrice);
            }
            return next;
          });
        }
        const fulfilled = results.filter((result) => result.status === 'fulfilled');
        const backendMsg = fulfilled.map((result) => result.message).find((msg) => Boolean(msg));
        markPriceUpdatePending(successIds.map(({ tp_id }) => tp_id), false);
        success(
          backendMsg ||
            `Updated prices for ${successIds.length} ticket(s)${failedCount ? `, ${failedCount} failed` : ''}`,
        );
        refreshListedTicketsAfterPriceChange();
      }

      if (failedCount > 0 && successIds.length === 0) {
        const allTransient = failedResults.every((result) =>
          isBackendActionTimeoutError(result.reason),
        );
        if (allTransient) {
          info(PRICE_UPDATE_TIMEOUT_INFO);
        } else {
          showError(
            getErrorMessage(failedResults[0]?.reason, 'Failed to update price on VL side please try again.'),
          );
        }
      }
    });
  } catch (err: any) {
    if (isBackendActionTimeoutError(err)) {
      info(PRICE_UPDATE_TIMEOUT_INFO);
    } else {
      showError(getErrorMessage(err, 'Failed to update ticket prices'));
    }
  } finally {
    markPriceUpdatePending(bulkPriceTpIds, false);
    closeBulkPriceModal();
    setBulkSelectedTicketIds(new Set());
    setBulkSelectedTickets([]);
    setBulkPriceLoading(false);
    endStatusTableBusy();
    // Keep optimistic prices — syncTicketsAfterStatusChange() refetches and wipes them.
    void fetchTrackingCounts();
  }
};

const handleBulkPriceConfirm = async () => {
  if (bulkPriceLoading) return;
  if (!bulkSelectedTickets.length) {
    showError('No tickets selected');
    return;
  }

  const validation = canBulkUpdateTickets(bulkSelectedTickets.map((ticket) => ticket.tp_id));
  if (!validation.allowed) {
    showError(validation.reason || 'Cannot change price for selected tickets');
    return;
  }

  const invalidTicket = bulkSelectedTickets.find(ticket => {
    const value = String(bulkPriceIndividual[ticket.tp_id] || '').trim();
    const num = parseFloat(value);
    return !value || isNaN(num) || num < 0;
  });

  if (invalidTicket) {
    showError('All selected tickets must have a valid price.');
    return;
  }

  const selfPricingTickets = bulkSelectedTickets.filter(
    (ticket) => getResolvedClientType(ticket) === 'self-pricing',
  );
  if (selfPricingTickets.length > 0) {
    const uniquePrices = [
      ...new Set(
        selfPricingTickets.map((ticket) =>
          Number(parseFloat(String(bulkPriceIndividual[ticket.tp_id] || '').trim())),
        ),
      ),
    ];
    const priceSummary =
      uniquePrices.length === 1
        ? `New price: $${uniquePrices[0].toFixed(2)}`
        : 'Multiple new prices selected';
    selfPricingPriceConfirmActionRef.current = executeBulkPriceConfirm;
    setSelfPricingPriceConfirmCount(selfPricingTickets.length);
    setSelfPricingPriceConfirmSummary(
      `Do you want to change the price for ${selfPricingTickets.length} self-pricing ticket(s)? ${priceSummary}.`,
    );
    setShowSelfPricingPriceConfirm(true);
    return;
  }

  await executeBulkPriceConfirm();
};

const initBulkSoldFeeConfig = async (tickets?: Ticket[]) => {
  if ((!marketplacesList.length && !marketplacesLoading) || marketplacesError) {
    try {
      await refetchMarketplaces();
    } catch (err: any) {
      console.error('Failed to refetch marketplaces for bulk sold:', err);
    }
  }

  const ticketsForFees = tickets?.length ? tickets : bulkSelectedTickets;

 setBulkSoldLogitixAmounts(
  Object.fromEntries(
    ticketsForFees.map(t => [t.tp_id, '0'])   
  )
);
  setBulkSoldCommonLogitixAmount('');
  setBulkSoldFeeConfigReady(false);
  setBulkSoldFeeConfigByTicketId({});

  const configs = await loadBulkSoldFeeConfigs(ticketsForFees);
  setBulkSoldFeeConfigByTicketId(configs);
  setBulkSoldFeeConfigReady(true);
};

const getBulkTicketSoldFeeConfig = (
  ticketId: string,
  feeConfigByTicketId: Record<string, SoldFeeConfig>,
): SoldFeeConfig => feeConfigByTicketId[ticketId] ?? DEFAULT_SOLD_FEE_CONFIG;

const isBulkSoldTransitionReady = (
  tickets: Ticket[],
  logitixAmounts: Record<string, string>,
  marketplaceId: string,
  feeConfigReady: boolean,
  marketplaces: MarketplaceOption[],
  feeConfigByTicketId: Record<string, SoldFeeConfig>,
): boolean => {
  if (!feeConfigReady || !marketplaceId) return false;
  const mp = marketplaces.find(m => m.id === marketplaceId);
  if (!mp) return false;

  return tickets.every(ticket => {
    const listPrice = Number(ticket.tp_list_price) || 0;
    const rawValue = logitixAmounts[ticket.tp_id];
    const value = String(rawValue ?? '').trim();
    const logitixValue = value
      ? Number(parseFloat(value))
      : Math.round(listPrice * (100 - mp.vl_fee_percentage)) / 100;
    if (!Number.isFinite(logitixValue) || logitixValue <= 0) return false;
    const feeConfig = getBulkTicketSoldFeeConfig(ticket.tp_id, feeConfigByTicketId);
    const quickPct = effectiveSoldQuickCutPercent(
      mp.vl_fee_percentage,
      feeConfig.feeMode,
      feeConfig.superClientPercent,
    );
    const quickCutDollars = Number(roundToFixed(listPrice * (quickPct / 100), 2));
    const settlement = Math.round((logitixValue - quickCutDollars) * 100) / 100;
    return settlement > 0;
  });
};

const handleApplyBulkStatusListingPrice = () => {
  const value = String(bulkStatusListingCommonPrice).trim();
  const priceNum = parseFloat(value);
  if (!value || isNaN(priceNum) || priceNum <= 0) {
    showError('Enter a valid price to apply to all tickets');
    return;
  }
  const normalized = String(roundToFixed(priceNum, 2));
  setBulkStatusListingPrices(
    Object.fromEntries(bulkSelectedTickets.map(ticket => [ticket.tp_id, normalized]))
  );
  setBulkStatusListingCommonPrice(normalized);
};

/** Prefer "Apply same list price" common field when set; else per-seat modal prices / existing list price. */
const resolveBulkListingPriceByTpId = (tickets: Ticket[]): Record<string, string> => {
  const commonRaw = String(bulkStatusListingCommonPrice || '').trim();
  const commonNum = parseFloat(commonRaw);
  if (commonRaw && !isNaN(commonNum) && commonNum > 0) {
    const normalized = String(roundToFixed(commonNum, 2));
    return Object.fromEntries(tickets.map((ticket) => [ticket.tp_id, normalized]));
  }
  return {
    ...getDefaultBulkStatusListingPrices(tickets),
    ...Object.fromEntries(
      Object.entries(bulkStatusListingPrices).filter(([, value]) => String(value || '').trim()),
    ),
  };
};

const handleBulkNewStatusSelect = (statusValue: string) => {
  setBulkNewStatus(statusValue);
  const normalized = normalizeStatusValue(statusValue);
  if (normalized === 'List' || normalized === 'List_NonVL') {
    const priceMap = getDefaultBulkStatusListingPrices(bulkSelectedTickets);
    setBulkStatusListingPrices(priceMap);
    const uniquePrices = [
      ...new Set(Object.values(priceMap).filter((v) => String(v).trim() !== '')),
    ];
    setBulkStatusListingCommonPrice(uniquePrices.length === 1 ? uniquePrices[0] : '');
    resetBulkSoldState();
  } else if (normalized === 'Sold') {
    setBulkStatusListingPrices({});
    setBulkStatusListingCommonPrice('');
    setBulkSoldMarketplaceId('');
    setBulkSoldLogitixAmounts({});
    setBulkSoldCommonLogitixAmount('');
    void initBulkSoldFeeConfig(bulkSelectedTickets);
  } else {
    setBulkStatusListingPrices({});
    setBulkStatusListingCommonPrice('');
    resetBulkSoldState();
  }
};

// Handle bulk status change
const handleBulkStatusChange = async (
  confirmedOrOverrides: boolean | { tickets?: Ticket[]; status?: string } = false,
) => {
  const overrides =
    typeof confirmedOrOverrides === 'object' && confirmedOrOverrides
      ? confirmedOrOverrides
      : undefined;
  const confirmed = confirmedOrOverrides === true;
  const ticketsForUpdate = overrides?.tickets?.length ? overrides.tickets : bulkSelectedTickets;
  const statusForUpdate = overrides?.status || bulkNewStatus;
  if (ticketsForUpdate.length === 0 || !statusForUpdate) return;
  if (bulkStatusChangeLoading || statusTableBusyCount > 0) return;

  const targetStatus = normalizeStatusValue(statusForUpdate);
  if (isNonVlStatusChange(targetStatus) && !skipNonVlStatusConfirmRef.current) {
    nonVlStatusConfirmActionRef.current = async () => {
      skipNonVlStatusConfirmRef.current = true;
      try {
        await handleBulkStatusChange(overrides);
      } finally {
        skipNonVlStatusConfirmRef.current = false;
      }
    };
    setNonVlStatusConfirmSummary(
      buildNonVlStatusConfirmSummary(targetStatus, ticketsForUpdate.length),
    );
    setShowNonVlStatusConfirm(true);
    return;
  }

  beginStatusTableBusy('Updating ticket status…');
  setBulkStatusChangeLoading(true);
  const tpIds = ticketsForUpdate.map(t => t.tp_id);
  suppressListPublishChainToastsRef.current = true;

  try {
    if (targetStatus === 'Verified_NonVL') {
      const allEligible = ticketsForUpdate.every(
        (ticket) => normalizeStatusValue(ticket.tp_status || '') === 'ToBeVerified',
      );
      if (!allEligible) {
        showError('Only tickets in To Be Verified status can be verified (Non-VL).');
        return;
      }
      const missingFields = ticketsForUpdate.some((ticket) =>
        isMissingVerifyRequiredFields(ticket),
      );
      if (missingFields) {
        showError(
          'Event name, event date/time, section, row, and seat numbers are required before verification.',
        );
        return;
      }
      try {
        const result = await verifyTicketPlacements(tpIds);
        updateLocalTicketStatus(tpIds, 'Verified');
        success(result?.message || `Verified ${tpIds.length} ticket(s) (Non-VL)`);
        closeBulkStatusModal();
        setBulkSelectedTicketIds(new Set());
        setBulkSelectedTickets([]);
        if (activeTab !== 'Verification') {
          switchTabUnlessEventSearch('Verification');
        }
        setFilterStatus('Verified');
        await syncTicketsAfterStatusChange();
        syncDashboardNotifications();
      } catch (error: any) {
        showError(getErrorMessage(error, 'Failed to verify tickets (Non-VL)'));
      }
      return;
    }

    if (targetStatus === 'List_NonVL') {
      const fromVerified = ticketsForUpdate.filter(
        (ticket) => normalizeStatusValue(ticket.tp_status || '') === 'Verified',
      );
      const fromDelisted = ticketsForUpdate.filter((ticket) => {
        const status = normalizeStatusValue(ticket.tp_status || '');
        return status === 'Delist' || status === 'DelistInProgress' || status === 'DelistRequested';
      });
      const unsupported = ticketsForUpdate.filter(
        (ticket) => !fromVerified.includes(ticket) && !fromDelisted.includes(ticket),
      );
      if (unsupported.length > 0) {
        showError(
          'Only Verified or Delisted / Delist Requested tickets can be listed (Non-VL).',
        );
        return;
      }
      if (fromVerified.length > 0 && fromDelisted.length > 0) {
        showError('Select only Verified tickets or only Delisted tickets for Listed (Non-VL), not both.');
        return;
      }
      // Prefer common "Apply same list price" when filled; else per-seat fields / existing price.
      const priceByTpId = resolveBulkListingPriceByTpId(ticketsForUpdate);
      // Keep modal inputs in sync so UI matches what we publish.
      setBulkStatusListingPrices(priceByTpId);
      const invalidPrice = ticketsForUpdate.find((ticket) => {
        const value = String(priceByTpId[ticket.tp_id] || '').trim();
        const num = parseFloat(value);
        return !value || isNaN(num) || num <= 0;
      });
      if (invalidPrice) {
        showError('All selected tickets must have a valid listing price greater than 0.');
        return;
      }
      try {
        const byPrice = new Map<number, string[]>();
        ticketsForUpdate.forEach((ticket) => {
          const price = Number(parseFloat(String(priceByTpId[ticket.tp_id] || '').trim()));
          const existing = byPrice.get(price) || [];
          existing.push(ticket.tp_id);
          byPrice.set(price, existing);
        });
        await Promise.all(
          Array.from(byPrice.entries()).map(([price, ids]) => callPublishNonVlListing(ids, price)),
        );
        const priceUpdates = ticketsForUpdate.map((ticket) => ({
          tp_id: ticket.tp_id,
          priceNum: Number(parseFloat(String(priceByTpId[ticket.tp_id] || '').trim())),
        }));
        updateLocalTicketStatus(tpIds, 'List');
        setFilteredTickets((prevTickets) =>
          sortManageTickets(
            prevTickets.map((ticket) => {
              const updated = priceUpdates.find((item) => item.tp_id === ticket.tp_id);
              if (!updated) return ticket;
              return {
                ...ticket,
                tp_status: 'List',
                tp_list_price: updated.priceNum,
                tp_updated_at: new Date().toISOString(),
              };
            }),
          ),
        );
        setStatusFilterSourceTickets((prevTickets) =>
          prevTickets.map((ticket) => {
            const updated = priceUpdates.find((item) => item.tp_id === ticket.tp_id);
            if (!updated) return ticket;
            return {
              ...ticket,
              tp_status: 'List',
              tp_list_price: updated.priceNum,
              tp_updated_at: new Date().toISOString(),
            };
          }),
        );
        const samplePrice = priceUpdates[0]?.priceNum;
        success(
          `Listed ${tpIds.length} ticket(s) (Non-VL)${
            Number.isFinite(samplePrice) ? ` at $${samplePrice}` : ''
          }`,
        );
        closeBulkStatusModal();
        setBulkSelectedTicketIds(new Set());
        setBulkSelectedTickets([]);
        if (activeTab !== 'Listed') {
          switchTabUnlessEventSearch('Listed');
        }
        await syncTicketsAfterStatusChange();
        syncDashboardNotifications();
      } catch (error: any) {
        showError(getErrorMessage(error, 'Failed to list tickets (Non-VL)'));
        await syncTicketsAfterStatusChange();
      }
      return;
    }

    if (targetStatus === 'Delist_NonVL') {
      const allEligible = ticketsForUpdate.every((ticket) => {
        const status = normalizeStatusValue(ticket.tp_status || '');
        return status === 'List' || status === 'DelistInProgress' || status === 'DelistRequested';
      });
      if (!allEligible) {
        showError('Only Listed or Delist Requested tickets can be delisted (Non-VL).');
        return;
      }
      try {
        const results = await Promise.allSettled(
          tpIds.map((tp_id) =>
            updateTicketStatusSilent({ variables: { tp_id, status: 'Delist' } }).then((res) => {
              const affected = res?.data?.update_ticket_placement?.returning?.length || 0;
              if (!affected) throw new Error('No ticket was updated.');
              return tp_id;
            }),
          ),
        );
        const successIds = results
          .filter((r): r is PromiseFulfilledResult<string> => r.status === 'fulfilled')
          .map((r) => r.value);
        if (!successIds.length) {
          showError('Failed to delist tickets (Non-VL).');
          return;
        }
        updateLocalTicketStatus(successIds, 'Delist');
        success(`Delisted ${successIds.length} ticket(s) (Non-VL)`);
        closeBulkStatusModal();
        setBulkSelectedTicketIds(new Set());
        setBulkSelectedTickets([]);
        if (activeTab !== 'Delisted') switchTabUnlessEventSearch('Delisted');
        await syncTicketsAfterStatusChange();
        syncDashboardNotifications();
      } catch (error: any) {
        showError(getErrorMessage(error, 'Failed to delist tickets (Non-VL)'));
      }
      return;
    }

    if (targetStatus === 'DelistInProgress_NonVL') {
      const allEligible = ticketsForUpdate.every((ticket) => {
        const status = normalizeStatusValue(ticket.tp_status || '');
        return status === 'Verified' || status === 'Delist';
      });
      if (!allEligible) {
        showError('Only Verified or Delisted tickets can move to Delist Requested (Non-VL).');
        return;
      }
      try {
        const results = await Promise.allSettled(
          tpIds.map((tp_id) =>
            updateTicketStatusSilent({
              variables: { tp_id, status: 'DelistInProgress' },
            }).then((res) => {
              const affected = res?.data?.update_ticket_placement?.returning?.length || 0;
              if (!affected) throw new Error('No ticket was updated.');
              return tp_id;
            }),
          ),
        );
        const successIds = results
          .filter((r): r is PromiseFulfilledResult<string> => r.status === 'fulfilled')
          .map((r) => r.value);
        if (!successIds.length) {
          showError('Failed to update tickets to Delist Requested (Non-VL).');
          return;
        }
        updateLocalTicketStatus(successIds, 'DelistInProgress');
        success(`Moved ${successIds.length} ticket(s) to Delist Requested (Non-VL)`);
        closeBulkStatusModal();
        setBulkSelectedTicketIds(new Set());
        setBulkSelectedTickets([]);
        if (activeTab !== 'Delisted') switchTabUnlessEventSearch('Delisted');
        await syncTicketsAfterStatusChange();
        syncDashboardNotifications();
      } catch (error: any) {
        showError(getErrorMessage(error, 'Failed to update Delist Requested (Non-VL)'));
      }
      return;
    }

    if (targetStatus === 'Return_NonVL') {
      const allEligible = ticketsForUpdate.every((ticket) => {
        const status = normalizeStatusValue(ticket.tp_status || '');
        return status === 'Delist' || status === 'DelistInProgress' || status === 'DelistRequested';
      });
      if (!allEligible) {
        showError('Only Delisted / Delist Requested tickets can be returned (Non-VL).');
        return;
      }
      try {
        const results = await Promise.allSettled(
          tpIds.map((tp_id) =>
            updateTicketStatusSilent({ variables: { tp_id, status: 'Return' } }).then((res) => {
              const affected = res?.data?.update_ticket_placement?.returning?.length || 0;
              if (!affected) throw new Error('No ticket was updated.');
              return tp_id;
            }),
          ),
        );
        const successIds = results
          .filter((r): r is PromiseFulfilledResult<string> => r.status === 'fulfilled')
          .map((r) => r.value);
        if (!successIds.length) {
          showError('Failed to return tickets (Non-VL).');
          return;
        }
        updateLocalTicketStatus(successIds, 'Return');
        success(`Returned ${successIds.length} ticket(s) (Non-VL)`);
        closeBulkStatusModal();
        setBulkSelectedTicketIds(new Set());
        setBulkSelectedTickets([]);
        if (activeTab !== 'Delisted') switchTabUnlessEventSearch('Delisted');
        await syncTicketsAfterStatusChange();
        syncDashboardNotifications();
      } catch (error: any) {
        showError(getErrorMessage(error, 'Failed to return tickets (Non-VL)'));
      }
      return;
    }

    if (targetStatus === 'Unsold_NonVL') {
      const allEligible = ticketsForUpdate.every(
        (ticket) => normalizeStatusValue(ticket.tp_status || '') === 'List',
      );
      if (!allEligible) {
        showError('Only Listed tickets can be marked Unsold (Non-VL).');
        return;
      }
      try {
        const results = await Promise.allSettled(
          tpIds.map((tp_id) =>
            updateTicketStatusSilent({ variables: { tp_id, status: 'Unsold' } }).then((res) => {
              const affected = res?.data?.update_ticket_placement?.returning?.length || 0;
              if (!affected) throw new Error('No ticket was updated.');
              return tp_id;
            }),
          ),
        );
        const successIds = results
          .filter((r): r is PromiseFulfilledResult<string> => r.status === 'fulfilled')
          .map((r) => r.value);
        if (!successIds.length) {
          showError('Failed to mark tickets Unsold (Non-VL).');
          return;
        }
        updateLocalTicketStatus(successIds, 'Unsold');
        success(`Marked ${successIds.length} ticket(s) Unsold (Non-VL)`);
        closeBulkStatusModal();
        setBulkSelectedTicketIds(new Set());
        setBulkSelectedTickets([]);
        if (activeTab !== 'Unsold') switchTabUnlessEventSearch('Unsold');
        await syncTicketsAfterStatusChange();
        syncDashboardNotifications();
      } catch (error: any) {
        showError(getErrorMessage(error, 'Failed to mark Unsold (Non-VL)'));
      }
      return;
    }

    if (targetStatus === 'Verified') {
      const allEligible = ticketsForUpdate.every(
        (ticket) => normalizeStatusValue(ticket.tp_status || '') === 'ToBeVerified',
      );
      if (!allEligible) {
        showError('Only tickets in To Be Verified status can be verified.');
        return;
      }
      const eventIds = [
        ...new Set(ticketsForUpdate.map((ticket) => String(ticket.e_id || '').trim())),
      ].filter(Boolean);
      if (eventIds.length !== 1) {
        showError('Verify tickets from one event at a time.');
        return;
      }
      const missingFields = ticketsForUpdate.some((ticket) =>
        isMissingVerifyRequiredFields(ticket),
      );
      if (missingFields) {
        showError(
          'Event name, event date/time, section, row, and seat numbers are required before verification.',
        );
        return;
      }

      // Stay ToBeVerified until Production ID is saved, then mark Verified.
      closeBulkStatusModal();
      setBulkSelectedTicketIds(new Set());
      setBulkSelectedTickets([]);
      setBulkStatusChangeLoading(false);
      endStatusTableBusy();
      await openPidConfirmationForTickets(ticketsForUpdate);
      return;
    }

    // List: publish (Verified / Deleted) or undo delist (Delisted tab)
    if (targetStatus === 'List') {
      const undoToListTickets = ticketsForUpdate.filter(ticket => {
        const status = normalizeStatusValue(ticket.tp_status || '');
        return status === 'Delist' || status === 'DelistInProgress' || status === 'DelistRequested';
      });
      const publishToListTickets = ticketsForUpdate.filter(
        ticket => normalizeStatusValue(ticket.tp_status || '') === 'Verified',
      );
      const deletedToListTickets = ticketsForUpdate.filter(
        ticket => normalizeStatusValue(ticket.tp_status || '') === 'Deleted',
      );
      const unsupportedToList = ticketsForUpdate.filter(
        ticket =>
          !undoToListTickets.includes(ticket) &&
          !publishToListTickets.includes(ticket) &&
          !deletedToListTickets.includes(ticket),
      );

      if (unsupportedToList.length > 0) {
        showError(
          'Only Verified, Deleted, or Delisted tickets can be moved to Listed in bulk.',
        );
        return;
      }

      const listSourceGroups = [undoToListTickets, publishToListTickets, deletedToListTickets].filter(g => g.length > 0);
      if (listSourceGroups.length > 1) {
        showError('Select only one source status group when moving tickets to Listed in bulk.');
        return;
      }

      const successIds: string[] = [];

      if (deletedToListTickets.length > 0) {
        const deletedPriceByTpId = resolveBulkListingPriceByTpId(deletedToListTickets);
        setBulkStatusListingPrices(deletedPriceByTpId);
        const invalidDeletedPrice = deletedToListTickets.find(ticket => {
          const value = String(deletedPriceByTpId[ticket.tp_id] || '').trim();
          const num = parseFloat(value);
          return !value || isNaN(num) || num <= 0;
        });

        if (invalidDeletedPrice) {
          showError('All selected tickets must have a valid listing price greater than 0.');
          return;
        }

        try {
          const byPrice = new Map<number, string[]>();
          deletedToListTickets.forEach((ticket) => {
            const price = Number(parseFloat(String(deletedPriceByTpId[ticket.tp_id] || '').trim()));
            const ids = byPrice.get(price) || [];
            ids.push(ticket.tp_id);
            byPrice.set(price, ids);
          });
          await Promise.all(
            Array.from(byPrice.entries()).map(([price, tpIds]) => callPublishNonVlListing(tpIds, price)),
          );
          successIds.push(...deletedToListTickets.map(ticket => ticket.tp_id));
          updateLocalTicketStatus(successIds, 'List');
          success(`Listed ${successIds.length} ticket(s)`);
          if (activeTab !== 'Listed') switchTabUnlessEventSearch('Listed');
        } catch (deletedError: any) {
          showError(getErrorMessage(deletedError, 'Failed to list deleted tickets'));
          return;
        }
      } else if (undoToListTickets.length > 0) {
        // Prefer common "Apply same list price" when filled; else per-seat / existing.
        const undoPriceByTpId = resolveBulkListingPriceByTpId(undoToListTickets);
        setBulkStatusListingPrices(undoPriceByTpId);
        const invalidUndoPrice = undoToListTickets.find(ticket => {
          const value = String(undoPriceByTpId[ticket.tp_id] || '').trim();
          const num = parseFloat(value);
          return !value || isNaN(num) || num <= 0;
        });

        if (invalidUndoPrice) {
          showError('All selected tickets must have a valid listing price greater than 0.');
          return;
        }

        const hasLargePriceChange = undoToListTickets.some(ticket =>
          requiresPriceChangeConfirmation(
            ticket.tp_list_price,
            Number(parseFloat(String(bulkStatusListingPrices[ticket.tp_id] || '').trim())),
          ),
        );
        if (hasLargePriceChange && !confirmed) {
          setPriceConfirmationFlow('bulkStatus');
          setShowBulkStatusModal(false);
          return;
        }

        try {
          const undoTpIds = undoToListTickets.map((ticket) => ticket.tp_id);
          await executeBatchedUndoDelistWithFallback(undoTpIds);

          const priceUpdates = undoToListTickets.map((ticket) => ({
            tp_id: ticket.tp_id,
            priceNum: Number(parseFloat(String(undoPriceByTpId[ticket.tp_id] || '').trim())),
          }));

          const tpIdsByPrice = new Map<number, string[]>();
          priceUpdates.forEach(({ tp_id, priceNum }) => {
            const existing = tpIdsByPrice.get(priceNum) || [];
            existing.push(tp_id);
            tpIdsByPrice.set(priceNum, existing);
          });

          const undoPriceIds = undoToListTickets.map((ticket) => ticket.tp_id);
          markPriceUpdatePending(undoPriceIds, true);
          try {
            await Promise.all(
              Array.from(tpIdsByPrice.entries()).map(async ([priceNum, tp_ids]) => {
                const res = await editTicketPrice({
                  variables: { tp_ids, tp_list_price: priceNum },
                });
                const message = String(res?.data?.editTicketPriceBySuperUser?.message || '').trim();
                if (!isVictoryLivePriceUpdateSuccess(message)) {
                  throw new Error(priceUpdateFailureMessage(message));
                }
              }),
            );

          const undoSuccess = priceUpdates;
          successIds.push(...undoSuccess.map((item) => item.tp_id));
          if (successIds.length > 0) {
            const now = new Date().toISOString();
            undoSuccess.forEach(({ tp_id, priceNum }) => {
              localPriceOverridesRef.current[tp_id] = { price: priceNum, lastPricedAt: now };
            });
            updateLocalTicketStatus(successIds, 'List');
            setFilteredTickets(prevTickets =>
              sortManageTickets(prevTickets.map(ticket => {
                const updated = undoSuccess.find(item => item.tp_id === ticket.tp_id);
                if (!updated) return ticket;
                return {
                  ...ticket,
                  tp_status: 'List',
                  tp_list_price: updated.priceNum,
                  last_priced_at: now,
                  tp_updated_at: now,
                };
              }))
            );
            success(`Moved ${successIds.length} ticket(s) back to Listed`);
            if (activeTab !== 'Listed') {
              switchTabUnlessEventSearch('Listed');
            }
          }
          } finally {
            markPriceUpdatePending(undoPriceIds, false);
          }
        } catch (undoError: any) {
          showError(getErrorMessage(undoError, 'Failed to move tickets back to Listed'));
        }
      } else {
           // Verified → Listed must go through Victory Live steps (PID → Submit → List on Marketplaces).
        closeBulkStatusModal();
        setBulkSelectedTicketIds(new Set());
        setBulkSelectedTickets([]);
        setBulkStatusChangeLoading(false);
        endStatusTableBusy();
        suppressListPublishChainToastsRef.current = false;
        await handleVlProgressAction(publishToListTickets.map((ticket) => ticket.tp_id));
        return;

      
       
      }

      setBulkSelectedTicketIds(new Set());
      setBulkSelectedTickets([]);
      closeBulkStatusModal();
      await syncTicketsAfterStatusChange();
      return;
    }

    const isBulkSettlementOverride =
      ticketsForUpdate.every(ticket => normalizeStatusValue(ticket.tp_status || '') === 'Sold') &&
      (targetStatus === 'Sold' || targetStatus === 'Settled' || targetStatus === 'SettlementInProgress');

    if (isBulkSettlementOverride) {
      if (!canOverrideSettlementStatus) {
        info(soldPaymentStartedMessage);
        return;
      }
      const paymentStatus = getPaymentStatusForSettlementOverride(targetStatus);
      if (paymentStatus === undefined) {
        showError('That settlement status could not be applied.');
        return;
      }
      try {
        const result = await callUpdateSettlementPaymentStatusRest(tpIds, paymentStatus);
        const updatedIds: string[] = Array.isArray(result?.updatedTpIds) ? result.updatedTpIds : tpIds;
        if (!updatedIds.length) {
          showError('No sold tickets were updated.');
          return;
        }
        updateLocalTicketPaymentStatus(updatedIds, paymentStatus);
        success(result?.message || `Updated settlement status for ${updatedIds.length} ticket(s).`);
        closeBulkStatusModal();
        setBulkSelectedTicketIds(new Set());
        setBulkSelectedTickets([]);
        await syncTicketsAfterStatusChange();
        syncDashboardNotifications();
      } catch (error: any) {
        showError(getErrorMessage(error, 'Failed to update settlement status'));
      }
      return;
    }

    const qaBulkStatuses = new Set(['UnderReview', 'PaidManuallyViaQA', 'QADeniedPayout']);
    if (qaBulkStatuses.has(targetStatus)) {
      const results = await Promise.allSettled(
        ticketsForUpdate.map(ticket =>
          updateTicketStatusSilent({
            variables: { tp_id: ticket.tp_id, status: targetStatus }
          }).then(res => {
            const affected = res?.data?.update_ticket_placement?.returning?.length || 0;
            if (!affected) throw new Error('No ticket was updated.');
            return ticket.tp_id;
          }),
        ),
      );
      const successIds = results
        .filter((result): result is PromiseFulfilledResult<string> => result.status === 'fulfilled')
        .map(result => result.value);
      const failedCount = results.filter(result => result.status === 'rejected').length;

      if (successIds.length > 0) {
        updateLocalTicketStatus(successIds, targetStatus);
        success(
          `${successIds.length} ticket(s) marked ${getStatusLabel(targetStatus)} successfully${failedCount ? `, ${failedCount} failed` : ''}`,
        );
        if (getTabForStatus(targetStatus) !== activeTab) {
          switchTabUnlessEventSearch(getTabForStatus(targetStatus));
        }
        setBulkSelectedTicketIds(new Set());
        setBulkSelectedTickets([]);
        closeBulkStatusModal();
        await syncTicketsAfterStatusChange();
      }

      if (failedCount > 0 && successIds.length === 0) {
        showError('Failed to update selected tickets to the chosen QA status.');
      }
      return;
    }

    // Sold - confirm from bulk status modal (marketplace + fee preview).
    // Dropdown may call this without marketplace selected — open that modal instead of Bulk Mark as Sold.
    if (targetStatus === 'Sold') {
      if (!bulkSoldMarketplaceId) {
        setBulkSelectedTickets(ticketsForUpdate);
        setBulkSelectedTicketIds(new Set(ticketsForUpdate.map((t) => t.tp_id)));
        setBulkStatusChangeLoading(false);
        endStatusTableBusy();
        setShowBulkSoldModal(false);
        setBulkStatusListingPrices({});
        setBulkStatusListingCommonPrice('');
        setBulkSoldMarketplaceId('');
        setBulkSoldLogitixAmounts({});
        setBulkSoldCommonLogitixAmount('');
        setBulkNewStatus('Sold');
        setShowBulkStatusModal(true);
        void initBulkSoldFeeConfig(ticketsForUpdate);
        return;
      }
      await handleBulkSoldConfirm();
      return;
    }
if (targetStatus === 'Unsold') {
  const ineligible = ticketsForUpdate.filter(ticket => {
    const status = normalizeStatusValue(ticket.tp_status || '');
    return status !== 'List';
  });
  if (ineligible.length) {
    showError('Only Listed tickets can be marked as Unsold in bulk.');
    return;
  }

  setStatusTableBusyMessage('Marking tickets as Unsold…');
  try {
    const tpIds = ticketsForUpdate.map(t => t.tp_id);
    const response = await callMarkTicketsUnsoldRest(tpIds);
    success(response?.message || `Marked ${tpIds.length} ticket(s) as Unsold`);
    updateLocalTicketStatus(tpIds, 'Unsold');
    setBulkSelectedTicketIds(new Set());
    setBulkSelectedTickets([]);
    closeBulkStatusModal();

    // ✅ Switch to Unsold tab after successful update
    if (activeTab !== 'Unsold') {
      handleTabChange('Unsold');
    }

    await syncTicketsAfterStatusChange();
  } catch (error: any) {
    showError(getErrorMessage(error, 'Failed to mark tickets as Unsold'));
    await syncTicketsAfterStatusChange();
  }
  return;
}
    // Return — Delisted / Delist Requested tickets (Node REST API)
    if (targetStatus === 'Return') {
      const ineligible = ticketsForUpdate.filter(ticket => {
        const status = normalizeStatusValue(ticket.tp_status || '');
        return status !== 'Delist' && status !== 'DelistInProgress' && status !== 'DelistRequested';
      });
      if (ineligible.length) {
        showError(
          'Only Delisted or Delist Requested tickets can be returned. Deselect Listing Request Cancelled or other statuses.',
        );
        return;
      }

      const eventIds = ticketsForUpdate.map(t => t.e_id).filter(Boolean);
      const sameEvent = eventIds.length > 0 && eventIds.every(id => id === eventIds[0]);
      if (!sameEvent) {
        showError('Please select tickets from the same event to return in bulk.');
        return;
      }

      setStatusTableBusyMessage('Returning tickets…');
      try {
        const { message: returnMessage, type: returnType } = await executeReturnTicketsRest(tpIds);
        updateLocalTicketStatus(tpIds, 'Return');
        if (returnType === 'success') {
          success(returnMessage || `Returned ${tpIds.length} ticket(s)`);
        } else {
          info(returnMessage || `Returned ${tpIds.length} ticket(s)`);
        }
        setFilterStatus('Return');
        setBulkSelectedTicketIds(new Set());
        setBulkSelectedTickets([]);
        closeBulkStatusModal();
        await syncTicketsAfterStatusChange();
      } catch (returnError: any) {
        showError(getErrorMessage(returnError, 'Failed to return tickets'));
        await syncTicketsAfterStatusChange();
      }
      return;
    }

    // DelistInProgress -> Delist: confirm delist (admin API — no updateTicketStatus / SES)
    if (targetStatus === 'Delist') {
      const tpIds = ticketsForUpdate.map((ticket) => ticket.tp_id);
      const allListed = ticketsForUpdate.every(
        (ticket) => normalizeStatusValue(ticket.tp_status || '') === 'List',
      );
      if (allListed) {
        setBulkSelectedTicketIds(new Set());
        setBulkSelectedTickets([]);
        closeBulkStatusModal();
        requestListedTicketDelist(tpIds, ticketsForUpdate);
        return;
      }
      setStatusTableBusyMessage('Confirming delist…');
      let successIds: string[] = [];
      let failedCount = 0;
      try {
        const delistResult = await executeAdminOrSuperUserDelistRest(tpIds);
        successIds = delistResult.successIds;
        failedCount = tpIds.filter((id) => !successIds.includes(id)).length;
      } catch (error: any) {
        showError(getErrorMessage(error, 'Failed to delist tickets'));
        setBulkSelectedTicketIds(new Set());
        setBulkSelectedTickets([]);
        closeBulkStatusModal();
        await syncTicketsAfterStatusChange();
        return;
      }

      if (successIds.length > 0) {
        updateLocalTicketStatus(successIds, 'Delist');
        success(`Delisted ${successIds.length} ticket(s)${failedCount ? `, ${failedCount} failed` : ''}`);
        if (activeTab !== 'Delisted') {
          switchTabUnlessEventSearch('Delisted');
        }
      }
      if (failedCount > 0 && successIds.length === 0) {
        showError('Failed to delist tickets');
      }

      setBulkSelectedTicketIds(new Set());
      setBulkSelectedTickets([]);
      closeBulkStatusModal();
      await syncTicketsAfterStatusChange();
      return;
    }

    // Delist requested (from Listed/Verified) — email modal + delistTickets API
    if (targetStatus === 'DelistInProgress' || targetStatus === 'DelistRequested') {
      openBulkDelistEmailModal(tpIds);
      return;
    }

    // Listing Request Cancelled (ToBeVerified) — use dedicated cancel API, not updateTicketStatus
    if (targetStatus === 'ListingRequestCancelled' || targetStatus === 'Invalid') {
      const notEligible = ticketsForUpdate.filter(
        t => {
          const status = normalizeStatusValue(t.tp_status || '');
          return status !== 'ToBeVerified';
        },
      );
      if (notEligible.length) {
        showError('Only tickets in To Be Verified status can be marked as Listing Request Cancelled.');
        return;
      }

      const results = await Promise.allSettled(
        ticketsForUpdate.map(ticket => callCancelListingRequestByAdminRest(ticket.tp_id)),
      );
      const successIds = ticketsForUpdate
        .filter((_, index) => results[index].status === 'fulfilled')
        .map(ticket => ticket.tp_id);
      const failedCount = results.filter(result => result.status === 'rejected').length;

      if (successIds.length > 0) {
        updateLocalTicketStatus(successIds, 'ListingRequestCancelled');
        success(
          `Cancelled ${successIds.length} listing request(s)${failedCount ? `, ${failedCount} failed` : ''}. Find them under the Listing Request Cancelled tab.`,
        );
        if (activeTab !== 'Listing Request Cancelled') {
          switchTabUnlessEventSearch('Listing Request Cancelled');
        }
        setFilterStatus('All Listing Request Cancelled');
      }

      if (failedCount > 0 && successIds.length === 0) {
        const firstError = results.find(
          (result): result is PromiseRejectedResult => result.status === 'rejected',
        );
        showError(getErrorMessage(firstError?.reason, 'Failed to cancel listing requests'));
      }

      setBulkSelectedTicketIds(new Set());
      setBulkSelectedTickets([]);
      closeBulkStatusModal();
      await syncTicketsAfterStatusChange();
      return;
    }

    // ListingRequestCancelled / Deleted -> ToBeVerified: use destination-specific restore mutation
    // so the ticket regains the publish visibility required by the To Be Verified tab.
    if (targetStatus === 'ToBeVerified') {
      const notEligible = ticketsForUpdate.filter((t) => {
        const status = normalizeStatusValue(t.tp_status || '');
        return status !== 'ListingRequestCancelled' && status !== 'Deleted';
      });
      if (notEligible.length) {
        showError(
          'Only tickets in Listing Request Cancelled and Deleted can be moved to To Be Verified in bulk.',
        );
        return;
      }

      const results = await Promise.allSettled(
        ticketsForUpdate.map(ticket => {
          const isDeleted = normalizeStatusValue(ticket.tp_status || '') === 'Deleted';
          const restoreMutation = isDeleted ? restoreDeletedToVerification : restoreCancelledListingToVerification;
          return restoreMutation({
            variables: { tp_id: ticket.tp_id, updated_at: new Date().toISOString() },
          }).then(
            result => {
              const updated = result?.data?.update_ticket_placement?.returning?.length || 0;
              if (updated <= 0) {
                throw new Error('Unable to restore ticket to To Be Verified');
              }
              return ticket.tp_id;
            },
          );
        }),
      );
      const successIds = ticketsForUpdate
        .filter((_, index) => results[index].status === 'fulfilled')
        .map(ticket => ticket.tp_id);
      const failedCount = results.filter(result => result.status === 'rejected').length;

      if (successIds.length > 0) {
        updateLocalTicketStatus(successIds, 'ToBeVerified', undefined, 'Verification', {
          tp_is_published: false,
          tp_validity_status: null,
          tp_deleted_at: null,
        });
        success(
          `Moved ${successIds.length} ticket(s) to To Be Verified${failedCount ? `, ${failedCount} failed` : ''}`,
        );
        if (activeTab !== 'Verification') {
          switchTabUnlessEventSearch('Verification');
        }
        setFilterStatus('ToBeVerified');
      }

      if (failedCount > 0 && successIds.length === 0) {
        const firstError = results.find(
          (result): result is PromiseRejectedResult => result.status === 'rejected',
        );
        showError(getErrorMessage(firstError?.reason, 'Failed to move tickets to To Be Verified'));
      }

      setBulkSelectedTicketIds(new Set());
      setBulkSelectedTickets([]);
      closeBulkStatusModal();
      await syncTicketsAfterStatusChange();
      return;
    }

    // Deleted - bulk delete tickets
    if (targetStatus === 'Deleted') {
      const results = await Promise.allSettled(
        ticketsForUpdate.map(ticket => {
          return deleteTicketStatus({
            variables: {
              tp_id: ticket.tp_id
            }
          });
        })
      );
      
      const successIds = ticketsForUpdate
        .filter((_, index) => results[index].status === 'fulfilled')
        .map(ticket => ticket.tp_id);
      const failedCount = results.filter(result => result.status === 'rejected').length;

      if (successIds.length > 0) {
        // Remove deleted tickets from current tab
        setFilteredTickets((prevTickets) =>
          prevTickets.filter((t) => !successIds.includes(t.tp_id))
        );
        setStatusFilterSourceTickets((prevTickets) =>
          prevTickets.filter((t) => !successIds.includes(t.tp_id))
        );

        // Update tab counts
        setTabCounts((prev) => {
          const updated = { ...prev };
          successIds.forEach(() => {
            updated.Deleted = (updated.Deleted || 0) + 1;
            updated[activeTab] = Math.max(0, (updated[activeTab] || 0) - 1);
          });
          return updated;
        });

        updateLocalTicketStatus(successIds, 'Deleted');
        success(
          `Deleted ${successIds.length} ticket(s)${failedCount ? `, ${failedCount} failed` : ''}`
        );

        // Switch to Deleted tab to see the deleted tickets
        if (activeTab !== 'Deleted') {
          switchTabUnlessEventSearch('Deleted');
        }
      }

      if (failedCount > 0 && successIds.length === 0) {
        showError('Failed to delete selected tickets');
      }

      setBulkSelectedTicketIds(new Set());
      setBulkSelectedTickets([]);
      closeBulkStatusModal();
      await syncTicketsAfterStatusChange();
      return;
    }

    // Generic fallback: call REST endpoint in batch
    await callUpdateTicketStatusRest(tpIds, targetStatus === 'Verified', false);
    updateLocalTicketStatus(tpIds, statusForUpdate);
    success(`Updated ${tpIds.length} ticket(s) to ${getStatusLabel(statusForUpdate)}`);
    setBulkSelectedTicketIds(new Set());
    setBulkSelectedTickets([]);
    setShowBulkStatusModal(false);
    setBulkNewStatus('');
    await syncTicketsAfterStatusChange();
    return;
  } catch (error: any) {
    console.error('Bulk update failed', error);
    showError(getErrorMessage(error, `Failed to update selected tickets`));
    await syncTicketsAfterStatusChange();
  } finally {
    suppressListPublishChainToastsRef.current = false;
    setBulkStatusChangeLoading(false);
    endStatusTableBusy();
  }
};

// Bulk delist with email modal
const [showBulkDelistEmailModal, setShowBulkDelistEmailModal] = useState(false);
const [pendingBulkDelistTpIds, setPendingBulkDelistTpIds] = useState<string[]>([]);
const [delistEmailMode, setDelistEmailMode] = useState<'qa' | 'different'>('qa');

function openBulkDelistEmailModal(tpIds: string[]) {
  setPendingBulkDelistTpIds(tpIds);
  const defaultEmail = getDefaultDelistEmail(tpIds);
  resetDelistEmail({
    email: defaultEmail,
    confirmEmail: defaultEmail
  });
  setShowBulkDelistEmailModal(true);
  setShowBulkStatusModal(false);
}

const onBulkDelistEmailSubmit = async (data: { email: string; confirmEmail: string }) => {
  const requestedTpIds = [...pendingBulkDelistTpIds];
  if (!requestedTpIds.length) return;

  setShowBulkDelistEmailModal(false);
  beginStatusTableBusy('Submitting delist request…');
  try {
    await executeDelistWithFallback(requestedTpIds, data.email);
    updateLocalTicketStatus(requestedTpIds, 'DelistInProgress');
    success(`Delist request submitted for ${requestedTpIds.length} ticket(s)`);
    setBulkSelectedTicketIds(new Set());
    setBulkSelectedTickets([]);
    if (activeTab !== 'Delisted') {
      switchTabUnlessEventSearch('Delisted');
    }
    setFilterStatus('DelistInProgress');
    await syncTicketsAfterStatusChange();
  } catch (error: any) {
    showError(getErrorMessage(error, 'Failed to request delist'));
    await syncTicketsAfterStatusChange();
  } finally {
    endStatusTableBusy();
    setPendingBulkDelistTpIds([]);
    resetDelistEmail();
  }
};

// Handle checkbox selection — always go through seat selection so group rows
// (215–216) and single seats (217) can be selected together.
const handleBulkSelectTicket = (ticketId: string, checked: boolean) => {
  const ticket =
    getManageTicketDisplayTickets(manageTicketTableRows).find((t) => t.tp_id === ticketId) ||
    filteredTickets.find((t) => t.tp_id === ticketId);
  const seatIds = ticket ? generateSeats(ticket).map((seat) => seat.id) : [];
  if (seatIds.length > 0) {
    handleSelectSeatsByIds(seatIds, checked);
    return;
  }

  setBulkSelectedTicketIds((prev) => {
    const newSet = new Set(prev);
    if (checked) {
      newSet.add(ticketId);
    } else {
      newSet.delete(ticketId);
    }
    setSelectedTpIds(Array.from(newSet));
    setShowBulkActions(newSet.size > 0);
    return newSet;
  });
};

// Handle select all on current page / visible rows
const handleBulkSelectAllPage = (checked: boolean) => {
  if (checked) {
    const allIds = getManageTicketDisplayTickets(manageTicketTableRows).map((ticket) => ticket.tp_id);
    
    // Generate all seat IDs for grouped and individual tickets
    const allSeatIds: { [key: string]: boolean } = {};
    manageTicketTableRows.forEach((row) => {
      if (row.kind === 'group') {
        row.tickets.forEach((ticket) => {
          const seats = generateSeats(ticket);
          seats.forEach((seat) => {
            allSeatIds[seat.id] = true;
          });
        });
      } else if (row.kind === 'ticket') {
        const seats = generateSeats(row.ticket);
        seats.forEach((seat) => {
          allSeatIds[seat.id] = true;
        });
      }
    });
    
    setBulkSelectedTicketIds(new Set(allIds));
    setSelectedTpIds(allIds);
    setShowBulkActions(allIds.length > 0);
    setSelectedSeats(allSeatIds);
    setSelectAllSeats(true);
  } else {
    setBulkSelectedTicketIds(new Set());
    setSelectedTpIds([]);
    setShowBulkActions(false);
    setSelectedSeats({});
    setSelectAllSeats(false);
  }
};

// Open bulk status change modal
const openBulkStatusModal = (ticketIds = Array.from(bulkSelectedTicketIds)) => {
  const fromDisplay = getManageTicketDisplayTickets(manageTicketTableRows).filter((t) =>
    ticketIds.includes(t.tp_id),
  );
  const selectedTicketsList =
    fromDisplay.length > 0
      ? fromDisplay
      : filteredTickets.filter((t) => ticketIds.includes(t.tp_id));
  const validation = canBulkUpdateTickets(ticketIds);
  
  if (!validation.allowed) {
    showError(validation.reason || 'Cannot perform bulk action on selected tickets');
    return;
  }
  
  setBulkSelectedTickets(selectedTicketsList);
  setShowBulkStatusModal(true);
  setBulkNewStatus('');
  // Prefill listing prices from existing list price (e.g. $200 on seats).
  const priceMap = Object.fromEntries(
    selectedTicketsList.map((t) => {
      const price = Number(t.tp_list_price);
      return [
        t.tp_id,
        Number.isFinite(price) && price > 0 ? String(roundToFixed(price, 2)) : '',
      ];
    }),
  );
  setBulkStatusListingPrices(priceMap);
  const uniquePrices = [
    ...new Set(Object.values(priceMap).filter((v) => String(v).trim() !== '')),
  ];
  setBulkStatusListingCommonPrice(uniquePrices.length === 1 ? uniquePrices[0] : '');
};

const openBulkListModal = () => {
  if (!bulkSelectedTickets.length) {
    showError('No tickets selected for bulk List action.');
    return;
  }

  setBulkListCommonPrice('');
  setBulkListedPrices(
    Object.fromEntries(
      bulkSelectedTickets.map(t => [t.tp_id, t.tp_list_price != null ? String(t.tp_list_price) : ''])
    )
  );
  setShowBulkStatusModal(false);
  setShowBulkListModal(true);
};

const closeBulkListModal = () => {
  setShowBulkListModal(false);
  setBulkListedPrices({});
  setBulkListCommonPrice('');
  setBulkListLoading(false);
};

const openBulkSoldModal = async (tickets?: Ticket[]) => {
  const selectedTicketsList = tickets?.length
    ? tickets
    : filteredTickets.filter(t => bulkSelectedTicketIds.has(t.tp_id));

  if (!selectedTicketsList.length) {
    showError('No tickets selected for bulk Sold action.');
    return;
  }

  if ((!marketplacesList.length && !marketplacesLoading) || marketplacesError) {
    try {
      await refetchMarketplaces();
    } catch (err: any) {
      console.error('Failed to refetch marketplaces for bulk sold modal:', err);
    }
  }

  setBulkSelectedTickets(selectedTicketsList);
  setBulkSoldMarketplaceId('');
  setBulkSoldCommonLogitixAmount('');
  setShowBulkStatusModal(false);
  setShowBulkSoldModal(true);
  await initBulkSoldFeeConfig(selectedTicketsList);
};

/**
 * Row / seat status dropdowns for multi-ticket selections must follow the same
 * paths as bulk actions (prices, Sold marketplace modal, delist email, etc.).
 */
const applyMultiTicketStatusChangeFromUi = async (tickets: Ticket[], status: string) => {
  if (!tickets.length || !status) return;

  const byId = new Map<string, Ticket>();
  tickets.forEach((ticket) => {
    if (ticket?.tp_id) byId.set(ticket.tp_id, ticket);
  });
  const uniqueTickets = Array.from(byId.values());
  if (uniqueTickets.length <= 1) {
    if (uniqueTickets[0]) {
      void handleStatusChangeConfirm(uniqueTickets[0], status);
    }
    return;
  }

  const targetStatus = normalizeStatusValue(status);
  setBulkSelectedTickets(uniqueTickets);
  setBulkSelectedTicketIds(new Set(uniqueTickets.map((t) => t.tp_id)));
  setBulkNewStatus(status);
  setBulkStatusListingPrices((prev) => ({
    ...getDefaultBulkStatusListingPrices(uniqueTickets),
    ...Object.fromEntries(
      Object.entries(prev).filter(([, value]) => String(value || '').trim()),
    ),
  }));

  if (targetStatus === 'Sold') {
    // Same modal as bulk checkbox → Change Status → Sold (not "Bulk Mark as Sold").
    setShowBulkSoldModal(false);
    setBulkStatusListingPrices({});
    setBulkStatusListingCommonPrice('');
    setBulkSoldMarketplaceId('');
    setBulkSoldLogitixAmounts({});
    setBulkSoldCommonLogitixAmount('');
    setBulkNewStatus('Sold');
    setShowBulkStatusModal(true);
    void initBulkSoldFeeConfig(uniqueTickets);
    return;
  }

  // Listed (Non-VL): open bulk status modal so admin can set listing prices (same as Listed price UX).
  if (targetStatus === 'List_NonVL') {
    const priceMap = getDefaultBulkStatusListingPrices(uniqueTickets);
    setBulkStatusListingPrices(priceMap);
    const uniquePrices = [
      ...new Set(Object.values(priceMap).filter((v) => String(v).trim() !== '')),
    ];
    setBulkStatusListingCommonPrice(uniquePrices.length === 1 ? uniquePrices[0] : '');
    setBulkNewStatus('List_NonVL');
    setShowBulkStatusModal(true);
    return;
  }

  if (targetStatus === 'DelistInProgress' || targetStatus === 'DelistRequested') {
    openBulkDelistEmailModal(uniqueTickets.map((t) => t.tp_id));
    return;
  }

  await handleBulkStatusChange({ tickets: uniqueTickets, status });
};

const closeBulkSoldModal = () => {
  setShowBulkSoldModal(false);
  resetBulkSoldState();
};

const openSoldModalForTicket = async (ticket: Ticket) => {
  if (!ticket) return;

  if ((!marketplacesList.length && !marketplacesLoading) || marketplacesError) {
    try {
      await refetchMarketplaces();
    } catch (err: any) {
      console.error('Failed to refetch marketplaces for sold modal:', err);
    }
  }

  setSoldModalTicket(ticket);
  setSoldMarketplaceId('');
setSoldLogitixAmount('0');
  setSoldFeeConfigReady(false);
  const initialClientType = getResolvedClientType(ticket);
  if (initialClientType === 'self-pricing') {
    setSoldFeeMode('super');
    setSoldSuperClientPercent(SELF_PRICING_FEE_FALLBACK);
  } else {
    setSoldFeeMode('default');
    setSoldSuperClientPercent(null);
  }
  setShowSoldModal(true);

  if (ticket.u_id) {
    const currentType = getResolvedClientType(ticket);
    if (currentType === 'self-pricing') {
      const percent = await resolveSelfPricingClientPercent(ticket.u_id);
      setSoldFeeMode('super');
      setSoldSuperClientPercent(percent);
    } else {
      const conciergePercent = await fetchUserFeePercent(ticket.u_id);
      if (conciergePercent != null) {
        setSoldFeeMode('super');
        setSoldSuperClientPercent(conciergePercent);
      } else {
        setSoldFeeMode('default');
        setSoldSuperClientPercent(null);
      }
    }
  }

  setSoldFeeConfigReady(true);
};

// Get common status badge class
const getCtBadgeClassForStatus = (status: string): string => {
  const s = (status || '').toLowerCase().replace(/\s/g, '');
  if (s === 'verified' || s === 'verified_nonvl') return styles.ctStatusBadgeVerified;
  if (s === 'list' || s === 'listed' || s === 'list_nonvl') return styles.ctStatusBadgeListed;
  if (s === 'sold') return styles.ctStatusBadgeSold;
  if (s === 'tobeverified') return styles.ctStatusBadgeToBeVerified;
  if (s === 'invalid') return styles.ctStatusBadgeInvalid;
  if (s === 'delist' || s === 'delist_nonvl' || s === 'delistinprogress' || s === 'delistinprogress_nonvl') {
    return styles.ctStatusBadgeDefault;
  }
  return styles.ctStatusBadgeDefault;
};
  // Filtered status counts for dropdown
  const [filteredStatusCounts, setFilteredStatusCounts] = useState<Record<string, number>>({});

  // Sync Listing Expired tab count whenever trackingCounts updates
  useEffect(() => {
    if (isMarketingUser) return;
    setTabCounts(prev => ({ ...prev, 'Listing Expired': trackingCounts.listingExpired }));
  }, [trackingCounts.listingExpired, isMarketingUser]);

  // Delist email modal states
  const [showDelistEmailModal, setShowDelistEmailModal] = useState(false);
  const [pendingDelistTpIds, setPendingDelistTpIds] = useState<string[]>([]);
  const { register: registerDelistEmail, handleSubmit: handleDelistEmailSubmit, formState: { errors: delistEmailErrors }, reset: resetDelistEmail, watch: watchDelistEmail } = useForm<{ email: string; confirmEmail: string }>({
    defaultValues: { email: '', confirmEmail: '' },
    mode: 'onTouched'
  });

  // ==================== HELPER FUNCTIONS FOR FILTER COUNTS ====================
  
const calculateFilteredCounts = (tickets: Ticket[], currentTab: string) => {
  const counts: Record<string, number> = {};
  
  switch (currentTab) {
    case 'Verification':
      const toBeVerifiedCount = tickets.filter(t => t.tp_status === 'ToBeVerified').length;
      const verifiedCount = tickets.filter(t => t.tp_status === 'Verified').length;
      counts['All Verification'] = toBeVerifiedCount + verifiedCount;
      counts['ToBeVerified'] = toBeVerifiedCount;
      counts['Verified'] = verifiedCount;
      break;
    case 'Verified':
      // When viewing the Verified tab directly, the tickets array is already scoped to Verified
      counts['Verified'] = tickets.length;
      break;
    case 'Listed':
      counts['All Listed'] = tickets.length;
      counts['List'] = tickets.filter(t => t.tp_status === 'List').length;
      break;
    case 'Sold':
      counts['All Sold'] = tickets.length;
      counts['Sold'] = tickets.filter(t => getSoldStatusFilterValue(t) === 'Sold').length;
      counts['Settled'] = tickets.filter(t => getSoldStatusFilterValue(t) === 'Settled').length;
      counts['SettlementInProgress'] = tickets.filter(
        t => getSoldStatusFilterValue(t) === 'SettlementInProgress'
      ).length;
      counts['Failed'] = tickets.filter(t => getSoldStatusFilterValue(t) === 'Failed').length;
      counts['Voided_Payout'] = tickets.filter(t => getSoldStatusFilterValue(t) === 'Voided_Payout').length;
      counts['PaidManuallyViaQA'] = tickets.filter(t => getSoldStatusFilterValue(t) === 'PaidManuallyViaQA').length;
      counts['QADeniedPayout'] = tickets.filter(t => getSoldStatusFilterValue(t) === 'QADeniedPayout').length;
      counts['UnderReview'] = tickets.filter(t => getSoldStatusFilterValue(t) === 'UnderReview').length;
      break;
    case 'Delisted':
      counts['All Delisted'] = tickets.length;
      counts['Delist'] = tickets.filter(t => t.tp_status === 'Delist').length;
      counts['DelistInProgress'] = tickets.filter(
        t => t.tp_status === 'DelistInProgress' || t.tp_status === 'DelistRequested'
      ).length;
      counts['Return'] = tickets.filter(t => t.tp_status === 'Return').length;
      break;
    case 'Deleted':
      counts['All Deleted'] = tickets.length;
      counts['Deleted'] = tickets.length;
      break;
    case 'Listing Request Cancelled':
      counts['All Listing Request Cancelled'] = tickets.length;
      counts['ListingRequestCancelled'] = tickets.filter(t => t.tp_status === 'ListingRequestCancelled').length;
      break;
    case 'Returned':
      // When viewing Returned tab directly, tickets array should be scoped to returned tickets
      counts['Return'] = tickets.length;
      break;
    case 'Listing Expired':
      counts['All Expired'] = tickets.length;
      counts['Closed'] = tickets.filter(t => t.tp_status === 'Closed').length;
      break;
    case 'Unsold':
      counts['All Unsold'] = tickets.length;
      counts['Unsold'] = tickets.filter(t => t.tp_status === 'Unsold').length;
      break;
    default:
      break;
  }
  
  return counts;
};
  // ==================== FETCH TRACKING COUNTS ====================

  const { refetch: refetchExpired } = useQuery(GET_EXPIRED_TICKETS, {
    fetchPolicy: 'network-only',
    skip: true,
    onError: () => {
      setTrackingCounts(prev => ({ ...prev, listingExpired: 0 }));
    }
  });

  const { refetch: refetchFailed } = useQuery(GET_FAILED_TRANSFER_TICKETS, {
    fetchPolicy: 'network-only',
    skip: true,
    onError: () => {
      setTrackingCounts(prev => ({ ...prev, failedTransfer: 0 }));
    }
  });

  const getFailedTransferCutoffDate = () => {
    const cutoff = new Date(Date.now() - 48 * 60 * 60 * 1000);
    return cutoff.toISOString();
  };

  const filterFailedTransferByCutoff = (tickets: Ticket[], cutoffIso: string): Ticket[] => {
    const cutoffTs = Date.parse(cutoffIso);
    if (!Number.isFinite(cutoffTs)) return tickets;
    return tickets.filter((ticket: any) => {
      const updatedAtTs = Date.parse(ticket?.tp_updated_at || '');
      if (!Number.isFinite(updatedAtTs)) {
        // Keep rows with missing timestamps for operational visibility.
        return true;
      }
      return updatedAtTs <= cutoffTs;
    });
  };

  const [verificationCutoffDate] = useState(() => {
    const cutoff = new Date(Date.now() - 48 * 60 * 60 * 1000);
    return cutoff.toISOString();
  });

  const parseEventDateValue = (value: any): Date | null => {
    if (!value) return null;

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value;
    }

    const asString = String(value).trim();
    if (!asString) return null;

    // First try native parsing for ISO/timestamptz values.
    const nativeParsed = new Date(asString);
    if (!Number.isNaN(nativeParsed.getTime())) {
      return nativeParsed;
    }

    // Fallback: parse DD/MM/YYYY (and DD/MM/YYYY HH:mm[:ss]) explicitly.
    const datePart = asString.split(' ')[0];
    const [ddRaw, mmRaw, yyyyRaw] = datePart.split('/');
    const dd = Number(ddRaw);
    const mm = Number(mmRaw);
    const yyyy = Number(yyyyRaw);
    if (!Number.isFinite(dd) || !Number.isFinite(mm) || !Number.isFinite(yyyy)) {
      return null;
    }
    const parsed = new Date(yyyy, mm - 1, dd);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return parsed;
  };

  const isUnparseableEventTimezoneLabel = (value: any): boolean => {
    const asString = String(value ?? '').trim();
    if (!asString) return true;
    if (asString.toLowerCase() === 'invalid date') return true;
    // Human labels like "Monday ~ Oct 06 ~ 7:00 pm CDT" are display-only.
    return asString.includes('~');
  };

  const getAutoExpireEventFields = (ticket: any) => ({
    e_date: ticket?.e_date ?? ticket?.ticket?.event?.e_date,
    e_date_time_zone: ticket?.e_date_time_zone ?? ticket?.ticket?.event?.e_date_time_zone,
  });

  const isEventExpired = (ticket: any, nowTs: number) => {
    const { e_date, e_date_time_zone } = getAutoExpireEventFields(ticket);

    // Primary: event start timestamp from e_date (matches DB e.e_date < NOW()).
    const eventDate = parseEventDateValue(e_date);
    if (eventDate && eventDate.getTime() <= nowTs) {
      return true;
    }

    // Fallback: only parse machine-readable timezone values.
    if (!isUnparseableEventTimezoneLabel(e_date_time_zone)) {
      const tzDate = parseEventDateValue(e_date_time_zone);
      if (tzDate && tzDate.getTime() <= nowTs) {
        return true;
      }
    }

    return false;
  };

  const mergeAutoExpireCandidates = (expireCandidatesData: any): any[] => {
    const fromManageView = expireCandidatesData?.filtermanagetickets || [];
    const fromPlacement = expireCandidatesData?.ticket_placement || [];
    const seen = new Set<string>();
    const merged: any[] = [];

    [...fromManageView, ...fromPlacement].forEach((ticket: any) => {
      const tpId = ticket?.tp_id;
      if (!tpId || seen.has(tpId)) return;
      seen.add(tpId);
      merged.push(ticket);
    });

    return merged;
  };

  const mapPlacementToExpiredTicket = (row: any): Ticket => {
    const user = row?.ticket?.user;
    const event = row?.ticket?.event;
    const fullName = [user?.u_first_name, user?.u_last_name].filter(Boolean).join(' ').trim();
    return {
      tp_id: row.tp_id,
      t_id: row.t_id || row?.ticket?.t_id,
      tp_section: row.tp_section,
      tp_row: row.tp_row,
      tp_seat_no: row.tp_seat_no,
      tp_list_price: row.tp_list_price,
      tp_status: row.tp_status,
      tp_updated_at: row.tp_updated_at,
      tp_delist_requested_email: row.tp_delist_requested_email,
      tp_is_support_vanderbilt_nil_fund: row.tp_is_support_vanderbilt_nil_fund,
      e_id: event?.e_id,
      e_name: event?.e_name || '',
      e_date: event?.e_date || '',
      e_address: event?.e_address || '',
      e_brand_name: event?.e_brand_name,
      e_status: event?.e_status,
      e_time_zone: event?.e_time_zone,
      e_date_time_zone: event?.e_date_time_zone,
      e_vl_production_id: event?.e_vl_production_id,
      u_id: user?.u_id || row?.ticket?.u_id || '',
      u_first_name: user?.u_first_name,
      u_last_name: user?.u_last_name,
      u_email_id: user?.u_email_id,
      u_original_email: user?.u_original_email,
      u_pricing_mode: user?.u_pricing_mode,
      u_fee_percentage: user?.u_fee_percentage,
      u_full_name: fullName,
      l_name: '',
    } as Ticket;
  };

  const mergeExpiredTicketsResponse = (responseData: any): Ticket[] => {
    const fromView = (responseData?.filterreturntickets || []) as Ticket[];
    const viewIds = new Set(fromView.map((ticket) => ticket.tp_id));
    const orphans = (responseData?.closed_orphan_placements || [])
      .filter((row: any) => row?.tp_id && !viewIds.has(row.tp_id))
      .map(mapPlacementToExpiredTicket);
    return [...fromView, ...orphans];
  };

  const getExpiredTicketsCount = (responseData: any): number => {
    const closedCount = responseData?.closed_placement_aggregate?.aggregate?.count;
    const returnCount = responseData?.filterreturntickets_aggregate?.aggregate?.count;
    // closed_placement can be 0 when startDateTime/endDateTime were omitted — don't let that
    // mask a real filterreturntickets aggregate. Prefer the larger of the two when both exist.
    if (typeof closedCount === 'number' && typeof returnCount === 'number') {
      return Math.max(closedCount, returnCount);
    }
    if (typeof closedCount === 'number' && closedCount > 0) return closedCount;
    if (typeof returnCount === 'number') return returnCount;
    if (typeof closedCount === 'number') return closedCount;
    return mergeExpiredTicketsResponse(responseData).length;
  };

  const extractTicketsFromActiveTabQuery = (
    responseData: any,
    tab: string,
  ): { tickets: Ticket[]; totalCountValue: number } | null => {
    if (!responseData) return null;

    switch (tab) {
      case 'Verification':
      case 'Verified': {
        const tickets = Array.isArray(responseData.filtermanagetickets)
          ? responseData.filtermanagetickets
          : [];
        const totalCountValue = responseData.filtermanagetickets_aggregate?.aggregate?.count;
        if (totalCountValue == null && tickets.length === 0) return null;
        return {
          tickets,
          totalCountValue: totalCountValue ?? tickets.length,
        };
      }
      case 'Listed': {
        const tickets = Array.isArray(responseData.filterlisttickets)
          ? responseData.filterlisttickets
          : [];
        const totalCountValue = responseData.filterlisttickets_aggregate?.aggregate?.count;
        if (totalCountValue == null && tickets.length === 0) return null;
        return {
          tickets,
          totalCountValue: totalCountValue ?? tickets.length,
        };
      }
      case 'Sold':
      case 'Under Review': {
        const tickets = Array.isArray(responseData.FilterSoldTickets)
          ? responseData.FilterSoldTickets
          : [];
        const totalCountValue = responseData.FilterSoldTickets_aggregate?.aggregate?.count;
        if (totalCountValue == null && tickets.length === 0) return null;
        return {
          tickets,
          totalCountValue: totalCountValue ?? tickets.length,
        };
      }
      case 'Delisted': {
        const tickets = Array.isArray(responseData.filterreturntickets)
          ? responseData.filterreturntickets
          : [];
        const totalCountValue = responseData.filterreturntickets_aggregate?.aggregate?.count;
        if (totalCountValue == null && tickets.length === 0) return null;
        return {
          tickets,
          totalCountValue: totalCountValue ?? tickets.length,
        };
      }
      case 'Listing Request Cancelled': {
        const tickets = Array.isArray(responseData.filterListingRequestCancelledTickets)
          ? responseData.filterListingRequestCancelledTickets
          : [];
        const totalCountValue =
          responseData.filterListingRequestCancelledTickets_aggregate?.aggregate?.count;
        if (totalCountValue == null && tickets.length === 0) return null;
        return {
          tickets,
          totalCountValue: totalCountValue ?? tickets.length,
        };
      }
      case 'Deleted': {
        const tickets = mapDeletedPlacementRows(responseData.ticket_placement);
        const totalCountValue = responseData.ticket_placement_aggregate?.aggregate?.count;
        if (totalCountValue == null && tickets.length === 0) return null;
        return {
          tickets,
          totalCountValue: totalCountValue ?? tickets.length,
        };
      }
      case 'Listing Expired':
        return {
          tickets: mergeExpiredTicketsResponse(responseData),
          totalCountValue: getExpiredTicketsCount(responseData),
        };
      case 'Unsold': {
        const tickets = Array.isArray(responseData.filterUnsoldTickets)
          ? responseData.filterUnsoldTickets
          : [];
        const totalCountValue = responseData.filterUnsoldTickets_aggreagate?.aggregate?.count;
        if (totalCountValue == null && tickets.length === 0) return null;
        return {
          tickets,
          totalCountValue: totalCountValue ?? tickets.length,
        };
      }
      default:
        return null;
    }
  };

  const normalizeCardDetailTrackingRows = (tickets: any[] = []): Ticket[] =>
    tickets.map((ticket: any) => ({
      ...ticket,
      e_name: ticket?.e_name || ticket?.ticket?.event?.e_name || '',
      e_date: ticket?.e_date || ticket?.ticket?.event?.e_date || '',
      e_address: ticket?.e_address || ticket?.ticket?.event?.e_address || '',
      e_vl_production_id:
        ticket?.e_vl_production_id ||
        ticket?.ticket?.event?.e_vl_production_id ||
        '',
      u_id: ticket?.u_id || ticket?.ticket?.user?.u_id || '',
      u_full_name:
        ticket?.u_full_name ||
        ticket?.full_name ||
        ticket?.ticket?.user?.u_full_name ||
        '',
      u_first_name: ticket?.u_first_name || ticket?.ticket?.user?.u_first_name || '',
      u_last_name: ticket?.u_last_name || ticket?.ticket?.user?.u_last_name || '',
      u_email_id: ticket?.u_email_id || ticket?.ticket?.user?.u_email_id || ''
    })) as Ticket[];

  async function fetchTrackingCounts() {
    // Auto-move verification tickets to Listing Expired once event date-time has passed.
    const now = Date.now();
    let autoExpiredCount = 0;
    if (!autoExpireInFlightRef.current) {
      autoExpireInFlightRef.current = true;
      try {
        const { data: expireCandidatesData, error: expireQueryError, errors: expireQueryErrors } = await fetchTicketsToAutoExpire({
          variables: {
            pricingMode: '%'
          }
        });
        if (expireQueryError || expireQueryErrors?.length) {
          console.error('[ManageTickets] Auto-expire candidate query failed:', expireQueryError || expireQueryErrors);
        } else {
          const tpIdsToExpire: string[] = mergeAutoExpireCandidates(expireCandidatesData)
            .filter((ticket: any) => isEventExpired(ticket, now))
            .map((ticket: any) => ticket?.tp_id)
            .filter(Boolean);

          console.log('[ManageTickets] Auto-expire scan:', {
            candidatesFetched: mergeAutoExpireCandidates(expireCandidatesData).length,
            pastEventMatches: tpIdsToExpire.length
          });

          if (tpIdsToExpire.length > 0) {
            const expireResult = await bulkExpireTickets({ variables: { tp_ids: tpIdsToExpire } });
            if (expireResult.errors?.length) {
              console.error('[ManageTickets] Auto-expire bulk errors:', expireResult.errors);
            }
            let bulkAffectedRows = expireResult?.data?.update_ticket_placement?.affected_rows || 0;

            // Fallback: if bulk update is blocked by role/permission constraints,
            // try per-ticket updates using the existing single status mutation path.
            if (bulkAffectedRows === 0) {
              let singleUpdatedCount = 0;
              for (const tpId of tpIdsToExpire) {
                try {
                  const singleRes = await updateTicketStatusSilent({
                    variables: {
                      tp_id: tpId,
                      status: 'Closed'
                    }
                  });
                  if (singleRes.errors?.length) {
                    console.error('[ManageTickets] Auto-expire single update errors:', { tpId, errors: singleRes.errors });
                  }
                  const updated = singleRes?.data?.update_ticket_placement?.returning?.length || 0;
                  if (updated > 0) {
                    singleUpdatedCount += 1;
                  }
                } catch (singleUpdateError) {
                  console.error('[ManageTickets] Auto-expire single update failed:', { tpId, singleUpdateError });
                }
              }
              bulkAffectedRows = singleUpdatedCount;
              console.log('[ManageTickets] Auto-expire fallback result:', {
                candidates: tpIdsToExpire.length,
                singleUpdatedCount
              });
            }

            autoExpiredCount = bulkAffectedRows;
            console.log('[ManageTickets] Auto-expired tickets:', {
              candidates: tpIdsToExpire.length,
              affectedRows: bulkAffectedRows
            });
          }
        }
      } catch (autoExpireError) {
        console.error('[ManageTickets] Auto-expire failed:', autoExpireError);
      } finally {
        autoExpireInFlightRef.current = false;
      }
    }

    if (autoExpiredCount > 0) {
      await refetch();
    }

    const getTrackingCount = (responseData: any): number => {
      if (responseData?.closed_placement_aggregate?.aggregate?.count != null) {
        return responseData.closed_placement_aggregate.aggregate.count;
      }
      return (
        responseData?.ticket_placement_aggregate?.aggregate?.count ||
        responseData?.filterreturntickets_aggregate?.aggregate?.count ||
        responseData?.filtermanagetickets_aggregate?.aggregate?.count ||
        0
      );
    };

    

    const getTrackingTickets = (responseData: any): Ticket[] => {
      if (responseData?.closed_orphan_placements || responseData?.filterreturntickets) {
        return mergeExpiredTicketsResponse(responseData);
      }
      if (Array.isArray(responseData?.ticket_placement)) return responseData.ticket_placement as Ticket[];
      if (Array.isArray(responseData?.filtermanagetickets)) return responseData.filtermanagetickets as Ticket[];
      if (Array.isArray(responseData?.filterreturntickets)) return responseData.filterreturntickets as Ticket[];
      return [];
    };

    try {
      const trackingVariables = {
        ...getManageTicketsTrackingVariables(
          eventVenueSearch ? `%${eventVenueSearch}%` : '%',
          getCustomerSearchPattern(clientSearch),
        ),
        ...getQueryVariables(),
      };
      if (startDate) {
        const range = getLocalDayRangeUTC(startDate);
        (trackingVariables as Record<string, unknown>).startdate = range?.startUTC ?? startDate;
      } else {
        (trackingVariables as Record<string, unknown>).startdate = '1970-01-01';
      }
      if (endDate) {
        const range = getLocalDayRangeUTC(endDate);
        (trackingVariables as Record<string, unknown>).enddate = range?.endUTC ?? endDate;
      } else {
        (trackingVariables as Record<string, unknown>).enddate = '9999-12-31';
      }

      const expiredResult = await refetchExpired(trackingVariables);
      const cutoffDate = getFailedTransferCutoffDate();
      const failedResult = await refetchFailed(trackingVariables);

 const expiredTickets = mergeExpiredTicketsResponse(expiredResult?.data);
      const failedTickets = filterFailedTransferByCutoff(
        getTrackingTickets(failedResult?.data),
        cutoffDate
      );

      // Keep tracking cards in the same user scope as table data for marketing users.
      const scopedExpiredTickets = isMarketingUser
        ? expiredTickets.filter((ticket) => managedUserIds.has(ticket.u_id))
        : expiredTickets;
      const scopedFailedTickets = isMarketingUser
        ? failedTickets.filter((ticket) => managedUserIds.has(ticket.u_id))
        : failedTickets;

      setTrackingCounts({
        listingExpired: isMarketingUser ? scopedExpiredTickets.length : getTrackingCount(expiredResult?.data),
        failedTransfer: scopedFailedTickets.length
      });
      const ids = new Set<string>(
        (isMarketingUser
          ? scopedFailedTickets
          : filterFailedTransferByCutoff((failedResult?.data?.filtermanagetickets || []) as Ticket[], cutoffDate))
          .map((t: any) => t.tp_id as string)
      );
      setFailedTransferTicketIds(ids);

      const modalOpen = showCardDetailModalRef.current;
      if (modalOpen === 'FAILED_TRANSFER') {
        setCardDetailTickets(sortManageTickets(normalizeCardDetailTrackingRows(scopedFailedTickets as any[])));
      } else if (modalOpen === 'LISTING_EXPIRED') {
        setCardDetailTickets(sortManageTickets(normalizeCardDetailTrackingRows(scopedExpiredTickets as any[])));
      }
    } catch {
      // silent
    }
  };

  const updateLocalTicketPrice = (tpIds: string[], newPrice: number) => {
    const now = new Date().toISOString();
    tpIds.forEach((id) => {
      localPriceOverridesRef.current[id] = { price: newPrice, lastPricedAt: now };
    });
    const updateTicket = (ticket: Ticket) =>
      tpIds.includes(ticket.tp_id)
        ? {
            ...ticket,
            tp_list_price: newPrice,
            last_priced_at: now,
          }
        : ticket;

    setFilteredTickets((prevTickets) => prevTickets.map(updateTicket));
    setStatusFilterSourceTickets((prevTickets) => prevTickets.map(updateTicket));

    if (hasActiveEventSearch(eventVenueSearch)) {
      setEventGroupedByStatus((prev) => {
        const next = createEmptyEventGroupedTickets();

        for (const section of EVENT_TICKET_VIEW_SECTIONS) {
          for (const ticket of prev[section.key] ?? []) {
            next[section.key].push(updateTicket(ticket));
          }
        }

        return next;
      });
    }
  };

  /** Apply live price updates from Socket.IO (cron / other admin syncs). */
  const applySocketConciergePriceUpdates = useCallback(
    (payload: ConciergePricesUpdatedPayload) => {
      const updates = payload?.updates || [];
      if (!updates.length) return;

      const byTpId = new Map(
        updates
          .filter((u) => u?.tpId && u.newPrice != null && Number.isFinite(Number(u.newPrice)))
          .map((u) => [u.tpId, u]),
      );
      if (!byTpId.size) return;

      const now = new Date().toISOString();
      const applyTicket = (ticket: Ticket): Ticket => {
        const row = byTpId.get(ticket.tp_id);
        if (!row) return ticket;
        const lastPricedAt = row.lastPricedAt || payload.at || now;
        const nextPrice = Number(row.newPrice);
        localPriceOverridesRef.current[ticket.tp_id] = {
          price: nextPrice,
          lastPricedAt,
        };
        return {
          ...ticket,
          tp_list_price: nextPrice,
          last_priced_at: lastPricedAt,
          ...(row.vlListingId ? { tp_vl_listing_id: row.vlListingId } : {}),
          ...(row.vlItemId ? { tp_vl_item_id: row.vlItemId } : {}),
        };
      };

      setFilteredTickets((prev) => {
        let changed = false;
        const next = prev.map((ticket) => {
          if (!byTpId.has(ticket.tp_id)) return ticket;
          changed = true;
          return applyTicket(ticket);
        });
        return changed ? next : prev;
      });

      if (hasActiveEventSearch(eventVenueSearch)) {
        setEventGroupedByStatus((prev) => {
          let changed = false;
          const next = createEmptyEventGroupedTickets();
          for (const section of EVENT_TICKET_VIEW_SECTIONS) {
            for (const ticket of prev[section.key] ?? []) {
              if (byTpId.has(ticket.tp_id)) changed = true;
              next[section.key].push(applyTicket(ticket));
            }
          }
          if (!changed) return prev;
          return next;
        });
      }
    },
    [eventVenueSearch],
  );

  const applySocketHoldUpdates = useCallback(
    (payload: TicketHoldsUpdatedPayload) => {
      const updates = payload?.updates || [];
      if (!updates.length) return;

      const byTpId = new Map(
        updates
          .filter((u) => u?.tpId && typeof u.onHold === 'boolean')
          .map((u) => [u.tpId, u.onHold === true]),
      );
      if (!byTpId.size) return;

      void apolloClient
        .query({
          query: GET_LIST_TICKETS,
          variables: getQueryVariables(),
          fetchPolicy: 'network-only',
        })
        .then(({ data: refreshedData }) => {
          const refreshedCount = refreshedData?.FilterListed_onHold_aggregate?.aggregate?.count;
          if (typeof refreshedCount === 'number') setOnHoldCount(refreshedCount);
        })
        .catch((error) => {
          console.error('[ManageTickets] Failed to refresh on-hold count:', error);
        });

      const applyTicket = (ticket: Ticket): Ticket => {
        if (!byTpId.has(ticket.tp_id)) return ticket;
        return { ...ticket, on_hold: byTpId.get(ticket.tp_id) };
      };

      setFilteredTickets((prev) => {
        let changed = false;
        const next: Ticket[] = [];
        for (const ticket of prev) {
          if (!byTpId.has(ticket.tp_id)) {
            next.push(ticket);
            continue;
          }
          const nextHold = byTpId.get(ticket.tp_id) === true;
          if (onHoldFilter && !nextHold) {
            changed = true;
            continue;
          }
          if (ticket.on_hold === nextHold) {
            next.push(ticket);
            continue;
          }
          changed = true;
          next.push(applyTicket(ticket));
        }
        return changed ? sortManageTickets(next) : prev;
      });

      if (hasActiveEventSearch(eventVenueSearch)) {
        setEventGroupedByStatus((prev) => {
          let changed = false;
          const next = createEmptyEventGroupedTickets();
          for (const section of EVENT_TICKET_VIEW_SECTIONS) {
            for (const ticket of prev[section.key] ?? []) {
              if (byTpId.has(ticket.tp_id)) changed = true;
              next[section.key].push(applyTicket(ticket));
            }
          }
          if (!changed) return prev;
          for (const key of EVENT_TICKET_VIEW_SECTIONS.map((s) => s.key)) {
            next[key] = sortManageTickets(next[key]);
          }
          return next;
        });
      }
    },
    [apolloClient, eventVenueSearch, onHoldFilter],
  );

  useConciergePriceSocket(applySocketConciergePriceUpdates, true, applySocketHoldUpdates);

  // Price update mutation — Step 6: only show success when VL+QA agree (backend throws otherwise).
  // Never fall back to a DB-only price write; that would leave the dashboards out of sync.
  const [editTicketPrice, { loading: priceLoading }] = useMutation(EDIT_TICKET_PRICE_BY_SUPER_USER, {
    fetchPolicy: 'network-only',
onCompleted: (data) => {
  if (suppressListPublishChainToastsRef.current) {
    return;
  }

  const payload = data?.editTicketPriceBySuperUser;
  const message = String(payload?.message || '').trim();
  const priceTpIds = pendingPriceTpIdsRef.current.length
    ? pendingPriceTpIdsRef.current
    : selectedTpIds;

  if (payload?.requiresConfirmation) {
    info(message || 'Price update requires confirmation.');
    pendingPriceTpIdsRef.current = [];
    markPriceUpdatePending(priceTpIds, false);
    handlePriceUpdateSuccess();
    return;
  }

  if (!isVictoryLivePriceUpdateSuccess(message)) {
    showError(priceUpdateFailureMessage(message));
    pendingPriceTpIdsRef.current = [];
    markPriceUpdatePending(priceTpIds, false);
    handlePriceUpdateSuccess();
    return;
  }

  const price = pendingFinalPriceRef.current;
  if (priceTpIds.length && Number.isFinite(price) && !isNaN(price)) {
    updateLocalTicketPrice(priceTpIds, price);
  }
  pendingPriceTpIdsRef.current = [];
  markPriceUpdatePending(priceTpIds, false);
  success(message);
  handlePriceUpdateSuccess();
},
    onError: (err) => {
      const priceTpIds = [...pendingPriceTpIdsRef.current];
      pendingPriceTpIdsRef.current = [];
      markPriceUpdatePending(priceTpIds, false);
      if (suppressListPublishChainToastsRef.current) {
        return;
      }
      if (isBackendActionTimeoutError(err)) {
        info(PRICE_UPDATE_TIMEOUT_INFO);
        handlePriceUpdateSuccess();
        return;
      }
      showError(
        getErrorMessage(err, 'Failed to update price on VL side please try again.'),
      );
      handlePriceUpdateSuccess();
    },
  });

  const [publishNonVlListingMutation] = useMutation(PUBLISH_NON_VL_LISTING, { fetchPolicy: 'network-only' });
  const [restoreDeletedToVerification] = useMutation(RESTORE_DELETED_TO_VERIFICATION, { fetchPolicy: 'network-only' });

  // Update Ticket Details Mutation
  const [updateTicketDetails] = useMutation(UPDATE_TICKET_DETAILS, {
    fetchPolicy: 'network-only',
    onError: () => {
      // Error handled by executeEditDetailsWithFallback
    }
  });

  const [updateTicketCommissionOverride] = useMutation(UPDATE_TICKET_COMMISSION_OVERRIDE, { fetchPolicy: 'network-only' });
  const [updateTicketBarcodeStatus] = useMutation(UPDATE_TICKET_BARCODE_STATUS, { fetchPolicy: 'network-only' });
  const [updateListedTicketCost] = useMutation(UPDATE_LISTED_TICKET_COST, { fetchPolicy: 'network-only' });

  const handleBarcodeStatusUpdate = async (tickets: Ticket[], status: BarcodeStatusValue) => {
    const tpIds = [...new Set(tickets.map((ticket) => ticket.tp_id).filter(Boolean))];
    if (!tpIds.length) return;
    try {
      const result = await updateTicketBarcodeStatus({ variables: { tpIds, status } });
      if (result.data?.update_ticket_placement?.affected_rows !== tpIds.length) {
        throw new Error('Not all ticket barcode statuses were updated.');
      }
      updateLocalTicketDetails(tpIds, { tp_barcode_status: status });
      success(`Barcode status set to ${status}.`);
    } catch (error) {
      showError(getErrorMessage(error, 'Unable to update barcode status.'));
    }
  };

  const openCommissionModal = (ticket: Ticket) => {
    if (!canEditSoldCommission) return;
    setSelectedTicketForCommission(ticket);
    setCommissionOverrideInput(
      ticket.tp_sales_rep_commission_override == null
        ? getSalesRepCommission(ticket).toFixed(2)
        : Number(ticket.tp_sales_rep_commission_override).toFixed(2),
    );
    setShowCommissionModal(true);
  };

  const handleCommissionOverrideSave = async () => {
    if (!canEditSoldCommission || !selectedTicketForCommission || commissionOverrideLoading) return;
    const rawValue = commissionOverrideInput.trim();
    if (!rawValue || !/^\d+(\.\d{1,2})?$/.test(rawValue)) {
      showError('Enter a valid commission amount with up to 2 decimal places.');
      return;
    }
    const amount = Number(rawValue);
    if (!Number.isFinite(amount) || amount < 0) {
      showError('Commission amount cannot be negative.');
      return;
    }

    setCommissionOverrideLoading(true);
    try {
      const result = await updateTicketCommissionOverride({
        variables: { tp_id: selectedTicketForCommission.tp_id, commissionOverride: amount },
      });
      if (!result.data?.update_ticket_placement?.affected_rows) {
        throw new Error('No ticket line was updated.');
      }
      const updateCommission = (ticket: Ticket) =>
        ticket.tp_id === selectedTicketForCommission.tp_id
          ? { ...ticket, tp_sales_rep_commission_override: amount }
          : ticket;
      setFilteredTickets((prevTickets) => sortManageTickets(prevTickets.map(updateCommission)));
      setUnfilteredTabTickets((prevTickets) => sortManageTickets(prevTickets.map(updateCommission)));
      success('Commission amount updated successfully.');
      closeCommissionModal();
      await refetch();
    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to update commission amount.'));
    } finally {
      setCommissionOverrideLoading(false);
    }
  };


  const [validateUpdate] = useMutation(VALIDATE_UPDATE, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (suppressListPublishChainToastsRef.current) {
        return;
      }
      const message = (data?.updateTicketStatus?.message || '').trim();
      const normalized = message.toLowerCase();

      const hasSuccess = normalized.includes('success');
      const hasNotFound = normalized.includes('not found');
      const hasFailure =
        normalized.includes('failed') ||
        normalized.includes('error') ||
        normalized.includes("couldn't");
      const hasZeroUpdated =
        normalized.includes('0 ticket updated successfully') ||
        normalized.includes('0 tickets updated successfully');

      if ((hasNotFound && hasZeroUpdated) || hasFailure) {
        showError(message || 'Ticket verification failed');
      } else if (hasSuccess && hasNotFound) {
        info(message || 'Ticket verification processed with partial success');
      } else if (hasSuccess) {
        success(message || 'Status updated successfully');
        // Auto-switch to Verification tab when tickets are verified
        if (activeTab !== 'Verification') {
          setTimeout(() => switchTabUnlessEventSearch('Verification'), 300);
        }
      } else {
        info(message || 'Status update processed');
      }
      refetch();
      refetchExpired();
      refetchFailed();
      syncDashboardNotifications();
      handleBulkActionComplete();
    },
    onError: (err) => {
      showError(getErrorMessage(err, 'Failed to update ticket status'));
      handleBulkActionComplete();
    }
  });

  const [publishTickets] = useMutation(PUBLISH_TICKETS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (suppressListPublishChainToastsRef.current) {
        return;
      }
      const message = data?.publishTickets?.message || '';
      const type = resolveActionMessageType(message);
      if (type === 'error') showError(message || 'Failed to publish tickets');
      else if (type === 'success') {
        success(message || 'Ticket(s) published successfully');
        // Auto-switch to Listed tab when tickets are published
        if (activeTab !== 'Listed') {
          setTimeout(() => switchTabUnlessEventSearch('Listed'), 300);
        }
      }
      else info(message || 'Publish request processed');
      refetch();
      refetchExpired();
      refetchFailed();
      syncDashboardNotifications();
      handleBulkActionComplete();
    },
    onError: (err) => {
      showError(getErrorMessage(err, 'Failed to publish tickets'));
      handleBulkActionComplete();
    }
  });

  const [updateTicketStatus] = useMutation(UPDATE_TICKET_STATUS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (suppressListPublishChainToastsRef.current) {
        return;
      }
      if (data?.update_ticket_placement?.returning?.length > 0) {
        success('Ticket status updated successfully!');
        setShowStatusModal(false);
        const updatedTicket = data.update_ticket_placement.returning[0];
        clearSeatCardStatusForTpIds([updatedTicket.tp_id]);
        updateLocalTicketStatus([updatedTicket.tp_id], updatedTicket.tp_status);
        // Auto-switch to the tab for the new status
        const targetTab = getTabForStatus(updatedTicket.tp_status);
        if (targetTab !== activeTab) {
          switchTabUnlessEventSearch(targetTab);
        }
        refetch();
        refetchExpired();
        refetchFailed();
        syncDashboardNotifications();
      } else {
        info('No changes were made');
        setShowStatusModal(false);
      }
      setStatusChangeLoading(false);
    },
    onError: (err) => {
      showError(getErrorMessage(err, 'Failed to update ticket status'));
      setShowStatusModal(false);
      setStatusChangeLoading(false);
    }
  });
const [unfilteredTabTickets, setUnfilteredTabTickets] = useState<Ticket[]>([]);
  const [ticketGroupingBoundaryTickets, setTicketGroupingBoundaryTickets] = useState<Ticket[]>([]);
  const [loadedGroupingBoundaryKey, setLoadedGroupingBoundaryKey] = useState('');
  const ticketGroupingBoundaryRequestRef = useRef(0);
  const [fetchTicketGroupingBoundaryTickets] = useLazyQuery(GET_TICKET_GROUPING_BOUNDARIES, {
    fetchPolicy: 'network-only',
  });
  const [deleteTicketStatus] = useMutation(DELETE_TICKET_STATUS, { fetchPolicy: 'network-only' });
  const [updateTicketStatusSilent] = useMutation(UPDATE_TICKET_STATUS, { fetchPolicy: 'network-only' });
  const [restoreCancelledListingToVerification] = useMutation(RESTORE_CANCELLED_LISTING_TO_VERIFICATION, { fetchPolicy: 'network-only' });
  const [bulkExpireTickets] = useMutation(BULK_EXPIRE_TICKETS, { fetchPolicy: 'network-only' });
  const [fetchTicketsToAutoExpire] = useLazyQuery(GET_TICKETS_TO_AUTO_EXPIRE, { fetchPolicy: 'network-only' });
  const autoExpireInFlightRef = useRef(false);

  const [getSuperUserForSold] = useLazyQuery(GET_SUPER_USER_BY_UID, { fetchPolicy: 'no-cache' });
  const [fetchUserNotesForVlShare] = useLazyQuery(GET_USER_NOTES, { fetchPolicy: 'network-only' });
  const [addQaNote] = useMutation(ADD_USER_NOTE, { fetchPolicy: 'network-only' });
  const [fetchSeatLevelNotes] = useLazyQuery(GET_USER_VICTORY_LIVE_SEAT_NOTES, { fetchPolicy: 'network-only' });
  const [fetchLeagueCategoryNames] = useLazyQuery(GET_LEAGUE_CATEGORIES_BY_IDS, { fetchPolicy: 'network-only' });
  const [insertSeatLevelNote] = useMutation(INSERT_USER_VICTORY_LIVE_SEAT_NOTE, { fetchPolicy: 'network-only' });
  const [updateSeatLevelNote] = useMutation(UPDATE_USER_VICTORY_LIVE_SEAT_NOTE, { fetchPolicy: 'network-only' });

  const listedTicketCostIds = isSuperUser && (activeTab === 'Listed' || activeTab === 'Sold')
    ? filteredTickets.map((ticket) => ticket.tp_id).filter(Boolean)
    : [];
  const listedTicketCostIdsKey = listedTicketCostIds.join(',');
  useEffect(() => {
    if (!isSuperUser || (activeTab !== 'Listed' && activeTab !== 'Sold')) {
      setListedTicketCosts({});
      setListedTicketFees({});
      setListedCostDrafts({});
      return;
    }
    if (!listedTicketCostIds.length) {
      setListedTicketCosts({});
      setListedTicketFees({});
      return;
    }

    let cancelled = false;
    apolloClient.query({
      query: GET_LISTED_TICKET_COSTS,
      variables: { tp_ids: listedTicketCostIds },
      fetchPolicy: 'network-only',
    }).then(({ data }) => {
      if (cancelled) return;
      const costs: Record<string, number | null> = {};
      const fees: Record<string, { fee: number | null; pricingMode: string }> = {};
      (data?.ticket_placement ?? []).forEach((row: {
        tp_id: string;
        tp_cost: number | string | null;
        ticket?: { user?: { u_fee_percentage?: number | string | null; u_pricing_mode?: string | null } | null } | null;
      }) => {
        costs[row.tp_id] = row.tp_cost == null ? null : Number(row.tp_cost);
        const fee = Number(row.ticket?.user?.u_fee_percentage);
        fees[row.tp_id] = {
          fee: Number.isFinite(fee) ? fee : null,
          pricingMode: String(row.ticket?.user?.u_pricing_mode || ''),
        };
      });
      setListedTicketCosts(costs);
      setListedTicketFees(fees);
    }).catch((error: unknown) => {
      if (!cancelled) showError(getErrorMessage(error, 'Unable to load ticket costs'));
    });
    return () => {
      cancelled = true;
    };
  }, [apolloClient, isSuperUser, activeTab, listedTicketCostIdsKey]);

  const fetchMatchingSeatLevelNote = async (ticket: {
    u_id?: string | null;
    lc_id?: string | null;
    tp_section?: string | null;
    tp_row?: string | null;
    tp_seat_no?: string | number | null;
  }): Promise<SeatLevelNote | null> => {
    const userId = String(ticket.u_id || '').trim();
    const teamId = String(ticket.lc_id || '').trim();
    const section = String(ticket.tp_section || '').trim();
    const row = String(ticket.tp_row || '').trim();
    const seatBounds = getSeatBounds(ticket.tp_seat_no);
    if (!userId || !teamId || !section || !row || !seatBounds) return null;

    try {
      const result = await fetchSeatLevelNotes({ variables: { userId } });
      const notes = (result.data?.user_victory_live_seat_notes || []).map(toSeatLevelNote);

      return findMatchingSeatLevelNote(notes, {
        userId,
        teamId,
        section,
        row,
        seatStart: seatBounds.start,
        seatEnd: seatBounds.end,
      });
    } catch (error) {
      console.warn('Failed to load seat-level VL notes', error);
      return null;
    }
  };

  const resolveListingPopupNotes = async (
    tickets: Array<{
      u_id?: string | null;
      lc_id?: string | null;
      tp_section?: string | null;
      tp_row?: string | null;
      tp_seat_no?: string | number | null;
      tp_internal_notes?: string | null;
      tp_external_notes?: string | null;
      internalNotes?: string | null;
      externalNotes?: string | null;
    }>,
  ) => {
    const fallback = {
      internalNotes: String(tickets[0]?.tp_internal_notes || tickets[0]?.internalNotes || ''),
      externalNotes: String(tickets[0]?.tp_external_notes || tickets[0]?.externalNotes || ''),
    };
    const userIds = [...new Set(tickets.map((ticket) => String(ticket.u_id || '').trim()).filter(Boolean))];
    const notesByUser = new Map<string, SeatLevelNote[]>();
    await Promise.all(
      userIds.map(async (userId) => {
        try {
          const result = await fetchSeatLevelNotes({ variables: { userId } });
          const notes: SeatLevelNote[] = (result.data?.user_victory_live_seat_notes || []).map(toSeatLevelNote);
          const teamIds = [...new Set(notes.map((note) => note.teamId).filter(Boolean))];
          let namedNotes = notes;
          if (teamIds.length) {
            try {
              const teams = await fetchLeagueCategoryNames({ variables: { categoryIds: teamIds } });
              namedNotes = withSeatNoteTeamNames(
                notes,
                (teams.data?.league_categories || []).map((team: { lc_id?: string; lc_name?: string | null }) => ({
                  id: team.lc_id,
                  name: team.lc_name,
                })),
              );
            } catch (error) {
              console.warn('Failed to load seat-note team names', error);
            }
          }
          notesByUser.set(userId, namedNotes);
        } catch (error) {
          console.warn('Failed to load seat-level VL notes', error);
          notesByUser.set(userId, []);
        }
      }),
    );

    const locations = tickets.map((ticket) => ({
      userId: String(ticket.u_id || '').trim(),
      teamId: String(ticket.lc_id || '').trim(),
      eventName: String((ticket as { e_name?: string | null }).e_name || '').trim(),
      section: String(ticket.tp_section || '').trim(),
      row: String(ticket.tp_row || '').trim(),
    }));
    const matched = tickets.map((ticket, index) => {
      const location = locations[index];
      const seatBounds = getSeatBounds(ticket.tp_seat_no);
      if (!location.userId || !location.section || !location.row || !seatBounds) return null;
      return findSeatLevelNoteForListing(notesByUser.get(location.userId) || [], {
        ...location,
        seatStart: seatBounds.start,
        seatEnd: seatBounds.end,
      });
    });
    const seats = matched.every(Boolean)
      ? tickets.flatMap((ticket, index) => {
          const note = matched[index];
          if (!note) return [];
          return expandListingSeatNumbers(ticket.tp_seat_no).map((seat) => ({
            section: String(ticket.tp_section || ''),
            row: String(ticket.tp_row || ''),
            seat,
            internalNote: note.internalNote,
            externalNote: note.externalNote,
          }));
        })
      : [];
    const seeded = seats.length ? formatListingPopupNotes(seats) : fallback;
    return seedListingNoteFields({
      notes: [...notesByUser.values()].flat(),
      locations,
      seatNumbers: tickets.flatMap((ticket) => expandListingSeatNumbers(ticket.tp_seat_no)),
      internalSeed: seeded.internalNotes,
      externalSeed: seeded.externalNotes,
    });
  };

  const loadEditDetailNoteChoices = async (tickets: Ticket[]) => {
    const requestId = ++editDetailsNotesRequestRef.current;
    const locations: ListingNoteLocation[] = tickets.map((ticket) => ({
      userId: String(ticket.u_id || '').trim(),
      teamId: String(ticket.lc_id || '').trim(),
      eventName: String(ticket.e_name || '').trim(),
      section: String(ticket.tp_section || '').trim(),
      row: String(ticket.tp_row || '').trim(),
    }));
    const userIds = [...new Set(locations.map((location) => location.userId).filter(Boolean))];
    const notes: SeatLevelNote[] = [];
    await Promise.all(userIds.map(async (userId) => {
      try {
        const result = await fetchSeatLevelNotes({ variables: { userId } });
        const rows: SeatLevelNote[] = (result.data?.user_victory_live_seat_notes || []).map(toSeatLevelNote);
        const teamIds = [...new Set(rows.map((note) => note.teamId).filter(Boolean))];
        let namedNotes = rows;
        if (teamIds.length) {
          try {
            const teams = await fetchLeagueCategoryNames({ variables: { categoryIds: teamIds } });
            namedNotes = withSeatNoteTeamNames(
              rows,
              (teams.data?.league_categories || []).map((team: { lc_id?: string; lc_name?: string | null }) => ({
                id: team.lc_id,
                name: team.lc_name,
              })),
            );
          } catch (error) {
            console.warn('Failed to load seat-note team names', error);
          }
        }
        notes.push(...namedNotes);
      } catch (error) {
        console.warn('Failed to load seat-level VL notes', error);
      }
    }));
    if (requestId !== editDetailsNotesRequestRef.current) return;
    setEditInternalNoteChoices(withListingAmenityNotes(listingNoteChoices(notes, locations, 'internalNote')));
    setEditExternalNoteChoices(withListingAmenityNotes(listingNoteChoices(notes, locations, 'externalNote')));
  };

  const persistSeatLevelNotes = async (targets: SeatLevelNoteSaveTarget[]) => {
    const validTargets = targets.filter(
      (target) =>
        target.userId &&
        target.teamId &&
        target.section &&
        target.row &&
        Number.isFinite(target.seatStart) &&
        Number.isFinite(target.seatEnd) &&
        target.seatStart > 0 &&
        target.seatEnd >= target.seatStart,
    );
    if (!validTargets.length) return;

    const notesByUser = new Map<string, SeatLevelNote[]>();
    for (const userId of [...new Set(validTargets.map((target) => target.userId))]) {
      const result = await fetchSeatLevelNotes({ variables: { userId } });
      notesByUser.set(
        userId,
        (result.data?.user_victory_live_seat_notes || []).map(toSeatLevelNote),
      );
    }

    for (const target of validTargets) {
      const existing = findMatchingSeatLevelNote(notesByUser.get(target.userId) || [], target);
      if (existing) {
        await updateSeatLevelNote({
          variables: {
            vlsn_id: existing.id,
            internal_note: target.internalNote,
            external_note: target.externalNote,
          },
        });
        continue;
      }

      const result = await insertSeatLevelNote({
        variables: {
          u_id: target.userId,
          lc_id: target.teamId,
          section: target.section,
          seat_row: target.row,
          seat_start: target.seatStart,
          seat_end: target.seatEnd,
          internal_note: target.internalNote,
          external_note: target.externalNote,
        },
      });
      const inserted = result.data?.insert_user_victory_live_seat_notes_one;
      if (inserted) {
        const notes = notesByUser.get(target.userId) || [];
        notes.push({ id: String(inserted.vlsn_id), ...target });
        notesByUser.set(target.userId, notes);
      }
    }
  };

  const requestSeatNotesSave = (targets: SeatLevelNoteSaveTarget[]) => {
    if (targets.length) setPendingSeatNotesSave(targets);
  };

  const handleSeatNotesSaveChoice = async (shouldSave: boolean) => {
    const targets = pendingSeatNotesSave;
    setPendingSeatNotesSave(null);
    if (!shouldSave || !targets?.length) return;
    setSeatNotesSaveLoading(true);
    try {
      await persistSeatLevelNotes(targets);
      success('Seat-level notes saved to the user profile.');
    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to save seat-level notes to the user profile'));
    } finally {
      setSeatNotesSaveLoading(false);
    }
  };
  /** Matches User Details: super_user.percentage → user.u_fee_percentage → 15 */
  const rememberUserFeePercents = (tickets: Array<{ u_id?: string; u_fee_percentage?: unknown; ticket_user?: { u_fee_percentage?: unknown } | null }>) => {
    for (const ticket of tickets || []) {
      const fee = Number(ticket?.u_fee_percentage ?? ticket?.ticket_user?.u_fee_percentage);
      if (ticket?.u_id && Number.isFinite(fee) && fee > 0) {
        userFeePercentByIdRef.current[ticket.u_id] = fee;
      }
    }
  };

  const ensureUserFeePercents = async (userIds: string[]) => {
    rememberUserFeePercents(filteredTickets);
    rememberUserFeePercents(unfilteredTabTickets);
    const missing = [...new Set(userIds.filter((id) => id && userFeePercentByIdRef.current[id] == null))];
    if (!missing.length) return;
    try {
      const fetched = await fetchUserFeePercentages(missing);
      Object.assign(userFeePercentByIdRef.current, fetched);
    } catch (err) {
      console.error('Failed to fetch user fee percent:', err);
    }
  };

  const fetchUserFeePercent = async (uId: string): Promise<number | null> => {
    await ensureUserFeePercents([uId]);
    const fee = userFeePercentByIdRef.current[uId];
    return Number.isFinite(fee) && fee > 0 ? fee : null;
  };

  const resolveSelfPricingClientPercent = async (uId: string): Promise<number> => {
    try {
      const { data } = await getSuperUserForSold({ variables: { u_id: uId } });
      const superUserPercent = Number(data?.super_user?.[0]?.percentage);
      if (Number.isFinite(superUserPercent) && superUserPercent > 0) {
        return superUserPercent;
      }
    } catch (err) {
      console.error('Failed to fetch super user percent:', err);
    }
    const profileFee = await fetchUserFeePercent(uId);
    if (profileFee != null) return profileFee;
    return SELF_PRICING_FEE_FALLBACK;
  };

  const [refreshNotifications] = useLazyQuery(GET_NOTIFICATIONS, {
    fetchPolicy: 'network-only',
    onCompleted: (notificationData) => {
      handleGlobalState((prev: typeof globalState) =>
        mergeNotificationQueryIntoGlobalState(prev, notificationData),
      );
    }
  });

  const syncDashboardNotifications = () => {
    if (!globalState?.profile?.id) return;
    const adminId = globalState.profile.id;
    const runSync = () => {
      refreshNotifications({
        variables: getNotificationQueryVariables(adminId),
      });
    };
    scheduleNotificationSyncRetries(runSync);
  };

  async function callUpdateTicketStatusRest(ticketPlacementId: string[], isValid: boolean, isUndoRequest = false) {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;

    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    const response = await fetch(`${nodeApiBaseUrl}/api/updateTicketStatus`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        input: {
          ticketPlacementId,
          isValid,
          isUndoRequest
        }
      })
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message =
        responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to update ticket status';
      throw new Error(message);
    }
    return responseData;
  };

  const callDelistTicketByAdminRest = async (ticketPlacementId: string) => {
    return delistTicketByAdminRequest(ticketPlacementId);
  };

  const prepareVlShareModal = async (selectedTickets: Ticket[]) => {
    vlShareNotesEditedRef.current = false;
    const initialPrices: Record<string, string> = {};
    const initialSections: Record<string, string> = {};
    const initialRows: Record<string, string> = {};
    const initialSeats: Record<string, string> = {};
    for (const ticket of selectedTickets) {
      const price = Number(ticket.tp_list_price);
      initialPrices[ticket.tp_id] =
        Number.isFinite(price) && price > 0 ? String(roundToFixed(price, 2)) : '';
      initialSections[ticket.tp_id] = String(ticket.tp_section || '');
      initialRows[ticket.tp_id] = String(ticket.tp_row || '');
      initialSeats[ticket.tp_id] = String(ticket.tp_seat_no ?? '');
    }
    const allSelfPricing = selectedTickets.every(
      (ticket) => getResolvedClientType(ticket) === 'self-pricing',
    );
    const pricingMethods = [
      ...new Set(
        selectedTickets
          .map((ticket) => normalizePricingMethod(ticket.tp_pricing_method))
          .filter((method): method is 'MANUAL' | 'FOLLOW_MARKET' => Boolean(method)),
      ),
    ];
    setVlSharePricingMethod(
      allSelfPricing ? 'NONE' : pricingMethods.length === 1 ? pricingMethods[0] : '',
    );
    setVlSharePrices(initialPrices);
    setVlShareSections(initialSections);
    setVlShareRows(initialRows);
    setVlShareSeats(initialSeats);
    const listingNotes = await resolveListingPopupNotes(selectedTickets);
    const internalChoices = withListingAmenityNotes(listingNotes.internalChoices);
    const externalChoices = withListingAmenityNotes(listingNotes.externalChoices);
    const internalDraft = composeListingNoteDraft(
      listingNotes.internalQuery,
      internalChoices,
      listingNotes.customNote,
      'internal',
    );
    const externalDraft = composeListingNoteDraft(
      listingNotes.externalQuery,
      externalChoices,
      listingNotes.customNote,
      'external',
    );
    setVlShareInternalNoteChoices(internalChoices);
    setVlShareExternalNoteChoices(externalChoices);
    setVlShareListingCustom(listingNotes.customNote);
    setVlShareInternalNoteQuery(internalDraft.query);
    setVlShareExternalNoteQuery(externalDraft.query);
    setVlShareInternalNotes(internalDraft.sent);
    setVlShareExternalNotes(externalDraft.sent);
    setVlShareQaNotes('');
    setVlEnabledMarketplaces(VL_MARKETPLACE_OPTIONS.map((m) => m.id));
    setBulkSelectedTicketIds(new Set(selectedTickets.map((ticket) => ticket.tp_id).filter(Boolean)));
    setShowVlShareModal(true);
  };

  const handleVlProgressAction = async (tpIdsOverride?: string[]) => {
    const tpIds = tpIdsOverride?.length ? tpIdsOverride : Array.from(bulkSelectedTicketIds);
    if (tpIds.length === 0) {
      info('No tickets selected');
      return;
    }
    if (tpIdsOverride?.length) {
      setBulkSelectedTicketIds(new Set(tpIdsOverride));
    }
    setOpenActionMenu(null);

    const selectedTickets = tpIds
      .map((tpId) => filteredTickets.find((ticket) => ticket.tp_id === tpId))
      .filter((ticket): ticket is Ticket => Boolean(ticket));
    const vlProgress = getVlProgressForTickets(selectedTickets);
    if (!vlProgress) {
      showError(
        'VL action needs all selected tickets to share the same status: To Be Verified or Verified.',
      );
      return;
    }

    if (activeTab !== 'Verification') {
      showError(
        vlProgress.step === 'confirm'
          ? 'Production ID confirmation is only available on the Verification tab.'
          : vlProgress.step === 'submit'
          ? 'Submit to VL is only available on the Verification tab.'
          : 'List on Marketplace is only available on the Verification tab.',
      );
      return;
    }

    if (vlProgress.step === 'confirm') {
      await openPidConfirmationForTickets(selectedTickets);
      return;
    }

    if (vlProgress.step === 'share' && activeTab !== 'Verification') {
      showError('List on Marketplace is only available on the Verification tab.');
      return;
    }

    if (vlProgress.step === 'share') {
      wrongPidFlowRef.current = null;
      await prepareVlShareModal(selectedTickets);
      return;
    }

    // Submit step (Verification): create VL inventory directly (no second PID review).
    await executeSubmitListingsToVl(tpIds, selectedTickets);
  };

  const executeSubmitListingsToVl = async (
    tpIds: string[],
    selectedTickets: Ticket[],
    options?: { preserveStatus?: boolean; clearWrongPid?: boolean },
  ) => {
    beginStatusTableBusy('Submitting to Victory Live…');
    try {
      // Submit now marks Verified in DB directly — do not chain sync (VL Tickets/Search often times out).
      // Listed relist passes preserveStatus so a failed purchase order leaves the ticket Listed.
      const submitResult = await submitListingsToVl(tpIds, {
        defaultListPrice: 9999,
        preserveStatus: options?.preserveStatus,
        clearWrongPid: options?.clearWrongPid,
      });
      const mismatch =
        submitResult.results?.find((r) => r.code === 'VL_PRODUCTION_MISMATCH' && r.productionMismatch)
          ?.productionMismatch ||
        (submitResult.code === 'VL_PRODUCTION_MISMATCH' ? submitResult.details : null);

      if (mismatch?.eventId) {
        info(
          mismatch.reason ||
            'No exact Victory Live production match. Map the correct production, then retry Submit.',
        );
        await handleOpenProductionSync(selectedTickets);
        return { remapped: true };
      }

      const dbSyncFail = submitResult.results?.some((r) =>
        String(r.message || '').toLowerCase().includes('vl ok but db'),
      );
      showVlToast(
        summarizeVlActionToast({
          actionLabel: 'Submit',
          result: submitResult,
          successMessage: options?.preserveStatus
            ? `Created Victory Live inventory for ${submitResult.successCount || 0} seat(s). Status stays Listed.`
            : `Submitted ${submitResult.successCount || 0} seat(s) to Victory Live → Verified`,
          extraFailNote: dbSyncFail
            ? 'Admin was not updated — use Sync from VL if inventory already exists on Victory Live.'
            : undefined,
        }),
      );

      for (const row of submitResult.results || []) {
        if (row.status !== 'success' || !row.tpId) continue;
        const listingId = String(row.vlListingId || row.vlTicketGroupId || '').trim();
        const itemId = String(row.vlItemId || '').trim();
        if (!listingId && !itemId) continue;
        updateLocalTicketDetails([row.tpId], {
          ...(listingId ? { tp_vl_listing_id: listingId } : {}),
          ...(itemId ? { tp_vl_item_id: itemId } : {}),
          ...(options?.clearWrongPid ? { tp_wrong_pid: false } : {}),
        });
      }

      handleBulkActionComplete();
      await handleRefresh();
      const fullySucceeded =
        (submitResult.failedCount || 0) === 0 &&
        (submitResult.unknownCount || 0) === 0 &&
        (submitResult.successCount || 0) > 0;
      return { remapped: false, fullySucceeded };
    } catch (error: any) {
      showVlToast(toastForVlCaughtError(error, 'Submit', 'Victory Live action failed'));
      return { remapped: false, fullySucceeded: false };
    } finally {
      endStatusTableBusy();
    }
  };

  const handleGroupVlListings = async (tpIdsOverride?: string[]) => {
    const tpIds = tpIdsOverride?.length ? tpIdsOverride : Array.from(bulkSelectedTicketIds);
    if (tpIds.length < 2) {
      showError('Select at least 2 tickets from different VL listing groups to group');
      return;
    }
    setOpenActionMenu(null);
    const selectedTickets = tpIds
      .map((tpId) => filteredTickets.find((ticket) => ticket.tp_id === tpId))
      .filter((ticket): ticket is Ticket => Boolean(ticket));
    const statuses = selectedTickets.map((t) => normalizeStatusValue(t.tp_status || ''));
    if (!statuses.every((s) => s === 'Verified' || s === 'List')) {
      showError('Group on VL requires all selected tickets to be Verified or Listed');
      return;
    }
    const clientIds = new Set(selectedTickets.map((ticket) => String(ticket.u_id || '').trim()));
    if (clientIds.size > 1) {
      showError('Group on VL requires all selected seats to belong to the same client');
      return;
    }

    beginStatusTableBusy('Grouping listings on Victory Live…');
    try {
      const result = await groupVlListings(tpIds);
      const validationFailed = result.status === 'FAILURE' || result.validation?.ok === false;
      if (validationFailed) {
        const codes = (result.validation?.exceptions || [])
          .map((e) => e.errorCode)
          .filter(Boolean);
        showError(
          result.message ||
            `Group validation failed${codes.length ? ` (${[...new Set(codes)].join(', ')})` : ''} — routed to Exception Queue`,
        );
      } else {
        showVlToast(
          summarizeVlActionToast({
            actionLabel: 'Group',
            result,
            successMessage: result.message || `Grouped ${result.successCount || 0} seat(s) on Victory Live`,
          }),
        );
      }
      handleBulkActionComplete();
      await handleRefresh();
    } catch (error: any) {
      showVlToast(toastForVlCaughtError(error, 'Group', 'Group on Victory Live failed'));
    } finally {
      endStatusTableBusy();
    }
  };

  const handleSplitVlListings = (tpIdsOverride?: string[]) => {
    const tpIds = tpIdsOverride?.length ? tpIdsOverride : Array.from(bulkSelectedTicketIds);
    if (tpIds.length === 0) {
      info('No tickets selected');
      return;
    }
    setOpenActionMenu(null);
    const selectedTickets = tpIds
      .map((tpId) => filteredTickets.find((ticket) => ticket.tp_id === tpId))
      .filter((ticket): ticket is Ticket => Boolean(ticket));
    const statuses = selectedTickets.map((t) => normalizeStatusValue(t.tp_status || ''));
    if (!statuses.every((s) => s === 'Verified' || s === 'List')) {
      showError('Split on VL requires all selected tickets to be Verified or Listed');
      return;
    }
    const splitClientIds = new Set(selectedTickets.map((ticket) => String(ticket.u_id || '').trim()));
    if (splitClientIds.size > 1) {
      showError('Split on VL requires all selected seats to belong to the same client');
      return;
    }
    if (selectedTickets.length < 2) {
      showError('Select a group with at least 2 seats to split');
      return;
    }

    const sorted = [...selectedTickets].sort((a, b) => {
      const seatA = Number(a.tp_seat_no);
      const seatB = Number(b.tp_seat_no);
      if (Number.isFinite(seatA) && Number.isFinite(seatB) && seatA !== seatB) {
        return seatA - seatB;
      }
      return String(a.tp_id).localeCompare(String(b.tp_id));
    });
    // Default: split into individuals when qty=2 (leave 1, move 1) — matches seats 11-12 example.
    const defaultQty = Math.min(1, sorted.length - 1);
    const previewSeats = sorted
      .slice(-Math.max(1, defaultQty))
      .map((t) => String(t.tp_seat_no ?? '').trim())
      .filter(Boolean)
      .join(', ');

    setVlSplitTpIds(sorted.map((t) => t.tp_id));
    setVlSplitGroupSize(sorted.length);
    setVlSplitQuantity(String(Math.max(1, defaultQty)));
    setVlSplitSeatPreview(previewSeats);
    setShowVlSplitModal(true);
  };

  const handleConfirmVlSplit = async () => {
    const quantity = Number(vlSplitQuantity);
    if (!Number.isFinite(quantity) || quantity < 1 || quantity >= vlSplitGroupSize) {
      showError(`Enter a quantity from 1 to ${Math.max(1, vlSplitGroupSize - 1)}`);
      return;
    }

    setVlSplitLoading(true);
    beginStatusTableBusy('Splitting listings on Victory Live…');
    try {
      const result = await splitVlListings(vlSplitTpIds, { splitQuantity: Math.floor(quantity) });
      const validationFailed = result.status === 'FAILURE' || result.validation?.ok === false;
      if (validationFailed) {
        const codes = (result.validation?.exceptions || [])
          .map((e) => e.errorCode)
          .filter(Boolean);
        showError(
          result.message ||
            `Split validation failed${codes.length ? ` (${[...new Set(codes)].join(', ')})` : ''}`,
        );
      } else {
        showVlToast(
          summarizeVlActionToast({
            actionLabel: 'Split',
            result,
            successMessage: result.message || `Split ${result.successCount || 0} seat(s) on Victory Live`,
          }),
        );
      }
      setShowVlSplitModal(false);
      handleBulkActionComplete();
      await handleRefresh();
    } catch (error: any) {
      showVlToast(toastForVlCaughtError(error, 'Split', 'Split on Victory Live failed'));
    } finally {
      setVlSplitLoading(false);
      endStatusTableBusy();
    }
  };

  /** Open VL production search/map popup to overwrite event mapping. */
  const handleOpenProductionSync = async (tickets: Ticket[]) => {
    if (!tickets.length) {
      info('No tickets selected');
      return;
    }
    setOpenActionMenu(null);

    const ticketForEvent =
      tickets.find((t) => t.e_id || (t as any).ticket?.event?.e_id) || tickets[0];
    const eventId = String(
      ticketForEvent?.e_id || (ticketForEvent as any)?.ticket?.event?.e_id || '',
    ).trim();
    if (!eventId) {
      showError('Missing event id for production mapping');
      return;
    }

    const eventName = String(
      ticketForEvent?.e_name || (ticketForEvent as any)?.ticket?.event?.e_name || '',
    );
    const venue = String(
      ticketForEvent?.e_address || (ticketForEvent as any)?.ticket?.event?.e_address || '',
    );
    const dateTbd = isEventDateTbdForVerification(ticketForEvent);
    const eventDate = dateTbd
      ? ''
      : formatVenueEventDateYmd(
          ticketForEvent?.e_date || (ticketForEvent as any)?.ticket?.event?.e_date || '',
          ticketForEvent?.e_time_zone ||
            (ticketForEvent as any)?.ticket?.event?.e_time_zone ||
            null,
        );
    const tpIds = tickets.map((t) => t.tp_id);

    setVlProductionModalMode('retry-submit');
    setVlProductionMismatch({
      code: 'VL_PRODUCTION_MISMATCH',
      eventId,
      eventName,
      venue,
      eventDate,
      reason: 'Overwrite the Victory Live production, then move tickets to Verified.',
      queriesTried: [],
      candidates: [],
    });
    setVlProductionSearchQuery(eventName.trim());
    setVlProductionSearchResults([]);
    setVlSelectedProductionId('');
    setVlPendingSubmitTpIds(tpIds);
    setShowVlProductionMapModal(true);

    setVlProductionSearchLoading(true);
    try {
      const result = await searchVlProductions({
        query: eventName.trim() || undefined,
        eventId,
        eventName: eventName || undefined,
        venue: venue || undefined,
        eventDate: eventDate || undefined,
      });
      setVlProductionSearchResults(result.productions || []);
      if (result.productions?.length) {
        setVlSelectedProductionId('');
        if (!result.suggestedProductionId && !result.productions.some((p) => p.isSuggested)) {
          info(
            result.dateTbdMonths
              ? 'No exact Production ID match. Review Date TBD results (today through next 6 months) and pick the correct one.'
              : 'No exact Production ID match. Review all ±2 day results and pick the correct one.',
          );
        }
      } else {
        setVlSelectedProductionId('');
        info('No Victory Live productions found for that search');
      }
    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to search Victory Live productions'));
    } finally {
      setVlProductionSearchLoading(false);
    }
  };

  /** Open VL production search to write/overwrite PID in DB only (no VL submit). */
  const handleOpenAddPid = async (tickets: Ticket[]) => {
    if (!tickets.length) {
      info('No tickets selected');
      return;
    }
    setOpenActionMenu(null);

    // Always operate on the full display group (all seats), never a single expanded seat.
    const fromTable = expandTicketsToDisplayGroups(tickets, manageTicketTableRows);
    const fromPool = expandTicketsToSameDisplayGroup(
      tickets,
      filteredTickets.length ? filteredTickets : tickets,
      activeTab,
    );
    const groupedTickets =
      fromPool.length >= fromTable.length ? fromPool : fromTable;
    const preferredEventId =
      tickets.map((t) => getTicketEventId(t)).find(Boolean) ||
      groupedTickets.map((t) => getTicketEventId(t)).find(Boolean) ||
      '';
    const scopedTickets = preferredEventId
      ? groupedTickets.filter((t) => getTicketEventId(t) === preferredEventId)
      : groupedTickets;
    const addPidTickets = scopedTickets.length ? scopedTickets : groupedTickets;

    const eventIds = [
      ...new Set(addPidTickets.map((t) => getTicketEventId(t)).filter(Boolean)),
    ];
    if (eventIds.length !== 1) {
      showError('Add PID for tickets from one event at a time.');
      return;
    }

    const ticketForEvent =
      addPidTickets.find((t) => getTicketEventId(t)) || addPidTickets[0];
    const eventId = eventIds[0];
    const eventName = String(
      ticketForEvent?.e_name || (ticketForEvent as any)?.ticket?.event?.e_name || '',
    );
    const venue = String(
      ticketForEvent?.e_address || (ticketForEvent as any)?.ticket?.event?.e_address || '',
    );
    const dateTbd = isEventDateTbdForVerification(ticketForEvent);
    const eventDate = dateTbd
      ? ''
      : formatVenueEventDateYmd(
          ticketForEvent?.e_date || (ticketForEvent as any)?.ticket?.event?.e_date || '',
          ticketForEvent?.e_time_zone ||
            (ticketForEvent as any)?.ticket?.event?.e_time_zone ||
            null,
        );
    const tpIds = addPidTickets.map((t) => t.tp_id);

    setVlProductionModalMode('add-pid');
    setVlProductionMismatch({
      code: 'VL_PRODUCTION_MISMATCH',
      eventId,
      eventName,
      venue,
      eventDate,
      reason:
        'Save the Production ID to QuickAsyst only. Ticket status stays the same and Victory Live is not updated.',
      queriesTried: [],
      candidates: [],
    });
    setVlProductionSearchQuery(eventName.trim());
    setVlProductionSearchResults([]);
    setVlSelectedProductionId('');
    setVlEnteredProductionId('');
    setVlAddPidTab('search');
    setVlPendingSubmitTpIds(tpIds);
    setShowVlProductionMapModal(true);

    setVlProductionSearchLoading(true);
    try {
      const result = await searchVlProductions({
        query: eventName.trim() || undefined,
        eventId,
        eventName: eventName || undefined,
        venue: venue || undefined,
        eventDate: eventDate || undefined,
      });
      setVlProductionSearchResults(result.productions || []);
      if (result.productions?.length) {
        setVlSelectedProductionId('');
        if (!result.suggestedProductionId && !result.productions.some((p) => p.isSuggested)) {
          info(
            result.dateTbdMonths
              ? 'No exact Production ID match. Review Date TBD results (today through next 6 months) and pick the correct one.'
              : 'No exact Production ID match. Review all ±2 day results and pick the correct one.',
          );
        }
      } else {
        setVlSelectedProductionId('');
        info('No Victory Live productions found for that search');
      }
    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to search Victory Live productions'));
    } finally {
      setVlProductionSearchLoading(false);
    }
  };

  const confirmWrongPid = async () => {
    if (!wrongPidConfirm || wrongPidLoading) return;
    setWrongPidLoading(true);
    try {
      await markWrongPid(wrongPidConfirm.tpIds);
      const idSet = new Set(wrongPidConfirm.tpIds);
      setFilteredTickets((prev) =>
        prev.map((ticket) =>
          idSet.has(ticket.tp_id)
            ? { ...ticket, tp_wrong_pid: true, tp_vl_item_id: null, tp_vl_listing_id: null }
            : ticket,
        ),
      );
      success('Wrong PID inventory removed from Victory Live. Ticket status was not changed.');
      setWrongPidConfirm(null);
      setOpenActionMenu(null);
      await handleRefresh();
    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to remove the wrong Production ID inventory'));
    } finally {
      setWrongPidLoading(false);
    }
  };

  const openWrongPidResubmit = async (tickets: Ticket[]) => {
    wrongPidFlowRef.current = 'resubmit';
    setOpenActionMenu(null);
    await handleOpenProductionSync(tickets);
  };

  const openWrongPidRelist = async (tickets: Ticket[]) => {
    const readyToList =
      tickets.length > 0 &&
      tickets.every((ticket) => String(ticket.tp_vl_item_id || ticket.tp_vl_listing_id || '').trim());
    wrongPidFlowRef.current = 'relist';
    setOpenActionMenu(null);
    if (readyToList) {
      await prepareVlShareModal(tickets);
      return;
    }
    await handleOpenProductionSync(tickets);
  };

  const renderVlRowActionItems = (tickets: Ticket[], rowTab: string) => {
    if (!tickets.length) return null;
    const statuses = tickets.map((t) => normalizeStatusValue(t.tp_status || ''));
    const unique = [...new Set(statuses)];
    if (unique.length !== 1) return null;
    const status = unique[0];
    const tpIds = tickets.map((t) => t.tp_id);
    const hasVlIds = tickets.every((t) => Boolean(t.tp_vl_listing_id || t.tp_vl_item_id));
    const vlProgress = getVlProgressForTickets(tickets);
    const items: React.ReactNode[] = [];

    const canRowVlProgress = Boolean(vlProgress) && rowTab === 'Verification';
    if (canRowVlProgress && vlProgress) {
      items.push(
        <button
          key="vl-progress"
          className={styles.actionMenuItem}
          onClick={(e) => {
            e.stopPropagation();
            void handleVlProgressAction(tpIds);
          }}
        >
          {vlProgress.step === 'submit' ? <ExternalLink size={14} /> : <Share2 size={14} />}
          {' '}
          {vlProgress.label}
        </button>,
      );
    }

    // Verified with a mapped PID can still overwrite/remap (failed submit, wrong event, etc.).
    const hasMappedPid = tickets.every((ticket) => {
      const pid = String(getTicketVlProductionId(ticket, vlProductionIdByEventId) || '').trim();
      return Boolean(pid && /^\d+$/.test(pid));
    });
    if (status === 'Verified' && rowTab === 'Verification' && hasMappedPid) {
      items.push(
        <button
          key="vl-remap-production"
          className={styles.actionMenuItem}
          title="Overwrite the mapped Victory Live Production ID, then resubmit inventory"
          onClick={(e) => {
            e.stopPropagation();
            void handleOpenProductionSync(tickets);
          }}
        >
          <MapPinned size={14} /> Remap PID
        </button>,
      );
    }

    // DB-only Add/overwrite PID — does not change status or submit to VL.
    if (
      !isSuperUser &&
      (status === 'ToBeVerified' || status === 'Verified' || status === 'List') &&
      (rowTab === 'Verification' || rowTab === 'Listed')
    ) {
      items.push(
        <button
          key="vl-add-pid"
          className={styles.actionMenuItem}
          title="Save Production ID for the whole ticket group in QuickAsyst only (does not update Victory Live)"
          onClick={(e) => {
            e.stopPropagation();
            void handleOpenAddPid(tickets);
          }}
        >
          <MapPinned size={14} /> Add PID
        </button>,
      );
    }

    if (!isSuperUser && (status === 'ToBeVerified' || status === 'Verified' || status === 'List')) {
      items.push(
        <button
          key="vl-sync"
          className={styles.actionMenuItem}
          title="Choose what to sync from Victory Live (merge/split, barcode, price, notes, …)"
          onClick={(e) => {
            e.stopPropagation();
            openSyncFromVlScopeModal(tpIds);
          }}
        >
          <RefreshCcw size={14} /> Sync from VL
        </button>,
      );
      if (hasVlIds) {
        items.push(
          <button
            key="vl-check-barcodes"
            className={styles.actionMenuItem}
            title="See whether Victory Live has barcodes on these seats"
            disabled={barcodeSyncSummary?.phase === 'crawling' || barcodeSyncSummary?.phase === 'verifying'}
            onClick={(e) => {
              e.stopPropagation();
              void handleCheckBarcodes(tpIds);
            }}
          >
            <ClipboardCheck size={14} /> Check Barcodes
          </button>,
          <button
            key="vl-barcodes"
            className={styles.actionMenuItem}
            title="Resync barcodes — confirm Seasons/Purchase accounts first"
            disabled={barcodeSyncSummary?.phase === 'crawling' || barcodeSyncSummary?.phase === 'verifying'}
            onClick={(e) => {
              e.stopPropagation();
              void handleSyncBarcodes(tpIds, tickets);
            }}
          >
            <ScanBarcode size={14} /> Resync Barcodes
          </button>,
        );
      }
    }

    if (
      !isSuperUser &&
      (rowTab === 'Listed' || status === 'List') &&
      status === 'List'
    ) {
      items.push(
        <button
          key="vl-prices-refresh"
          className={styles.actionMenuItem}
          onClick={(e) => {
            e.stopPropagation();
            void handleRefreshConciergePrices(tpIds);
          }}
        >
          <RefreshCcw size={14} /> Refresh VL Prices
        </button>,
      );
    }

    return items.length ? <>{items}</> : null;
  };

  const openSyncFromVlScopeModal = (tpIdsOverride?: string[]) => {
    const tpIds = tpIdsOverride?.length ? tpIdsOverride : Array.from(bulkSelectedTicketIds);
    if (tpIds.length === 0) {
      info('No tickets selected');
      return;
    }
    setOpenActionMenu(null);
    const selectedTickets = tpIds
      .map((tpId) => filteredTickets.find((ticket) => ticket.tp_id === tpId))
      .filter((ticket): ticket is Ticket => Boolean(ticket));
    const statuses = selectedTickets.map((t) => normalizeStatusValue(t.tp_status || ''));
    if (!statuses.every((s) => s === 'ToBeVerified' || s === 'Verified' || s === 'List')) {
      showError('Sync from VL requires To Be Verified, Verified, or Listed tickets');
      return;
    }
    const clientIds = new Set(selectedTickets.map((ticket) => String(ticket.u_id || '').trim()));
    if (clientIds.size > 1) {
      showError('Sync from VL requires all selected seats to belong to the same client');
      return;
    }
    setSyncFromVlScopeTpIds(tpIds);
    setShowSyncFromVlScopeModal(true);
  };

  const handleSyncVlGroups = async (
    tpIdsOverride?: string[],
    syncScope?: SyncFromVlScope | SyncFromVlScope[],
  ) => {
    const tpIds = tpIdsOverride?.length ? tpIdsOverride : Array.from(bulkSelectedTicketIds);
    if (tpIds.length === 0) {
      info('No tickets selected');
      return;
    }
    if (!syncScope) {
      openSyncFromVlScopeModal(tpIds);
      return;
    }
    const syncScopes = Array.isArray(syncScope) ? syncScope : [syncScope];
    if (syncScopes.length === 0) {
      showError('Select at least one item to sync');
      return;
    }
    setOpenActionMenu(null);
    const selectedTickets = tpIds
      .map((tpId) => filteredTickets.find((ticket) => ticket.tp_id === tpId))
      .filter((ticket): ticket is Ticket => Boolean(ticket));
    const statuses = selectedTickets.map((t) => normalizeStatusValue(t.tp_status || ''));
    if (!statuses.every((s) => s === 'ToBeVerified' || s === 'Verified' || s === 'List')) {
      showError('Sync from VL requires To Be Verified, Verified, or Listed tickets');
      return;
    }
    const clientIds = new Set(selectedTickets.map((ticket) => String(ticket.u_id || '').trim()));
    if (clientIds.size > 1) {
      showError('Sync from VL requires all selected seats to belong to the same client');
      return;
    }

    beginStatusTableBusy('Syncing from Victory Live…');
    setSyncFromVlConfirming(true);
    const scopeLabel = syncScopes.map((scope) => scope.replace(/_/g, ' ')).join(', ');
    const shareOnly = syncScopes.length === 1 && syncScopes[0] === 'share_status';
    try {
      const result = await syncVlListingStatus(tpIds, { syncScope: syncScopes });
      const successCount = result.successCount || 0;
      const unchangedCount = result.unchangedCount || 0;
      const listingUpdates = (result.results || []).filter(
        (r) => r.vlListingId && !isVlUnknownStatus(r.status) && String(r.status || '').toLowerCase() !== 'failed',
      );

      const syncedStatusByTpId = new Map<string, string>();
      for (const row of result.results || []) {
        const ticket = selectedTickets.find((item) => item.tp_id === row.tpId);
        const nextStatus = normalizeStatusValue(String(row.newStatus || ''));
        if (
          !ticket ||
          !nextStatus ||
          shareOnly ||
          (String(ticket.tp_status || '') === 'List' && nextStatus === 'Verified') ||
          (row.status !== 'success' && row.status !== 'unchanged')
        ) {
          continue;
        }
        syncedStatusByTpId.set(row.tpId, nextStatus);
      }
      const syncedIdsByStatus = new Map<string, string[]>();
      syncedStatusByTpId.forEach((status, tpId) => {
        const ids = syncedIdsByStatus.get(status) || [];
        ids.push(tpId);
        syncedIdsByStatus.set(status, ids);
      });
      syncedIdsByStatus.forEach((ids, status) => updateLocalTicketStatus(ids, status));

      for (const row of result.results || []) {
        if (isVlUnknownStatus(row.status) || String(row.status || '').toLowerCase() === 'failed') continue;
        if (Array.isArray(row.enabledMarketplaceIds)) {
          setCachedEnabledMarketplaceIds(row.tpId, row.enabledMarketplaceIds);
        }
        if (row.shared != null) {
          setCachedVlSharedStatus(row.tpId, row.shared);
        } else if (
          !shareOnly &&
          (row.newStatus === 'List' || row.newStatus === 'Verified')
        ) {
          setCachedVlSharedStatus(row.tpId, row.newStatus === 'List');
        }
      }

      setFilteredTickets((prev) =>
        prev.map((ticket) => {
          const row = result.results?.find((r) => r.tpId === ticket.tp_id);
          if (!row) return ticket;
          if (isVlUnknownStatus(row.status) || String(row.status || '').toLowerCase() === 'failed') return ticket;
          return {
            ...ticket,
            ...(row.vlListingId ? { tp_vl_listing_id: row.vlListingId } : {}),
            ...(row.vlItemId ? { tp_vl_item_id: row.vlItemId } : {}),
            ...(row.barcode != null
              ? { tp_barcode: row.barcode, tp_barcode_attached: Boolean(row.barcode) }
              : {}),
            ...(row.section != null ? { tp_section: row.section } : {}),
            ...(row.row != null ? { tp_row: row.row } : {}),
            ...(row.seat != null ? { tp_seat_no: row.seat } : {}),
            ...(row.onHold != null ? { on_hold: row.onHold } : {}),
            ...(Array.isArray(row.vlMarketplaces)
              ? { tp_vl_marketplaces: row.vlMarketplaces }
              : Array.isArray(row.enabledMarketplaceIds)
                ? { tp_vl_marketplaces: getVlMarketplaceLabels(row.enabledMarketplaceIds) }
                : {}),
            ...((row.vlShare ?? row.shared) != null
              ? { tp_vl_share: row.vlShare ?? row.shared }
              : {}),
            ...(row.eventName ? { e_name: row.eventName } : {}),
            ...(row.venue ? { e_address: row.venue } : {}),
            ...(row.productionId ? { tp_vl_production_id: row.productionId } : {}),
            ...(row.listPrice != null && Number.isFinite(Number(row.listPrice))
              ? { tp_list_price: Number(row.listPrice) }
              : {}),
            ...(row.internalNotes != null
              ? { internalNotes: row.internalNotes, tp_internal_notes: row.internalNotes }
              : {}),
            ...(row.externalNotes != null
              ? { externalNotes: row.externalNotes, tp_external_notes: row.externalNotes }
              : {}),
            ...(syncedStatusByTpId.has(ticket.tp_id)
              ? { tp_status: syncedStatusByTpId.get(ticket.tp_id) }
              : {}),
          };
        }),
      );

      showVlToast(
        summarizeVlActionToast({
          actionLabel: `Sync from VL (${scopeLabel})`,
          result,
          successMessage:
            listingUpdates.length > 0 && syncScopes.length === 1 && syncScopes[0] === 'merge_split'
              ? `Synced ${listingUpdates.length} seat(s) from Victory Live` +
                (unchangedCount ? ` (${unchangedCount} already current)` : '')
              : `Synced ${successCount} seat(s) — ${scopeLabel}` +
                (unchangedCount ? ` (${unchangedCount} already current)` : ''),
          skipMessage: unchangedCount
            ? `No Victory Live ${scopeLabel} changes found (${unchangedCount} already current)`
            : result.message || `No Victory Live ${scopeLabel} changes found`,
        }),
      );
      setShowSyncFromVlScopeModal(false);
      handleBulkActionComplete();
      await handleRefresh();
    } catch (error: any) {
      showVlToast(toastForVlCaughtError(error, 'Sync from VL', 'Sync from VL failed'));
    } finally {
      setSyncFromVlConfirming(false);
      endStatusTableBusy();
    }
  };

  /** Open confirm-gate pop-up (auto-populated). Sync only after Confirm. */
  const openBarcodeSyncConfirm = async (ticketsForContext?: Ticket[]) => {
    setOpenActionMenu(null);
    setShowBarcodeSyncConfirmModal(true);
    setBarcodeSyncRecommendLoading(true);
    setBarcodeSyncRecommendError(null);
    setBarcodeSyncRecommendation(null);
    try {
      const ticket =
        ticketsForContext?.[0] ||
        (bulkSelectedTicketIds.size
          ? filteredTickets.find((t) => bulkSelectedTicketIds.has(t.tp_id))
          : undefined);
      const recommendation = await recommendBarcodeSyncAccounts({
        eventName: ticket?.e_name,
        venue: ticket?.e_address,
        league: ticket?.l_name,
        eventId: ticket?.e_id,
      });
      setBarcodeSyncRecommendation(recommendation);
    } catch (error: any) {
      setBarcodeSyncRecommendError(
        getErrorMessage(error, 'Failed to load recommended barcode accounts'),
      );
    } finally {
      setBarcodeSyncRecommendLoading(false);
    }
  };

  const buildBarcodeSyncFailures = (result: CheckBarcodesResult): string[] => {
    const qaBarcodeByTpId = new Map(
      filteredTickets.map((ticket) => [String(ticket.tp_id), String(ticket.tp_barcode || ticket.barcode || '').trim()]),
    );
    const failures: string[] = [];
    for (const seat of result.seats || []) {
      const seatLabel = `Sec ${seat.section || '—'} / Row ${seat.row || '—'} / Seat ${seat.seat ?? '—'}`;
      const vlBarcode = String(seat.barcode || '').trim();
      const qaBarcode = qaBarcodeByTpId.get(String(seat.tpId)) || '';
      if (!seat.barcodeAttached) {
        failures.push(`${seatLabel}: missing barcode on Victory Live`);
        continue;
      }
      if (qaBarcode && vlBarcode && qaBarcode !== vlBarcode) {
        failures.push(
          `${seatLabel}: barcode mismatch (QuickAsyst: ${qaBarcode}; Victory Live: ${vlBarcode})`,
        );
      }
    }
    return failures;
  };

  /** After Confirm: crawl selected account ids, poll completion, then verify barcodes. */
  const confirmBarcodeSync = async (accountIds: string[]) => {
    if (!accountIds.length) {
      info('Select at least one account to sync');
      return;
    }
    if (!barcodeSyncTargetTpIds.length) {
      showError('Select at least one seat before Sync Barcodes so verification can run after crawl.');
      return;
    }
    setBarcodeSyncConfirming(true);
    beginStatusTableBusy('Starting Victory Live account crawl…');
    try {
      const crawl = await startVlAccountCrawl({
        accountIds,
        ticketPlacementIds: barcodeSyncTargetTpIds,
      });
      const crawlResults = (crawl.results || []).map((row) => ({
        accountId: row.accountId,
        status: row.success ? 'processing' as const : 'failed' as const,
        message: row.success ? 'Crawl started on Victory Live' : (row.message || 'Failed to start crawl'),
        username: row.username,
        marketplaceName: row.marketplaceName,
      }));
      const startedAccountIds = crawlResults
        .filter((row) => row.status === 'processing')
        .map((row) => row.accountId);

      setShowBarcodeSyncConfirmModal(false);
      setShowCheckBarcodesModal(false);
      setCheckBarcodesResult(null);
      setCheckBarcodesError(null);
      setShowBarcodeSyncSummaryModal(true);
      setBarcodeSyncSummary({
        phase: 'crawling',
        accountIds: crawl.accountIds || accountIds,
        crawlMessage: crawl.message || 'Crawl requested. Waiting for Victory Live to finish…',
        crawlResults,
      });

      if (!startedAccountIds.length) {
        setBarcodeSyncSummary((prev) =>
          prev
            ? {
                ...prev,
                phase: 'failure',
                crawlMessage: 'Crawl did not start.',
                verificationMessage: 'Could not start crawl for any selected account.',
              }
            : prev,
        );
        showError(crawl.message || 'Failed to start Victory Live account crawl');
        return;
      }

      const maxPollAttempts = 120;
      let statusDone = false;
      for (let attempt = 0; attempt < maxPollAttempts; attempt += 1) {
        const status = await getVlAccountCrawlStatus(startedAccountIds);
        setBarcodeSyncSummary((prev) =>
          prev
            ? {
                ...prev,
                phase: 'crawling',
                crawlMessage: `Crawl in progress: ${status.processingCount} processing, ${status.completedCount} completed, ${status.failedCount} failed.`,
                crawlResults: status.results.map((row) => ({
                  accountId: row.accountId,
                  status: row.status,
                  crawlStatus: row.crawlStatus,
                  message: row.message || row.lastErrorMessage || row.crawlStatus || undefined,
                  username: prev.crawlResults.find((item) => item.accountId === row.accountId)?.username,
                  marketplaceName: prev.crawlResults.find((item) => item.accountId === row.accountId)?.marketplaceName,
                })),
              }
            : prev,
        );
        if (status.complete) {
          statusDone = true;
          break;
        }
        await sleep(5000);
      }

      if (!statusDone) {
        setBarcodeSyncSummary((prev) =>
          prev
            ? {
                ...prev,
                phase: 'failure',
                crawlMessage: 'Timed out waiting for Victory Live crawl to complete.',
                verificationMessage: 'Timed out waiting for Victory Live crawl to complete.',
              }
            : prev,
        );
        showError('Timed out while waiting for crawl completion. Please retry Check Barcodes.');
        return;
      }

      setBarcodeSyncSummary((prev) =>
        prev
          ? {
              ...prev,
              phase: 'verifying',
              verificationMessage: 'Crawl completed. Verifying seat barcodes…',
            }
          : prev,
      );

      const eventId = String(
        filteredTickets.find((ticket) => barcodeSyncTargetTpIds.includes(ticket.tp_id))?.e_id || '',
      ).trim();
      const verification = await checkVlBarcodes(
        barcodeSyncTargetTpIds,
        eventId ? { eventId, persist: true, logVerification: true } : { persist: true, logVerification: true },
      );
      const failures = buildBarcodeSyncFailures(verification);

      setShowCheckBarcodesModal(false);
      setCheckBarcodesResult(null);

      setBarcodeSyncSummary((prev) =>
        prev
          ? {
              ...prev,
              phase: failures.length ? 'failure' : 'success',
              crawlMessage: 'Crawl completed on Victory Live.',
              verificationResult: verification,
              failures,
              verificationMessage: failures.length
                ? `${failures.length} seat(s) failed barcode verification.`
                : `Success: all ${verification.total} seat(s) have matching barcodes in Victory Live.`,
            }
          : prev,
      );

      if (failures.length) {
        showError(`${failures.length} seat(s) failed barcode verification`);
      } else {
        success(`Barcode sync complete: all ${verification.total} seat(s) verified`);
      }

      void refetch();
    } catch (error: any) {
      const message = getErrorMessage(error, 'Sync barcodes failed');
      setBarcodeSyncSummary((prev) =>
        prev
          ? {
              ...prev,
              phase: 'failure',
              crawlMessage:
                prev.phase === 'crawling'
                  ? 'Crawl stopped with an error.'
                  : 'Crawl completed on Victory Live.',
              verificationMessage: message,
            }
          : prev,
      );
      showError(message);
    } finally {
      setBarcodeSyncConfirming(false);
      endStatusTableBusy();
    }
  };

  /** Sync / Resync Barcodes: open confirm gate (never auto-crawls). */
  const handleSyncBarcodes = async (_tpIdsOverride?: string[], ticketsForContext?: Ticket[]) => {
    const tpIds = _tpIdsOverride?.length ? _tpIdsOverride : Array.from(bulkSelectedTicketIds);
    if (!tpIds.length) {
      info('Select at least one seat before Sync Barcodes');
      return;
    }
    const tickets =
      ticketsForContext ||
      (tpIds.length
        ? tpIds
            .map((id) => filteredTickets.find((t) => t.tp_id === id))
            .filter(Boolean) as Ticket[]
        : undefined);
    setBarcodeSyncTargetTpIds(tpIds);
    setBarcodeSyncSummary(null);
    setShowBarcodeSyncSummaryModal(false);
    setShowCheckBarcodesModal(false);
    setCheckBarcodesResult(null);
    await openBarcodeSyncConfirm(tickets);
  };

  const handleCheckBarcodes = async (tpIdsOverride?: string[]) => {
    if (barcodeSyncSummary?.phase === 'crawling' || barcodeSyncSummary?.phase === 'verifying') {
      info('Barcode crawl is in progress. Wait for completion before running Check Barcodes.');
      return;
    }
    const tpIds = tpIdsOverride?.length ? tpIdsOverride : Array.from(bulkSelectedTicketIds);
    if (!tpIds.length) {
      info('No tickets selected');
      return;
    }
    const eventId = String(
      filteredTickets.find((ticket) => tpIds.includes(ticket.tp_id))?.e_id || '',
    ).trim();
    setOpenActionMenu(null);
    setShowBarcodeSyncSummaryModal(false);
    setShowCheckBarcodesModal(true);
    setCheckBarcodesLoading(true);
    setCheckBarcodesError(null);
    setCheckBarcodesResult(null);
    try {
      const result = await checkVlBarcodes(tpIds, eventId ? { eventId } : undefined);
      setCheckBarcodesResult(result);
      const barcodeByTpId = new Map(
        (result.seats || []).map((seat) => [String(seat.tpId), String(seat.barcode || '').trim()]),
      );
      if (barcodeByTpId.size) {
        setFilteredTickets((prev) =>
          prev.map((ticket) => {
            if (!barcodeByTpId.has(ticket.tp_id)) return ticket;
            const barcode = barcodeByTpId.get(ticket.tp_id) || '';
            return {
              ...ticket,
              tp_barcode: barcode || null,
              barcode: barcode || null,
              tp_barcode_attached: Boolean(barcode),
            };
          }),
        );
      }
      void refetch();
    } catch (error: any) {
      setCheckBarcodesError(getErrorMessage(error, 'Failed to check barcodes'));
    } finally {
      setCheckBarcodesLoading(false);
    }
  };

  /** Step 7: pull current VL list prices into Admin (Follow the Market / free list price on VL). */
  const handleRefreshConciergePrices = async (tpIdsOverride?: string[]) => {
    const tpIds = tpIdsOverride?.length ? tpIdsOverride : Array.from(bulkSelectedTicketIds);
    if (tpIds.length === 0) {
      info('No tickets selected');
      return;
    }
    setOpenActionMenu(null);
    const selectedTickets = tpIds
      .map((tpId) => filteredTickets.find((ticket) => ticket.tp_id === tpId))
      .filter((ticket): ticket is Ticket => Boolean(ticket));

    const listed = selectedTickets.filter(
      (t) => normalizeStatusValue(t.tp_status || '') === 'List',
    );
    if (!listed.length) {
      showError('Refresh VL prices requires Listed tickets');
      return;
    }

    setConciergePriceLoading(true);
    beginStatusTableBusy('Refreshing list prices from Victory Live…');
    try {
      const result = await syncConciergeVlPrices(
        listed.map((t) => t.tp_id),
        { includeAllHistory: true },
      );
      const successCount = result.successCount || 0;
      const unchangedCount = result.unchangedCount || 0;
      const upCount = result.upCount || 0;
      const downCount = result.downCount || 0;

      setFilteredTickets((prev) =>
        prev.map((ticket) => {
          const row = result.results?.find((r) => r.tpId === ticket.tp_id);
          if (!row || row.newPrice == null) return ticket;
          if (
            row.status === 'skipped' ||
            row.status === 'failed' ||
            isVlUnknownStatus(row.status)
          ) {
            return ticket;
          }
          return {
            ...ticket,
            tp_list_price: row.newPrice,
            ...(row.vlListingId ? { tp_vl_listing_id: row.vlListingId } : {}),
            ...(row.vlItemId ? { tp_vl_item_id: row.vlItemId } : {}),
          };
        }),
      );

      setConciergePriceResults(result.results || []);
      setConciergeRecentChanges(result.recentChanges || []);
      setShowConciergePriceModal(true);

      showVlToast(
        summarizeVlActionToast({
          actionLabel: 'Refresh VL prices',
          result,
          successMessage:
            `Updated ${successCount} price(s): ${upCount} up, ${downCount} down` +
            (unchangedCount ? `; ${unchangedCount} unchanged` : ''),
          skipMessage: unchangedCount
            ? `No price changes (${unchangedCount} already current)`
            : result.message || 'No VL price updates',
        }),
      );
      handleBulkActionComplete();
      await handleRefresh();
    } catch (error: any) {
      showVlToast(toastForVlCaughtError(error, 'Refresh VL prices', 'Refresh VL prices failed'));
    } finally {
      setConciergePriceLoading(false);
      endStatusTableBusy();
    }
  };

  const handleShareVlListings = async () => {
    const tpIds = Array.from(bulkSelectedTicketIds);
    if (tpIds.length === 0) {
      info('No tickets selected');
      return;
    }
    if (vlEnabledMarketplaces.length === 0) {
      showError('Select at least one marketplace, or cancel.');
      return;
    }
    const shareTickets = tpIds
      .map((id) => filteredTickets.find((ticket) => ticket.tp_id === id))
      .filter((ticket): ticket is Ticket => Boolean(ticket));
    const allSelfPricing = shareTickets.every(
      (ticket) => getResolvedClientType(ticket) === 'self-pricing',
    );
    if (
      !allSelfPricing &&
      vlSharePricingMethod !== 'MANUAL' &&
      vlSharePricingMethod !== 'FOLLOW_MARKET' &&
      vlSharePricingMethod !== 'NONE'
    ) {
      showError('Please select a pricing method.');
      return;
    }

    const ticketsForBarcodeContext = shareTickets;

    const listPriceByTpId: Record<string, number> = {};
    const sectionByTpId: Record<string, string> = {};
    const rowByTpId: Record<string, string> = {};
    const seatByTpId: Record<string, string> = {};
    for (const tpId of tpIds) {
      const raw = (vlSharePrices[tpId] ?? '').trim().replace(/,/g, '');
      const price = Number(raw);
      if (!Number.isFinite(price) || price <= 0) {
        showError('Enter a valid list price greater than 0 for each seat.');
        return;
      }
      const section = (vlShareSections[tpId] ?? '').trim();
      const row = (vlShareRows[tpId] ?? '').trim();
      const seat = (vlShareSeats[tpId] ?? '').trim();
      if (!section || !row || !seat) {
        showError('Enter section, row, and seat for each ticket.');
        return;
      }
      listPriceByTpId[tpId] = Number(roundToFixed(price, 2));
      sectionByTpId[tpId] = section;
      rowByTpId[tpId] = row;
      seatByTpId[tpId] = seat;
    }

    setVlShareLoading(true);
    beginStatusTableBusy('Sharing listings on Victory Live…');
    let shouldAutoSyncBarcodes = false;
    const clearingWrongPid = wrongPidFlowRef.current === 'relist';
    try {
      const result = await shareVlListings(tpIds, {
        shareExchange: true,
        clearWrongPid: clearingWrongPid,
        enabledMarketplaceIds: vlEnabledMarketplaces,
        listPriceByTpId,
        sectionByTpId,
        rowByTpId,
        seatByTpId,
        internalNotes: vlShareInternalNotes,
        externalNotes: vlShareExternalNotes,
        pricingMethod: allSelfPricing
          ? 'NONE'
          : vlSharePricingMethod === 'MANUAL' || vlSharePricingMethod === 'FOLLOW_MARKET'
            ? vlSharePricingMethod
            : 'NONE',
      });
      const toast = summarizeVlActionToast({
        actionLabel: 'Share',
        result,
        successMessage:
          result.results?.find((r) => r.status === 'success')?.message ||
          'All inventory is listed on all marketplaces.',
        extraFailNote:
          'Admin was not changed. Please try again, or use Sync from VL if Victory Live already listed them.',
      });
      const successfulTpIds = new Set(successfulVlTpIds(result));
      const listedOk = toast.type === 'success' || successfulTpIds.size > 0;

      if (listedOk) {
        const listedIds = successfulTpIds.size ? Array.from(successfulTpIds) : toast.type === 'success' ? tpIds : [];
        setCachedEnabledMarketplaceIdsForMany(listedIds, vlEnabledMarketplaces);
        setCachedVlSharedStatusForMany(listedIds, true);
        for (const tpId of listedIds) {
          const savedVl = (result.results || []).find((row) => row.tpId === tpId && row.status === 'success');
          const savedListingId = String(savedVl?.vlListingId || '').trim();
          const savedItemId = String(savedVl?.vlItemId || '').trim();
          updateLocalTicketDetails([tpId], {
            tp_list_price: listPriceByTpId[tpId],
            tp_section: sectionByTpId[tpId],
            tp_row: rowByTpId[tpId],
            tp_seat_no: seatByTpId[tpId],
            tp_pricing_method:
              allSelfPricing || vlSharePricingMethod === 'NONE'
                ? null
                : vlSharePricingMethod === 'MANUAL' || vlSharePricingMethod === 'FOLLOW_MARKET'
                  ? vlSharePricingMethod
                  : null,
            tp_status: 'List',
            tp_vl_share: true,
            tp_vl_marketplaces: getVlMarketplaceLabels(vlEnabledMarketplaces),
            ...(clearingWrongPid ? { tp_wrong_pid: false } : {}),
            ...(savedListingId ? { tp_vl_listing_id: savedListingId } : {}),
            ...(savedItemId ? { tp_vl_item_id: savedItemId } : {}),
            tp_internal_notes: vlShareInternalNotes,
            tp_external_notes: vlShareExternalNotes,
            internalNotes: vlShareInternalNotes,
            externalNotes: vlShareExternalNotes,
          });
        }
        const seatNoteTargets = ticketsForBarcodeContext
          .filter((ticket) => listedIds.includes(String(ticket.tp_id)))
          .map((ticket) => {
            const seatBounds = getSeatBounds(vlShareSeats[ticket.tp_id]);
            return seatBounds
              ? {
                  userId: String(ticket.u_id || '').trim(),
                  teamId: String(ticket.lc_id || '').trim(),
                  section: (vlShareSections[ticket.tp_id] || '').trim(),
                  row: (vlShareRows[ticket.tp_id] || '').trim(),
                  seatStart: seatBounds.start,
                  seatEnd: seatBounds.end,
                  internalNote: vlShareInternalNotes.trim(),
                  externalNote: vlShareExternalNotes.trim(),
                }
              : null;
          })
          .filter((target): target is SeatLevelNoteSaveTarget => Boolean(target));
        if (seatNoteTargets.length) requestSeatNotesSave(seatNoteTargets);
        shouldAutoSyncBarcodes = toast.type === 'success';
      }
      if (clearingWrongPid && (toast.type === 'success' || successfulTpIds.size > 0)) {
        wrongPidFlowRef.current = null;
      }
      const qaNote = vlShareQaNotes.trim();
      if (qaNote) {
        const userIds = [...new Set(shareTickets.map((ticket) => String(ticket.u_id || '').trim()).filter(Boolean))];
        try {
          await Promise.all(
            userIds.map((userId) => addQaNote({ variables: { userId, type: 'qa', content: qaNote } })),
          );
        } catch (noteError) {
          console.warn('Failed to save QA note from List on VL', noteError);
          showError('The QA note could not be saved. It was not sent to Victory Live.');
        }
      }
      showVlToast(toast);
      setShowVlShareModal(false);
      handleBulkActionComplete();
      if (shouldAutoSyncBarcodes && activeTab !== 'Listed') {
        switchTabUnlessEventSearch('Listed');
      }
      await handleRefresh();
    } catch (error: any) {
      showVlToast(toastForVlCaughtError(error, 'Share', 'Failed to share listings on Victory Live'));
    } finally {
      setVlShareLoading(false);
      endStatusTableBusy();
    }

    // After List on Marketplace succeeds, open barcode confirm gate (does not auto-crawl).
    if (shouldAutoSyncBarcodes) {
      await handleSyncBarcodes(tpIds, ticketsForBarcodeContext);
    }
  };

  const openPidConfirmationForTickets = async (tickets: Ticket[]) => {
    if (!tickets.length) return;
    const eventIds = [
      ...new Set(
        tickets
          .map((ticket) => String(ticket.e_id || '').trim())
          .filter(Boolean),
      ),
    ];
    if (eventIds.length !== 1) {
      showError('Verify tickets from one event at a time so its Production ID can be confirmed.');
      return;
    }

    const ticket = tickets[0];
    const eventId = eventIds[0];
    const eventName = String(ticket.e_name || '').trim();
    const venue = String(ticket.e_address || '').trim();
    const dateTbd = isEventDateTbdForVerification(ticket);
    const eventDate = dateTbd
      ? ''
      : formatVenueEventDateYmd(ticket.e_date, ticket.e_time_zone) ||
        String(ticket.e_date || '').slice(0, 10);
    const context: ProductionMismatchDetails = {
      code: 'VL_PRODUCTION_MISMATCH',
      eventId,
      eventName,
      venue,
      eventDate,
      reason:
        'Select the correct Production ID. Submit to VL and Mark Verified saves the PID and creates Victory Live inventory.',
      queriesTried: [],
      candidates: [],
    };

    // Open modal immediately; status is not written until confirm succeeds.
    setVlProductionModalMode('confirm');
    setVlProductionMismatch(context);
    setVlProductionSearchQuery(eventName);
    setVlProductionSearchResults([]);
    setVlSelectedProductionId('');
    setVlPendingSubmitTpIds(tickets.map((row) => row.tp_id));
    setShowVlProductionMapModal(true);
    setOpenStatusDropdown(null);
    setVlProductionSearchLoading(true);
    try {
      const result = await searchVlProductions({
        eventId,
        eventName,
        venue,
        eventDate: eventDate || undefined,
      });
      const productions = result.productions || [];
      setVlProductionSearchResults(productions);
      // Never auto-select a suggested match — PID is only saved after the admin clicks a row.
      setVlSelectedProductionId('');
      const tbdWindow =
        result.eventDateStart && result.eventDateEnd
          ? `${result.eventDateStart} to ${result.eventDateEnd}`
          : 'today through the next 6 months';
      if (!productions.length) {
        info(
          dateTbd || result.dateTbdMonths
            ? `No matching Victory Live production found for Date TBD (${tbdWindow}). Search again or escalate.`
            : 'No matching Victory Live production found within ±2 days. Search again or escalate.',
        );
      } else if (!result.suggestedProductionId && !productions.some((p) => p.isSuggested)) {
        info(
          dateTbd || result.dateTbdMonths
            ? `No exact Production ID match. Review Date TBD results (${tbdWindow}) and pick the correct one.`
            : 'No exact Production ID match. Review all ±2 day results and pick the correct one.',
        );
      }
    } catch (error: any) {
      showError(
        getErrorMessage(
          error,
          'Production ID search failed. Status stays To Be Verified until a PID is confirmed.',
        ),
      );
    } finally {
      setVlProductionSearchLoading(false);
    }
  };

  const handleSearchVlProductions = async () => {
    if (!vlProductionMismatch?.eventId && !vlProductionSearchQuery.trim()) {
      showError('Enter an event/venue/date search');
      return;
    }
    setVlProductionSearchLoading(true);
    try {
      const result = await searchVlProductions({
        query: vlProductionSearchQuery.trim() || undefined,
        eventId: vlProductionMismatch?.eventId,
        eventName: vlProductionMismatch?.eventName,
        venue: vlProductionMismatch?.venue,
        eventDate: vlProductionMismatch?.eventDate,
      });
      setVlProductionSearchResults(result.productions || []);
      if (result.productions?.length) {
        setVlSelectedProductionId('');
        if (!result.suggestedProductionId && !result.productions.some((p) => p.isSuggested)) {
          info('No exact Production ID match. Review all ±2 day results and pick the correct one.');
        }
      } else {
        setVlSelectedProductionId('');
        info('No Victory Live productions found for that search');
      }
    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to search Victory Live productions'));
    } finally {
      setVlProductionSearchLoading(false);
    }
  };

  const handleMapVlProductionAndRetry = async () => {
    const eventId = vlProductionMismatch?.eventId;
    const isAddPidOnly = vlProductionModalMode === 'add-pid';
    const wrongPidFlow = wrongPidFlowRef.current;
    const keepCurrentStatus = wrongPidFlow === 'relist';
    const usingEnteredPid = isAddPidOnly && vlAddPidTab === 'enter';
    const productionId = (
      usingEnteredPid ? vlEnteredProductionId : vlSelectedProductionId
    ).trim();
    if (!eventId) {
      showError('Missing event id for production mapping');
      return;
    }
    if (!/^\d+$/.test(productionId)) {
      showError(
        usingEnteredPid
          ? 'Enter a valid numeric Production ID'
          : 'Select a Victory Live production id',
      );
      return;
    }

    setVlProductionMapLoading(true);
    beginStatusTableBusy(isAddPidOnly ? 'Saving Production ID…' : 'Saving Production ID…');
    let shouldSubmitToVl = false;
    let submitTpIds: string[] = [];
    let ticketsForSubmit: Ticket[] = [];
    try {
      let tpIds = vlPendingSubmitTpIds.length
        ? vlPendingSubmitTpIds
        : Array.from(bulkSelectedTicketIds);
      if (!tpIds.length) {
        throw new Error(
          isAddPidOnly
            ? 'No tickets selected for Add PID'
            : vlProductionModalMode === 'confirm'
              ? 'No tickets selected for Submit to VL'
              : 'No tickets selected for production overwrite',
        );
      }

      // Add PID and Verify save the production id on every seat in the selected display group.
      const seedTickets = tpIds
        .map(
          (tpId) =>
            filteredTickets.find((ticket) => ticket.tp_id === tpId) ||
            currentPageTickets.find((ticket) => ticket.tp_id === tpId) ||
            selectedTicketsList.find((ticket) => ticket.tp_id === tpId),
        )
        .filter((ticket): ticket is Ticket => Boolean(ticket));
      const fromTable = expandTicketsToDisplayGroups(seedTickets, manageTicketTableRows).filter(
        (ticket) => getTicketEventId(ticket) === eventId,
      );
      const fromPool = expandTicketsToSameDisplayGroup(
        seedTickets,
        filteredTickets.length ? filteredTickets : seedTickets,
        activeTab,
      ).filter((ticket) => getTicketEventId(ticket) === eventId);
      const expanded = fromPool.length >= fromTable.length ? fromPool : fromTable;
      if (expanded.length) {
        tpIds = expanded.map((ticket) => ticket.tp_id).filter(Boolean);
      }

      const selectedProduction = vlProductionSearchResults.find(
        (row) => String(row.productionId) === productionId,
      );
      const result = await confirmVlProduction({
        eventId,
        productionId,
        ticketPlacementIds: tpIds,
        stubhubId: selectedProduction?.stubhubId ?? null,
        seatgeekId: selectedProduction?.seatgeekId ?? null,
        ...(isAddPidOnly || keepCurrentStatus ? { savePidOnly: true } : {}),
      });
      const effectiveEventId = String(result.eventId || eventId).trim();
      const stubhubId =
        selectedProduction?.stubhubId ?? result.production?.stubhubId ?? null;
      const seatgeekId =
        selectedProduction?.seatgeekId ?? result.production?.seatgeekId ?? null;

      if (stubhubId || seatgeekId) {
        persistMarketplaceLinks({
          ...marketplaceLinksByProductionIdRef.current,
          [productionId]: {
            stubhubId: stubhubId ?? marketplaceLinksByProductionIdRef.current[productionId]?.stubhubId ?? null,
            seatgeekId: seatgeekId ?? marketplaceLinksByProductionIdRef.current[productionId]?.seatgeekId ?? null,
          },
        });
      }

      const confirmedSet = new Set(
        Array.isArray(result.ticketPlacementIds) && result.ticketPlacementIds.length
          ? result.ticketPlacementIds.map((id: string) => String(id))
          : tpIds,
      );

      // Add PID: DB write only — keep ticket status, do not submit to VL.
      if (isAddPidOnly) {
        const patchPidOnly = (ticket: Ticket) =>
          confirmedSet.has(ticket.tp_id)
            ? {
                ...ticket,
                e_id: effectiveEventId || ticket.e_id,
                tp_vl_production_id: productionId,
                tp_stubhub_id: stubhubId ?? ticket.tp_stubhub_id,
                tp_seatgeek_id: seatgeekId ?? ticket.tp_seatgeek_id,
              }
            : ticket;
        setFilteredTickets((prev) => prev.map(patchPidOnly));
        setEventGroupedByStatus((prev) => {
          const next = createEmptyEventGroupedTickets();
          for (const section of EVENT_TICKET_VIEW_SECTIONS) {
            next[section.key] = (prev[section.key] || []).map(patchPidOnly);
          }
          return next;
        });
        success(
          result.message ||
            `Production ${productionId} saved to QuickAsyst. Victory Live was not updated.`,
        );
        wrongPidFlowRef.current = null;
        setShowVlProductionMapModal(false);
        setVlProductionMismatch(null);
        setVlPendingSubmitTpIds([]);
        handleBulkActionComplete();
        return;
      }

      const patchAfterConfirm = (ticket: Ticket) =>
        confirmedSet.has(ticket.tp_id)
          ? {
              ...ticket,
              ...(keepCurrentStatus ? {} : { tp_status: 'Verified' }),
              e_id: effectiveEventId || ticket.e_id,
              tp_vl_production_id: productionId,
              tp_stubhub_id: stubhubId ?? ticket.tp_stubhub_id,
              tp_seatgeek_id: seatgeekId ?? ticket.tp_seatgeek_id,
            }
          : ticket;
        const productionFields: Partial<Ticket> = {
          tp_vl_production_id: productionId,
        };
        if (effectiveEventId) productionFields.e_id = effectiveEventId;
        if (stubhubId != null) productionFields.tp_stubhub_id = stubhubId;
        if (seatgeekId != null) productionFields.tp_seatgeek_id = seatgeekId;
        if (!keepCurrentStatus) {
          updateLocalTicketStatus(
            Array.from(confirmedSet),
            'Verified',
            undefined,
            undefined,
            productionFields,
          );
        }
      ticketsForSubmit = (
        tpIds
          .map((tpId) => filteredTickets.find((ticket) => ticket.tp_id === tpId))
          .filter((ticket): ticket is Ticket => Boolean(ticket))
      ).map(patchAfterConfirm);
      if (vlProductionModalMode === 'retry-submit' && !keepCurrentStatus) {
        handleBulkActionComplete();
        switchTabUnlessEventSearch('Verification');
        setFilterStatus('Verified');
      }
      shouldSubmitToVl = true;
      submitTpIds = tpIds;
    } catch (error: any) {
      showError(getErrorMessage(error, isAddPidOnly ? 'Failed to save Production ID' : 'Failed to map production'));
    } finally {
      endStatusTableBusy();
      if (!shouldSubmitToVl) {
        setVlProductionMapLoading(false);
      }
    }

    // Keep the PID modal + top loader visible until inventory is created.
    if (shouldSubmitToVl && submitTpIds.length) {
      try {
        const submitOutcome = await executeSubmitListingsToVl(submitTpIds, ticketsForSubmit, {
          preserveStatus: wrongPidFlow === 'relist',
          clearWrongPid: wrongPidFlow === 'resubmit',
        });
        if (wrongPidFlow === 'relist' && submitOutcome?.fullySucceeded) {
          wrongPidFlowRef.current = 'relist';
          setBulkSelectedTicketIds(new Set(submitTpIds));
          await prepareVlShareModal(ticketsForSubmit);
        } else if (!submitOutcome?.remapped) {
          wrongPidFlowRef.current = null;
        }
        if (!submitOutcome?.remapped) {
          setShowVlProductionMapModal(false);
          setVlProductionMismatch(null);
          setVlPendingSubmitTpIds([]);
        }
      } finally {
        setVlProductionMapLoading(false);
      }
    }
  };

  const callCancelListingRequestByAdminRest = async (ticketPlacementId: string) => {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;

    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    if (!nodeApiBaseUrl) throw new Error('Backend API URL not configured');

    const response = await fetch(`${nodeApiBaseUrl}/api/cancelListingRequestBySuperUser`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        input: { ticketPlacementId }
      })
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message =
        responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to cancel listing request';
      throw new Error(message);
    }
    return responseData;
  };

  const callUndoDelistRequestToListRest = async (tpId: string) => {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;

    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    if (!nodeApiBaseUrl) throw new Error('Backend API URL not configured');

    const response = await fetch(`${nodeApiBaseUrl}/api/undoDelistRequestToList`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        input: { tpId }
      })
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message =
        responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to undo delist request';
      throw new Error(message);
    }
    return responseData;
  };

  const callDelistTicketsRest = async (
    ticketPlacementIds: string[],
    requestedEmailId: string,
    onProgress?: (progress: DelistJobProgress) => void,
  ) => {
    return delistTicketsRequest(ticketPlacementIds, requestedEmailId, onProgress);
  };

  const getDelistRequestEmail = () =>
    loggedInEmail || String(globalState?.profile?.mailId || '').trim();

  const assertDelistTicketsRestSuccess = (data: any, fallbackMessage: string) => {
    const message = String(data?.message || '').trim();
    const status = String(data?.status || '').toLowerCase();
    const normalized = message.toLowerCase();
    if (
      status === 'failure' ||
      (normalized.includes('fail') && !normalized.includes('successfully'))
    ) {
      throw new Error(message || fallbackMessage);
    }
    return message;
  };

  const clearSeatCardStatusForTpIds = useCallback((tpIds: string[]) => {
    if (!tpIds.length) return;
    const idSet = new Set(tpIds.filter(Boolean));
    setSeatCardStatusBySeatId((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(next)) {
        for (const id of idSet) {
          if (key === id || key.startsWith(`${id}-seat-`)) {
            delete next[key];
            break;
          }
        }
      }
      return next;
    });
  }, []);

  const requestListedTicketDelist = useCallback((ticketPlacementIds: string[], tickets: Ticket[] = []) => {
    const ids = ticketPlacementIds.filter(Boolean);
    if (!ids.length) return;
    setListedDelistProgress(null);
    setListedDelistTpIds(ids);
    setListedDelistStoredEmail(
      tickets.map((ticket) => ticket.tp_delist_requested_email?.trim()).find(Boolean) || '',
    );
    setShowListedDelistConfirm(true);
  }, []);

  const cancelListedDelistConfirm = useCallback(() => {
    if (statusChangeLoading) return;
    clearSeatCardStatusForTpIds(listedDelistTpIds);
    setShowListedDelistConfirm(false);
    setListedDelistTpIds([]);
    setListedDelistStoredEmail('');
    setListedDelistProgress(null);
  }, [clearSeatCardStatusForTpIds, listedDelistTpIds, statusChangeLoading]);

  const confirmListedDelist = async (requestedEmailId?: string, retryStep?: DelistUiStep) => {
    if (!listedDelistTpIds.length || statusChangeLoading) return;
    const ids = [...listedDelistTpIds];
    setListedDelistProgress((prev) => ({
      currentStep: retryStep || 1,
      completedSteps: retryStep
        ? (prev?.completedSteps || []).filter((step) => step < retryStep)
        : [],
      failedStep: null,
      message: undefined,
    }));
    const ok = await runListedTicketDelist(ids, setListedDelistProgress, requestedEmailId);
    if (ok) {
      setShowListedDelistConfirm(false);
      setListedDelistTpIds([]);
      setListedDelistStoredEmail('');
      setListedDelistProgress(null);
    }
  };

  const openListedMarketplaceEdit = useCallback((tpIds: string[], tickets: Ticket[]) => {
    const ids = tpIds.filter(Boolean);
    if (!ids.length || !tickets.length) return;
    setListedMarketplaceEditTpIds(ids);
    setListedMarketplaceEditTickets(tickets);
    setListedEditMarketplaces(
      getVlMarketplaceIdsForTicket(ids[0], tickets.find((ticket) => ticket.tp_id === ids[0])),
    );
    setShowListedMarketplaceEdit(true);
  }, []);

  const buildVlSharePayloadForTickets = (tpIds: string[], tickets: Ticket[]) => {
    const listPriceByTpId: Record<string, number> = {};
    const sectionByTpId: Record<string, string> = {};
    const rowByTpId: Record<string, string> = {};
    const seatByTpId: Record<string, string> = {};

    for (const tpId of tpIds) {
      const ticket =
        tickets.find((row) => row.tp_id === tpId) ||
        filteredTickets.find((row) => row.tp_id === tpId);
      if (!ticket) continue;
      const price = Number(ticket.tp_list_price);
      if (!Number.isFinite(price) || price <= 0) {
        throw new Error('List price must be greater than 0 before updating Victory Live.');
      }
      const section = String(ticket.tp_section || '').trim();
      const row = String(ticket.tp_row || '').trim();
      const seat = String(ticket.tp_seat_no ?? '').trim();
      if (!section || !row || !seat) {
        throw new Error('Section, row, and seat are required.');
      }
      listPriceByTpId[tpId] = Number(roundToFixed(price, 2));
      sectionByTpId[tpId] = section;
      rowByTpId[tpId] = row;
      seatByTpId[tpId] = seat;
    }

    return {
      listPriceByTpId,
      sectionByTpId,
      rowByTpId,
      seatByTpId,
    };
  };

  const handleUpdateListedMarketplaces = async () => {
    const tpIds = listedMarketplaceEditTpIds;
    const tickets = listedMarketplaceEditTickets;
    if (!tpIds.length || listedMarketplaceEditLoading) return;
    if (listedEditMarketplaces.length === 0) {
      showError('Select at least one marketplace.');
      return;
    }

    let sharePayload;
    try {
      sharePayload = buildVlSharePayloadForTickets(tpIds, tickets);
    } catch (error: any) {
      showError(getErrorMessage(error, 'Missing ticket details for Victory Live update.'));
      return;
    }

    setListedMarketplaceEditLoading(true);
    beginStatusTableBusy('Updating marketplace selection on Victory Live…');
    try {
      const result = await shareVlListings(tpIds, {
        shareExchange: true,
        enabledMarketplaceIds: listedEditMarketplaces,
        updateMarketplaceNotes: true,
        ...sharePayload,
      });

      const successTpIds = successfulVlTpIds(result);
      const toast = summarizeVlActionToast({
        actionLabel: 'Marketplace update',
        result,
        successMessage: 'Marketplace selection updated on Victory Live.',
      });

      if (toast.type === 'success') {
        setCachedEnabledMarketplaceIdsForMany(tpIds, listedEditMarketplaces);
        updateLocalTicketDetails(tpIds, {
          tp_vl_marketplaces: getVlMarketplaceLabels(listedEditMarketplaces),
        });
        setShowListedMarketplaceEdit(false);
        setListedMarketplaceEditTpIds([]);
        setListedMarketplaceEditTickets([]);
      } else if (successTpIds.length) {
        setCachedEnabledMarketplaceIdsForMany(successTpIds, listedEditMarketplaces);
        updateLocalTicketDetails(successTpIds, {
          tp_vl_marketplaces: getVlMarketplaceLabels(listedEditMarketplaces),
        });
      }
      showVlToast(toast);
      if (toast.type !== 'error') {
        await handleRefresh();
      }
    } catch (error: any) {
      showVlToast(
        toastForVlCaughtError(error, 'Marketplace update', 'Failed to update marketplace selection on Victory Live'),
      );
    } finally {
      setListedMarketplaceEditLoading(false);
      endStatusTableBusy();
    }
  };

  const renderListedVlControls = (tickets: Ticket[]) => {
    if (activeTab !== 'Listed' || !tickets.length) return null;
    const representative = tickets[0];
    if (
      !isVlListedTicketStatus(representative.tp_status) ||
         !ticketHasVlListingMarkers(representative)
    ) {
      return null;
    }
    const tpIds = tickets.map((row) => row.tp_id).filter(Boolean);
    const busy =
      statusTableBusyCount > 0 ||
      listedMarketplaceEditLoading ||
      listedSharedToggleLoading ||
      statusChangeLoading;
    return (
      <ListedVlRowControls
        enabledMarketplaceIds={getVlMarketplaceIdsForTicket(representative.tp_id, representative)}
        disabled={busy}
        className={styles.listedVlControlsColumn}
        onEditMarketplaces={(e) => {
          e.stopPropagation();
          openListedMarketplaceEdit(tpIds, tickets);
        }}
      />
    );
  };

  const toggleListedSharedStatus = async (
    tpIds: string[],
    tickets: Ticket[],
    targetShared: boolean,
  ) => {
    if (!tpIds.length || listedSharedToggleLoading) return;

    let sharePayload;
    try {
      sharePayload = buildVlSharePayloadForTickets(tpIds, tickets);
    } catch (error: any) {
      showError(getErrorMessage(error, 'Missing ticket details for Victory Live update.'));
      return;
    }

    const enabledMarketplaceIds = targetShared
      ? getVlMarketplaceIdsForTicket(tpIds[0], tickets[0])
      : [];

    setListedSharedToggleLoading(true);
    beginStatusTableBusy(
      targetShared ? 'Sharing on Victory Live…' : 'Unsharing on Victory Live…',
    );
    try {
      const result = await shareVlListings(tpIds, {
        enabledMarketplaceIds,
        ...sharePayload,
        shareExchange: targetShared,
      });

      const successTpIds = successfulVlTpIds(result);
      const toast = summarizeVlActionToast({
        actionLabel: targetShared ? 'Share' : 'Unshare',
        result,
        successMessage: targetShared
          ? 'Ticket shared on selected marketplaces. Status remains Listed.'
          : 'Ticket unshared from all marketplaces. Status remains Listed.',
      });

      if (toast.type === 'success') {
        setCachedVlSharedStatusForMany(successTpIds.length ? successTpIds : tpIds, targetShared);
        await handleRefresh();
      } else if (successTpIds.length) {
        setCachedVlSharedStatusForMany(successTpIds, targetShared);
        await handleRefresh();
      }
      showVlToast(toast);
    } catch (error: any) {
      showVlToast(
        toastForVlCaughtError(
          error,
          targetShared ? 'Share' : 'Unshare',
          targetShared ? 'Failed to share on Victory Live.' : 'Failed to unshare on Victory Live.',
        ),
      );
    } finally {
      setListedSharedToggleLoading(false);
      endStatusTableBusy();
    }
  };

  useEffect(() => subscribeVlSupportUpdates(() => setVlSupportReloadToken((n) => n + 1)), []);

  const withVlSupport = (tickets: Ticket[]): Ticket[] =>
    tickets.map((t) => ({ ...t, vl_support_requests: vlSupportByTpId[t.tp_id] ?? [] }));

  /** Listed tab: tabs, filters and the selection bar stay pinned while the ticket list scrolls. */
  const stickyToolbarRef = useRef<HTMLDivElement | null>(null);
  const [stickyToolbarHeight, setStickyToolbarHeight] = useState(0);
  const pinListedToolbar = activeTab === 'Listed';
  useEffect(() => {
    const el = stickyToolbarRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setStickyToolbarHeight(el.offsetHeight));
    observer.observe(el);
    setStickyToolbarHeight(el.offsetHeight);
    return () => observer.disconnect();
  }, [pinListedToolbar]);

  const canUseVlSupport = (activeTab === 'Listed' || activeTab === 'Verification') && !isSuperUser;
  /** Verification tab: only listings still in To Be Verified / Verified can ask Victory Live for the PID. */
  const canContactVlForRow = (tickets: Ticket[]) =>
    activeTab !== 'Verification' ||
    tickets.every((t) => t.tp_status === 'ToBeVerified' || t.tp_status === 'Verified');

  const [vlSupportBulkCompleting, setVlSupportBulkCompleting] = useState(false);

  /** Selected tickets that are part of an open Victory Live support ticket. */
  const openSupportTicketIdsIn = (tickets: Ticket[]): string[] =>
    tickets
      .filter((t) => (vlSupportByTpId[t.tp_id] || []).some((r) => r.vsr_status === 'contacted'))
      .map((t) => t.tp_id);

  /** Bulk "Mark complete": closes the open support tickets the selected listings belong to. */
  const handleVlSupportBulkComplete = async (tickets: Ticket[]) => {
    const tpIds = openSupportTicketIdsIn(tickets);
    if (!tpIds.length || vlSupportBulkCompleting) return;
    setVlSupportBulkCompleting(true);
    try {
      const result = await completeVlSupportRequest(tpIds);
      success(
        result.updatedCount > 1
          ? `${result.updatedCount} support tickets marked complete`
          : 'Victory Live support ticket marked complete',
      );
      clearSelectionState();
      setVlSupportReloadToken((n) => n + 1);
      if (vlSupportFilter !== 'all') void handleRefresh();
    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to mark the support tickets complete'));
    } finally {
      setVlSupportBulkCompleting(false);
    }
  };

  /** Builds the bulk email details from the selected tickets: events and seats only, never a user's name. */
  const openVlSupportBulk = (tickets: Ticket[]) => {
    const scope = bulkSupportScope(tickets);
    if (!scope.ok) {
      showError(scope.reason);
      return;
    }
    const byEvent = new Map<string, Ticket[]>();
    for (const ticket of tickets) {
      const key = ticket.e_id || ticket.e_name;
      byEvent.set(key, [...(byEvent.get(key) || []), ticket]);
    }
    const events = Array.from(byEvent.values()).map((eventTickets) => {
      const rep = eventTickets[0];
      const time = formatTime(rep.e_date, rep.e_time_zone, rep.e_date_time_zone, rep.e_time);
      const date = formatDate(rep.e_date, rep.e_time_zone, rep.e_date_time_zone, rep.e_time);
      const pid =
        eventTickets.map((t) => String(t?.tp_vl_production_id || '').trim()).find(Boolean) ||
        getDisplayedTicketVlProductionId(rep, vlProductionIdByEventId) ||
        'N/A';
      return {
        eventName: rep.e_name || 'N/A',
        eventDate: time ? `${date} ${time}` : date,
        pid,
        listings: groupListings(
          eventTickets.map((t) => ({ section: t.tp_section || 'N/A', row: t.tp_row || 'N/A', seat: t.tp_seat_no })),
        ),
      };
    });
    setVlSupportBulk({ tpIds: tickets.map((t) => t.tp_id), bulk: { events } });
  };

  const handleVlSupportComplete = async (rowKey: string, tickets: Ticket[]) => {
    if (vlSupportCompletingKey) return;
    setVlSupportCompletingKey(rowKey);
    try {
      await completeVlSupportRequest(tickets.map((ticket) => ticket.tp_id));
      success('Victory Live support ticket marked complete');
      setVlSupportReloadToken((n) => n + 1);
    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to mark Victory Live support as completed'));
    } finally {
      setVlSupportCompletingKey(null);
    }
  };

  const renderVlSupportControl = (rowKey: string, tickets: Ticket[]) => {
    if (!canUseVlSupport || !tickets.length || !canContactVlForRow(tickets)) return null;
    return (
      <VlSupportControl
        tickets={withVlSupport(tickets)}
        open={vlSupportOpenKey === rowKey}
        busy={vlSupportCompletingKey === rowKey}
        onToggle={() => {
          setVlSupportThread(null);
          setVlSupportOpenKey((prev) => (prev === rowKey ? null : rowKey));
        }}
        onComplete={() => void handleVlSupportComplete(rowKey, tickets)}
        threadOpen={vlSupportThread?.rowKey === rowKey}
        onOpenThread={(ticketId) => {
          setVlSupportOpenKey(null);
          setVlSupportThread((prev) => (prev?.rowKey === rowKey ? null : { rowKey, ticketId }));
        }}
      />
    );
  };

  const renderVlSupportPanel = (
    rowKey: string,
    tickets: Ticket[],
    representative: Ticket,
    eventName: string,
    section: string,
    seatRow: string,
    seats: string,
  ) => {
    if (!canUseVlSupport || !tickets.length || !canContactVlForRow(tickets)) return null;
    if (vlSupportThread?.rowKey === rowKey) {
      return (
        <tr key={`vl-thread-${rowKey}`} className={styles.expandedRow}>
          <td colSpan={tableColSpan}>
            <div style={{ maxWidth: 760, margin: '8px 0', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px' }}>
              <VlSupportThread
                ticketId={vlSupportThread.ticketId}
                onClose={() => setVlSupportThread(null)}
                onChanged={() => setVlSupportReloadToken((n) => n + 1)}
              />
            </div>
          </td>
        </tr>
      );
    }
    if (vlSupportOpenKey !== rowKey) return null;
    const time = formatTime(
      representative.e_date,
      representative.e_time_zone,
      representative.e_date_time_zone,
      representative.e_time,
    );
    const date = formatDate(
      representative.e_date,
      representative.e_time_zone,
      representative.e_date_time_zone,
      representative.e_time,
    );
    const pid =
      tickets.map((t) => String(t?.tp_vl_production_id || '').trim()).find(Boolean) ||
      getDisplayedTicketVlProductionId(representative, vlProductionIdByEventId) ||
      'N/A';
    const details: VlSupportEventDetails = {
      eventName: eventName || 'N/A',
      eventDate: time ? `${date} ${time}` : date,
      pid,
      section: section || 'N/A',
      row: seatRow || 'N/A',
      seats: seats || 'N/A',
    };
    return (
      <tr key={`vl-support-${rowKey}`} className={styles.expandedRow}>
        <td colSpan={tableColSpan}>
          <VlSupportPanel
            tpIds={tickets.map((t) => t.tp_id)}
            details={details}
            trackingAvailable={vlSupportAvailable}
            ownerInitials={myVlSupportInitials}
            lockedType={activeTab === 'Verification' ? 'need_pid' : undefined}
            onClose={() => setVlSupportOpenKey(null)}
            onError={() => undefined}
            onSent={(result) => {
              if (result.redirectedForNonProd) {
                info(`Test environment: email went to ${result.sentTo ?? 'the QA inbox'}, not Victory Live.`);
              } else {
                success(`Email sent to ${result.sentTo ?? 'Victory Live'}`);
              }
              if (result.warning) info(result.warning);
              setVlSupportOpenKey(null);
              setVlSupportReloadToken((n) => n + 1);
              if (vlSupportFilter !== 'all') void handleRefresh();
            }}
          />
        </td>
      </tr>
    );
  };

  const renderListedSharedToggle = (tickets: Ticket[]) => {
    if (activeTab !== 'Listed' || !tickets.length) return null;
    const representative = tickets[0];
    if (
      !isVlListedTicketStatus(representative.tp_status) ||
      !ticketHasVlListingMarkers({
        tp_vl_listing_id: representative.tp_vl_listing_id,
        tp_vl_item_id: representative.tp_vl_item_id,
      })
    ) {
      return null;
    }
    const tpIds = tickets.map((row) => row.tp_id).filter(Boolean);
    const isShared = getVlSharedStatusForTicket(representative.tp_id, representative);
    const busy =
      statusTableBusyCount > 0 ||
      listedMarketplaceEditLoading ||
      listedSharedToggleLoading ||
      statusChangeLoading;

    return (
      <ListedSharedToggle
        isShared={isShared}
        disabled={busy}
        onToggle={(e) => {
          e.stopPropagation();
          setListedShareConfirm({
            tpIds,
            tickets,
            targetShared: !isShared,
          });
        }}
      />
    );
  };

  const cancelListedShareConfirm = () => {
    if (listedSharedToggleLoading) return;
    setListedShareConfirm(null);
  };

  const confirmListedShareToggle = async () => {
    if (!listedShareConfirm || listedSharedToggleLoading) return;
    const { tpIds, tickets, targetShared } = listedShareConfirm;
    try {
      await toggleListedSharedStatus(tpIds, tickets, targetShared);
    } finally {
      setListedShareConfirm(null);
    }
  };

  const renderListedMarketplaceCell = (tickets: Ticket[]) => {
    if (activeTab === 'Sold') {
      const marketplaceName =
        tickets.map((ticket) => getTicketMarketplaceName(ticket)).find(Boolean) || '';
      return (
        <td className={styles.tdMarketplace} onClick={(e) => e.stopPropagation()}>
          {marketplaceName || <span className={styles.marketplaceCellEmpty}>—</span>}
        </td>
      );
    }
    return (
      <td className={styles.tdMarketplace} onClick={(e) => e.stopPropagation()}>
        {renderListedVlControls(tickets) ?? <span className={styles.marketplaceCellEmpty}>—</span>}
      </td>
    );
  };

  const renderListedSharedCell = (tickets: Ticket[]) => (
    <td className={styles.tdShared} onClick={(e) => e.stopPropagation()}>
      {renderListedSharedToggle(tickets) ?? <span className={styles.marketplaceCellEmpty}>—</span>}
    </td> 
  );

  const renderPricingMethodLabel = (
    pricingMethod: Ticket['tp_pricing_method'],
    ticket?: Ticket | null,
  ) => {
    if (ticket && getResolvedClientType(ticket) === 'self-pricing') {
      return null;
    }
    const normalized = normalizePricingMethod(pricingMethod);
    if (normalized === 'MANUAL') {
      return (
        <span
          className={styles.manualPriceBadge}
          title="Manually Priced"
          aria-label="Manually Priced"
        >
          <DollarSign className={styles.pricingMethodPriceIcon} size={13} aria-hidden="true" />
        </span>
      );
    }
    if (normalized === 'FOLLOW_MARKET') {
      return (
        <span
          className={styles.followMarketPriceBadge}
          title="Follow the Market"
          aria-label="Follow the Market"
        >
          <TrendingUp className={styles.pricingMethodPriceIcon} size={13} aria-hidden="true" />
        </span>
      );
    }
    return null;
  };

  /** Listed tickets: remove from Logitix/Victory Live, then mark Delisted. */
  const runListedTicketDelist = async (
    ticketPlacementIds: string[],
    onProgress?: (progress: DelistJobProgress) => void,
    requestedEmailId?: string,
  ) => {
    if (!ticketPlacementIds.length) return true;
    beginStatusTableBusy(LISTED_DELIST_BUSY_MESSAGE);
    setStatusChangeLoading(true);
    try {
      const { successIds, message, failedDetails } = await executeAdminOrSuperUserDelistRest(
        ticketPlacementIds,
        onProgress,
        requestedEmailId,
      );
      const failedCount = ticketPlacementIds.filter((id) => !successIds.includes(id)).length;
      if (!successIds.length) {
        throw new Error(
          formatDelistCleanupFailureMessage({
            failedCount: ticketPlacementIds.length,
            failedDetails,
            fallbackMessage:
              message ||
              'Delist failed on Victory Live — admin status was not changed. Please try again.',
          }),
        );
      }
      ticketPlacementIds.forEach((id) => {
        if (!successIds.includes(id) && localStatusOverridesRef.current[id] === 'Delist') {
          delete localStatusOverridesRef.current[id];
        }
      });
      clearSeatCardStatusForTpIds(ticketPlacementIds.filter((id) => !successIds.includes(id)));
      updateLocalTicketStatus(successIds, 'Delist');
      if (failedCount > 0) {
        showError(
          formatDelistCleanupFailureMessage({
            failedCount,
            failedDetails,
          }),
        );
        return false;
      }
      success(formatUnshareDelistSuccessMessage(successIds.length));
      if (activeTab !== 'Delisted') switchTabUnlessEventSearch('Delisted');
      await syncTicketsAfterStatusChange();
      syncDashboardNotifications();
      return true;
    } catch (error: any) {
      ticketPlacementIds.forEach((id) => {
        if (localStatusOverridesRef.current[id] === 'Delist') {
          delete localStatusOverridesRef.current[id];
        }
      });
      clearSeatCardStatusForTpIds(ticketPlacementIds);
      if (error instanceof DelistJobError) {
        onProgress?.({
          currentStep: error.currentStep,
          completedSteps: error.completedSteps,
          failedStep: error.failedStep || error.currentStep || 1,
          message: error.message,
        });
      } else {
        onProgress?.({
          currentStep: 1,
          completedSteps: [],
          failedStep: 1,
          message: getErrorMessage(error, 'Failed to delist ticket(s)'),
        });
      }
      await syncTicketsAfterStatusChange();
      return false;
    } finally {
      setStatusChangeLoading(false);
      endStatusTableBusy();
    }
  };

  /** Listed → Delisted: append "." to VL row, unshare, then delete Victory Live inventory. */
  const executeAdminOrSuperUserDelistRest = async (
    ticketPlacementIds: string[],
    onProgress?: (progress: DelistJobProgress) => void,
    requestedEmailId?: string,
  ) => {
    if (!ticketPlacementIds.length) {
      return { successIds: [] as string[], message: '' };
    }
    const data = await callDelistTicketsRest(
      ticketPlacementIds,
      (requestedEmailId || getDelistRequestEmail()).trim(),
      onProgress,
    );
    const message = assertDelistTicketsRestSuccess(
      data,
      "We couldn't complete the delist request. Please try again.",
    );
    const successIds = readDelistSuccessTpIds(data);
    return {
      successIds,
      message: message || 'Ticket delisted successfully',
      failedDetails: Array.isArray(data?.failedDetails) ? data.failedDetails : [],
    };
  };

  async function callReturnTicketsRest(ticketPlacementId: string[]) {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;

    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    if (!nodeApiBaseUrl) throw new Error('Backend API URL not configured');

    const response = await fetch(`${nodeApiBaseUrl}/api/returnTickets`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        input: { ticketPlacementId },
      }),
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message =
        responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to return tickets';
      throw new Error(message);
    }
    return responseData;
  };

  async function executeReturnTicketsRest(ticketPlacementId: string[]) {
    const response = await callReturnTicketsRest(ticketPlacementId);
    const message = String(response?.message || '');
    const type = resolveActionMessageType(message);
    if (type === 'error') throw new Error(message || 'Failed to return tickets');
    return { message, type };
  };

  const callMarkTicketsUnsoldRest = async (ticketPlacementIds: string[]) => {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;

    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    if (!nodeApiBaseUrl) throw new Error('Backend API URL not configured');

    const response = await fetch(`${nodeApiBaseUrl}/api/markTicketsUnsold`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        input: { ticketPlacementIds }
      })
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message =
        responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to mark ticket as Unsold';
      throw new Error(message);
    }
    return responseData;
  };
const callDeleteTicketRest = async (ticketPlacementId: string) => {
  const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
  const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
  const hasuraRole = localStorage.getItem('x-hasura-user-role');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (idToken) headers.Authorization = `Bearer ${idToken}`;
  if (hasuraRole) headers['x-hasura-role'] = hasuraRole;

  const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
  if (!nodeApiBaseUrl) throw new Error('Backend API URL not configured');

  const response = await fetch(`${nodeApiBaseUrl}/api/deleteTicket`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      input: { ticketPlacementId }
    })
  });
  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      responseData?.message ||
      responseData?.error ||
      responseData?.errors?.[0]?.message ||
      'Failed to delete ticket';
    throw new Error(message);
  }
  return responseData;
};

  const callPublishTicketsRest = async (ticketPlacementIds: string[], listPrice: number) => {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;

    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    const response = await fetch(`${nodeApiBaseUrl}/api/publishTickets`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        input: {
          publishTicketInput: {
            ticketPlacementIds,
            listPrice
          }
        }
      })
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message =
        responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to publish tickets';
      throw new Error(message);
    }
    return responseData;
  };

  /** Non-VL list / delist-undo: always writes List + price (publishTickets skips Delist / invalid seats). */
  const callPublishNonVlListing = async (ticketPlacementIds: string[], listPrice: number) => {
    const result = await publishNonVlListingMutation({
      variables: { tp_ids: ticketPlacementIds, tp_list_price: listPrice },
    });
    const affected = result?.data?.update_ticket_placement?.affected_rows ?? 0;
    if (!affected) {
      throw new Error('No tickets were updated. Reload and try again.');
    }
    return result.data.update_ticket_placement;
  };

  const callMarkAsSoldRest = async (
    ticketPlacementId: string,
    soldAmount: number,
    logitixAmount: number,
    marketplaceName: string
  ) => {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;

    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    const response = await fetch(`${nodeApiBaseUrl}/api/markAsSold`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        input: {
          ticketPlacementId,
          soldAmount,
          logitixAmount,
          marketplaceName
        }
      })
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message =
        responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to mark ticket as sold';
      throw new Error(message);
    }
    return responseData;
  };



  // ---------- Marketplace persistence (same as User Details) ----------
const getMarketplaceCache = (userId: string): Record<string, string> => {
  try {
    const cached = localStorage.getItem(`marketplace_cache_${userId}`);
    return cached ? JSON.parse(cached) : {};
  } catch {
    return {};
  }
};

const saveMarketplaceCache = (userId: string, tpId: string, marketplace: string) => {
  try {
    const cache = getMarketplaceCache(userId);
    cache[tpId] = marketplace;
    localStorage.setItem(`marketplace_cache_${userId}`, JSON.stringify(cache));
    console.log(`✅ CACHED marketplace for ${tpId} (user ${userId}): ${marketplace}`);
  } catch (err) {
    console.warn('Failed to cache marketplace:', err);
  }
};

  const callUpdateSettlementPaymentStatusRest = async (
    ticketPlacementIds: string[],
    paymentStatus: string | null,
  ) => {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;

    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    const response = await fetch(`${nodeApiBaseUrl}/api/updateSettlementPaymentStatus`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        input: {
          ticketPlacementIds,
          paymentStatus,
        },
      }),
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message =
        responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to update settlement status';
      throw new Error(message);
    }
    return responseData;
  };

  const { data: usersListData } = useQuery(GET_USERS_LIST, {
    skip: !shouldLoadUsersList,
    variables: {
      pageSize: 2000,
      pageOffset: 0,
      searchText: "%",
      order_by: [{ u_created_at: "desc" }]
    },
    fetchPolicy: 'network-only',
    onError: (err) => {
      console.error('Failed to load users list for client type mapping:', err);
    }
  });

  const getClientDisplayName = (ticket: Ticket | null | undefined): string => {
    if (!ticket) return 'N/A';
    const explicitName = ticket.u_full_name?.trim();
    if (explicitName) return explicitName;

    const splitName = `${ticket.u_first_name || ''} ${ticket.u_last_name || ''}`.trim();
    if (splitName) return splitName;

    return ticket.u_email_id?.trim() || 'N/A';
  };

  const getResolvedClientType = (ticket: any): 'self-pricing' | 'concierge' | '' => {
    const ticketType = String(ticket?.u_pricing_mode || '').trim().toLowerCase();
    if (ticketType === 'self-pricing' || ticketType === 'concierge') return ticketType;
    const usersListType = String(
      (usersListData?.user || []).find((u: any) => u?.u_id === ticket?.u_id)?.u_pricing_mode || ''
    )
      .trim()
      .toLowerCase();
    if (usersListType === 'self-pricing' || usersListType === 'concierge') return usersListType as 'self-pricing' | 'concierge';
    const feePercent = Number(ticket?.u_fee_percentage ?? ticket?.ticket_user?.u_fee_percentage);
    if (Number.isFinite(feePercent) && feePercent > 0) {
      return feePercent <= 15 ? 'self-pricing' : 'concierge';
    }
    return '';
  };

  const resolveSoldFeeConfigForTicket = async (ticket: Ticket): Promise<SoldFeeConfig> => {
    const clientType = getResolvedClientType(ticket);

    if (clientType === 'self-pricing') {
      const percent = ticket.u_id
        ? await resolveSelfPricingClientPercent(ticket.u_id)
        : SELF_PRICING_FEE_FALLBACK;
      return { feeMode: 'super', superClientPercent: percent };
    }

    if (ticket.u_id) {
      const conciergePercent = await fetchUserFeePercent(ticket.u_id);
      if (conciergePercent != null) {
        return { feeMode: 'super', superClientPercent: conciergePercent };
      }
    }

    return DEFAULT_SOLD_FEE_CONFIG;
  };

  async function loadBulkSoldFeeConfigs(tickets: Ticket[]): Promise<Record<string, SoldFeeConfig>> {
    if (!tickets.length) return {};
    await ensureUserFeePercents(tickets.map((ticket) => ticket.u_id).filter((id): id is string => Boolean(id)));
    const configByUserKey = new Map<string, SoldFeeConfig>();
    const configs: Record<string, SoldFeeConfig> = {};

    await Promise.all(
      tickets.map(async (ticket) => {
        const userKey = ticket.u_id || ticket.tp_id;
        if (!configByUserKey.has(userKey)) {
          configByUserKey.set(userKey, await resolveSoldFeeConfigForTicket(ticket));
        }
        configs[ticket.tp_id] = configByUserKey.get(userKey)!;
      })
    );

    return configs;
  };

const getTargetPricingMode = (): string => {
  if (isSuperUser && (activeTab === 'Sold' || activeTab === 'Delisted')) return '';
  if (clientType === 'Self Pricing') return 'self-pricing';
  if (clientType === 'Concierge') return 'concierge';
  return '';
};

  const [markAsSoldMutation, { loading: markAsSoldLoading }] = useMutation(MARK_AS_SOLD, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (markAsSoldUiHandledRef.current) return;
      const rawMsg = (data?.markAsSold?.message || '').toString();
      const normalized = rawMsg.toLowerCase();
      if (!normalized.includes('success')) {
        showError(rawMsg || 'Failed to mark ticket as sold');
        markAsSoldPendingTpIdRef.current = null;
        return;
      }
      markAsSoldUiHandledRef.current = true;
      const tpId = markAsSoldPendingTpIdRef.current;
      if (tpId) {
        updateLocalTicketStatus([tpId], 'Sold');
      }
      success(rawMsg || 'Ticket marked as sold successfully');
      closeSoldModal();
      // Auto-switch to Sold tab when ticket is marked as sold
      if (activeTab !== 'Sold') {
        setTimeout(() => switchTabUnlessEventSearch('Sold'), 300);
      }
      void refetch();
      void refetchExpired();
      void refetchFailed();
      markAsSoldPendingTpIdRef.current = null;
      globalThis.setTimeout(() => {
        markAsSoldUiHandledRef.current = false;
      }, 0);
    },
  });

  // Mark as Sold
const markTicketsAsSold = async (tpIds: string[]) => {
  let updatedCount = 0;
  let failedCount = 0;
  const updatedIds: string[] = [];

  await withSuppressedBulkMutationToasts(async () => {
    for (const tpId of tpIds) {
      try {
        const res = await updateTicketStatus({
          variables: {
            tp_id: tpId,
            status: 'Sold'
          }
        });
        const affected = res?.data?.update_ticket_placement?.returning?.length || 0;
        if (affected > 0) {
          updatedCount += 1;
          updatedIds.push(tpId);
        } else {
          failedCount += 1;
        }
      } catch {
        failedCount += 1;
      }
    }
  });

  if (updatedIds.length > 0) {
    updateLocalTicketStatus(updatedIds, 'Sold');
  }

  if (updatedCount > 0 && failedCount === 0) {
    success(`${updatedCount} ticket(s) marked as sold successfully`);
    if (activeTab !== 'Sold') {
        setTimeout(() => switchTabUnlessEventSearch('Sold'), 300);
    }
  } else if (updatedCount > 0) {
    info(`${updatedCount} ticket(s) marked as sold. ${failedCount} failed.`);
    if (activeTab !== 'Sold') {
        setTimeout(() => switchTabUnlessEventSearch('Sold'), 300);
    }
  } else {
    showError('Failed to mark selected tickets as sold');
  }

  refetch();
  refetchExpired();
  refetchFailed();
  handleBulkActionComplete();
};

const handleBulkActionComplete = () => {
  clearSelectionState();
};

const getLocalDayRangeUTC = (dateStr: string): { startUTC: string; endUTC: string; startTimestamp: string; endTimestamp: string } | null => {
  if (!dateStr) return null;

  // If it's a datetime-local string (YYYY-MM-DDTHH:mm), preserve the time
  if (dateStr.includes('T')) {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;
    const iso = date.toISOString();
    return {
      startUTC: iso,
      endUTC: iso,
      startTimestamp: iso,
      endTimestamp: iso,
    };
  }

  // Date-only: start of day UTC
  return {
    startUTC: dateStr,
    endUTC: dateStr,
    startTimestamp: `${dateStr}T00:00:00Z`,
    endTimestamp: `${dateStr}T23:59:59Z`,
  };
};
  // ==================== DATE FILTER HELPERS ====================
const getTodayStr = () => {
  const today = new Date();

  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};
const getTomorrowStr = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const year = tomorrow.getFullYear();
  const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
  const day = String(tomorrow.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};
  const getNext7DaysEndStr = () => {
    const next7 = new Date();
    next7.setDate(next7.getDate() + 6);  // +6 gives exactly 7 days (today through 6 days from now)
    return next7.toISOString().split('T')[0];
  };

  const applyDatePreset = (preset: 'today' | 'tomorrow' | 'next7days') => {
    setDatePreset(preset);
    setCustomRangeStart('');
    setCustomRangeEnd('');
    if (preset === 'today') {
      const todayStr = getTodayStr();
      setStartDate(todayStr);
      setEndDate(todayStr);
    } else if (preset === 'tomorrow') {
      const tomorrowStr = getTomorrowStr();
      setStartDate(tomorrowStr);
      setEndDate(tomorrowStr);
    } else if (preset === 'next7days') {
      const todayStr = getTodayStr();
      const endStr = getNext7DaysEndStr();
      setStartDate(todayStr);
      setEndDate(endStr);
    }
    setPageOffset(0);
  };

  const clearDateFilter = () => {
    setDatePreset(null);
    setCustomRangeStart('');
    setCustomRangeEnd('');
    setStartDate('');
    setEndDate('');
    setPageOffset(0);
  };

  const applyCustomRange = () => {
    if (customRangeStart && customRangeEnd) {
      setDatePreset(null);
      setStartDate(customRangeStart);
      setEndDate(customRangeEnd);
      setShowCustomRangeModal(false);
      setPageOffset(0);
    } else {
      info('Please select both start and end dates for custom range');
    }
  };

  const getActiveDateFilterLabel = () => {
    if (datePreset === 'today') return 'Today';
    if (datePreset === 'tomorrow') return 'Tomorrow';
    if (datePreset === 'next7days') return 'Next 7 days';
    if (startDate && endDate && (startDate !== '' || endDate !== '')) {
      if (startDate === endDate) return startDate;
      return `${startDate} → ${endDate}`;
    }
    return null;
  };

  // ==================== FILTER OPTIONS ====================

const getFilterStatusOptions = () => {
  const options = [];
  
  switch (activeTab) {
    case 'Verification':
      options.push(
        { value: 'All Verification', label: `All Verification (${filteredStatusCounts['All Verification'] || 0})` },
        { value: 'ToBeVerified', label: `To Be Verified (${filteredStatusCounts['ToBeVerified'] || 0})` },
        { value: 'Verified', label: `Verified (${filteredStatusCounts['Verified'] || 0})` }
      );
      break;
    case 'Listed':
      options.push(
        { value: 'All Listed', label: `All Listed (${filteredStatusCounts['All Listed'] || 0})` },
        { value: 'List', label: `Listed (${filteredStatusCounts['List'] || 0})` }
      );
      break;
    case 'Sold':
      options.push(
        { value: 'All Sold', label: `All Sold (${filteredStatusCounts['All Sold'] || 0})` },
        { value: 'Sold', label: `Sold (${filteredStatusCounts['Sold'] || 0})` },
        { value: 'Settled', label: `Settled (${filteredStatusCounts['Settled'] || 0})` },
        { value: 'SettlementInProgress', label: `Settlement In Progress (${filteredStatusCounts['SettlementInProgress'] || 0})` },
        { value: 'Failed', label: `Failed (${filteredStatusCounts['Failed'] || 0})` },
        { value: 'Voided_Payout', label: `Voided Payout (${filteredStatusCounts['Voided_Payout'] || 0})` },
        { value: 'PaidManuallyViaQA', label: `Paid Manually via QA (${filteredStatusCounts['PaidManuallyViaQA'] || 0})` },
        { value: 'QADeniedPayout', label: `QA Denied Payout (${filteredStatusCounts['QADeniedPayout'] || 0})` },
        { value: 'UnderReview', label: `Under Review (${filteredStatusCounts['UnderReview'] || 0})` }
      );
      break;
    case 'Delisted':
      options.push(
        { value: 'All Delisted', label: `All Delisted (${filteredStatusCounts['All Delisted'] || 0})` },
        { value: 'DelistInProgress', label: `Delist Requested (${filteredStatusCounts['DelistInProgress'] || 0})` },
        { value: 'Delist', label: `Delisted (${filteredStatusCounts['Delist'] || 0})` },
        { value: 'Return', label: `Returned (${filteredStatusCounts['Return'] || 0})` }
      );
      break;
    case 'Listing Request Cancelled':
      options.push(
        { value: 'All Listing Request Cancelled', label: `All Listing Request Cancelled (${filteredStatusCounts['All Listing Request Cancelled'] || 0})` },
        { value: 'ListingRequestCancelled', label: `Listing Request Cancelled (${filteredStatusCounts['ListingRequestCancelled'] || 0})` }
      );
      break;
    case 'Listing Expired':
      options.push(
        { value: 'All Expired', label: `All Expired (${filteredStatusCounts['All Expired'] || 0})` },
        { value: 'Closed', label: `Listing Request Expired (${filteredStatusCounts['Closed'] || 0})` }
      );
      break;
    case 'Unsold':
      options.push(
        { value: 'All Unsold', label: `All Unsold (${filteredStatusCounts['All Unsold'] || 0})` },
        { value: 'Unsold', label: `Unsold (${filteredStatusCounts['Unsold'] || 0})` }
      );
      break;
    case 'Deleted':
      options.push(
        { value: 'All Deleted', label: `All Deleted (${filteredStatusCounts['All Deleted'] || 0})` },
        { value: 'Deleted', label: `Deleted (${filteredStatusCounts['Deleted'] || 0})` }
      );
      break;
    default:
      options.push(
        { value: 'All Verification', label: `All Verification (${filteredStatusCounts['All Verification'] || 0})` },
        { value: 'ToBeVerified', label: `To Be Verified (${filteredStatusCounts['ToBeVerified'] || 0})` },
        { value: 'Verified', label: `Verified (${filteredStatusCounts['Verified'] || 0})` }
      );
  }
  
  return options;
};

  const getDefaultStatusByTab = (tab: string) => {
    switch (tab) {
      case 'Verification': return 'All Verification';
      case 'Listed': return 'All Listed';
      case 'Sold': return 'All Sold';
      case 'Delisted': return 'All Delisted';
      case 'Listing Request Cancelled': return 'All Listing Request Cancelled';
      case 'Listing Expired': return 'All Expired';
      case 'Unsold': return 'All Unsold';
      case 'Deleted': return 'All Deleted';
      default: return isSuperUser ? 'All Listed' : 'All Verification';
    }
  };

  const getDefaultStatus = () => getDefaultStatusByTab(activeTab);

  const lastPricedFilterButtonLabel = useMemo(() => {
    const activeFilterCount =
      lastPricedFilter.length +
      (lastPricedFrom || lastPricedTo ? 1 : 0) +
      (lastPricedCustomFilter ? 1 : 0);
    if (!activeFilterCount) return 'Filter by Last Priced';
    return `Last Priced (${activeFilterCount})`;
  }, [lastPricedFilter, lastPricedFrom, lastPricedTo, lastPricedCustomFilter]);

  const lastPricedRange = getLastPricedRange(lastPricedFrom, lastPricedTo);
  const lastPricedRangeError = lastPricedFrom && lastPricedTo && !lastPricedRange
    ? 'From date and time must be before or equal to To date and time.'
    : '';
  const applyLastPricedCustomRange = (variables: Record<string, unknown>) => {
    if (!lastPricedCustomFilter) return;
    const existingFrom = variables.lastPricedFrom;
    const existingTo = variables.lastPricedTo;
    const existingFromTime = typeof existingFrom === 'string' ? Date.parse(existingFrom) : Number.NaN;
    const existingToTime = typeof existingTo === 'string' ? Date.parse(existingTo) : Number.NaN;
    const customFromTime = Date.parse(lastPricedCustomFilter.range.from);
    const customToTime = Date.parse(lastPricedCustomFilter.range.to);

    variables.lastPricedFrom = new Date(
      Math.max(Number.isFinite(existingFromTime) ? existingFromTime : -8640000000000000, customFromTime),
    ).toISOString();
    variables.lastPricedTo = new Date(
      Math.min(Number.isFinite(existingToTime) ? existingToTime : 8640000000000000, customToTime),
    ).toISOString();
    variables.lastPricedRangeIncludeNull = false;
  };
  const clearLastPricedCustomFilter = () => {
    setLastPricedCustomValue('');
    setLastPricedCustomUnit('Hours');
    setLastPricedCustomWindow('within');
    setLastPricedCustomFilter(null);
    setLastPricedCustomError('');
  };
  const handleLastPricedCustomFilter = () => {
    const value = Number(lastPricedCustomValue);
    if (!lastPricedCustomValue.trim() || !Number.isFinite(value) || value <= 0) {
      setLastPricedCustomError('Enter a positive number.');
      return;
    }

    const range = getLastPricedCustomRange(value, lastPricedCustomUnit, Date.now(), lastPricedCustomWindow);
    if (!range) {
      setLastPricedCustomError('Enter a valid positive number.');
      return;
    }

    setLastPricedCustomFilter({
      value,
      unit: lastPricedCustomUnit,
      range,
    });
    setLastPricedCustomError('');
    setPageOffset(0);
    setShowLastPricedFilterDropdown(false);
  };

  useEffect(() => {
    if (!isSuperUser) return;
    if (!SUPER_USER_MANAGE_TABS.includes(activeTab as (typeof SUPER_USER_MANAGE_TABS)[number])) {
      setActiveTab("Listed");
    }
  }, [isSuperUser, activeTab]);

  useEffect(() => {
    localStorage.setItem(MANAGE_TICKETS_TAB_STORAGE_KEY, activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (!globalState?.error?.errorClicked) return;
    // When tp ids are present, resolve the tab from live ticket status (cross-tab fetch).
    if (getMailParserErrorTpIds(globalState.error)?.length) return;
    const tab = getManageTicketsTabFromErrorStatus(globalState.error.status || '');
    setActiveTab(tab);
    setFilterStatus(getDefaultStatusByTab(tab));
    setPageOffset(0);
  }, [
    globalState.error.errorClicked,
    globalState.error.status,
    globalState.error.errorSearchCancel,
    globalState.error.ticketPlacementId,
    globalState.error.approximateTpArray,
  ]);

  useEffect(() => {
    setFilterStatus(getDefaultStatus());
    setBarcodeFilter('all');
    setPidStatusFilter('all');
    setVlShareFilter('all');
    setShowVlShareFilterDropdown(false);
    setVlSupportFilter('all');
    setVlSupportOwner('company');
    setShowVlSupportFilterDropdown(false);
    setShowVlSupportOwnerDropdown(false);
    setLastPricedFilter([]);
    setLastPricedFrom('');
    setLastPricedTo('');
    clearLastPricedCustomFilter();
    setOnHoldFilter(false);
    setPageOffset(0);
    setGroupsPerFullWindow(null);
    setLoadedSeatWindowIndex(0);
  }, [activeTab]);

  const filterStatusOptions = getFilterStatusOptions();

  // ==================== DATA FETCHING ====================

  const { data: salesRepLookupData } = useQuery<SalesRepLookupResponse>(GET_SALES_REPS_BY_EMAIL, {
    skip: !isMarketingUser || !loggedInEmail,
    variables: { email: loggedInEmail },
    fetchPolicy: "network-only",
  });
  const scopedSalesRepId = salesRepLookupData?.sales_reps?.[0]?.sr_id || null;

  const { data: managedUsersByRepData } = useQuery<{ managedUsers: Array<{ u_id: string }> }>(GET_MANAGED_USERS_BY_SALES_REP, {
    skip: !isMarketingUser || !scopedSalesRepId,
    variables: { salesRepId: scopedSalesRepId },
    fetchPolicy: "network-only",
  });

  const managedUserIds = useMemo(
    () => new Set((managedUsersByRepData?.managedUsers || []).map((user) => user.u_id)),
    [managedUsersByRepData]
  );

  const trackingCountsDeferralReadyRef = useRef(false);

useEffect(() => {
  const delayMs = trackingCountsDeferralReadyRef.current ? 0 : 2500;
  trackingCountsDeferralReadyRef.current = true;
  const timer = window.setTimeout(() => {
    void fetchTrackingCounts();
  }, delayMs);
  return () => window.clearTimeout(timer);
}, [isMarketingUser, managedUserIds.size, startDate, endDate, eventVenueSearch, clientSearch]);

  // Users list is only needed as fallback when ticket rows lack u_pricing_mode.
  useEffect(() => {
    const timer = window.setTimeout(() => setShouldLoadUsersList(true), 4000);
    return () => window.clearTimeout(timer);
  }, []);

  const { data: leaguesData } = useQuery<{ leagues: League[] }>(GET_LEAGUES_LIST, {
    fetchPolicy: 'network-only',
  });

  const { data: marketplacesData, loading: marketplacesLoading, error: marketplacesError, refetch: refetchMarketplaces } = useQuery(GET_MARKETPLACES_FOR_TICKETS, {
    fetchPolicy: 'network-only',
  });

  const soldTicketTpIds = useMemo(() => {
    if (activeTab !== 'Sold') return [] as string[];
    return filteredTickets.map((ticket) => ticket.tp_id).filter(Boolean);
  }, [activeTab, filteredTickets]);

  const { data: soldMarketplaceData } = useQuery(GET_TICKET_MARKETPLACES_BY_TP_IDS, {
    variables: { tpIds: soldTicketTpIds },
    skip: activeTab !== 'Sold' || soldTicketTpIds.length === 0,
    fetchPolicy: 'network-only',
  });

  const marketplaceByTpId = useMemo(() => {
    const map = new Map<string, string>();
    const rows = Array.isArray(soldMarketplaceData?.ticket_placement)
      ? soldMarketplaceData.ticket_placement
      : [];
    rows.forEach((row: { tp_id?: string; tp_marketplace_name?: string | null }) => {
      const tpId = String(row?.tp_id ?? '').trim();
      const name = typeof row?.tp_marketplace_name === 'string' ? row.tp_marketplace_name.trim() : '';
      if (tpId && name) map.set(tpId, name);
    });
    return map;
  }, [soldMarketplaceData]);

  const getTicketMarketplaceName = useCallback((ticket: Ticket): string => {
    const fromApi = marketplaceByTpId.get(ticket.tp_id);
    if (fromApi) return fromApi;
    if (ticket.u_id) {
      const cached = getMarketplaceCache(ticket.u_id)[ticket.tp_id];
      if (cached && String(cached).trim()) return String(cached).trim();
    }
    return '';
  }, [marketplaceByTpId]);

  const marketplacesList: MarketplaceOption[] = useMemo(() => {
    const dbList: MarketplaceOption[] = Array.isArray(marketplacesData?.marketplaces)
      ? marketplacesData.marketplaces
      : [];
    return dbList
      .filter((marketplace) => marketplace.active ?? true)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [marketplacesData]);

  const bulkSoldActiveInStatusModal =
    showBulkStatusModal && normalizeStatusValue(bulkNewStatus) === 'Sold';

  useEffect(() => {
    if (!bulkSoldActiveInStatusModal || !bulkSoldMarketplaceId || !bulkSoldFeeConfigReady) return;
    const mp = marketplacesList.find(m => m.id === bulkSoldMarketplaceId);
    if (!mp || bulkSelectedTickets.length === 0) return;

    setBulkSoldLogitixAmounts(prev => {
      const next = { ...prev };
      bulkSelectedTickets.forEach(ticket => {
        const list = Number(ticket.tp_list_price) || 0;
        const expected = Math.round(list * (100 - mp.vl_fee_percentage)) / 100;
        next[ticket.tp_id] = String(Number(roundToFixed(expected, 2)));
      });
      return next;
    });
  }, [
    bulkSoldActiveInStatusModal,
    bulkSoldMarketplaceId,
    bulkSoldFeeConfigReady,
    bulkSelectedTickets,
    marketplacesList,
  ]);

  useEffect(() => {
    if (!showSoldModal || !soldModalTicket || !soldMarketplaceId || !soldFeeConfigReady) return;
    const mp = marketplacesList.find(m => m.id === soldMarketplaceId);
    if (!mp) return;
    const list = Number(soldModalTicket.tp_list_price) || 0;
    const expected = Math.round(list * (100 - mp.vl_fee_percentage)) / 100;
    setSoldLogitixAmount(String(Number(roundToFixed(expected, 2))));
  }, [
    showSoldModal,
    soldMarketplaceId,
    soldModalTicket?.tp_id,
    soldModalTicket?.tp_list_price,
    soldFeeConfigReady,
    marketplacesList,
  ]);

  const isMailParserErrorView = Boolean(globalState?.error?.errorClicked);
  const effectiveTab = isMailParserErrorView
    ? getManageTicketsTabFromErrorStatus(globalState.error.status || '')
    : activeTab;
  const mailParserErrorTpIds = isMailParserErrorView
    ? getMailParserErrorTpIds(globalState.error)
    : null;
  const isMailParserTpLookupView = isMailParserErrorView && Boolean(mailParserErrorTpIds?.length);

  useEffect(() => {
    effectiveTabRef.current = effectiveTab;
  }, [effectiveTab]);

const ticketsQueryKey = JSON.stringify({
    tab: effectiveTab,
    mailParser: isMailParserErrorView
      ? {
          status: globalState.error.status,
          cancel: globalState.error.errorSearchCancel,
          tpIds: mailParserErrorTpIds,
          text: globalState.error.text,
        }
      : null,
    pageSize,
    filterStatus,
    barcodeFilter,
    pidStatusFilter,
    vlShareFilter,
    vlSupportFilter,
    vlSupportOwner,
    onHoldFilter,
    clientType,
    selectedLeague,
    startDate,
    endDate,
    eventVenueSearch,
    clientSearch,
    stripeAccountFilter,
    cardFilter,
    lastPricedFrom,
    lastPricedTo,
    lastPricedFilter,
    lastPricedCustomFilter,
  });
  const sortRequestContextKey = JSON.stringify({
    tab: effectiveTab,
    mailParser: isMailParserErrorView
      ? {
          status: globalState.error.status,
          cancel: globalState.error.errorSearchCancel,
          tpIds: mailParserErrorTpIds,
          text: globalState.error.text,
        }
      : null,
    pageSize,
    filterStatus,
    barcodeFilter,
    pidStatusFilter,
    vlShareFilter,
    vlSupportFilter,
    vlSupportOwner,
    onHoldFilter,
    clientType,
    selectedLeague,
    startDate,
    endDate,
    eventVenueSearch,
    clientSearch,
    stripeAccountFilter,
    cardFilter,
    lastPricedFrom,
    lastPricedTo,
    lastPricedFilter,
    lastPricedCustomFilter,
    pricingMethodFilter,
  });
  const sortRequestContextKeyRef = useRef(sortRequestContextKey);
  sortRequestContextKeyRef.current = sortRequestContextKey;
  if (prevTicketsQueryKeyRef.current !== ticketsQueryKey) {
    prevTicketsQueryKeyRef.current = ticketsQueryKey;
    queryFetchIdRef.current += 1;
    hasAppliedActiveTabDataRef.current = false;
  }
  const ticketsQueryFetchId = queryFetchIdRef.current;

  const getActiveQuery = () => {
    switch (effectiveTab) {
      case 'Verification': return GET_MANAGE_TICKETS;
      case 'Verified': return GET_MANAGE_TICKETS;
      case 'Listed': return GET_LIST_TICKETS;
      case 'Sold': return GET_SOLD_TICKETS;
      case 'Delisted': return GET_DELIST_AND_RETURN_TICKETS;
      case 'Listing Request Cancelled': return GET_LISTING_REQUEST_CANCELLED_TICKETS;
      case 'Listing Expired': return GET_EXPIRED_TICKETS;
      case 'Unsold': return GET_DELIST_AND_UNSOLD_TICKETS;
      case 'Deleted': return GET_DELETED_TICKETS;
      case 'Under Review': return GET_SOLD_TICKETS;
      default: return GET_LIST_TICKETS;
    }
  };

  const getQueryByTab = (tab: string) => {
    switch (tab) {
      case 'Verification': return GET_MANAGE_TICKETS;
      case 'Verified': return GET_MANAGE_TICKETS;
      case 'Listed': return GET_LIST_TICKETS;
      case 'Sold': return GET_SOLD_TICKETS;
      case 'Delisted': return GET_DELIST_AND_RETURN_TICKETS;
      case 'Listing Request Cancelled': return GET_LISTING_REQUEST_CANCELLED_TICKETS;
      case 'Listing Expired': return GET_EXPIRED_TICKETS;
      case 'Unsold': return GET_DELIST_AND_UNSOLD_TICKETS;
      case 'Deleted': return GET_DELETED_TICKETS;
      case 'Under Review': return GET_SOLD_TICKETS;
      default: return GET_LIST_TICKETS;
    }
  };

  // Event view only needs row payloads — avoid heavy tab queries with sibling aggregates/notes.
  const getEventViewQueryByTab = (tab: string) => {
    switch (tab) {
      case 'Verification': return EVENT_VIEW_GET_MANAGE_TICKETS;
      case 'Listed': return EVENT_VIEW_GET_LIST_TICKETS;
      case 'Sold': return EVENT_VIEW_GET_SOLD_TICKETS;
      case 'Delisted': return EVENT_VIEW_GET_DELIST_AND_RETURN_TICKETS;
      case 'Listing Request Cancelled': return EVENT_VIEW_GET_LISTING_REQUEST_CANCELLED_TICKETS;
      case 'Deleted': return EVENT_VIEW_GET_DELETED_TICKETS;
      case 'Under Review': return EVENT_VIEW_GET_SOLD_TICKETS;
      default: return EVENT_VIEW_GET_LIST_TICKETS;
    }
  };

  const getTicketStatus = (): string | null => {
    if (isMailParserErrorView) {
      return null;
    }

    if (effectiveTab === 'Verification') {
      if (filterStatus === 'ToBeVerified' || filterStatus === 'Verified') {
        return filterStatus;
      }
      return null;
    }

    switch (effectiveTab) {
      case 'Verified':
        return filterStatus === 'Verified' ? filterStatus : 'Verified';
      case 'Listed':
        return filterStatus === 'All Listed' ? null : filterStatus;
           case 'Sold': {
        // QA-only statuses — aa status ki matrame restrict
        if (['PaidManuallyViaQA', 'QADeniedPayout', 'UnderReview'].includes(filterStatus)) {
          return filterStatus;
        }
        // Payment-driven filters ki tp_status='Sold' unna tickets matrame —
        // PaidManuallyViaQA (tp_status != 'Sold') leak avvakoodadu.
        if (
          filterStatus === 'Sold' ||
          filterStatus === 'Settled' ||
          filterStatus === 'SettlementInProgress' ||
          filterStatus === 'Failed' ||
          filterStatus === 'Voided_Payout'
        ) {
          return 'Sold';
        }
        // 'All Sold' — QA statuses kuda included
        return null;
      }
      case 'Delisted':
        return filterStatus === 'All Delisted' ? null : filterStatus;
      case 'Listing Request Cancelled':
        return filterStatus === 'All Listing Request Cancelled' ? null : filterStatus;
      case 'Listing Expired':
        return filterStatus === 'All Expired' ? null : 'Closed';
      case 'Unsold':
        return filterStatus === 'All Unsold' ? null : filterStatus;
      default:
        return null;
    }
  };

// Update the getQueryVariables function (around line 1260-1300) to include proper pricingMode filtering:
const [fetchDeletedCountQuery, { data: deletedCountData }] = useLazyQuery(GET_DELETED_TICKETS, {
  fetchPolicy: 'network-only',
});


const fetchDeletedCount = useCallback(() => {
  const vars: any = {
    pageSize: 1,
    pageOffset: 0,
    search_event: eventVenueSearch ? `%${eventVenueSearch}%` : '%',
    search_client: getCustomerSearchPattern(clientSearch),
    pricingMode: clientType === 'Self Pricing' ? 'self-pricing' 
                : clientType === 'Concierge' ? 'concierge' 
                : '%',
  };

  // --- FIX: Convert local dates to UTC ranges ---
  if (startDate) {
    const range = getLocalDayRangeUTC(startDate);
    if (range) vars.startdate = range.startUTC;
  } else {
    vars.startdate = '1970-01-01T00:00:00Z';
  }
  if (endDate) {
    const range = getLocalDayRangeUTC(endDate);
    if (range) vars.enddate = range.endUTC;
  } else {
    vars.enddate = '9999-12-31T23:59:59Z';
  }

  if (selectedLeague) vars.leagueId = selectedLeague;

  Object.keys(vars).forEach((k) => {
    const v = vars[k];
    if (v === null || v === undefined || (typeof v === 'string' && v.trim() === '')) {
      delete vars[k];
    }
  });

  fetchDeletedCountQuery({ variables: vars });
}, [
  eventVenueSearch,
  clientSearch,
  startDate,
  endDate,
  selectedLeague,
  clientType,
  fetchDeletedCountQuery,
]);

useEffect(() => {
  const count = deletedCountData?.ticket_placement_aggregate?.aggregate?.count;
  if (count !== undefined && count !== null) {
    setTabCounts((prev) => ({ ...prev, Deleted: count }));
  }
}, [deletedCountData]);

  const lastPricedBucketQueryRange = useMemo(
    () => (lastPricedFilter.length ? getLastPricedBucketQueryRange(lastPricedFilter) : null),
    [lastPricedFilter],
  );

function getQueryVariables() {
  const useServerPagination = shouldPreferServerPagination({
    isEventGroupedView: hasActiveEventSearch(eventVenueSearch),
    isMailParserTpLookupView,
    isMailParserErrorView,
    isMarketingUser,
    effectiveTab,
  });
  const shouldLoadFullTabResults =
    !useServerPagination &&
    usesClientSidePaginationForTab(effectiveTab, {
      isMailParserErrorView,
      isMarketingUser,
    });

  // Server pages: over-fetch a seat window, then page the display groups inside it.
  // Client pages: capped load (not 5k–10k) then paginate groups in the browser.
  // When all seats fit in one oversample, keep offset 0 and paginate groups in the browser.
  // When they do not, advance the seat offset only after the groups in the current
  // window are exhausted. A page index times the fetch size skips those groups.
  const groupedFetchSize = getGroupedPageSeatFetchSize(pageSize);
  const knownSeatTotal = Math.max(totalCount, tabCounts[effectiveTab] || 0);
  const directSeatPagination = usesDirectSeatServerPagination({
    isEventGroupedView,
    isMailParserTpLookupView,
    isMailParserErrorView,
    isMarketingUser,
    effectiveTab,
  });
  const fitsOneSeatFetch =
    !directSeatPagination && useServerPagination && serverSeatResultFitsOneFetch(knownSeatTotal, pageSize);
  const seatWindowIndex = directSeatPagination || fitsOneSeatFetch
    ? 0
    : getGroupedSeatWindowIndex(pageOffset, groupsPerFullWindow);
  pendingSeatWindowIndexRef.current = seatWindowIndex;
  const effectivePageSize = directSeatPagination
    ? pageSize
    : shouldLoadFullTabResults
      ? FULL_TAB_CLIENT_FETCH_LIMIT
      : groupedFetchSize;
  const effectivePageOffset = directSeatPagination
    ? Math.max(0, pageOffset)
    : shouldLoadFullTabResults || fitsOneSeatFetch
      ? 0
      : seatWindowIndex * groupedFetchSize;

  const baseVars: any = {
    pageSize: effectivePageSize,
    pageOffset: effectivePageOffset,
    search_event: eventVenueSearch ? `%${eventVenueSearch}%` : "%",
    search_client: isSuperUser && (effectiveTab === 'Sold' || effectiveTab === 'Delisted' || effectiveTab === 'Unsold')
      ? '%'
      : getCustomerSearchPattern(clientSearch),
    verificationCutoff: verificationCutoffDate,
    order_by: effectiveTab === 'Deleted'
      ? [{ tp_updated_at: 'desc' }, { tp_id: 'asc' }]
      : getManageTicketServerOrderBy(
        directSeatPagination && isManageTicketTableSortEnabled(effectiveTab, isMailParserErrorView)
          ? tableSort
          : null,
        effectiveTab,
        ),
  };

  if (globalState?.error?.errorClicked) {
    if (globalState.error.text === "Error-filter" || globalState.error.text === "Success-filter") {
      baseVars.search_event = "%";
      baseVars.search_client = "%";
    }
    const tpFilter = getMailParserErrorTpIds(globalState.error);
    if (tpFilter) {
      baseVars.array_tpid = tpFilter;
    }
  } else {
    // --- FIX: Convert local dates to UTC ranges ---
    if (startDate) {
      const range = getLocalDayRangeUTC(startDate);
      if (range) {
        baseVars.startdate = range.startUTC;
        baseVars.startDateTime = range.startTimestamp;
      }
    } else {
      baseVars.startdate = '1970-01-01';
      baseVars.startDateTime = '1970-01-01T00:00:00Z';
    }
    if (endDate) {
      const range = getLocalDayRangeUTC(endDate);
      if (range) {
        baseVars.enddate = range.endUTC;
        baseVars.endDateTime = range.endTimestamp;
      }
    } else {
      baseVars.enddate = '9999-12-31';
      baseVars.endDateTime = '9999-12-31T23:59:59Z';
    }

    if (selectedLeague) baseVars.leagueId = selectedLeague;

    const status = getTicketStatus();
    if (status) baseVars.ticketStatus = status;
  }

  const pricingModeMap: Record<string, string> = {
    'Self Pricing': 'self-pricing',
    'Concierge': 'concierge',
  };
  baseVars.pricingMode = isMailParserErrorView ||
    clientType === 'All' ||
    (isSuperUser && (effectiveTab === 'Sold' || effectiveTab === 'Delisted' || effectiveTab === 'Unsold'))
    ? '%'
    : (pricingModeMap[clientType] ?? '%');

  if (cardFilter === "FAILED_TRANSFER") baseVars.transferStatus = "Failed";
  if (cardFilter === "RETURN_FAILED") baseVars.returnStatus = "Failed";

 if (effectiveTab === 'Sold') {
  const paymentStatus = getSoldPaymentStatusForQuery(filterStatus);
  const payoutType = getSoldPayoutTypeForQuery(filterStatus);
  if (paymentStatus !== null) baseVars.paymentStatus = paymentStatus;
  if (payoutType !== null) baseVars.payoutType = payoutType;
  if (filterStatus === 'Voided_Payout') baseVars.ticketStatus = 'Sold';
    baseVars.stripeAccountFilter = isSuperUser
      ? {}
      : getStripeAccountFilterForQuery(stripeAccountFilter);
}

  if (effectiveTab === 'Delisted') {
    baseVars.delistedStatuses = getDelistedStatusesForQuery(filterStatus);
  }

  if (effectiveTab === 'Listed' || (effectiveTab === 'Sold' && !isSuperUser)) {
    baseVars.barcodeStatusFilter = barcodeStatusFilterForQuery(barcodeFilter);
  }
if (effectiveTab === 'Listed') {
  baseVars.vlShareFilter = vlShareFilterForQuery(vlShareFilter);
  baseVars.vlSupportFilter = vlSupportFilterForQuery(vlSupportFilter, resolvedVlSupportOwner);
  baseVars.pidStatusFilter = getPidStatusFilterForQuery(pidStatusFilter);
    baseVars.onHoldIn = onHoldFilter ? [true] : [true, false];
    baseVars.ticketPricingMethod =
      pricingMethodFilter === 'all'
        ? '%'
        : pricingMethodFilter === 'NONE'
          ? '__NONE__'
          : pricingMethodFilter;

    // 1) Prefer explicit date+time range from the picker
    const lastPricedRange = getLastPricedRange(lastPricedFrom, lastPricedTo);
    if (lastPricedRange) {
      // Ensure timestamptz format
      const fromDate = lastPricedRange.from
        ? new Date(lastPricedRange.from).toISOString()
        : '0001-01-01T00:00:00Z';
      const toDate = lastPricedRange.to
        ? new Date(lastPricedRange.to).toISOString()
        : '9999-12-31T23:59:59.999Z';
      baseVars.lastPricedFrom = fromDate;
      baseVars.lastPricedTo = toDate;
      baseVars.lastPricedRangeIncludeNull = false;
    } else if (lastPricedBucketQueryRange) {
      baseVars.lastPricedFrom = lastPricedBucketQueryRange.from;
      baseVars.lastPricedTo = lastPricedBucketQueryRange.to;
      baseVars.lastPricedRangeIncludeNull = false;
    }

    // 3) If user typed an invalid range (from > to), do not send a broken filter
    if (lastPricedFrom && lastPricedTo && !lastPricedRange) {
      delete baseVars.lastPricedFrom;
      delete baseVars.lastPricedTo;
      baseVars.lastPricedRangeIncludeNull = true;
    }
    applyLastPricedCustomRange(baseVars);

  }

  Object.keys(baseVars).forEach((k) => {
    const v = (baseVars as any)[k];
    if (v === null || v === undefined || (typeof v === 'string' && v.trim() === '')) {
      delete (baseVars as any)[k];
    }
  });

  return baseVars;
}

  const isEventGroupedView = hasActiveEventSearch(eventVenueSearch) &&
    isEventGroupedTab(activeTab) &&
    isDefaultLocalStatusFilter(activeTab, filterStatus);
  const serverPaginationActive = shouldPreferServerPagination({
    isEventGroupedView,
    isMailParserTpLookupView,
    isMailParserErrorView,
    isMarketingUser,
    effectiveTab,
  });
  const directSeatPagination = usesDirectSeatServerPagination({
    isEventGroupedView,
    isMailParserTpLookupView,
    isMailParserErrorView,
    isMarketingUser,
    effectiveTab,
  });
  const serverGroupClientPaging =
    serverPaginationActive && serverSeatResultFitsOneFetch(totalCount, pageSize);
  const usesLocalStatusFiltering =
    !isEventGroupedView &&
    !isMailParserErrorView &&
    isLocalStatusFilterTab(effectiveTab) &&
    (!serverPaginationActive || effectiveTab === 'Verification');

  const usesClientSidePagination =
    !isEventGroupedView &&
    !serverPaginationActive &&
    (isMailParserTpLookupView ||
      usesClientSidePaginationForTab(effectiveTab, {
        isMailParserErrorView,
        isMarketingUser,
      }));

  const scopeTicketsForDisplay = useCallback(
    (tickets: Ticket[]): Ticket[] => {
      const scoped = isMarketingUser
        ? tickets.filter((ticket) => managedUserIds.has(ticket.u_id))
        : tickets;
      const targetPricingMode = isSuperUser && (activeTab === 'Sold' || activeTab === 'Delisted')
        ? ''
        : getTargetPricingMode();
      const filtered = targetPricingMode
        ? scoped.filter((ticket) => getResolvedClientType(ticket) === targetPricingMode)
        : scoped;
      return sortManageTickets(filtered);
    },
    [isMarketingUser, managedUserIds, clientType, activeTab, isSuperUser],
  );

  const extractTabTicketsFromResponse = (tab: string, responseData: any): Ticket[] => {
    if (tab === 'Verification') return filterVerificationTickets(responseData?.filtermanagetickets || []);
    if (tab === 'Verified') return (responseData?.filtermanagetickets || []).filter((t: any) => t.tp_status === 'Verified');
    if (tab === 'Listed') return responseData?.filterlisttickets || [];
    if (tab === 'Sold') return responseData?.FilterSoldTickets || [];
    if (tab === 'Delisted') return responseData?.filterreturntickets || [];
    if (tab === 'Deleted') return mapDeletedPlacementRows(responseData?.ticket_placement);
    if (tab === 'Listing Request Cancelled') return responseData?.filterListingRequestCancelledTickets || [];
    if (tab === 'Unsold') return responseData?.filterUnsoldTickets || [];
    if (tab === 'Listing Expired') return mergeExpiredTicketsResponse(responseData);
    return [];
  };

  const enrichTicketsWithVlListingIds = useCallback(
    async (tickets: Ticket[]): Promise<Ticket[]> => {
      const tpIds = tickets.map((ticket) => ticket.tp_id).filter(Boolean);
      if (!tpIds.length) return tickets;

      const rowHasVlListingFields = (ticket: Ticket) =>
        'tp_vl_listing_id' in ticket &&
        'tp_vl_item_id' in ticket &&
        'tp_wrong_pid' in ticket;
      const listingLookupIds = tickets
        .filter((ticket) => ticket.tp_id && !rowHasVlListingFields(ticket))
        .map((ticket) => ticket.tp_id);
      const rowHasMarketplaceFields = (ticket: Ticket) =>
        'tp_vl_production_id' in ticket &&
        'tp_stubhub_id' in ticket &&
        'tp_seatgeek_id' in ticket;
      const productionLookupIds = tickets
        .filter((ticket) => ticket.tp_id && !rowHasMarketplaceFields(ticket))
        .map((ticket) => ticket.tp_id);
      if (!listingLookupIds.length && !productionLookupIds.length) {
        return tickets;
      }

      // Read VL listing ids from DB only. Do not call syncVlListingStatus here —
      // that hits the external VL API and blocked every tab paint. Sync runs on
      // explicit Sync / Group actions instead. Sold rows already select these fields.
      const byId = new Map<
        string,
        {
          tp_vl_listing_id?: string | null;
          tp_vl_item_id?: string | null;
          tp_wrong_pid?: boolean | null;
        }
      >();
      const chunkSize = 400;
      for (let i = 0; i < listingLookupIds.length; i += chunkSize) {
        const chunk = listingLookupIds.slice(i, i + chunkSize);
        try {
          const { data } = await apolloClient.query({
            query: GET_VL_LISTING_IDS_BY_TP_IDS,
            variables: { tpIds: chunk },
            // Always pull latest DB mapping so VL split/group changes appear after refresh.
            fetchPolicy: 'network-only',
          });
          const rows = data?.ticket_placement || [];
          for (const row of rows) {
            if (row?.tp_id) {
              byId.set(row.tp_id, {
                ...(byId.get(row.tp_id) || {}),
                tp_vl_listing_id: row.tp_vl_listing_id,
                tp_vl_item_id: row.tp_vl_item_id,
                tp_wrong_pid: row.tp_wrong_pid === true,
              });
            }
          }
        } catch (err) {
          console.warn('[ManageTickets] Failed to load VL listing ids for grouping', err);
        }
      }

      const productionByTpId = new Map<string, string>();
      const marketplaceByTpId = new Map<string, { stubhubId: string | null; seatgeekId: string | null }>();
      for (let i = 0; i < productionLookupIds.length; i += chunkSize) {
        const chunk = productionLookupIds.slice(i, i + chunkSize);
        try {
          const { data } = await apolloClient.query({
            query: GET_PLACEMENT_VL_PRODUCTION_IDS,
            variables: { tpIds: chunk },
            fetchPolicy: 'network-only',
          });
          for (const row of data?.ticket_placement || []) {
            const tpId = String(row?.tp_id || '').trim();
            const productionId = String(row?.tp_vl_production_id || '').trim();
            if (tpId && productionId) productionByTpId.set(tpId, productionId);
            if (tpId) {
              const stubhubId = String(row?.tp_stubhub_id || '').trim() || null;
              const seatgeekId = String(row?.tp_seatgeek_id || '').trim() || null;
              if (stubhubId || seatgeekId) {
                marketplaceByTpId.set(tpId, { stubhubId, seatgeekId });
              }
            }
          }
        } catch (err) {
          console.warn('[ManageTickets] Failed to load seat production ids', err);
        }
      }

      if (!byId.size && !productionByTpId.size && !marketplaceByTpId.size) {
        return tickets;
      }
      return tickets.map((ticket) => {
        const hit = byId.get(ticket.tp_id);
        const seatProductionId =
          productionByTpId.get(ticket.tp_id) ||
          String(ticket.tp_vl_production_id || '').trim() ||
          undefined;
        const seatLinks = marketplaceByTpId.get(ticket.tp_id);
        if (!hit && !seatProductionId && !seatLinks) return ticket;
        return {
          ...ticket,
          ...(hit
            ? {
                tp_vl_listing_id: hit.tp_vl_listing_id ?? ticket.tp_vl_listing_id,
                tp_vl_item_id: hit.tp_vl_item_id ?? ticket.tp_vl_item_id,
                tp_wrong_pid: hit.tp_wrong_pid === true,
              }
            : {}),
          ...(seatProductionId ? { tp_vl_production_id: seatProductionId } : {}),
          ...(seatLinks
            ? {
                tp_stubhub_id: seatLinks.stubhubId ?? null,
                tp_seatgeek_id: seatLinks.seatgeekId ?? null,
              }
            : {}),
        };
      });
    },
    [apolloClient],
  );

  const applyVlProductionIdLocally = (
    eventId: string,
    productionId: string,
    marketplaceIds?: { stubhubId?: string | null; seatgeekId?: string | null },
  ) => {
    const eid = String(eventId || '').trim();
    const pid = String(productionId || '').trim();
    if (!eid || !pid) return;
    localVlProductionIdsByEventRef.current[eid] = pid;
    setVlProductionIdByEventId((prev) =>
      prev[eid] === pid ? prev : { ...prev, [eid]: pid },
    );
    if (marketplaceIds) {
      const next = {
        stubhubId:
          Object.prototype.hasOwnProperty.call(marketplaceIds, 'stubhubId')
            ? marketplaceIds.stubhubId ?? null
            : marketplaceLinksByProductionIdRef.current[pid]?.stubhubId ?? null,
        seatgeekId:
          Object.prototype.hasOwnProperty.call(marketplaceIds, 'seatgeekId')
            ? marketplaceIds.seatgeekId ?? null
            : marketplaceLinksByProductionIdRef.current[pid]?.seatgeekId ?? null,
      };
      marketplaceLinksByProductionIdRef.current = {
        ...marketplaceLinksByProductionIdRef.current,
        [pid]: next,
      };
      persistMarketplaceLinks({
        ...marketplaceLinksByProductionIdRef.current,
      });
    }
    const patch = (ticket: Ticket) =>
      String(ticket.e_id || (ticket as any).ticket?.event?.e_id || '').trim() === eid
        ? {
            ...ticket,
            e_vl_production_id: pid,
            ...(marketplaceIds && Object.prototype.hasOwnProperty.call(marketplaceIds, 'stubhubId')
              ? { e_stubhub_id: marketplaceIds.stubhubId ?? null }
              : {}),
            ...(marketplaceIds && Object.prototype.hasOwnProperty.call(marketplaceIds, 'seatgeekId')
              ? { e_seatgeek_id: marketplaceIds.seatgeekId ?? null }
              : {}),
          }
        : ticket;
    setFilteredTickets((prev) => prev.map(patch));
    setEventGroupedByStatus((prev) => {
      const next = createEmptyEventGroupedTickets();
      for (const section of EVENT_TICKET_VIEW_SECTIONS) {
        next[section.key] = (prev[section.key] || []).map(patch);
      }
      return next;
    });
  };

  // Keep PID + marketplace ids on visible tickets when enrich maps update (table paint races).
  useEffect(() => {
    if (
      !Object.keys(vlProductionIdByEventId).length &&
      !Object.keys(marketplaceLinksByProductionId).length
    ) {
      return;
    }
    setFilteredTickets((prev) => {
      let changed = false;
      const next = prev.map((ticket) => {
        const productionId = getTicketVlProductionId(ticket, vlProductionIdByEventId);
        const links = getTicketMarketplaceIds(
          ticket,
          marketplaceLinksByProductionId,
          vlProductionIdByEventId,
        );
        if (
          ticket.e_vl_production_id === (productionId || ticket.e_vl_production_id) &&
          (ticket.e_stubhub_id ?? null) === links.stubhubId &&
          (ticket.e_seatgeek_id ?? null) === links.seatgeekId
        ) {
          return ticket;
        }
        changed = true;
        return {
          ...ticket,
          ...(productionId ? { e_vl_production_id: productionId } : {}),
          e_stubhub_id: links.stubhubId,
          e_seatgeek_id: links.seatgeekId,
        };
      });
      return changed ? next : prev;
    });
  }, [vlProductionIdByEventId, marketplaceLinksByProductionId]);

  const fetchEventGroupedTickets = useCallback(async () => {
    if (!eventVenueSearch.trim() || eventVenueSearch.trim().length < 2) return;
    if (isMarketingUser && (!scopedSalesRepId || managedUserIds.size === 0)) {
      setEventGroupedByStatus(createEmptyEventGroupedTickets());
      setFilteredTickets([]);
      setTotalCount(0);
      return;
    }

    const fetchId = ++eventGroupedFetchIdRef.current;
    setEventGroupedLoading(true);
    try {
      const baseVars: Record<string, unknown> = {
        pageSize: 5000,
        pageOffset: 0,
        search_event: `%${eventVenueSearch.trim()}%`,
        search_client: isSuperUser && (activeTab === 'Sold' || activeTab === 'Delisted' || activeTab === 'Unsold')
          ? '%'
          : getCustomerSearchPattern(clientSearch),
        verificationCutoff: verificationCutoffDate,
        order_by: MANAGE_TICKETS_ORDER_BY,
      };
      if (startDate) {
        const range = getLocalDayRangeUTC(startDate);
        baseVars.startdate = range?.startUTC ?? startDate;
      }
      if (endDate) {
        const range = getLocalDayRangeUTC(endDate);
        baseVars.enddate = range?.endUTC ?? endDate;
      }
      if (selectedLeague) baseVars.leagueId = selectedLeague;
      // Always fetch all client types (don't filter by pricingMode at backend)
      // We apply client type filter locally so we can calculate accurate counts
      baseVars.pricingMode = '%';
const listedVars: Record<string, unknown> = {
        ...baseVars,
  vlShareFilter: vlShareFilterForQuery(vlShareFilter),
  vlSupportFilter: vlSupportFilterForQuery(vlSupportFilter, resolvedVlSupportOwner),
  pidStatusFilter: getPidStatusFilterForQuery(pidStatusFilter),
        ticketPricingMethod:
          pricingMethodFilter === 'all'
            ? '%'
            : pricingMethodFilter === 'NONE'
              ? '__NONE__'
              : pricingMethodFilter,
      };
      const lastPricedRange = getLastPricedRange(lastPricedFrom, lastPricedTo);
      if (lastPricedRange) {
        const fromDate = lastPricedRange.from
          ? new Date(lastPricedRange.from).toISOString()
          : '0001-01-01T00:00:00Z';
        const toDate = lastPricedRange.to
          ? new Date(lastPricedRange.to).toISOString()
          : '9999-12-31T23:59:59.999Z';
        listedVars.lastPricedFrom = fromDate;
        listedVars.lastPricedTo = toDate;
        listedVars.lastPricedRangeIncludeNull = false;
      } else if (lastPricedBucketQueryRange) {
        listedVars.lastPricedFrom = lastPricedBucketQueryRange.from;
        listedVars.lastPricedTo = lastPricedBucketQueryRange.to;
        listedVars.lastPricedRangeIncludeNull = false;
      }
      applyLastPricedCustomRange(listedVars);
      // Clean up null/empty variables
      Object.keys(baseVars).forEach((k) => {
        const v = (baseVars as any)[k];
        if (v === null || v === undefined || (typeof v === 'string' && v.trim() === '')) {
          delete (baseVars as any)[k];
        }
      });

      // Deleted uses ticket_placement + timestamptz dates + a different order_by type.
      const deletedVars: Record<string, unknown> = {
        pageSize: 5000,
        pageOffset: 0,
        search_event: `%${eventVenueSearch.trim()}%`,
        search_client: getCustomerSearchPattern(clientSearch),
        order_by: [{ tp_updated_at: 'desc' }, { tp_id: 'asc' }],
        startdate: startDate ? (getLocalDayRangeUTC(startDate)?.startUTC ?? `${startDate}T00:00:00Z`) : '1970-01-01T00:00:00Z',
        enddate: endDate ? (getLocalDayRangeUTC(endDate)?.endUTC ?? `${endDate}T23:59:59Z`) : '9999-12-31T23:59:59Z',
        pricingMode: '%',
      };

      const extractRows = (tab: string, responseData: Record<string, unknown>): Ticket[] => {
        if (tab === 'Verification') return filterVerificationTickets((responseData?.filtermanagetickets as Ticket[]) || []);
        if (tab === 'Listed') return (responseData?.filterlisttickets as Ticket[]) || [];
        if (tab === 'Sold') return (responseData?.FilterSoldTickets as Ticket[]) || [];
        if (tab === 'Delisted') return (responseData?.filterreturntickets as Ticket[]) || [];
        if (tab === 'Deleted') return mapDeletedPlacementRows(responseData?.ticket_placement as any[]);
        if (tab === 'Listing Request Cancelled') {
          return (responseData?.filterListingRequestCancelledTickets as Ticket[]) || [];
        }
        return [];
      };

      const tabsToFetch = [
        'Verification',
        'Listed',
        'Sold',
        'Delisted',
        'Listing Request Cancelled',
        'Deleted',
      ] as const;
      const settled = await Promise.allSettled(
        tabsToFetch.map(async (tab) => {
          const { data: responseData } = await apolloClient.query({
            query: getEventViewQueryByTab(tab),
            variables: tab === 'Deleted' ? deletedVars : tab === 'Listed' ? listedVars : baseVars,
            fetchPolicy: 'network-only',
          });
          return [tab, extractRows(tab, responseData)] as const;
        }),
      );

      // Ignore stale responses if the user changed search while requests were in-flight.
      if (fetchId !== eventGroupedFetchIdRef.current) return;

      const byTab: Record<string, Ticket[]> = {};
      for (const result of settled) {
        if (result.status === 'fulfilled') {
          const [tab, rows] = result.value;
          byTab[tab] = rows;
        } else {
          console.error('[ManageTickets] Event-group tab fetch failed:', result.reason);
        }
      }

      // Also fetch Unsold counts scoped to the event search using the full unsold query
      let unsoldCount: number | undefined = undefined;
      try {
        const { data: unsoldData } = await apolloClient.query({
          query: GET_DELIST_AND_UNSOLD_TICKETS,
          variables: baseVars,
          fetchPolicy: 'network-only',
        });
        unsoldCount = unsoldData?.filterUnsoldTickets_aggreagate?.aggregate?.count ?? undefined;
      } catch (unsoldErr) {
        console.error('[ManageTickets] Event-group unsold fetch failed:', unsoldErr);
      }

      // Listing Expired needs the same wide date window as tracking/getQueryVariables.
      // Without startDateTime/endDateTime, closed_placement_aggregate returns 0 and the
      // event-view summary shows "Listing Request Expired (0)" even when matches exist.
      let expiredCount: number | undefined = undefined;
      try {
        const expiredVars: Record<string, unknown> = {
          ...baseVars,
          startdate: baseVars.startdate ?? '1970-01-01',
          enddate: baseVars.enddate ?? '9999-12-31',
          startDateTime: startDate
            ? (getLocalDayRangeUTC(startDate)?.startTimestamp ?? `${startDate}T00:00:00Z`)
            : '1970-01-01T00:00:00Z',
          endDateTime: endDate
            ? (getLocalDayRangeUTC(endDate)?.endTimestamp ?? `${endDate}T23:59:59Z`)
            : '9999-12-31T23:59:59Z',
        };
        const { data: expiredData } = await apolloClient.query({
          query: GET_EXPIRED_TICKETS,
          variables: expiredVars,
          fetchPolicy: 'network-only',
        });
        expiredCount = getExpiredTicketsCount(expiredData);
      } catch (expiredErr) {
        console.error('[ManageTickets] Event-group expired fetch failed:', expiredErr);
      }
      const overrides = localStatusOverridesRef.current;
      const priceOverrides = localPriceOverridesRef.current;
      const scopedTickets = await enrichTicketsWithVlListingIds(
        applyPriceOverrides(
          [
            ...applyStatusOverrides(scopeTicketsForDisplay(byTab.Verification || []), overrides),
            ...applyStatusOverrides(scopeTicketsForDisplay(byTab.Listed || []), overrides),
            ...applyStatusOverrides(scopeTicketsForDisplay(byTab.Sold || []), overrides),
            ...applyStatusOverrides(scopeTicketsForDisplay(byTab.Delisted || []), overrides),
            ...applyStatusOverrides(scopeTicketsForDisplay(byTab['Listing Request Cancelled'] || []), overrides),
            ...applyStatusOverrides(scopeTicketsForDisplay(byTab.Deleted || []), overrides),
          ],
          priceOverrides,
        ),
      );
      if (fetchId !== eventGroupedFetchIdRef.current) return;
      reconcilePriceOverrides(priceOverrides, scopedTickets);
      const grouped = distributeEventGroupedTickets(
        excludeUndatedTicketsForDateFilter(scopedTickets, startDate, endDate),
      );
      const visibleTickets = getEventGroupedTicketsForTab(activeTab, grouped);

      setEventGroupedByStatus(grouped);
      setFilteredTickets(
        activeTab === 'Delisted' ? sortDelistedTabTickets(visibleTickets) : sortManageTickets(visibleTickets),
      );
      setTotalCount(visibleTickets.length);
      setEventGroupedUnsoldCount(typeof unsoldCount === 'number' ? unsoldCount : 0);
      setEventGroupedListingExpiredCount(typeof expiredCount === 'number' ? expiredCount : 0);
      // Update tab badges for event-grouped view. Include Unsold if fetched above.
      const delistedTickets = grouped.delisted ?? [];
      const listingRequestCancelledCount = delistedTickets.filter(
        (ticket) => ticket.tp_status === 'ListingRequestCancelled',
      ).length;
      const delistedCount = delistedTickets.length - listingRequestCancelledCount;
      setTabCounts((prev) => ({
        ...prev,
        Verification: grouped.pending.length,
        Listed: grouped.listed.length,
        Sold: grouped.sold.length,
        Delisted: delistedCount,
        'Listing Request Cancelled': listingRequestCancelledCount,
        Deleted: grouped.deleted.length,
        Unsold: (typeof unsoldCount === 'number' ? unsoldCount : prev.Unsold ?? 0),
        'Listing Expired': (typeof expiredCount === 'number' ? expiredCount : prev['Listing Expired'] ?? 0),
      }));
    } catch (eventGroupError) {
      if (fetchId !== eventGroupedFetchIdRef.current) return;
      console.error('[ManageTickets] Failed to load event-grouped tickets:', eventGroupError);
      setEventGroupedByStatus(createEmptyEventGroupedTickets());
      setFilteredTickets([]);
      setTotalCount(0);
    } finally {
      if (fetchId === eventGroupedFetchIdRef.current) {
        setEventGroupedLoading(false);
      }
    }
  }, [
    activeTab,
    eventVenueSearch,
    clientSearch,
    lastPricedFrom,
    lastPricedTo,
    lastPricedBucketQueryRange,
    startDate,
    endDate,
    selectedLeague,
    clientType,
    pricingMethodFilter,
    pidStatusFilter,
    vlShareFilter,
    vlSupportFilter,
    vlSupportOwner,
    verificationCutoffDate,
    apolloClient,
    isMarketingUser,
    scopedSalesRepId,
    managedUserIds,
    scopeTicketsForDisplay,
    enrichTicketsWithVlListingIds,
  ]);

  const applyTicketsTableState = useCallback(async (
    tickets: Ticket[],
    totalCountValue: number,
    tabForScope: string,
    skipTabScope = false,
  ) => {
    let resolvedTickets = tickets;
    let resolvedTotalCount = totalCountValue;

    if (mailParserErrorTpIds?.length) {
      const idSet = new Set(mailParserErrorTpIds);
      resolvedTickets = resolvedTickets.filter((ticket) => idSet.has(ticket.tp_id));
      resolvedTotalCount = resolvedTickets.length;
    }

    const scopedTickets = isMarketingUser
      ? resolvedTickets.filter((ticket) => managedUserIds.has(ticket.u_id))
      : resolvedTickets;
    const tabScopedTickets = skipTabScope
      ? scopedTickets
      : tabForScope === 'Verification'
      ? filterVerificationTickets(scopedTickets)
      : tabForScope === 'Verified'
      ? scopedTickets.filter((ticket) => ticket.tp_status === 'Verified')
      : scopedTickets;

    setUnfilteredTabTickets(tabScopedTickets);

    const targetPricingMode =
      isMailParserErrorView ||
      (isSuperUser && (effectiveTab === 'Sold' || effectiveTab === 'Delisted'))
        ? ''
        : getTargetPricingMode();
    const clientFilteredTickets = targetPricingMode
      ? tabScopedTickets.filter((ticket: any) => getResolvedClientType(ticket) === targetPricingMode)
      : tabScopedTickets;



    const scopedTotalCount = isMarketingUser
      ? tabScopedTickets.length
      : resolvedTotalCount;

    const mergedTickets = applyPriceOverrides(
      applyStatusOverrides(
        clientFilteredTickets.filter((ticket) =>
          tabForScope === 'Sold' && filterStatus === 'Voided_Payout'
            ? ticket.tp_payout_status === 'Voided_Payout'
            : true,
        ),
        localStatusOverridesRef.current,
      ),
      localPriceOverridesRef.current,
    );
    reconcileStatusOverrides(localStatusOverridesRef.current, clientFilteredTickets);
    reconcilePriceOverrides(localPriceOverridesRef.current, clientFilteredTickets);
    const sortedMergedTickets = tabForScope === 'Delisted'
      ? sortDelistedTabTickets(mergedTickets)
      : sortManageTickets(mergedTickets);
    setStatusFilterSourceTickets(sortedMergedTickets);

    const tabServerPaginationActive = shouldPreferServerPagination({
      isEventGroupedView,
      isMailParserTpLookupView,
      isMailParserErrorView,
      isMarketingUser,
      effectiveTab: tabForScope,
    });
    const activeLocalStatusFilters = getLocalStatusFiltersFromFilterStatus(tabForScope, filterStatus);
    const tabUsesLocalStatusFiltering =
      !isEventGroupedView &&
      !isMailParserErrorView &&
      isLocalStatusFilterTab(tabForScope) &&
      (!tabServerPaginationActive || tabForScope === 'Verification');

    const locallyFilteredTickets = applyLocalStatusFilters(sortedMergedTickets, tabForScope, activeLocalStatusFilters);
    const visibleTicketsBase = tabUsesLocalStatusFiltering
      ? (tabForScope === 'Delisted'
        ? sortDelistedTabTickets(locallyFilteredTickets)
        : sortManageTickets(locallyFilteredTickets))
      : sortedMergedTickets;

    const tabUsesClientSidePagination =
      !tabServerPaginationActive &&
      usesClientSidePaginationForTab(tabForScope, {
        isMailParserErrorView,
        isMarketingUser,
      });

    // Keep the full seat window. Display groups are paged in the table so a
    // 400-seat fetch is not treated as a single page of rows.
    const windowIndexForThisFetch = pendingSeatWindowIndexRef.current;
    const applyVisibleTickets = (visibleTickets: Ticket[]) => {
      const datedTickets = excludeUndatedTicketsForDateFilter(visibleTickets, startDate, endDate);
      const displayCount = tabServerPaginationActive
        ? scopedTotalCount
        : tabUsesClientSidePagination || tabUsesLocalStatusFiltering
          ? datedTickets.length
          : scopedTotalCount;

      // Always merge cached PID / SH / SG so the early unenriched paint cannot wipe icons.
      // List price stays on the value returned by this fetch.
      setFilteredTickets(
        attachCachedMarketplaceFields(
          applyPriceOverrides(datedTickets, localPriceOverridesRef.current),
        ),
      );
      setTotalCount(displayCount);

      const counts = (() => {
        const baseCounts = calculateFilteredCounts(sortedMergedTickets, tabForScope);
        if (tabUsesClientSidePagination || tabUsesLocalStatusFiltering) {
          return baseCounts;
        }
        return applyAggregateStatusCounts(baseCounts, tabForScope, scopedTotalCount);
      })();
      if (
        !(
          tabServerPaginationActive &&
          (tabForScope === 'Sold' || tabForScope === 'Delisted')
        )
      ) {
        setFilteredStatusCounts(counts);
      }

      setTabCounts(prev => {
        if (prev[tabForScope] === displayCount) return prev;
        return { ...prev, [tabForScope]: displayCount };
      });
      setLoadedSeatWindowIndex(windowIndexForThisFetch);
    };

    // Pull VL listing ids so admin groups match VL ticket groups after split/merge.
    // Paint only after this returns. An earlier list makes the table appear, then
    // the boundary fetch replaces it with the spinner and paints again.
    const enrichedVisible = await enrichTicketsWithVlListingIds(visibleTicketsBase);
    if (tabForScope !== effectiveTabRef.current) return;
    const visibleTickets = enrichedVisible;
    // Paint enriched rows once; Listed must not paint the pre-enrichment grouping.
    applyVisibleTickets(visibleTickets);
    if (tabForScope === effectiveTabRef.current) {
      hasAppliedActiveTabDataRef.current = true;
    }
  }, [
    mailParserErrorTpIds,
    isMarketingUser,
    managedUserIds,
    isMailParserErrorView,
    isMailParserTpLookupView,
    isEventGroupedView,
    filterStatus,
    clientType,
    stripeAccountFilter,
    pageSize,
    enrichTicketsWithVlListingIds,
    attachCachedMarketplaceFields,
    startDate,
    endDate,
  ]);

  const processActiveTabQueryData = useCallback(async (responseData: any, tab: string) => {
    if (tab !== effectiveTabRef.current) return;
    const parsed = extractTicketsFromActiveTabQuery(responseData, tab);
    if (!parsed) return;

    await applyTicketsTableState(parsed.tickets, parsed.totalCountValue, tab);
    if (sortRequestPendingKeyRef.current === sortRequestContextKeyRef.current) {
      sortRequestPendingKeyRef.current = null;
      setSortRequestPendingKey(null);
    }

    if (tab === 'Listed') {
      const holdCount = responseData?.FilterListed_onHold_aggregate?.aggregate?.count;
      if (typeof holdCount === 'number') {
        setOnHoldCount(holdCount);
      }
    }

    if (tab === 'Sold' && responseData?.FilterSoldTickets_soldOpen_aggregate) {
      setFilteredStatusCounts(extractSoldStatusCountsFromResponse(responseData));
    }

    if (tab === 'Delisted' && responseData?.FilterDelisted_delist_aggregate) {
      setFilteredStatusCounts(extractDelistedStatusCountsFromResponse(responseData));
    }

    // Sibling tab badges are loaded by GET_SIBLING_TAB_COUNTS (separate from row queries).
    // Keep this merge for any residual aggregate fields (e.g. Listing Expired / Deleted).
    const siblingCounts = extractSiblingTabCountsFromResponse(responseData);
    if (Object.keys(siblingCounts).length > 0) {
      setTabCounts((prev) => {
        const next = { ...prev };
        for (const [siblingTab, count] of Object.entries(siblingCounts)) {
          if (siblingTab !== tab) {
            next[siblingTab] = count;
          }
        }
        return next;
      });
    }
  }, [applyTicketsTableState]);

  useEffect(() => {
    processQueryDataForTableRef.current = processActiveTabQueryData;
  }, [processActiveTabQueryData]);

  const fetchMailParserErrorTickets = useCallback(async () => {
    if (!isMailParserTpLookupView || !mailParserErrorTpIds?.length) return;
    if (isMarketingUser && (!scopedSalesRepId || managedUserIds.size === 0)) {
      setFilteredTickets([]);
      setTotalCount(0);
      setUnfilteredTabTickets([]);
      return;
    }

    setMailParserErrorLoading(true);
    try {
      const idSet = new Set(mailParserErrorTpIds);
      const baseVars: Record<string, unknown> = {
        pageSize: 5000,
        pageOffset: 0,
        search_event: '%',
        search_client: '%',
        verificationCutoff: verificationCutoffDate,
        order_by: MANAGE_TICKETS_ORDER_BY,
        array_tpid: mailParserErrorTpIds,
        // omit leagueId/startdate/enddate/ticketStatus to avoid sending explicit nulls
        pricingMode: '%',
      };

      // Clean up null/empty variables
      Object.keys(baseVars).forEach((k) => {
        const v = (baseVars as any)[k];
        if (v === null || v === undefined || (typeof v === 'string' && v.trim() === '')) {
          delete (baseVars as any)[k];
        }
      });

      const tabsToFetch = [
        'Verification',
        'Listed',
        'Sold',
        'Delisted',
        'Listing Request Cancelled',
        'Unsold',
        'Listing Expired',
      ] as const;

      const responses = await Promise.all(
        tabsToFetch.map(async (tab) => {
          const { data: responseData } = await apolloClient.query({
            query: getQueryByTab(tab),
            variables: baseVars,
            fetchPolicy: 'network-only',
          });
          return extractTabTicketsFromResponse(tab, responseData);
        }),
      );

      const matchingTickets = sortManageTickets(
        responses.flat().filter((ticket) => idSet.has(ticket.tp_id)),
      );
      const scopedTickets = isMarketingUser
        ? matchingTickets.filter((ticket) => managedUserIds.has(ticket.u_id))
        : matchingTickets;

      let resolvedTab = effectiveTab;
      if (scopedTickets.length > 0) {
        resolvedTab = getTabForStatus(scopedTickets[0].tp_status || '');
        setActiveTab(resolvedTab);
        setFilterStatus(getDefaultStatusByTab(resolvedTab));
      }

      await applyTicketsTableState(scopedTickets, scopedTickets.length, resolvedTab, true);
    } catch (mailParserFetchError) {
      console.error('[ManageTickets] Failed to load mail parser tickets:', mailParserFetchError);
      setFilteredTickets([]);
      setTotalCount(0);
      setUnfilteredTabTickets([]);
    } finally {
      setMailParserErrorLoading(false);
    }
  }, [
    isMailParserTpLookupView,
    mailParserErrorTpIds,
    isMarketingUser,
    scopedSalesRepId,
    managedUserIds,
    verificationCutoffDate,
    apolloClient,
    effectiveTab,
    applyTicketsTableState,
  ]);

  useEffect(() => {
    if (!isMailParserTpLookupView) return;
    void fetchMailParserErrorTickets();
  }, [
    isMailParserTpLookupView,
    fetchMailParserErrorTickets,
    globalState?.error?.errorSearchCancel,
    globalState?.error?.ticketPlacementId,
    globalState?.error?.approximateTpArray,
  ]);

  useEffect(() => {
    if (!showClientDropdown) return;
    if (
      isEventGroupedView ||
      isMailParserTpLookupView ||
      isMarketingUser ||
      effectiveTab === 'Verification'
    ) {
      return;
    }
    if (lastClientTypeCountsQueryKeyRef.current === ticketsQueryKey) return;

    let isCancelled = false;

    const fetchClientTypeCounts = async () => {
      setClientTypeCountsLoading(true);
      try {
        const tab = effectiveTab;
        const query = getQueryByTab(tab);
        const sharedVars = {
          ...getQueryVariables(),
          pageSize: 1,
          pageOffset: 0,
        };

        const [allResult, selfResult, conciergeResult] = await Promise.all([
          apolloClient.query({ query, variables: { ...sharedVars, pricingMode: '%' }, fetchPolicy: 'network-only' }),
          apolloClient.query({ query, variables: { ...sharedVars, pricingMode: 'self-pricing' }, fetchPolicy: 'network-only' }),
          apolloClient.query({ query, variables: { ...sharedVars, pricingMode: 'concierge' }, fetchPolicy: 'network-only' }),
        ]);

        if (isCancelled) return;

        setClientTypeCounts({
          All: extractTicketsFromActiveTabQuery(allResult.data, tab)?.totalCountValue ?? 0,
          'Self Pricing': extractTicketsFromActiveTabQuery(selfResult.data, tab)?.totalCountValue ?? 0,
          Concierge: extractTicketsFromActiveTabQuery(conciergeResult.data, tab)?.totalCountValue ?? 0,
        });
        lastClientTypeCountsQueryKeyRef.current = ticketsQueryKey;
      } catch (countFetchError) {
        console.error('[ManageTickets] Failed to load client type counts:', countFetchError);
      } finally {
        if (!isCancelled) setClientTypeCountsLoading(false);
      }
    };

    void fetchClientTypeCounts();

    return () => {
      isCancelled = true;
    };
  }, [
    showClientDropdown,
    ticketsQueryKey,
    verificationCutoffDate,
    isEventGroupedView,
    isMailParserTpLookupView,
    isMarketingUser,
    apolloClient,
    effectiveTab,
  ]);

  useEffect(() => {
    if (
      effectiveTab !== 'Verification' ||
      isEventGroupedView ||
      isMailParserTpLookupView
    ) {
      return;
    }

    const sourceTickets = unfilteredTabTickets;
    const selfPricingTickets = sourceTickets.filter((t) => getResolvedClientType(t) === 'self-pricing');
    const conciergeTickets = sourceTickets.filter((t) => getResolvedClientType(t) === 'concierge');

    setClientTypeCounts({
      All: sourceTickets.length,
      'Self Pricing': selfPricingTickets.length,
      Concierge: conciergeTickets.length,
    });
  }, [
    effectiveTab,
    isEventGroupedView,
    isMailParserTpLookupView,
    unfilteredTabTickets,
    usersListData,
  ]);

useEffect(() => {
    if (!isEventGroupedView) {
      setEventGroupedByStatus(createEmptyEventGroupedTickets());
      return;
    }
    void fetchEventGroupedTickets();
  }, [isEventGroupedView, fetchEventGroupedTickets]);

  useEffect(() => {
    if (!isEventGroupedView) return;

    const delistedTickets = eventGroupedByStatus.delisted ?? [];
    const listingRequestCancelledCount = delistedTickets.filter(
      (ticket) => ticket.tp_status === 'ListingRequestCancelled',
    ).length;
    const delistedCount = delistedTickets.length - listingRequestCancelledCount;

    setTabCounts((prev) => ({
      ...prev,
      Verification: eventGroupedByStatus.pending.length,
      Listed: eventGroupedByStatus.listed.length,
      Sold: eventGroupedByStatus.sold.length,
      Delisted: delistedCount,
      'Listing Request Cancelled': listingRequestCancelledCount,
      Deleted: eventGroupedByStatus.deleted.length,
    }));

    const pricingFilteredTickets = getEventGroupedTicketsForTab(activeTab, eventGroupedByStatus).filter((ticket) =>
      activeTab !== 'Listed' ||
      pricingMethodFilter === 'all' ||
      (pricingMethodFilter === 'NONE'
        ? normalizePricingMethod(ticket.tp_pricing_method) == null
        : ticket.tp_pricing_method === pricingMethodFilter ||
          normalizePricingMethod(ticket.tp_pricing_method) === pricingMethodFilter),
    );
    const activeTabTickets = filterTicketsByOnHold(
      filterTicketsByBarcodeStatus(
        pricingFilteredTickets,
        shouldShowBarcodeStatus(activeTab, isSuperUser) ? barcodeFilter : 'all',
      ),
      activeTab === 'Listed' && onHoldFilter,
    );
    // Re-attach PID / marketplace ids from enrich caches (tab switches can drop ticket fields).
    const withMarketplace = activeTabTickets.map((ticket) => {
      const productionId = getTicketVlProductionId(ticket, vlProductionIdByEventId);
      const links = getTicketMarketplaceIds(
        ticket,
        marketplaceLinksByProductionId,
        vlProductionIdByEventId,
      );
      if (!productionId && !links.stubhubId && !links.seatgeekId) return ticket;
      return {
        ...ticket,
        ...(productionId ? { e_vl_production_id: productionId } : {}),
        e_stubhub_id: links.stubhubId,
        e_seatgeek_id: links.seatgeekId,
      };
    });
    setFilteredTickets(sortManageTickets(withMarketplace));
    setTotalCount(withMarketplace.length);
    setFilteredStatusCounts(calculateFilteredCounts(withMarketplace, activeTab));
    if (activeTab === 'Listed') {
      setOnHoldCount(
        (eventGroupedByStatus.listed || []).filter((ticket) => ticket.on_hold === true).length,
      );
    }
  }, [
    isEventGroupedView,
    eventGroupedByStatus,
    activeTab,
    pricingMethodFilter,
    barcodeFilter,
    onHoldFilter,
    vlProductionIdByEventId,
    marketplaceLinksByProductionId,
  ]);

  const { loading, error: queryError, data, refetch: refetchActiveTab } = useQuery<any>(getActiveQuery(), {
    variables: getQueryVariables(),
    skip: isEventGroupedView || isMailParserTpLookupView,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: false,
  });

  useEffect(() => {
    if (!sortRequestPendingKey) return;
    if (
      !isManageTicketSortRequestPending(sortRequestPendingKey, sortRequestContextKey) ||
      (!loading && queryError)
    ) {
      sortRequestPendingKeyRef.current = null;
      setSortRequestPendingKey(null);
    }
  }, [sortRequestPendingKey, sortRequestContextKey, loading, queryError]);

  useEffect(() => {
    if (isEventGroupedView || isMailParserTpLookupView || !data) return;
    if (ticketsQueryFetchId !== queryFetchIdRef.current) return;
    processActiveTabQueryData(data, effectiveTabRef.current);
  }, [
    data,
    ticketsQueryFetchId,
    isEventGroupedView,
    isMailParserTpLookupView,
    processActiveTabQueryData,
  ]);

  useEffect(() => {
    refreshTicketsTableRef.current = () => {
      if (isEventGroupedView) return fetchEventGroupedTickets();
      if (isMailParserTpLookupView) return fetchMailParserErrorTickets();
      return refetchActiveTab();
    };
  }, [isEventGroupedView, isMailParserTpLookupView, fetchEventGroupedTickets, fetchMailParserErrorTickets, refetchActiveTab]);

  // Debounce event/venue search
  useEffect(() => {
    const timer = setTimeout(() => {
      setEventVenueSearch(debouncedEventVenueSearch);
      setPageOffset(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [debouncedEventVenueSearch]);

  // Debounce client search - FIXED: This sets clientSearch from debouncedClientSearch
  useEffect(() => {
    const timer = setTimeout(() => {
      setClientSearch(debouncedClientSearch);
      setPageOffset(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [debouncedClientSearch]);

  useEffect(() => {
    if (!isSelfPricingListedView) return;
    setBarcodeFilter('all');
    setPidStatusFilter('all');
    setOnHoldFilter(false);
    setPricingMethodFilter('all');
    setClientType('All');
    setClientSearch('');
    setDebouncedClientSearch('');
    setShowBarcodeFilterDropdown(false);
    setShowPidStatusFilterDropdown(false);
    setShowPricingMethodFilterDropdown(false);
    setShowClientDropdown(false);
  }, [isSelfPricingListedView]);

  // Reset page offset when filters change
  useEffect(() => {
    setPageOffset(0);
  }, [filterStatus, barcodeFilter, onHoldFilter, clientType, selectedLeague, startDate, endDate, activeTab, eventVenueSearch, clientSearch]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const rawUntil = sessionStorage.getItem('manageTicketsBulkProcessingUntil');
    const refreshUntil = rawUntil ? Number(rawUntil) : 0;
    if (!Number.isFinite(refreshUntil) || refreshUntil <= Date.now()) {
      if (rawUntil) sessionStorage.removeItem('manageTicketsBulkProcessingUntil');
      return;
    }

    // Bulk add is queued asynchronously on backend; poll briefly so user sees rows appear.
    const intervalId = window.setInterval(() => {
      if (Date.now() >= refreshUntil) {
        sessionStorage.removeItem('manageTicketsBulkProcessingUntil');
        window.clearInterval(intervalId);
        return;
      }
      void refreshTicketsTableRef.current?.();
      void fetchTrackingCounts();
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (clientType === 'All') return;
    if (!usersListData?.user?.length) return;
    void refreshTicketsTableRef.current?.();
  }, [usersListData, clientType]);

  useEffect(() => {
    if (!isMarketingUser) return;
    if (!scopedSalesRepId) return;
    void refreshTicketsTableRef.current?.();
  }, [isMarketingUser, scopedSalesRepId, managedUserIds.size]);

  // Recalculate filtered counts when filteredTickets or activeTab changes (client-paginated tabs only).
  useEffect(() => {
    if (!usesLocalStatusFiltering) return;

    const tabUsesClientPagination = usesClientSidePaginationForTab(activeTab, {
      isMailParserErrorView,
      isMarketingUser,
    });
    if (!tabUsesClientPagination) return;

    const sourceTickets = statusFilterSourceTickets.length > 0 ? statusFilterSourceTickets : filteredTickets;
    if (sourceTickets.length > 0) {
      setFilteredStatusCounts(calculateFilteredCounts(sourceTickets, activeTab));
    } else {
      // Reset counts when no tickets
      const emptyCounts: Record<string, number> = {};
      switch (activeTab) {
        case 'Verification':
          emptyCounts['All Verification'] = 0;
          emptyCounts['ToBeVerified'] = 0;
          emptyCounts['Verified'] = 0;
          break;
        case 'Listed':
          emptyCounts['All Listed'] = 0;
          emptyCounts['List'] = 0;
          break;
        case 'Sold':
          emptyCounts['All Sold'] = 0;
          emptyCounts['Sold'] = 0;
          emptyCounts['Settled'] = 0;
          emptyCounts['SettlementInProgress'] = 0;
          emptyCounts['Failed'] = 0;
          break;
        case 'Delisted':
          emptyCounts['All Delisted'] = 0;
          emptyCounts['DelistInProgress'] = 0;
          emptyCounts['Delist'] = 0;
          emptyCounts['Return'] = 0;
          break;
        case 'Listing Request Cancelled':
          emptyCounts['All Listing Request Cancelled'] = 0;
          emptyCounts['ListingRequestCancelled'] = 0;
          break;
        case 'Listing Expired':
          emptyCounts['All Expired'] = 0;
          emptyCounts['Closed'] = 0;
          break;
        case 'Unsold':
          emptyCounts['All Unsold'] = 0;
          emptyCounts['Unsold'] = 0;
          break;
      }
      setFilteredStatusCounts(emptyCounts);
    }
  }, [filteredTickets, statusFilterSourceTickets, activeTab, usesLocalStatusFiltering, isMarketingUser, isMailParserErrorView]);

  // --- Fetch Deleted count on mount and whenever filters change ---
useEffect(() => {
  const timer = setTimeout(() => {
    void refreshTicketsTableRef.current?.();
    void fetchTrackingCounts();
    fetchDeletedCount();
  }, 300);
  return () => clearTimeout(timer);
}, [
  fetchDeletedCount,
  eventVenueSearch,
  clientSearch,
  startDate,
  endDate,
  selectedLeague,
  clientType,
]);

  // Tab badge counts for inactive tabs — run separately so row queries stay fast.
  useEffect(() => {
    if (isEventGroupedView || isMailParserTpLookupView) return;

    let isCancelled = false;
    const timer = setTimeout(async () => {
      try {
        const vars = getQueryVariables();
        const countVars: Record<string, unknown> = {
          search_event: vars.search_event ?? '%',
          search_client: vars.search_client ?? '%',
          pricingMode: vars.pricingMode ?? '%',
          startdate: vars.startdate,
          enddate: vars.enddate,
          startDateTime: vars.startDateTime,
          endDateTime: vars.endDateTime,
          leagueId: vars.leagueId,
          array_tpid: vars.array_tpid,
        };
        Object.keys(countVars).forEach((k) => {
          const v = countVars[k];
          if (v === null || v === undefined || (typeof v === 'string' && v.trim() === '')) {
            delete countVars[k];
          }
        });

        const { data: responseData } = await apolloClient.query({
          query: GET_SIBLING_TAB_COUNTS,
          variables: countVars,
          fetchPolicy: 'network-only',
        });
        if (isCancelled) return;

        const siblingCounts = extractSiblingTabCountsFromResponse(responseData);
        if (Object.keys(siblingCounts).length === 0) return;

        const active = effectiveTabRef.current;
        setTabCounts((prev) => {
          const next = { ...prev };
          for (const [siblingTab, count] of Object.entries(siblingCounts)) {
            if (siblingTab !== active) {
              next[siblingTab] = count;
            }
          }
          return next;
        });
      } catch (countError) {
        console.warn('[ManageTickets] Failed to load sibling tab counts', countError);
      }
    }, 150);

    return () => {
      isCancelled = true;
      clearTimeout(timer);
    };
  }, [
    tabCountsRefreshNonce,
    eventVenueSearch,
    clientSearch,
    startDate,
    endDate,
    selectedLeague,
    clientType,
    isEventGroupedView,
    isMailParserTpLookupView,
    apolloClient,
  ]);

useEffect(() => {
  if (!isEventGroupedView) return;
  
  const allTickets = [
    ...eventGroupedByStatus.pending,
    ...eventGroupedByStatus.listed,
    ...eventGroupedByStatus.sold,
    ...eventGroupedByStatus.delisted,
    ...eventGroupedByStatus.deleted,
  ];
  
  const selfPricingTickets = allTickets.filter(t => getResolvedClientType(t) === 'self-pricing');
  const conciergeTickets = allTickets.filter(t => getResolvedClientType(t) === 'concierge');
  
  setClientTypeCounts({
    All: allTickets.length,
    'Self Pricing': selfPricingTickets.length,
    'Concierge': conciergeTickets.length
  });
}, [isEventGroupedView, eventGroupedByStatus, usersListData]);
// useEffect(() => {
//   // Use filteredTickets for accurate count based on all active filters
//   const sourceTickets = usesLocalStatusFiltering ? statusFilterSourceTickets : filteredTickets;
//   const selfPricingTickets = sourceTickets.filter(t => {
//     const clientType = getResolvedClientType(t);
//     return clientType === 'self-pricing';
//   });
//   const conciergeTickets = sourceTickets.filter(t => {
//     const clientType = getResolvedClientType(t);
//     return clientType === 'concierge';
//   });
//   const allCount = sourceTickets.length;
//   const selfPricingCount = selfPricingTickets.length;
//   const conciergeCount = conciergeTickets.length;

//   setClientTypeCounts({
//     'All': allCount,
//     'Self Pricing': selfPricingCount,
//     'Concierge': conciergeCount
//   });
// }, [statusFilterSourceTickets, filteredTickets, usesLocalStatusFiltering, usersListData, resolvedClientTypeByUserId]);
  const handleOpenCardDetailModal = async (modalType: string) => {
    const getModalTrackingTickets = (responseData: any): Ticket[] => {
      if (modalType === 'LISTING_EXPIRED') {
        return mergeExpiredTicketsResponse(responseData);
      }
      if (Array.isArray(responseData?.ticket_placement)) return responseData.ticket_placement as Ticket[];
      if (Array.isArray(responseData?.filterreturntickets)) return responseData.filterreturntickets as Ticket[];
      if (Array.isArray(responseData?.filtermanagetickets)) return responseData.filtermanagetickets as Ticket[];
      return [];
    };

    const getModalTrackingCount = (responseData: any): number => {
      if (modalType === 'LISTING_EXPIRED') {
        return getExpiredTicketsCount(responseData);
      }
      return (
        responseData?.ticket_placement_aggregate?.aggregate?.count ||
        responseData?.filterreturntickets_aggregate?.aggregate?.count ||
        responseData?.filtermanagetickets_aggregate?.aggregate?.count ||
        0
      );
    };

    cardDetailRequestIdRef.current += 1;
    const requestId = cardDetailRequestIdRef.current;

    setShowCardDetailModal(modalType);
    setCardDetailTickets([]);
    setCardDetailLoading(true);

    try {
      let ticketsToShow: Ticket[] = [];

      if (modalType === "LISTING_EXPIRED") {
        const trackingVariables = {
          ...getManageTicketsTrackingVariables(
            eventVenueSearch ? `%${eventVenueSearch}%` : '%',
            getCustomerSearchPattern(clientSearch),
          ),
          ...getQueryVariables(),
        };
        if (startDate) {
          const range = getLocalDayRangeUTC(startDate);
          (trackingVariables as Record<string, unknown>).startdate = range?.startUTC ?? startDate;
        } else {
          (trackingVariables as Record<string, unknown>).startdate = '1970-01-01T00:00:00Z';
        }
        if (endDate) {
          const range = getLocalDayRangeUTC(endDate);
          (trackingVariables as Record<string, unknown>).enddate = range?.endUTC ?? endDate;
        } else {
          (trackingVariables as Record<string, unknown>).enddate = '9999-12-31T23:59:59Z';
        }
        const { data } = await refetchExpired(trackingVariables);
        ticketsToShow = sortManageTickets(normalizeCardDetailTrackingRows(getModalTrackingTickets(data)));
        setTrackingCounts(prev => ({
          ...prev,
          listingExpired: getModalTrackingCount(data)
        }));
      } else if (modalType === "FAILED_TRANSFER") {
        const cutoffDate = getFailedTransferCutoffDate();
        const trackingVariables = getManageTicketsTrackingVariables(
          eventVenueSearch ? `%${eventVenueSearch}%` : '%',
          getCustomerSearchPattern(clientSearch),
        );
        if (startDate) {
          const range = getLocalDayRangeUTC(startDate);
          (trackingVariables as Record<string, unknown>).startdate = range?.startUTC ?? startDate;
        } else {
          (trackingVariables as Record<string, unknown>).startdate = '1970-01-01T00:00:00Z';
        }
        if (endDate) {
          const range = getLocalDayRangeUTC(endDate);
          (trackingVariables as Record<string, unknown>).enddate = range?.endUTC ?? endDate;
        } else {
          (trackingVariables as Record<string, unknown>).enddate = '9999-12-31T23:59:59Z';
        }
        const { data } = await refetchFailed(trackingVariables);
        ticketsToShow = sortManageTickets(normalizeCardDetailTrackingRows(
          filterFailedTransferByCutoff(getModalTrackingTickets(data), cutoffDate)
        ));
        setTrackingCounts(prev => ({
          ...prev,
          failedTransfer: ticketsToShow.length
        }));
      }

      if (requestId !== cardDetailRequestIdRef.current) {
        return;
      }

      setCardDetailTickets(ticketsToShow);
      setCardDetailLoading(false);

      if (ticketsToShow.length === 0) {
        info(`No ${modalType === 'LISTING_EXPIRED' ? 'expired' : 'failed transfer'} tickets found`);
      }
    } catch {
      if (requestId !== cardDetailRequestIdRef.current) {
        return;
      }

      setCardDetailLoading(false);
      showError('Failed to load ticket details');
    }
  };

  // ==================== EXPORT HANDLERS ====================
const handleExport = () => {
  if (filteredTickets.length === 0) {
    info('No tickets to export');
    return;
  }

  const formattedTickets = filteredTickets.map((ticket: any) => ({
    ...ticket,
    'Event Time': formatEventTimeForCsv(ticket)
  }));

  exportToCSV(formattedTickets, {
    filename: `${activeTab.toLowerCase()}_tickets`,
    includeHeaders: true,
    includeSeatDetails: false,
    includeReturnEmail: shouldIncludeReturnEmailInExport(activeTab),
    includeCommission: shouldIncludeCommissionInExport(activeTab),
    dateFormat: exportOptions.dateFormat,
    delimiter: exportOptions.delimiter
  });

  setShowExportDropdown(false);
  success('Export started successfully');
};

  const handleAdvancedExport = () => {
    if (filteredTickets.length === 0) {
      info('No tickets to export');
      return;
    }
    setShowExportModal(true);
    setShowExportDropdown(false);
  };

  const handleExportSelected = () => {
    if (selectedTpIds.length === 0) {
      info('No tickets selected');
      return;
    }
    const selectedTickets = filteredTickets.filter(ticket => selectedTpIds.includes(ticket.tp_id));
    exportToCSV(selectedTickets, {
      filename: `selected_tickets_${selectedTpIds.length}`,
      includeHeaders: true,
      includeSeatDetails: exportOptions.includeSeatDetails,
      includeReturnEmail: shouldIncludeReturnEmailInExport(activeTab),
      includeCommission: shouldIncludeCommissionInExport(activeTab),
      dateFormat: exportOptions.dateFormat,
      delimiter: exportOptions.delimiter
    });
    setShowExportDropdown(false);
    success(`Exported ${selectedTpIds.length} tickets`);
  };

  const confirmExport = () => {
    exportToCSV(filteredTickets, {
      filename: `${activeTab.toLowerCase()}_tickets_advanced`,
      ...exportOptions,
      includeReturnEmail: shouldIncludeReturnEmailInExport(activeTab),
      includeCommission: shouldIncludeCommissionInExport(activeTab),
    });
    setShowExportModal(false);
    success(`Exported ${filteredTickets.length} tickets`);
  };

  // ==================== HANDLERS ====================

  const handleTabChange = (tab: string) => {
    const nextTab =
      isSuperUser && !SUPER_USER_MANAGE_TABS.includes(tab as (typeof SUPER_USER_MANAGE_TABS)[number])
        ? "Listed"
        : tab;
    // Re-clicking the active tab does not change the query, so clearing rows
    // here leaves the table empty. Filter resets apply only when the tab changes.
    if (nextTab === activeTab) return;
    setActiveTab(nextTab);
    setFilterStatus(getDefaultStatusByTab(nextTab));
    setStripeAccountFilter('all');
    setBarcodeFilter('all');
    setPageOffset(0);
    clearSelectionState();
    setExpandedRows([]);
    setCardFilter(null);
    // Clear previous tab rows immediately so empty tabs
    // never keep showing Verification/Listed data while the new query loads.
    setFilteredTickets([]);
    setTotalCount(0);
    setUnfilteredTabTickets([]);
  };

  function switchTabUnlessEventSearch(tab: string) {
    if (hasActiveEventSearch(eventVenueSearch)) return;
    handleTabChange(tab);
  }


  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      await fetchTrackingCounts();
      await refetch();
      await fetchDeletedCount();
      success('Data refreshed successfully');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newOffset: number) => {
    setPageOffset(Math.max(0, newOffset));
    clearSelectionState();
    setExpandedRows([]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    const fetchSizeChanged = getGroupedPageSeatFetchSize(pageSize) !== getGroupedPageSeatFetchSize(newPageSize);
    setPageSize(newPageSize);
    setPageOffset(0);
    // 10, 20, 50, and 100 all request the same 400-seat window. Clearing the
    // measured group count leaves every later page sliced from row 0.
    if (fetchSizeChanged) {
      setGroupsPerFullWindow(null);
      setLoadedSeatWindowIndex(0);
    }
    clearSelectionState();
    setExpandedRows([]);
  };

  const clearAllFilters = () => {
    setFilterStatus(getDefaultStatus());
    setBarcodeFilter('all');
    setPidStatusFilter('all');
    setVlShareFilter('all');
    setShowVlShareFilterDropdown(false);
    setVlSupportFilter('all');
    setVlSupportOwner('company');
    setShowVlSupportFilterDropdown(false);
    setShowVlSupportOwnerDropdown(false);
    setLastPricedFilter([]);
    setLastPricedFrom('');
    setLastPricedTo('');
    clearLastPricedCustomFilter();
    setOnHoldFilter(false);
    setClientType('All');
    setSelectedLeague('');
    setDebouncedEventVenueSearch("");
    setEventVenueSearch("");
    setDebouncedClientSearch("");
    setClientSearch("");
    clearDateFilter();
    setCardFilter(null);
    setPageOffset(0);
    success('Filters cleared');
  };

const handleLastPricedRangeChange = useCallback((field: 'from' | 'to', value: string) => {
  if (field === 'from') {
    setLastPricedFrom(value);
    // If new "from" is after existing "to", clear "to" so we don't send invalid range
    if (value && lastPricedTo && new Date(value).getTime() > new Date(lastPricedTo).getTime()) {
      setLastPricedTo('');
    }
  } else {
    setLastPricedTo(value);
    if (value && lastPricedFrom && new Date(value).getTime() < new Date(lastPricedFrom).getTime()) {
      setLastPricedFrom('');
    }
  }
  setPageOffset(0);
}, [lastPricedFrom, lastPricedTo]);

  // ==================== SEAT UTILITIES ====================

  const parseSeatNumbers = (seatNo: string | number | null | undefined): string[] =>
    parseAndSortSeatNumbers(seatNo);

const generateSeats = (ticket: Ticket): Seat[] => {
  const seatNumbers = parseSeatNumbers(ticket.tp_seat_no);
  const soldAmount = Number(ticket.tp_sold_amount ?? 0);
  const listAmount = Number(ticket.tp_list_price ?? 0);
  const seatPrice =
    normalizeStatusValue(ticket.tp_status || '') === 'Sold' && Number.isFinite(soldAmount) && soldAmount > 0
      ? soldAmount
      : Number.isFinite(listAmount)
        ? listAmount
        : 0;
  return seatNumbers.map((seatNum, index) => ({
    id: `${ticket.tp_id}-seat-${index}`,
    ticketId: ticket.tp_id,
    number: seatNum,
    seat_no: seatNum,
    section: ticket.tp_section || 'N/A',
    row: ticket.tp_row || 'N/A',
    status: ticket.tp_status || 'Unknown', 
    price: seatPrice,
    isDelisted: ['Delist', 'DelistInProgress', 'DelistRequested', 'Return'].includes(ticket.tp_status || ''),
    isMarked: false,
    tp_id: ticket.tp_id
  }));
};

  const getAllSeats = (): Seat[] => {
    if (!manageTicketTableRows) return [];
    const seats: Seat[] = [];
    manageTicketTableRows.forEach((row) => {
      if (row.kind === 'group') {
        if (expandedGroupKeys.includes(row.groupKey)) {
          row.tickets.forEach((ticket) => {
            seats.push(...generateSeats(ticket));
          });
        }
      } else if (row.kind === 'ticket' && expandedRows.includes(row.ticket.tp_id)) {
        seats.push(...generateSeats(row.ticket));
      }
    });
    return seats;
  };
// Helper to get status for a seat ID (works for collapsed groups too)
const getSeatStatus = (seatId: string): string => {
  const fromExpanded = getAllSeats().find((s) => s.id === seatId);
  if (fromExpanded?.status) return fromExpanded.status;

  const tpId = String(seatId).replace(/-seat-\d+$/, '');
  const ticket =
    getManageTicketDisplayTickets(manageTicketTableRows).find((t) => t.tp_id === tpId) ||
    filteredTickets.find((t) => t.tp_id === tpId);
  return ticket?.tp_status || '';
};

// Helper to check if a set of seat IDs has mixed statuses
const checkMixedStatuses = (seatIds: string[]): boolean => {
  if (seatIds.length <= 1) return false;
  const statuses = new Set<string>();
  for (const id of seatIds) {
    const status = getSeatStatus(id);
    if (status) statuses.add(status);
  }
  return statuses.size > 1;
};
  // ==================== SELECTION HANDLERS ====================

  const syncSeatSelectionState = (nextSelectedSeats: { [key: string]: boolean }) => {
    const allSeats = getAllSeats();
    const totalSeats = allSeats.length;
    const selectedSeatIds = Object.keys(nextSelectedSeats);
    const selectedCount = selectedSeatIds.length;
    setSelectedSeats(nextSelectedSeats);
    setSelectAllSeats(selectedCount === totalSeats && totalSeats > 0);
    const nextTpIds = resolveSelectedTicketIdsFromSeatSelection(
      selectedSeatIds,
      manageTicketTableRows,
      expandedGroupKeys,
      expandedRows,
    );
    setSelectedTpIds(nextTpIds);
    setBulkSelectedTicketIds(new Set(nextTpIds));
    const selectedTickets =
      getManageTicketDisplayTickets(manageTicketTableRows).filter((t) => nextTpIds.includes(t.tp_id));
    setBulkSelectedTickets(
      selectedTickets.length
        ? selectedTickets
        : filteredTickets.filter((t) => nextTpIds.includes(t.tp_id)),
    );
    setShowBulkActions(nextTpIds.length > 0);
  };

  const handleSelectAllSeats = () => {
    const newSelectAll = !selectAllSeats;
    const allSeats = getAllSeats();
    const newSelectedSeats: { [key: string]: boolean } = {};
    if (newSelectAll) {
      allSeats.forEach((seat) => {
        newSelectedSeats[seat.id] = true;
      });
          const allIds = allSeats.map(s => s.id);
    if (checkMixedStatuses(allIds)) {
      showError('You have selected seats with different statuses. Please select seats with the same status only.');
      return;
    }

    }
    
    syncSeatSelectionState(newSelectedSeats);
  };

  const handleSelectSeat = (seatId: string) => {
    setSelectedSeats((prev) => {
      const newSelected = { ...prev };
      if (newSelected[seatId]) {
        delete newSelected[seatId];
      } else {
        newSelected[seatId] = true;
      }
      const selectedIds = Object.keys(newSelected).filter(id => newSelected[id]);
    if (checkMixedStatuses(selectedIds)) {
      showError('You have selected seats with different statuses. Please select seats with the same status only.');
      return prev; // No change
    }
      syncSeatSelectionState(newSelected);
      return newSelected;
    });
  };

  const handleSelectSeatsByIds = (seatIds: string[], checked: boolean) => {
    setSelectedSeats((prev) => {
      const nextSelectedSeats = { ...prev };
      seatIds.forEach((seatId) => {
        if (checked) {
          nextSelectedSeats[seatId] = true;
        } else {
          delete nextSelectedSeats[seatId];
        }
      });
         const selectedIds = Object.keys(nextSelectedSeats).filter(id => nextSelectedSeats[id]);
    if (checkMixedStatuses(selectedIds)) {
      showError('You have selected seats with different statuses. Please select seats with the same status only.');
      return prev; // No change
    }
      syncSeatSelectionState(nextSelectedSeats);
      return nextSelectedSeats;
    });
  };

  const handlePriceUpdateSuccess = () => {
    handleBulkActionComplete();
    setShowPriceModal(false);
    void fetchTrackingCounts();
    // A last-priced filter is a snapshot of last_priced_at. Reload it after a
    // price save so a ticket that was just priced leaves that filter.
    refreshListedTicketsAfterPriceChange();
  };

  const getDefaultDelistEmail = (tpIds: string[]): string => {
    const firstMatch = filteredTickets.find((ticket) => tpIds.includes(ticket.tp_id));
    return (firstMatch?.u_email_id || firstMatch?.u_original_email || '').trim();
  };

  const openDelistEmailModal = (tpIds: string[]) => {
    setPendingDelistTpIds(tpIds);
    const defaultEmail = getDefaultDelistEmail(tpIds);
    resetDelistEmail({
      email: defaultEmail,
      confirmEmail: defaultEmail
    });
    setShowDelistEmailModal(true);
  };

  const onDelistEmailSubmit = async (data: { email: string; confirmEmail: string }) => {
    const requestedTpIds = [...pendingDelistTpIds];
    if (!requestedTpIds.length) return;

    const hadPreVerificationTickets = requestedTpIds.some((tpId) => {
      const ticket = filteredTickets.find((row) => row.tp_id === tpId);
      return normalizeStatusValue(ticket?.tp_status || '') === 'ToBeVerified';
    });
    const nextStatus = hadPreVerificationTickets ? 'ListingRequestCancelled' : 'DelistInProgress';

    setShowDelistEmailModal(false);
    beginStatusTableBusy('Submitting delist request…');
    try {
      await executeDelistWithFallback(requestedTpIds, data.email);
      updateLocalTicketStatus(requestedTpIds, nextStatus);
      if (hadPreVerificationTickets) {
        success('Listing request cancelled. Find the ticket under the Listing Request Cancelled tab.');
        setFilterStatus('All Listing Request Cancelled');
      } else {
        success('Delist request submitted. Find the ticket under the Delisted tab as Delist Requested.');
        setFilterStatus('DelistInProgress');
      }
      if (hadPreVerificationTickets) {
        if (activeTab !== 'Listing Request Cancelled') {
          switchTabUnlessEventSearch('Listing Request Cancelled');
        }
      } else if (activeTab !== 'Delisted') {
        switchTabUnlessEventSearch('Delisted');
      }
      await syncTicketsAfterStatusChange();
      await refetchExpired();
      await refetchFailed();
    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to request delist'));
      await syncTicketsAfterStatusChange();
    } finally {
      endStatusTableBusy();
      setPendingDelistTpIds([]);
      setDelistEmailMode('qa');
      resetDelistEmail();
    }
  };

  const closeDelistEmailModal = () => {
    setShowDelistEmailModal(false);
    setPendingDelistTpIds([]);
    setDelistEmailMode('qa');
    resetDelistEmail();
  };

  const isDelistWebhookUnavailable = (error: any): boolean => {
    const graphQlMsg = error?.graphQLErrors?.[0]?.message || '';
    const message = (error?.message || graphQlMsg || '').toLowerCase();
    return message.includes('not a valid json response from webhook') || message.includes('cannot post /api/delisttickets');
  };

  const setDelistWebhookBypass = (value: boolean) => {
    localStorage.setItem('delistWebhookUnavailable', value ? 'true' : 'false');
  };

  const executeDelistWithFallback = async (ticketPlacementIds: string[], requestedEmailId: string) => {
    // Always try primary delist request first; this is the path that creates notifications.
    try {
      await callDelistTicketsRest(ticketPlacementIds, requestedEmailId);
      setDelistWebhookBypass(false);
      return;
    } catch (error: any) {
      if (isDelistWebhookUnavailable(error)) {
        setDelistWebhookBypass(true);
      }
      throw error;
    }
  };

  const callRelistTicketBySuperUserRest = async (ticketPlacementIds: string | string[]) => {
    const ids = (Array.isArray(ticketPlacementIds) ? ticketPlacementIds : [ticketPlacementIds]).filter(
      Boolean,
    );
    if (!ids.length) {
      throw new Error('No tickets selected for relist');
    }
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;

    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    if (!nodeApiBaseUrl) throw new Error('Backend API URL not configured');

    const input =
      ids.length === 1
        ? { ticketPlacementId: ids[0] }
        : { ticketPlacementIds: ids };

    const response = await fetch(`${nodeApiBaseUrl}/api/relistTicketBySuperUser`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ input }),
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message =
        responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to relist ticket';
      throw new Error(message);
    }
    return responseData;
  };

  const assertRelistRestSuccess = (response: any) => {
    const message = String(response?.message || '').toLowerCase();
    if (message.includes('fail') || message.includes('error')) {
      throw new Error(response?.message || 'Failed to relist ticket');
    }
  };

  /** One Logitix BulkUpdate per selection — keeps VL groups intact on bulk undo. */
  const executeBatchedUndoDelistWithFallback = async (tpIds: string[]): Promise<void> => {
    if (!tpIds.length) return;
    const response = await callRelistTicketBySuperUserRest(tpIds);
    assertRelistRestSuccess(response);
  };

  const executeUndoDelistWithFallback = async (tpId: string, _currentStatus?: string): Promise<void> => {
    await executeBatchedUndoDelistWithFallback([tpId]);
  };
  type EditDetailsResult = {
    source: 'webhook';
    requiresConfirmation: boolean;
    message: string;
    successFlag: boolean;
  };

  const executeEditDetailsWithFallback = async (
    editTicketsInput: any
  ): Promise<EditDetailsResult> => {
    try {
      const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
      const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
      const hasuraRole = localStorage.getItem('x-hasura-user-role');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (idToken) headers.Authorization = `Bearer ${idToken}`;
      if (hasuraRole) headers['x-hasura-role'] = hasuraRole;

      const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
      if (!nodeApiBaseUrl) throw new Error('Backend API URL not configured');

      const response = await fetch(`${nodeApiBaseUrl}/api/editTickets`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ input: { editTicketsInput } }),
      });
      const editResponse = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          editResponse?.message ||
            editResponse?.error ||
            editResponse?.errors?.[0]?.message ||
            'Failed to update ticket details',
        );
      }

      const requiresConfirmation = Boolean(editResponse?.requiresConfirmation);

      if (requiresConfirmation) {
        return {
          source: 'webhook',
          requiresConfirmation: true,
          message: editResponse?.message || '',
          successFlag: false
        };
      }

      const message = editResponse?.message || '';
      const normalizedMessage = message.toLowerCase();
      const hasSuccessKeyword = normalizedMessage.includes('success');
      const hasErrorKeyword =
        normalizedMessage.includes('fail') ||
        normalizedMessage.includes('error') ||
        normalizedMessage.includes('already exist') ||
        normalizedMessage.includes('already exists') ||
        normalizedMessage.includes('invalid') ||
        normalizedMessage.includes('unauthorized') ||
        normalizedMessage.includes("couldn't update");
      const successFlag = !requiresConfirmation && hasSuccessKeyword && !hasErrorKeyword;
      return {
        source: 'webhook',
        requiresConfirmation: false,
        message,
        successFlag
      };
    } catch (error: any) {
      throw error;
    }
  };

  const handleBulkAction = async (action: string) => {
    if (selectedTpIds.length === 0) {
      info('No tickets selected');
      return;
    }

    // Add verification conditions for bulk verification
    if (action === 'Validate' || action === 'validate' || action === 'verify' || action === 'Verification') {
      // Only allow tickets that are ToBeVerified and have required fields.
      // Backend performs the authoritative Logitix/date validity checks.
      const invalidTickets = selectedTpIds
        .map(tpId => filteredTickets.find(t => t.tp_id === tpId))
        .filter(ticket => {
          if (!ticket) return true;
          // Only allow ToBeVerified
          if (ticket.tp_status !== 'ToBeVerified') return true;
          // Required fields
          if (
            !ticket.tp_section ||
            !ticket.tp_row ||
            ticket.tp_seat_no === null ||
            ticket.tp_seat_no === undefined ||
            String(ticket.tp_seat_no).trim() === '' ||
            ticket.tp_list_price === null ||
            ticket.tp_list_price === undefined
          ) return true;
          return false;
        });
      if (invalidTickets.length > 0) {
        showError('Some selected tickets cannot be verified. Only "To Be Verified" tickets with section, row, seat and price can be verified.');
        return;
      }
    }

    try {
      switch (action) {
     case 'markSold': {
  if (selectedTpIds.length === 0) {
    info('No tickets selected');
    return;
  }
  const validation = canBulkUpdateTickets(selectedTpIds);
  if (!validation.allowed) {
    showError(validation.reason || 'Cannot perform bulk Sold action on selected tickets');
    return;
  }
  const selectedTickets = filteredTickets.filter(t => selectedTpIds.includes(t.tp_id));
  if (selectedTickets.length === 1) {
    try {
      await openSoldModalForTicket(selectedTickets[0]);
    } catch (err) {
      console.error('Failed to open sold modal for single ticket', err);
      // still try to show modal with defaults
      setSoldModalTicket(selectedTickets[0]);
      setShowSoldModal(true);
    }
    return;  // important: stop here
  }
  setBulkSelectedTickets(selectedTickets);
  await openBulkSoldModal(selectedTickets);
  return;
}

        case 'delist': {
          const listedTpIds = selectedTpIds.filter((tpId) => {
            const row = filteredTickets.find((t) => t.tp_id === tpId);
            return normalizeStatusValue(row?.tp_status || '') === 'List';
          });
          if (listedTpIds.length === selectedTpIds.length) {
            requestListedTicketDelist(
              listedTpIds,
              filteredTickets.filter((ticket) => listedTpIds.includes(ticket.tp_id)),
            );
            return;
          }
          openDelistEmailModal(selectedTpIds);
          return;
        }
        case 'changePrice': {
          if (selectedTpIds.length > 1) {
            openBulkPriceModal(selectedTpIds);
            return;
          }
          const priceValidation = canBulkUpdateTickets(selectedTpIds);
          if (!priceValidation.allowed) {
            showError(priceValidation.reason || 'Cannot change price for selected tickets');
            return;
          }
          setSelectedMarketplaceId('');
          setShowPriceModal(true);
          if (selectedTpIds.length > 0) {
            const firstTicket = filteredTickets.find(t => t.tp_id === selectedTpIds[0]);
            if (firstTicket) {
              setSelectedTicketForPrice(firstTicket);
              setNewPrice(firstTicket.tp_list_price?.toString() || '0');
            }
          }
          return;
        }

        case 'Validate':
          {
            const selectedTickets = filteredTickets.filter((ticket) =>
              selectedTpIds.includes(ticket.tp_id),
            );
            // Status stays ToBeVerified until PID is saved.
            await openPidConfirmationForTickets(selectedTickets);
          }
          break;

        default:
          break;
      }

    } catch (error: any) {
      showError(getErrorMessage(error, `Failed to ${action}`));
      handleBulkActionComplete();
    }
  };

  const handleAction = async (
    action: string,
    ticket: Ticket,
    e: React.MouseEvent,
    options?: { tpIds?: string[] },
  ) => {
    e.stopPropagation();
    if (!beginManagedAction()) {
      info('Another action is already in progress.');
      return;
    }

    const statusActionsNeedingBusy = new Set(['Validate', 'Publish', 'Undo Delist', 'Return']);
    if (statusActionsNeedingBusy.has(action)) {
      beginStatusTableBusy(
        action === 'Validate'
          ? 'Verifying ticket…'
          : action === 'Publish'
            ? 'Publishing to Listed…'
            : action === 'Undo Delist'
              ? 'Moving ticket to Listed…'
              : 'Updating ticket status…',
      );
    }

    try {
      switch (action) {
        case 'Validate': {
          if (ticket.tp_status !== 'ToBeVerified') {
            showError('Only tickets in "To Be Verified" status can be verified.');
            return;
          }
          if (isMissingVerifyRequiredFields(ticket)) {
            showError(
              'Event name, event date/time, section, row, and seat numbers are required before verification.',
            );
            return;
          }
          // Status stays ToBeVerified until PID is saved.
          await openPidConfirmationForTickets([ticket]);
          break;
        }
        case 'Invalidate':
          await validateUpdate({
            variables: { ticketPlacementId: [ticket.tp_id], isValid: false, isUndoRequest: false }
          });
          break;
        case 'Delist':
          setOpenActionMenu(null);
          if (normalizeStatusValue(ticket.tp_status || '') === 'List') {
            requestListedTicketDelist([ticket.tp_id], [ticket]);
            return;
          }
          openDelistEmailModal([ticket.tp_id]);
          return;
        case 'Publish':
          if (!(Number(ticket.tp_list_price) > 0)) {
            showError('Please provide a valid price greater than 0 before listing.');
            return;
          }
          await callPublishTicketsRest([ticket.tp_id], Number(ticket.tp_list_price));
          updateLocalTicketStatus([ticket.tp_id], 'List', Number(ticket.tp_list_price));
          break;
        case 'Return': {
          const { message: returnMessage, type: returnType } = await executeReturnTicketsRest([ticket.tp_id]);
          updateLocalTicketStatus([ticket.tp_id], 'Return');
          if (returnType === 'success') success(returnMessage || 'Return request successful');
          else info(returnMessage || 'Return request processed');
          if (activeTab !== 'Delisted') {
            setTimeout(() => switchTabUnlessEventSearch('Delisted'), 300);
          }
          break;
        }
        case 'Undo Delist':
          await executeUndoDelistWithFallback(ticket.tp_id, ticket.tp_status);
          updateLocalTicketStatus([ticket.tp_id], 'List');
          if (activeTab !== 'Listed') {
            switchTabUnlessEventSearch('Listed');
          }
          break;
        case 'Change Price': {
          // Prefer full listing group when opened from a grouped row (all seats), not just the representative.
          const priceTpIds = [
            ...new Set(
              (options?.tpIds?.length ? options.tpIds : [ticket.tp_id])
                .map((id) => String(id || '').trim())
                .filter(Boolean),
            ),
          ];
          if (priceTpIds.length > 1) {
            openBulkPriceModal(priceTpIds);
            break;
          }
          setSelectedTicketForPrice(ticket);
          setSelectedTpIds(priceTpIds.length ? priceTpIds : [ticket.tp_id]);
          setNewPrice(ticket.tp_list_price?.toString() || '0');
          setSelectedMarketplaceId('');
          setShowPriceModal(true);
          break;
        }
  case 'Edit Details': {
  const editTpIds = [
    ...new Set(
      (options?.tpIds?.length ? options.tpIds : [ticket.tp_id])
        .map((id) => String(id || '').trim())
        .filter(Boolean),
    ),
  ];
  const resolvedEditIds = editTpIds.length ? editTpIds : [ticket.tp_id];
  const groupTickets = resolvedEditIds
    .map(
      (id) =>
        filteredTickets.find((row) => row.tp_id === id) ||
        statusFilterSourceTickets.find((row) => row.tp_id === id) ||
        (ticket.tp_id === id ? ticket : null),
    )
    .filter((row): row is Ticket => Boolean(row))
    .sort((a, b) => compareSeatNumbers(a.tp_seat_no, b.tp_seat_no));
  const ticketsForEdit = groupTickets.length ? groupTickets : [ticket];
  const noteSource = ticketsForEdit[0] || ticket;
  editDetailsNotesEditedRef.current = false;

  const parkingNotes = isParkingPassRow(noteSource.tp_row);
  const initialInternal = parkingNotes
    ? PARKING_INTERNAL_NOTE
    : String(noteSource.tp_internal_notes || noteSource.internalNotes || '').trim();
  const initialExternal = parkingNotes
    ? PARKING_EXTERNAL_NOTE
    : String(noteSource.tp_external_notes || noteSource.externalNotes || '').trim();
  editDetailsNotesBaselineRef.current = {
    internalNotes: initialInternal,
    externalNotes: initialExternal,
  };

  setSelectedTicketForEdit({
    ...noteSource,
    internalNotes: initialInternal,
    externalNotes: initialExternal,
  });
  setEditDetailsTpIds(ticketsForEdit.map((row) => row.tp_id));
  setEditDetailsTickets(
    ticketsForEdit.map((row) => ({
      ...row,
      internalNotes: initialInternal,
      externalNotes: initialExternal,
    })),
  );

const groupSeatNumbers = ticketsForEdit
  .map((row) => String(row.tp_seat_no ?? '').trim())
  .filter((v) => v !== '' && v !== 'N/A');

const combinedSeatDisplay =
  groupSeatNumbers.length > 1
    ? [...new Set(groupSeatNumbers)].join(', ')
    : groupSeatNumbers[0] || '';

// Expand ranges (e.g. "802-803") so start/end prefill correctly even for a single ticket
const allSeatNumbers: number[] = [];
ticketsForEdit.forEach((row) => {
  parseSeatNumbers(row.tp_seat_no).forEach((seatStr) => {
    const n = Number(seatStr);
    if (Number.isFinite(n)) allSeatNumbers.push(n);
  });
});
const startSeat = allSeatNumbers.length > 0 ? Math.min(...allSeatNumbers) : '';
const endSeat = allSeatNumbers.length > 0 ? Math.max(...allSeatNumbers) : '';

// Group edit = more than one ticket in the selected group
const isGroupEdit = ticketsForEdit.length > 1;

setEditFormData({
  section: noteSource.tp_section || '',
  row: noteSource.tp_row || '',
  setDetails: combinedSeatDisplay,
  seatStart: String(startSeat),
  seatEnd: String(endSeat),
  isGroupEdit,
  internalNotes: initialInternal,
  externalNotes: initialExternal,
});
  setShowEditModal(true);
  void loadEditDetailNoteChoices(ticketsForEdit);
  break;
}
        case 'Pricing Method Status': {
          const pricingTpIds = [
            ...new Set(
              (options?.tpIds?.length ? options.tpIds : [ticket.tp_id])
                .map((id) => String(id || '').trim())
                .filter(Boolean),
            ),
          ];
          const resolvedIds = pricingTpIds.length ? pricingTpIds : [ticket.tp_id];
          const ticketsForPricing = resolvedIds
            .map(
              (id) =>
                filteredTickets.find((row) => row.tp_id === id) ||
                statusFilterSourceTickets.find((row) => row.tp_id === id) ||
                (ticket.tp_id === id ? ticket : null),
            )
            .filter((row): row is Ticket => Boolean(row));
          const isSelfPricingOwner = ticketsForPricing.every(
            (row) => getResolvedClientType(row) === 'self-pricing',
          );
          const methods = [
            ...new Set(
              ticketsForPricing
                .map((row) => normalizePricingMethod(row.tp_pricing_method))
                .filter((method): method is 'MANUAL' | 'FOLLOW_MARKET' => Boolean(method)),
            ),
          ];
          setSelectedTicketForPricingMethod(ticketsForPricing[0] || ticket);
          setPricingMethodTpIds(resolvedIds);
          setPricingMethodFormData(
            isSelfPricingOwner
              ? 'NONE'
              : methods.length === 1
                ? methods[0]
                : resolvePricingMethodSelection(ticket.tp_pricing_method),
          );
          setShowPricingMethodModal(true);
          break;
        }
        case 'Change Status': {
          const presMenu = getManageTicketsStatusPresentation(ticket);
          const pk = (presMenu.iconStatus || '').toLowerCase();
          if (pk === 'settled' || pk === 'settlementinprogress') {
            info(
              pk === 'settled'
                ? 'This ticket is settled. Its status cannot be changed here.'
                : soldPaymentStartedMessage,
            );
            break;
          }
          if (normalizeStatusValue(ticket.tp_status || '') === 'Unsold' || normalizeStatusValue(ticket.tp_status || '') === 'UnderReview') {
    setOpenActionMenu(null);
    await openSoldModalForTicket(ticket);
    break;
  }
          setSelectedTicketForStatus(ticket);
          setNewStatus(normalizeStatusValue(ticket.tp_status || 'ToBeVerified') || 'ToBeVerified');
          setShowStatusModal(true);
          break;
        }
        case 'View Details':
          setSelectedTicketForView(ticket);
          setShowViewModal(true);
          break;
        case 'Price Change Logs': {
          const ids = (options?.tpIds?.length ? options.tpIds : [ticket.tp_id])
            .map((id) => String(id || '').trim())
            .filter(Boolean);
          setSelectedTicketForPriceLogs(ticket);
          setPriceLogsTpIds(ids.length ? [...new Set(ids)] : [ticket.tp_id]);
          setShowPriceLogsModal(true);
          break;
        }
        case 'View Client Profile':
          navigate(`/user/${ticket.u_id}?tab=tickets`);
          break;
        case 'View Invoice':
          navigate(`/invoice/${ticket.tp_id}`);
          break;
        default:
          break;
      }

    } catch (error: any) {
      showError(getErrorMessage(error, `Failed to ${action}`));
    } finally {
      if (statusActionsNeedingBusy.has(action)) {
        await syncTicketsAfterStatusChange();
        endStatusTableBusy();
      }
      endManagedAction();
    }

    setOpenActionMenu(null);
  };

  const handlePricingMethodStatusConfirm = async () => {
    if (!selectedTicketForPricingMethod || pricingMethodLoading) return;
    if (pricingMethodFormData !== 'MANUAL' && pricingMethodFormData !== 'FOLLOW_MARKET' && pricingMethodFormData !== 'NONE') {
      showError('Please select a valid pricing method.');
      return;
    }

    const pricingOwnerIsSelfPricing =
      getResolvedClientType(selectedTicketForPricingMethod) === 'self-pricing';
    if (
      pricingOwnerIsSelfPricing &&
      (pricingMethodFormData === 'MANUAL' || pricingMethodFormData === 'FOLLOW_MARKET')
    ) {
      showError('Self Pricing tickets cannot use Manual or Follow the Market. Select Nothing.');
      return;
    }

    try {
      setPricingMethodLoading(true);

      const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
      const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
      const hasuraRole = localStorage.getItem('x-hasura-user-role');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (idToken) headers.Authorization = `Bearer ${idToken}`;
      if (hasuraRole) headers['x-hasura-role'] = hasuraRole;

      const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
      if (!nodeApiBaseUrl) throw new Error('Backend API URL not configured');

      const methodToSave = pricingOwnerIsSelfPricing ? 'NONE' : pricingMethodFormData;

      const response = await fetch(`${nodeApiBaseUrl}/api/tickets/pricing-method`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          tpIds: pricingMethodTpIds.length ? pricingMethodTpIds : [selectedTicketForPricingMethod.tp_id],
          pricingMethod: methodToSave,
          tp_id: selectedTicketForPricingMethod.tp_id,
        }),
      });

      const parsed = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(parsed?.message || parsed?.error || parsed?.errors?.[0]?.message || 'Failed to update pricing method');
      }

      updateLocalTicketDetails(pricingMethodTpIds.length ? pricingMethodTpIds : [selectedTicketForPricingMethod.tp_id], {
        tp_pricing_method:
          methodToSave === 'MANUAL' || methodToSave === 'FOLLOW_MARKET' ? methodToSave : null,
      });
      success(parsed?.message || 'Pricing method updated successfully.');
      closePricingMethodModal();
    } catch (error: any) {
      console.error('Pricing method update error:', error);
      showError(getErrorMessage(error, 'Failed to update pricing method'));
    } finally {
      setPricingMethodLoading(false);
    }
  };

  const handleEditDetailsConfirm = async () => {
    if (!selectedTicketForEdit || editDetailsLoading) return;

    const ticketsForEdit =
      editDetailsTickets.length > 0
        ? editDetailsTickets
        : [selectedTicketForEdit];
    const groupTpIds = ticketsForEdit.map((row) => row.tp_id).filter(Boolean);

    if (
      !editFormData.section.trim() &&
      !editFormData.row.trim() &&
        !editFormData.setDetails.trim() &&  
      !editFormData.internalNotes.trim() &&
      !editFormData.externalNotes.trim() &&
      editFormData.internalNotes.trim() === editDetailsNotesBaselineRef.current.internalNotes &&
      editFormData.externalNotes.trim() === editDetailsNotesBaselineRef.current.externalNotes
    ) {
      info('Please fill at least one field to update');
      return;
    }

    try {
      const parseSeatInt = (value: string | number | null | undefined): number | null => {
        if (value === null || value === undefined) return null;
        const raw = String(value).trim();
        if (!raw) return null;
        if (raw.includes('-')) {
          const first = Number.parseInt(raw.split('-')[0], 10);
          return Number.isFinite(first) ? first : null;
        }
        if (raw.includes(',')) {
          const first = Number.parseInt(raw.split(',')[0].trim(), 10);
          return Number.isFinite(first) ? first : null;
        }
        const n = Number.parseInt(raw, 10);
        return Number.isFinite(n) ? n : null;
      };

  const preparedSection = editFormData.section.trim() || selectedTicketForEdit.tp_section || '';
const preparedRow = editFormData.row.trim() || selectedTicketForEdit.tp_row || '';
const preparedSetDetails = editFormData.setDetails.trim() || (selectedTicketForEdit as any).tp_set_details || '';
const currentSection = (selectedTicketForEdit.tp_section || '').trim().toLowerCase();
const currentRow = (selectedTicketForEdit.tp_row || '').trim().toLowerCase();
const sectionOrRowChanged =
  preparedSection.trim().toLowerCase() !== currentSection ||
  preparedRow.trim().toLowerCase() !== currentRow;

// Seat number field is only editable for Verified / ToBeVerified tickets
const seatNumberFieldVisible = ['Verified', 'ToBeVerified'].includes(
  selectedTicketForEdit.tp_status || '',
);

const currentGroupSeatNumber = ticketsForEdit
  .map((row) => String(row.tp_seat_no ?? '').trim())
  .filter(Boolean)
  .join(', ');

let preparedSeatNumber = '';
if (seatNumberFieldVisible) {
  // Always use Start / End seat inputs (both single & group edits)
  const startSeatVal = editFormData.seatStart.trim();
  const endSeatVal = editFormData.seatEnd.trim();
  if (startSeatVal && endSeatVal) {
    preparedSeatNumber =
      startSeatVal === endSeatVal ? startSeatVal : `${startSeatVal}-${endSeatVal}`;
  } else {
    preparedSeatNumber = currentGroupSeatNumber;
  }
} else {
  preparedSeatNumber = String(selectedTicketForEdit.tp_seat_no ?? '');
}

const normalizeSeatNumberInput = (value: string) =>
  value.trim().replace(/\s*,\s*/g, ',');
const currentSeatNumber = currentGroupSeatNumber;
const parseSeatNumberList = (value: string): number[] | null => {
  const seats: number[] = [];
  for (const part of value.split(',')) {
    const range = part.trim().match(/^(\d+)\s*-\s*(\d+)$/);
    if (range) {
      const start = Number(range[1]);
      const end = Number(range[2]);
      if (end < start || end - start + 1 > ticketsForEdit.length) return null;
      for (let seat = start; seat <= end; seat += 1) seats.push(seat);
      continue;
    }
    if (!/^\d+$/.test(part.trim())) return null;
    seats.push(Number(part.trim()));
    if (seats.length > ticketsForEdit.length) return null;
  }
  return seats.length === ticketsForEdit.length ? seats : null;
};
const seatNumberChanged =
  seatNumberFieldVisible &&
  normalizeSeatNumberInput(preparedSeatNumber) !== normalizeSeatNumberInput(currentSeatNumber);

// Parse first seat as integer for the backend's int field (existing contract)
const changedGroupSeatNumbers = seatNumberChanged && ticketsForEdit.length > 1
  ? parseSeatNumberList(preparedSeatNumber)
  : null;
if (seatNumberChanged && ticketsForEdit.length > 1 && !changedGroupSeatNumbers) {
  info(`Enter exactly ${ticketsForEdit.length} seat numbers, separated by commas.`);
  return;
}
const primarySeat = changedGroupSeatNumbers?.[0] ?? parseSeatInt(preparedSeatNumber);
if (primarySeat == null) {
  info('Please enter a valid seat number.');
  return;
}
const groupSeatUpdates = seatNumberChanged
  ? ticketsForEdit.map((row, index) => ({
tp_id: row.tp_id,
tp_seat_no: changedGroupSeatNumbers?.[index] ?? primarySeat,
    }))
  : [];

      const currentInternalNotes = getTicketCsvNotes(selectedTicketForEdit, 'internal');
      const currentExternalNotes = getTicketCsvNotes(selectedTicketForEdit, 'external');
      const internalNotes = editFormData.internalNotes.trim();
      const externalNotes = editFormData.externalNotes.trim();
      const notesChanged =
        internalNotes !== editDetailsNotesBaselineRef.current.internalNotes ||
        externalNotes !== editDetailsNotesBaselineRef.current.externalNotes;

  if (!sectionOrRowChanged && !seatNumberChanged && !notesChanged) {
  info('No changes detected.');
  return;
}

      if (sectionOrRowChanged || seatNumberChanged) {
        const inactiveStatuses = new Set(['Delist', 'Return', 'Unsold']);
        const groupTpIdSet = new Set(groupTpIds);
        for (const [index, row] of ticketsForEdit.entries()) {
          const seatNo = changedGroupSeatNumbers?.[index] ?? parseSeatInt(row.tp_seat_no);
          if (seatNo == null) continue;
          const conflictingTicket = filteredTickets.find(
            (ticket) =>
              !groupTpIdSet.has(ticket.tp_id) &&
              ticket.e_id === selectedTicketForEdit.e_id &&
              (ticket.tp_section || '').trim().toLowerCase() === preparedSection.toLowerCase() &&
              (ticket.tp_row || '').trim().toLowerCase() === preparedRow.toLowerCase() &&
              Number(ticket.tp_seat_no) === seatNo &&
              !inactiveStatuses.has(ticket.tp_status || ''),
          );
          if (conflictingTicket) {
            showError(
              `Another ticket already exists with section ${preparedSection}, row ${preparedRow}, seat ${seatNo}.`,
            );
            return;
          }
        }
      }

      setEditDetailsLoading(true);

      const storedMarketplaces = Array.isArray(selectedTicketForEdit.tp_vl_marketplaces);
      const enabledMarketplaceIds = getVlMarketplaceIdsForTicket(
        selectedTicketForEdit.tp_id,
        selectedTicketForEdit,
      );

    const editTicketsInput = {
  e_id: selectedTicketForEdit.e_id,
  tp_id: selectedTicketForEdit.tp_id,
  tp_section: preparedSection,
  tp_row: preparedRow,
  tp_seat_no: primarySeat,
  ...(groupSeatUpdates.length ? { group_seat_updates: groupSeatUpdates } : {}),
  ...(preparedSetDetails ? { tp_set_details: preparedSetDetails } : {}),
  ...(groupTpIds.length > 1 ? { group_tp_ids: groupTpIds } : {}),
  ...(notesChanged
    ? {
        internalNotes,
        externalNotes,
        ...(storedMarketplaces || enabledMarketplaceIds.length
          ? { enabledMarketplaceIds }
          : {}),
      }
    : {}),
};
      let editResult = await executeEditDetailsWithFallback(editTicketsInput);
      if (editResult?.requiresConfirmation) {
        const shouldProceed = window.confirm(
          editResult?.message ||
            'Matching ticket details exist in delisted/returned tickets. Do you want to proceed?',
        );
        if (!shouldProceed) {
          return;
        }
        editResult = await executeEditDetailsWithFallback({
          ...editTicketsInput,
          forceUpdate: true,
        });
      }

      const finalMessage = (editResult?.message || '').toLowerCase();
      const hasErrorMessage =
        finalMessage.includes('fail') || finalMessage.includes('error');
      if (editResult?.successFlag !== true || hasErrorMessage) {
        showError(editResult?.message || 'Failed to update ticket details');
        return;
      }

    updateLocalTicketDetails(groupTpIds, {
  tp_section: preparedSection,
  tp_row: preparedRow,
  ...(preparedSetDetails ? { tp_set_details: preparedSetDetails } : {}),
  internalNotes: editFormData.internalNotes.trim(),
  externalNotes: editFormData.externalNotes.trim(),
  tp_internal_notes: editFormData.internalNotes.trim(),
  tp_external_notes: editFormData.externalNotes.trim(),
});
      for (const seatUpdate of groupSeatUpdates) {
        updateLocalTicketDetails([seatUpdate.tp_id], {
          tp_seat_no: seatUpdate.tp_seat_no,
        });
      }

      const editSeatBounds = ticketsForEdit
        .map((row) => getSeatBounds(row.tp_seat_no))
        .filter((bounds): bounds is { start: number; end: number } => Boolean(bounds));
      if (editSeatBounds.length && notesChanged) {
        requestSeatNotesSave([{
          userId: String(selectedTicketForEdit.u_id || '').trim(),
          teamId: String(selectedTicketForEdit.lc_id || '').trim(),
          section: preparedSection.trim(),
          row: preparedRow.trim(),
          seatStart: Math.min(...editSeatBounds.map((bounds) => bounds.start)),
          seatEnd: Math.max(...editSeatBounds.map((bounds) => bounds.end)),
          internalNote: internalNotes,
          externalNote: externalNotes,
        }]);
      }

      const groupCount = Math.max(1, groupTpIds.length);
      setEditDetailsLoading(false);
      closeEditModal();
      success(
        groupCount > 1
          ? `Ticket details updated on Victory Live for ${groupCount} seats.`
          : 'Ticket details updated on Victory Live successfully!',
      );
    } catch (error: any) {
      console.error('Edit details error:', error);
      showError(getErrorMessage(error, 'Failed to update ticket details'));
    } finally {
      setEditDetailsLoading(false);
    }
  };

  const executePriceChangeConfirm = async (confirmed = false) => {
    if (priceLoading) return;
    if (!selectedTpIds.length || !newPrice) return;

    const ticketPrice = parseFloat(newPrice);
    if (!Number.isFinite(ticketPrice) || ticketPrice <= 0) {
      info('Please enter a price greater than zero');
      return;
    }

    // If a marketplace is selected, apply its fee and save the settled (user receives) amount
    const selectedMp = marketplacesList.find(m => m.id === selectedMarketplaceId);
    const finalPrice = selectedMp
      ? Math.max(0, ticketPrice - (ticketPrice * selectedMp.vl_fee_percentage) / 100)
      : ticketPrice;

    if (requiresPriceChangeConfirmation(selectedTicketForPrice?.tp_list_price, finalPrice) && !confirmed) {
      setPriceConfirmationFlow('single');
      setShowPriceModal(false);
      return;
    }

    pendingFinalPriceRef.current = finalPrice;
    pendingPriceTpIdsRef.current = [...selectedTpIds];
    markPriceUpdatePending(selectedTpIds, true);

    try {
      await editTicketPrice({
        variables: {
          tp_ids: selectedTpIds,
          tp_list_price: finalPrice
        }
      });
    } catch {
      // Toast and price stay on the previous value unless Victory Live confirmed success.
    } finally {
      markPriceUpdatePending(selectedTpIds, false);
    }
  };

  const handlePriceChangeConfirm = async () => {
    if (priceLoading) return;
    if (!selectedTpIds.length || !newPrice) return;

    const ticketPrice = parseFloat(newPrice);
    if (!Number.isFinite(ticketPrice) || ticketPrice <= 0) {
      info('Please enter a price greater than zero');
      return;
    }

    const priceModalTickets = selectedTpIds
      .map(
        (id) =>
          filteredTickets.find((t) => t.tp_id === id) ??
          statusFilterSourceTickets.find((t) => t.tp_id === id) ??
          (selectedTicketForPrice?.tp_id === id ? selectedTicketForPrice : null),
      )
      .filter((t): t is Ticket => Boolean(t));
    const selfPricingTickets = priceModalTickets.filter(
      (ticket) => getResolvedClientType(ticket) === 'self-pricing',
    );

    if (selfPricingTickets.length > 0) {
      const representative = selfPricingTickets[0];
      const currentPrice = Number(representative.tp_list_price ?? 0);
      selfPricingPriceConfirmActionRef.current = executePriceChangeConfirm;
      setSelfPricingPriceConfirmCount(selfPricingTickets.length);
      setSelfPricingPriceConfirmSummary(
        selfPricingTickets.length === 1
          ? `Change list price from $${Number.isFinite(currentPrice) ? currentPrice.toFixed(2) : '0.00'} to $${ticketPrice.toFixed(2)} for this self-pricing client?`
          : `Do you want to change the price for ${selfPricingTickets.length} self-pricing ticket(s) to $${ticketPrice.toFixed(2)}?`,
      );
      setShowSelfPricingPriceConfirm(true);
      return;
    }

    await executePriceChangeConfirm();
  };

  const confirmSelfPricingPriceChange = async () => {
    const action = selfPricingPriceConfirmActionRef.current;
    if (!action) return;
    try {
      await action();
    } finally {
      selfPricingPriceConfirmActionRef.current = null;
      setShowSelfPricingPriceConfirm(false);
      setSelfPricingPriceConfirmSummary('');
    }
  };

  const cancelSelfPricingPriceConfirm = () => {
    if (priceLoading || bulkPriceLoading) return;
    selfPricingPriceConfirmActionRef.current = null;
    setShowSelfPricingPriceConfirm(false);
    setSelfPricingPriceConfirmSummary('');
  };

  const cancelNonVlStatusConfirm = () => {
    if (statusChangeLoading || bulkStatusChangeLoading) return;
    nonVlStatusConfirmActionRef.current = null;
    setShowNonVlStatusConfirm(false);
    setNonVlStatusConfirmSummary('');
  };

  const confirmNonVlStatusChange = async () => {
    const action = nonVlStatusConfirmActionRef.current;
    if (!action) return;
    try {
      await action();
    } finally {
      nonVlStatusConfirmActionRef.current = null;
      setShowNonVlStatusConfirm(false);
      setNonVlStatusConfirmSummary('');
    }
  };
const handleStatusChangeConfirm = async (ticket: Ticket, status: string) => {
  if (!ticket || !status) return;

  const sanitizedStatus = normalizeStatusValue(status);
  if (!sanitizedStatus) {
    showError('That status could not be applied. Reload the page and try again, or contact support if it keeps happening.');
    return;
  }

  if (isNonVlStatusChange(sanitizedStatus) && !skipNonVlStatusConfirmRef.current) {
    nonVlStatusConfirmActionRef.current = async () => {
      skipNonVlStatusConfirmRef.current = true;
      try {
        await handleStatusChangeConfirm(ticket, status);
      } finally {
        skipNonVlStatusConfirmRef.current = false;
      }
    };
    setNonVlStatusConfirmSummary(buildNonVlStatusConfirmSummary(sanitizedStatus, 1));
    setShowNonVlStatusConfirm(true);
    return;
  }

  const currentStatus = normalizeStatusValue(ticket.tp_status || '');

  // Listed (Non-VL) price popup must open even if another status op is busy.
  if (
    (currentStatus === 'Verified' ||
      currentStatus === 'Delist' ||
      currentStatus === 'DelistInProgress' ||
      currentStatus === 'DelistRequested') &&
    sanitizedStatus === 'List_NonVL'
  ) {
    setShowStatusModal(false);
    setOpenStatusDropdown(null);
    setSeatCardStatusDropdownOpen({});
    const existing = Number(ticket.tp_list_price);
    setListedModalTicket(ticket);
    setListedPrice(
      Number.isFinite(existing) && existing > 0 ? String(roundToFixed(existing, 2)) : '',
    );
    setShowListedModal(true);
    return;
  }

  if (statusChangeLoading || statusTableBusyCount > 0 || actionInFlightRef.current) return;

  const commitDirectStatusUpdate = async (
    nextStatus: string,
    successMessage: string,
    targetTab?: string,
  ) => {
    beginStatusTableBusy('Updating ticket status…');
    setStatusChangeLoading(true);
    try {
      const result = await updateTicketStatusSilent({
        variables: { tp_id: ticket.tp_id, status: nextStatus }
      });
      const affected = result?.data?.update_ticket_placement?.returning?.length || 0;
      if (!affected) {
        throw new Error('No ticket was updated.');
      }
      updateLocalTicketStatus([ticket.tp_id], nextStatus);
      success(successMessage);
      setShowStatusModal(false);
      setOpenStatusDropdown(null);
      if (targetTab && targetTab !== activeTab) {
        switchTabUnlessEventSearch(targetTab);
      }
      await syncTicketsAfterStatusChange();
      syncDashboardNotifications();
    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to update ticket status'));
    } finally {
      setStatusChangeLoading(false);
      endStatusTableBusy();
    }
  };

  // Handle Deleted status - redirect to delete confirmation
  if (sanitizedStatus === 'Deleted') {
    setShowStatusModal(false);
    setSelectedTicketForDelete(ticket);
    setShowDeleteConfirmModal(true);
    return;
  }

  if (currentStatus === 'Deleted' && sanitizedStatus === 'ToBeVerified') {
    beginStatusTableBusy('Restoring ticket to To Be Verified…');
    setStatusChangeLoading(true);
    try {
      const result = await restoreDeletedToVerification({
        variables: { tp_id: ticket.tp_id, updated_at: new Date().toISOString() },
      });
      const updated = result?.data?.update_ticket_placement?.returning?.length || 0;
      if (updated <= 0) {
        throw new Error('Unable to restore ticket to To Be Verified');
      }
      success('Ticket moved to To Be Verified');
      updateLocalTicketStatus([ticket.tp_id], 'ToBeVerified', undefined, 'Verification', {
        tp_is_published: false,
        tp_validity_status: null,
        tp_deleted_at: null,
      });
      setShowStatusModal(false);
      if (activeTab !== 'Verification') {
        switchTabUnlessEventSearch('Verification');
      }
      setFilterStatus('ToBeVerified');
      await syncTicketsAfterStatusChange();
    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to move ticket to To Be Verified'));
      setShowStatusModal(false);
      await syncTicketsAfterStatusChange();
    } finally {
      setStatusChangeLoading(false);
      endStatusTableBusy();
    }
    return;
  }

  if (currentStatus === 'Deleted' && sanitizedStatus === 'List') {
    setShowStatusModal(false);
    setOpenStatusDropdown(null);
    setSeatCardStatusDropdownOpen({});
    const existing = Number(ticket.tp_list_price);
    setListedModalTicket(ticket);
    setListedPrice(
      Number.isFinite(existing) && existing > 0 ? String(roundToFixed(existing, 2)) : '',
    );
    setShowListedModal(true);
    return;
  }

  // Check if this is a Verified -> Listed transition
  if (
    currentStatus === 'Sold' &&
    (sanitizedStatus === 'Sold' || sanitizedStatus === 'Settled' || sanitizedStatus === 'SettlementInProgress')
  ) {
    if (!canOverrideSettlementStatus) {
      info(soldPaymentStartedMessage);
      setShowStatusModal(false);
      setOpenStatusDropdown(null);
      return;
    }
    const paymentStatus = getPaymentStatusForSettlementOverride(sanitizedStatus);
    if (paymentStatus === undefined) {
      showError('That settlement status could not be applied.');
      return;
    }
    beginStatusTableBusy('Updating settlement status…');
    setStatusChangeLoading(true);
    try {
      const result = await callUpdateSettlementPaymentStatusRest([ticket.tp_id], paymentStatus);
      const updatedIds: string[] = Array.isArray(result?.updatedTpIds) ? result.updatedTpIds : [ticket.tp_id];
      if (!updatedIds.length) {
        showError('No sold ticket was updated.');
        return;
      }
      updateLocalTicketPaymentStatus(updatedIds, paymentStatus);
      success(result?.message || 'Settlement status updated successfully.');
      setShowStatusModal(false);
      setOpenStatusDropdown(null);
      await syncTicketsAfterStatusChange();
      syncDashboardNotifications();
    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to update settlement status'));
      setShowStatusModal(false);
      await syncTicketsAfterStatusChange();
    } finally {
      setStatusChangeLoading(false);
      endStatusTableBusy();
    }
    return;
  }

  // Verify: keep ToBeVerified until Production ID is confirmed and saved; then status becomes Verified.
  if (currentStatus === 'ToBeVerified') {
    // Non-VL: QA-only verify — never opens PID / VL flow
    if (sanitizedStatus === 'Verified_NonVL') {
      if (isMissingVerifyRequiredFields(ticket)) {
        showError(
          'Event name, event date/time, section, row, and seat numbers are required before verification.',
        );
        return;
      }
      beginStatusTableBusy('Verifying ticket (Non-VL)…');
      setStatusChangeLoading(true);
      try {
        const result = await verifyTicketPlacements([ticket.tp_id]);
        updateLocalTicketStatus([ticket.tp_id], 'Verified');
        success(result?.message || 'Ticket verified successfully (Non-VL)');
        setShowStatusModal(false);
        setOpenStatusDropdown(null);
        if (activeTab !== 'Verification') {
          switchTabUnlessEventSearch('Verification');
        }
        setFilterStatus('Verified');
        await syncTicketsAfterStatusChange();
        syncDashboardNotifications();
      } catch (error: any) {
        showError(getErrorMessage(error, 'Failed to verify ticket'));
      } finally {
        setStatusChangeLoading(false);
        endStatusTableBusy();
      }
      return;
    }

    if (sanitizedStatus === 'Verified') {
      if (isMissingVerifyRequiredFields(ticket)) {
        showError(
          'Event name, event date/time, section, row, and seat numbers are required before verification.',
        );
        return;
      }
      setShowStatusModal(false);
      await openPidConfirmationForTickets([ticket]);
      return;
    }

    if (sanitizedStatus === 'Invalid' || sanitizedStatus === 'ListingRequestCancelled') {
      patchFailTransferModalRow(ticket.tp_id, {
        tp_status: 'ListingRequestCancelled',
        tp_updated_at: new Date().toISOString(),
      });
      beginStatusTableBusy('Cancelling listing request…');
      setStatusChangeLoading(true);
      try {
        await callCancelListingRequestByAdminRest(ticket.tp_id);
        success('Ticket marked as Listing Request Cancelled');
        updateLocalTicketStatus([ticket.tp_id], 'ListingRequestCancelled');
        removeTicketFromFailTransferModal(ticket.tp_id);
        setShowStatusModal(false);
        setFilterStatus('All Listing Request Cancelled');
        if (activeTab !== 'Listing Request Cancelled') {
          switchTabUnlessEventSearch('Listing Request Cancelled');
        }
        await refetch();
        await fetchTrackingCounts();
      } catch (error: any) {
        showError(getErrorMessage(error, 'Failed to cancel listing request'));
        patchFailTransferModalRow(ticket.tp_id, { tp_status: 'ToBeVerified' });
        setShowStatusModal(false);
      } finally {
        setStatusChangeLoading(false);
        endStatusTableBusy();
      }
      return;
    }
  }
  
  // Non-VL List paths handled above (price popup) before busy guards.
  
  // Non-VL: Listed / DelistInProgress → Delisted (no VL/Logitix remove)
  if (
    (currentStatus === 'List' ||
      currentStatus === 'DelistInProgress' ||
      currentStatus === 'DelistRequested') &&
    sanitizedStatus === 'Delist_NonVL'
  ) {
    await commitDirectStatusUpdate(
      'Delist',
      'Ticket delisted successfully (Non-VL)',
      'Delisted',
    );
    return;
  }

  // Non-VL: Verified / Delist → Delist Requested (skip delist email / VL)
  if (
    (currentStatus === 'Verified' || currentStatus === 'Delist') &&
    sanitizedStatus === 'DelistInProgress_NonVL'
  ) {
    await commitDirectStatusUpdate(
      'DelistInProgress',
      'Ticket moved to Delist Requested (Non-VL)',
      'Delisted',
    );
    return;
  }

  // Non-VL: Delist / DelistInProgress → Returned (no return REST / VL)
  if (
    (currentStatus === 'Delist' ||
      currentStatus === 'DelistInProgress' ||
      currentStatus === 'DelistRequested') &&
    sanitizedStatus === 'Return_NonVL'
  ) {
    await commitDirectStatusUpdate(
      'Return',
      'Ticket marked Returned (Non-VL)',
      'Delisted',
    );
    return;
  }

  // Non-VL: Listed → Unsold (direct status, no Unsold REST)
  if (currentStatus === 'List' && sanitizedStatus === 'Unsold_NonVL') {
    await commitDirectStatusUpdate(
      'Unsold',
      'Ticket marked Unsold (Non-VL)',
      'Unsold',
    );
    return;
  }

  if (currentStatus === 'Verified' && sanitizedStatus === 'List') {
    // Victory Live flow only: Submit to VL (or List on Marketplaces when already ingested).
    // Price ($200) is already on the seat; do not open a separate Listed price modal.
    setShowStatusModal(false);
    setOpenStatusDropdown(null);
    await handleVlProgressAction([ticket.tp_id]);
    return;
  }
  
  if ((currentStatus === 'List' || currentStatus === 'Unsold' || currentStatus === 'UnderReview') && sanitizedStatus === 'Sold') {
    setShowStatusModal(false);
    await openSoldModalForTicket(ticket);
    return;
  }

  // List -> Unsold should directly set Unsold status (not go through boolean REST flow).
  if (currentStatus === 'List' && sanitizedStatus === 'Unsold') {
    beginStatusTableBusy('Marking ticket as Unsold…');
    setStatusChangeLoading(true);
    try {
      const response = await callMarkTicketsUnsoldRest([ticket.tp_id]);
      success(response?.message || 'Ticket marked as Unsold successfully');
      updateLocalTicketStatus([ticket.tp_id], 'Unsold');
      setShowStatusModal(false);
      if (activeTab !== 'Unsold') switchTabUnlessEventSearch('Unsold');
      await syncTicketsAfterStatusChange();
      syncDashboardNotifications();
    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to update ticket status'));
      setShowStatusModal(false);
      await syncTicketsAfterStatusChange();
    } finally {
      setStatusChangeLoading(false);
      endStatusTableBusy();
    }
    return;
  }

  // Listed -> Delist: confirm, then remove from Logitix/Victory Live via delistTicketByAdmin.
  if (currentStatus === 'List' && sanitizedStatus === 'Delist') {
    setShowStatusModal(false);
    setOpenStatusDropdown(null);
    requestListedTicketDelist([ticket.tp_id], [ticket]);
    return;
  }

  // ListingRequestCancelled -> ToBeVerified: Hasura status only
  // (avoid updateTicketStatus REST — isValid:false means Delist and sends SES email)
  if (
    currentStatus === 'ListingRequestCancelled' &&
    sanitizedStatus === 'ToBeVerified'
  ) {
    beginStatusTableBusy('Saving status change…');
    setStatusChangeLoading(true);
    try {
      const result = await restoreCancelledListingToVerification({
        variables: { tp_id: ticket.tp_id }
      });
      const updated = result?.data?.update_ticket_placement?.returning?.length || 0;
      if (updated <= 0) {
        throw new Error('Unable to move ticket to To Be Verified');
      }
      success('Ticket moved to To Be Verified');
      updateLocalTicketStatus([ticket.tp_id], 'ToBeVerified');
      setShowStatusModal(false);
      if (activeTab !== 'Verification') {
        switchTabUnlessEventSearch('Verification');
      }
      setFilterStatus('ToBeVerified');
      await syncTicketsAfterStatusChange();
    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to move ticket to To Be Verified'));
      setShowStatusModal(false);
      await syncTicketsAfterStatusChange();
    } finally {
      setStatusChangeLoading(false);
      endStatusTableBusy();
    }
    return;
  }

  // DelistInProgress -> Delist: Confirm delist request (finalize delisting)
  if (currentStatus === 'DelistInProgress' && sanitizedStatus === 'Delist') {
    beginStatusTableBusy('Confirming delist…');
    setStatusChangeLoading(true);
    try {
      const { successIds, message } = await executeAdminOrSuperUserDelistRest([ticket.tp_id]);
      if (!successIds.includes(ticket.tp_id)) {
        throw new Error('Failed to delist ticket');
      }
      success(message || 'Ticket delisted successfully');
      updateLocalTicketStatus([ticket.tp_id], 'Delist');
      setShowStatusModal(false);
      if (activeTab !== 'Delisted') switchTabUnlessEventSearch('Delisted');
      await syncTicketsAfterStatusChange();
      syncDashboardNotifications();
    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to delist ticket'));
      setShowStatusModal(false);
      await syncTicketsAfterStatusChange();
    } finally {
      setStatusChangeLoading(false);
      endStatusTableBusy();
    }
    return;
  }


  // Delist -> List: Move delisted ticket back to listed
if (currentStatus === 'Delist' && sanitizedStatus === 'List') {
  beginStatusTableBusy('Moving ticket to Listed…');
  setStatusChangeLoading(true);
  try {
    await executeUndoDelistWithFallback(ticket.tp_id, ticket.tp_status);
    updateLocalTicketStatus([ticket.tp_id], 'List');
    setShowStatusModal(false);
    if (activeTab !== 'Listed') {
      switchTabUnlessEventSearch('Listed');
    }
    success('Ticket moved to Listed successfully');
    await syncTicketsAfterStatusChange();
  } catch (error: any) {
    showError(getErrorMessage(error, 'Failed to move ticket to Listed'));
    setShowStatusModal(false);
    await syncTicketsAfterStatusChange();
  } finally {
    setStatusChangeLoading(false);
    endStatusTableBusy();
  }
  return;
}

  // Delist -> DelistInProgress: Move delisted ticket back to delist requested
  if (currentStatus === 'Delist' && sanitizedStatus === 'DelistInProgress') {
    beginStatusTableBusy('Updating ticket status…');
    setStatusChangeLoading(true);
    try {
      await updateTicketStatusSilent({
        variables: {
          tp_id: ticket.tp_id,
          status: 'DelistInProgress'
        }
      });
      success('Ticket moved to Delist Requested');
      updateLocalTicketStatus([ticket.tp_id], 'DelistInProgress');
      setShowStatusModal(false);
      await syncTicketsAfterStatusChange();
      syncDashboardNotifications();
    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to move ticket to Delist Requested'));
      setShowStatusModal(false);
      await syncTicketsAfterStatusChange();
    } finally {
      setStatusChangeLoading(false);
      endStatusTableBusy();
    }
    return;
  }

  // Undo delist must use dedicated backend mutation to move DelistInProgress -> List.
  if (
    (currentStatus === 'DelistInProgress' || currentStatus === 'DelistRequested') &&
    sanitizedStatus === 'List'
  ) {
    beginStatusTableBusy('Moving ticket to Listed…');
    setStatusChangeLoading(true);
    try {
      await executeUndoDelistWithFallback(ticket.tp_id, ticket.tp_status);
      updateLocalTicketStatus([ticket.tp_id], 'List');
      setShowStatusModal(false);
      if (activeTab !== 'Listed') {
        switchTabUnlessEventSearch('Listed');
      }
      success('Ticket moved to Listed successfully');
      await syncTicketsAfterStatusChange();
    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to undo delist request'));
      setShowStatusModal(false);
      await syncTicketsAfterStatusChange();
    } finally {
      setStatusChangeLoading(false);
      endStatusTableBusy();
    }
    return;
  }

  if (
    (currentStatus === 'Delist' ||
      currentStatus === 'DelistInProgress' ||
      currentStatus === 'DelistRequested') &&
    sanitizedStatus === 'Return'
  ) {
    beginStatusTableBusy('Returning ticket…');
    setStatusChangeLoading(true);
    try {
      setShowStatusModal(false);
      const { message: returnMessage, type: returnType } = await executeReturnTicketsRest([ticket.tp_id]);
      updateLocalTicketStatus([ticket.tp_id], 'Return');
      if (returnType === 'success') success(returnMessage || 'Return request successful');
      else if (returnType === 'info') info(returnMessage || 'Return request processed');
      await syncTicketsAfterStatusChange();
    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to return ticket'));
      setShowStatusModal(false);
      await syncTicketsAfterStatusChange();
    } finally {
      setStatusChangeLoading(false);
      endStatusTableBusy();
    }
    return;
  }

  // Ensure "Delist Requested" uses enum-supported status and bypasses boolean REST status flow.
  if (sanitizedStatus === 'DelistRequested' || sanitizedStatus === 'DelistInProgress') {
    // Route through delist API flow to create feed notifications.
    setShowStatusModal(false);
    openDelistEmailModal([ticket.tp_id]);
    return;
  }

  const qaStatuses = new Set(['UnderReview', 'PaidManuallyViaQA', 'QADeniedPayout']);
  if (qaStatuses.has(sanitizedStatus)) {
    await commitDirectStatusUpdate(
      sanitizedStatus,
      sanitizedStatus === 'UnderReview'
        ? 'Ticket moved to Under Review successfully.'
        : sanitizedStatus === 'PaidManuallyViaQA'
          ? 'Ticket marked Paid Manually via QA successfully.'
          : 'Ticket marked QA Denied Payout successfully.',
      getTabForStatus(sanitizedStatus),
    );
    return;
  }

  // For all other status transitions, use backend status API (avoid direct Hasura mutation).
  beginStatusTableBusy('Saving status change…');
  setStatusChangeLoading(true);
  
  try {
    const isValidForBackend = sanitizedStatus === 'Verified';
    const restData = await callUpdateTicketStatusRest([ticket.tp_id], isValidForBackend, false);
    const rawMsg = String(restData?.message || '').trim();
    const msgType = resolveActionMessageType(rawMsg);
    if (msgType === 'error') {
      showError(rawMsg || 'Failed to update ticket status');
      await syncTicketsAfterStatusChange();
    } else {
      updateLocalTicketStatus([ticket.tp_id], sanitizedStatus);
      if (msgType === 'info') info(rawMsg || 'Status update processed');
      else success(rawMsg || `Ticket status updated to ${getStatusLabel(sanitizedStatus)} successfully!`);
      await syncTicketsAfterStatusChange();
    }
    setShowStatusModal(false);
    
  } catch (error: any) {
    showError(getErrorMessage(error, 'Failed to update ticket status'));
    setShowStatusModal(false);
    await syncTicketsAfterStatusChange();
  } finally {
    setStatusChangeLoading(false);
    endStatusTableBusy();
  }
};

const handleDeleteConfirm = async () => {
  if (!selectedTicketForDelete) return;
  if (deleteLoading || statusTableBusyCount > 0) return;

  beginStatusTableBusy('Deleting ticket…');
  setDeleteLoading(true);

  try {
    // Use GraphQL mutation directly instead of REST API
    const result = await deleteTicketStatus({
      variables: {
        tp_id: selectedTicketForDelete.tp_id
      }
    });
    
    const affected = result?.data?.update_ticket_placement?.returning?.length || 0;
    
    if (affected > 0) {
      // Update local state
      setFilteredTickets((prevTickets) =>
        prevTickets.filter((t) => t.tp_id !== selectedTicketForDelete.tp_id)
      );
      
      setStatusFilterSourceTickets((prevTickets) =>
        prevTickets.filter((t) => t.tp_id !== selectedTicketForDelete.tp_id)
      );
      
      // Update counts
      setTotalCount((prev) => Math.max(0, prev - 1));
      setTabCounts((prev) => ({
        ...prev,
        Deleted: (prev.Deleted || 0) + 1,
        [activeTab]: Math.max(0, (prev[activeTab] || 0) - 1)
      }));

      success('Ticket deleted successfully');
      setShowDeleteConfirmModal(false);
      setSelectedTicketForDelete(null);
      await syncTicketsAfterStatusChange();
    } else {
      showError('Failed to delete ticket. Please try again.');
    }
  } catch (error: any) {
    // If GraphQL mutation fails, try REST API as fallback
    try {
      await callDeleteTicketRest(selectedTicketForDelete.tp_id);
      
      setFilteredTickets((prevTickets) =>
        prevTickets.filter((t) => t.tp_id !== selectedTicketForDelete.tp_id)
      );
      // ... rest of the update logic
      success('Ticket deleted successfully');
    } catch (fallbackError: any) {
      showError(getErrorMessage(fallbackError, 'Failed to delete ticket'));
    }
    setShowDeleteConfirmModal(false);
  } finally {
    setDeleteLoading(false);
    endStatusTableBusy();
  }
};

const handleListedModalConfirm = async (confirmed = false) => {
  if (listedModalLoading) return;
  if (!listedModalTicket) {
    showError('No ticket selected');
    return;
  }
  
  if (!listedPrice) {
    showError('Please enter a valid listing price.');
    return;
  }
  
  const priceNum = parseFloat(listedPrice);
  if (isNaN(priceNum) || priceNum <= 0) {
    showError('Please enter a valid price greater than 0.');
    return;
  }

  if (requiresPriceChangeConfirmation(listedModalTicket.tp_list_price, priceNum) && !confirmed) {
    setPriceConfirmationFlow('listed');
    setShowListedModal(false);
    return;
  }
  
  beginStatusTableBusy('Publishing to Listed…');
  setListedModalLoading(true);
  try {
    await callPublishNonVlListing([listedModalTicket.tp_id], priceNum);

    updateLocalTicketStatus([listedModalTicket.tp_id], 'List', priceNum);
    success(`Ticket listed successfully (Non-VL) at $${priceNum}`);
    setShowListedModal(false);
    setListedModalTicket(null);
    setListedPrice('');
    if (activeTab !== 'Listed') {
      switchTabUnlessEventSearch('Listed');
    }
    await syncTicketsAfterStatusChange();
    syncDashboardNotifications();
    
  } catch (err: any) {
    showError(getErrorMessage(err, 'Failed to list ticket'));
    setShowListedModal(false);
    setListedModalTicket(null);
    setListedPrice('');
    await syncTicketsAfterStatusChange();
  } finally {
    setListedModalLoading(false);
    endStatusTableBusy();
  }

};

const handleBulkListConfirm = async (confirmed = false) => {
  if (bulkListLoading) return;
  if (!bulkSelectedTickets.length) {
    showError('No tickets selected');
    return;
  }

  const invalidTicket = bulkSelectedTickets.find(ticket => {
    const value = String(bulkListedPrices[ticket.tp_id] || '').trim();
    const num = parseFloat(value);
    return !value || isNaN(num) || num <= 0;
  });

  if (invalidTicket) {
    showError('All selected tickets must have a valid listing price greater than 0.');
    return;
  }

  const hasLargePriceChange = bulkSelectedTickets.some(ticket =>
    requiresPriceChangeConfirmation(
      ticket.tp_list_price,
      Number(parseFloat(String(bulkListedPrices[ticket.tp_id] || '').trim())),
    ),
  );
  if (hasLargePriceChange && !confirmed) {
    setPriceConfirmationFlow('bulkList');
    setShowBulkListModal(false);
    return;
  }

  beginStatusTableBusy('Publishing to Listed…');
  setBulkListLoading(true);

  try {
    await withSuppressedBulkMutationToasts(async () => {
      const results = await Promise.allSettled(
        bulkSelectedTickets.map(ticket => {
          const priceNum = Number(parseFloat(String(bulkListedPrices[ticket.tp_id] || '').trim()));
          return callPublishTicketsRest([ticket.tp_id], priceNum).then(() => ticket.tp_id);
        })
      );

      const successIds = results
        .filter(result => result.status === 'fulfilled')
        .map((result: any) => result.value);
      const failedCount = results.filter(result => result.status === 'rejected').length;

      if (successIds.length > 0) {
        const priceMap: Record<string, number> = {};
        successIds.forEach(id => {
          priceMap[id] = Number(parseFloat(String(bulkListedPrices[id] || '').trim()));
        });
        updateLocalTicketStatusWithPriceMap(successIds, 'List', priceMap);
        success(`Published ${successIds.length} ticket(s) to Listed${failedCount ? `, ${failedCount} failed` : ''}`);
      }

      if (failedCount > 0 && successIds.length === 0) {
        showError('Failed to publish selected tickets');
      }
    });
  } catch (err: any) {
    showError(getErrorMessage(err, 'Failed to publish selected tickets'));
  } finally {
    closeBulkListModal();
    setBulkSelectedTicketIds(new Set());
    setBulkSelectedTickets([]);
    setBulkNewStatus('');
    setBulkListLoading(false);
    endStatusTableBusy();
    await syncTicketsAfterStatusChange();
  }
};

const handleApplyBulkListPrice = () => {
  const value = String(bulkListCommonPrice).trim();
  const priceNum = parseFloat(value);
  if (!value || isNaN(priceNum) || priceNum <= 0) {
    showError('Enter a valid price to apply to all tickets');
    return;
  }
  setBulkListedPrices(
    Object.fromEntries(bulkSelectedTickets.map(ticket => [ticket.tp_id, String(priceNum)]))
  );
};

async function handleBulkSoldConfirm() {
  if (bulkSoldLoading) return;
  if (!bulkSelectedTickets.length) {
    showError('No tickets selected');
    return;
  }
  if (!bulkSoldMarketplaceId) {
    showError('Please select a marketplace.');
    return;
  }

  const mp = marketplacesList.find(m => m.id === bulkSoldMarketplaceId);
  if (!mp) {
    showError('Selected marketplace not found.');
    return;
  }

  const invalidTicket = bulkSelectedTickets.find(ticket => {
    const rawValue = bulkSoldLogitixAmounts[ticket.tp_id];
    const value = String(rawValue ?? '').trim();
    if (!value) {
      const selectedMp = marketplacesList.find(m => m.id === bulkSoldMarketplaceId);
      const listPrice = Number(ticket.tp_list_price) || 0;
      const expectedLogitix = selectedMp ? Math.round(listPrice * (100 - selectedMp.vl_fee_percentage)) / 100 : 0;
      return expectedLogitix <= 0;
    }
    const num = parseFloat(value);
    return isNaN(num) || num <= 0;
  });

  if (invalidTicket) {
    showError('Enter a valid amount received from Logitix for each ticket.');
    return;
  }

  beginStatusTableBusy('Marking tickets as sold…');
  setBulkSoldLoading(true);

  try {
    await withSuppressedBulkMutationToasts(async () => {
      const results = await Promise.allSettled(
        bulkSelectedTickets.map(ticket => {
          const listPrice = Number(ticket.tp_list_price) || 0;
          const logitixValue = Number(parseFloat(String(bulkSoldLogitixAmounts[ticket.tp_id] || '').trim()));
          const feeConfig = getBulkTicketSoldFeeConfig(ticket.tp_id, bulkSoldFeeConfigByTicketId);
          const quickPct = effectiveSoldQuickCutPercent(
            mp.vl_fee_percentage,
            feeConfig.feeMode,
            feeConfig.superClientPercent,
          );
          const quickCutDollars = Number(roundToFixed(listPrice * (quickPct / 100), 2));
          const settlement = Math.round((logitixValue - quickCutDollars) * 100) / 100;
         return callMarkAsSoldRest(ticket.tp_id, settlement, logitixValue, mp.name)
  .then(() => {
    saveMarketplaceCache(ticket.u_id, ticket.tp_id, mp.name);
    return ticket.tp_id;
  });
        })
      );

      const successIds = results
        .filter(result => result.status === 'fulfilled')
        .map((result: any) => result.value);
      const failedCount = results.filter(result => result.status === 'rejected').length;

      if (successIds.length > 0) {
        updateLocalTicketStatus(successIds, 'Sold');
        success(`${successIds.length} ticket(s) marked as sold${failedCount ? `, ${failedCount} failed` : ''}`);
        if (activeTab !== 'Sold') {
          handleTabChange('Sold');
        }
      }

      if (failedCount > 0 && successIds.length === 0) {
        showError('Failed to mark selected tickets as sold');
      }
    });
  } catch (err: any) {
    showError(getErrorMessage(err, 'Failed to mark selected tickets as sold'));
  } finally {
    closeBulkSoldModal();
    setBulkSelectedTicketIds(new Set());
    setBulkSelectedTickets([]);
    setBulkSoldLoading(false);
    endStatusTableBusy();
    await syncTicketsAfterStatusChange();
  }
};

const handleApplyBulkSoldAmount = () => {
  const value = String(bulkSoldCommonLogitixAmount).trim();
  const num = parseFloat(value);
  if (!value || isNaN(num) || num <= 0) {
    showError('Enter a valid logitix amount to apply to all tickets');
    return;
  }
  setBulkSoldLogitixAmounts(
    Object.fromEntries(bulkSelectedTickets.map(ticket => [ticket.tp_id, String(num)]))
  );
};

const closeSoldModal = () => {
    setShowSoldModal(false);
    setSoldModalTicket(null);
    setSoldMarketplaceId('');
    setSoldLogitixAmount('');
    setSoldFeeConfigReady(false);
    setSoldFeeMode('default');
    setSoldSuperClientPercent(null);
    setStatusChangeLoading(false);
  };

  // Confirm handler for Sold modal (same backend path as List Tickets → Mark as Sold)
const handleSoldModalConfirm = async () => {
  if (!soldModalTicket) {
    showError('No ticket selected');
    return;
  }
  
  if (!soldMarketplaceId) {
    showError('Please select a marketplace.');
    return;
  }
  
  const mp = marketplacesList.find(m => m.id === soldMarketplaceId);
  if (!mp) {
    showError('Selected marketplace not found.');
    return;
  }
  
  const listPrice = Number(soldModalTicket.tp_list_price) || 0;
  const logitixNum = parseFloat(String(soldLogitixAmount || '').trim());
  
  if (isNaN(logitixNum) || logitixNum <= 0) {
    showError('Please enter the amount received from Logitix.');
    return;
  }
  
  const quickPct = effectiveSoldQuickCutPercent(
    mp.vl_fee_percentage,
    soldFeeMode,
    soldSuperClientPercent,
  );
  const quickCutDollars = Number(roundToFixed(listPrice * (quickPct / 100), 2));
  const settlement = Math.round((logitixNum - quickCutDollars) * 100) / 100;
  
  if (settlement <= 0) {
    showError('Amount settled to user must be greater than zero.');
    return;
  }
  
  beginStatusTableBusy('Marking ticket as sold…');
  setStatusChangeLoading(true);
  
  try {
    const restData = await callMarkAsSoldRest(
      soldModalTicket.tp_id,
      settlement,
      logitixNum,
      mp.name
    );
    const rawMsg = String(restData?.message || '').trim();

    saveMarketplaceCache(soldModalTicket.u_id, soldModalTicket.tp_id, mp.name);
    updateLocalTicketStatus([soldModalTicket.tp_id], 'Sold');
    success(rawMsg || 'Ticket marked as sold successfully');
    if (activeTab !== 'Sold') {
      switchTabUnlessEventSearch('Sold');
    }
    closeSoldModal();
    await syncTicketsAfterStatusChange();
    await refetchExpired();
    await refetchFailed();
  } catch (err: any) {
    showError(getErrorMessage(err, 'Failed to mark ticket as sold'));
    closeSoldModal();

    await syncTicketsAfterStatusChange();
  } finally {
    setStatusChangeLoading(false);
    endStatusTableBusy();
  }
};

  const confirmPriceChange = async () => {
    const flow = priceConfirmationFlow;
    setPriceConfirmationFlow(null);
    if (flow === 'single') await executePriceChangeConfirm(true);
    if (flow === 'listed') await handleListedModalConfirm(true);
    if (flow === 'bulkPrice') await executeBulkPriceConfirm(true);
    if (flow === 'bulkList') await handleBulkListConfirm(true);
    if (flow === 'bulkStatus') await handleBulkStatusChange(true);
  };

  const cancelPriceChangeConfirmation = () => {
    const flow = priceConfirmationFlow;
    setPriceConfirmationFlow(null);
    if (flow === 'single') setShowPriceModal(true);
    if (flow === 'listed') setShowListedModal(true);
    if (flow === 'bulkPrice') setShowBulkPriceModal(true);
    if (flow === 'bulkList') setShowBulkListModal(true);
    if (flow === 'bulkStatus') setShowBulkStatusModal(true);
  };

  // ==================== UTILITY FUNCTIONS ====================

  const toggleRow = (ticketId: string) => {
    setExpandedRows(prev =>
      prev.includes(ticketId) ? prev.filter(id => id !== ticketId) : [...prev, ticketId]
    );
    clearSelectionState();
  };

  const toggleGroupRow = (groupKey: string) => {
    setExpandedGroupKeys((prev) => toggleExpandedGroupKeys(prev, groupKey));
    clearSelectionState();
  };

  const toggleActionMenu = (ticketId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenSeatActionMenu(null);
    setOpenActionMenu((current) => (current === ticketId ? null : ticketId));
  };

  const toggleSeatActionMenu = (menuKey: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenActionMenu(null);
    setOpenSeatActionMenu((current) => (current === menuKey ? null : menuKey));
  };

  const renderSeatCardStatus = (ticket: Ticket, seatId?: string, rowTab = activeTab) => {
    const seatKey = seatId || ticket.tp_id;
    const resolvedSeatStatus = seatCardStatusBySeatId[seatKey] || ticket.tp_status || '';
    const displayTicket = {
      ...ticket,
      tp_status: resolvedSeatStatus,
    };
    const pres = getManageTicketsStatusPresentation(displayTicket);
    const status = normalizeStatusValue(resolvedSeatStatus || ticket.tp_status || '').toLowerCase();
    const paymentUiKey = (pres.iconStatus || '').toLowerCase();
    const transitions = getStatusTransitions(getTicketTransitionStatus(displayTicket));
    const isReadOnlyStatus =
      isSuperUserListedStatusReadOnly(isSuperUser, rowTab) ||
      (!canOverrideSettlementStatus &&
        (paymentUiKey === 'settled' || paymentUiKey === 'settlementinprogress'));

    if (
      isReadOnlyStatus ||
      status === 'return' ||
      status === 'closed' ||
      status === 'unsold' ||
      status === 'invalid' ||
      !transitions.length
    ) {
      return (
        <span className={`${styles.seatStatusBadge} ${styles.statusBadge} ${getStatusColor(pres.iconStatus)}`}>
          {getStatusIcon(pres.iconStatus)} {pres.label}
        </span>
      );
    }

    const applySeatStatusChange = (nextStatus: string) => {
      const sanitizedNextStatus = normalizeStatusValue(nextStatus);
      const deferPaint = shouldDeferSeatStatusPaint(sanitizedNextStatus);
      const selectedIds = Array.from(bulkSelectedTicketIds);
      const applyToSelection =
        selectedIds.length > 1 && selectedIds.includes(ticket.tp_id);
      if (applyToSelection) {
        const selectedTickets =
          getManageTicketDisplayTickets(manageTicketTableRows).filter((t) =>
            selectedIds.includes(t.tp_id),
          );
        const ticketsForUpdate =
          selectedTickets.length > 1
            ? selectedTickets
            : filteredTickets.filter((t) => selectedIds.includes(t.tp_id));
        if (ticketsForUpdate.length > 1) {
          // Don't paint Delist/Verified/Sold until confirm + API succeed.
          if (!deferPaint) {
            setSeatCardStatusBySeatId((prev) => {
              const next = { ...prev };
              ticketsForUpdate.forEach((t) => {
                generateSeats(t).forEach((seat) => {
                  next[seat.id] = nextStatus;
                });
              });
              return next;
            });
          }
          void applyMultiTicketStatusChangeFromUi(ticketsForUpdate, nextStatus);
          return;
        }
      }
      // Don't paint Delist/Verified/Sold until confirm + API succeed.
      if (!deferPaint) {
        setSeatCardStatusBySeatId((prev) => ({ ...prev, [seatKey]: nextStatus }));
      }
      void handleStatusChangeConfirm(ticket, nextStatus);
    };

    return (
      <div className={styles.seatStatusDropdownWrapper} onClick={(e) => e.stopPropagation()}>
        <button
          className={`${styles.seatStatusBadge} ${styles.statusBadge} ${getStatusColor(pres.iconStatus)} ${styles.statusDropdownBtn}`}
          disabled={isStatusOperationInProgress}
          onClick={(e) => {
            e.stopPropagation();
            if (isStatusOperationInProgress) return;
            setSeatCardStatusDropdownOpen((prev) => ({
              ...prev,
              [seatKey]: !prev[seatKey],
            }));
          }}
        >
          {getStatusIcon(pres.iconStatus)} {pres.label}
          <ChevronDown size={10} style={{ marginLeft: 3 }} />
        </button>
        {seatCardStatusDropdownOpen[seatKey] && (
          <div className={styles.statusDropdownList}>
            {transitions.map((opt) => (
              <div
                key={opt.value}
                className={styles.statusDropdownListItem}
                onClick={(e) => {
                  e.stopPropagation();
                  setSeatCardStatusDropdownOpen((prev) => ({ ...prev, [seatKey]: false }));
                  applySeatStatusChange(opt.value);
                }}
              >
                {getStatusIcon(opt.value)} {opt.label}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderSeatCardActionMenu = () => null;

  const getStatusColor = (status: string): string => {
    if (!status) return styles.statusDefault;
    const s = status.toLowerCase();
      if (s === "voidedpayout") return styles.statusVoidedPayout;
    if (s === "verified" || s === "list") return styles.statusSuccess;
    if (s === "tobeverified") return styles.statusWarning;
    if (s === "invalid") return styles.statusError;
    if (s === "settled") return styles.statusVerified;
    if (s === "settlementinprogress") return styles.statusSuccess;
    if (s === "failed") return styles.statusError;
    if (s === "sold") return styles.statusSold;
    if (s === "underreview") return styles.statusUnderReview;
    if (s === "paidmanuallyviaqa") return styles.statusPaidManuallyViaQA;
    if (s === "qadeniedpayout") return styles.statusQADeniedPayout;
    if (s === "delist" || s === "delistinprogress" || s === "delistrequested" || s === "listingrequestcancelled" || s === "return") return styles.statusDelist;
    if (s === "unsold") return styles.statusUnsold;
    if (s === "closed" || s === "listing expired" || s === "listing request expired") return styles.statusExpired;
    if (s === "deleted") return styles.statusDeleted;
    return styles.statusDefault;
  };

  function getStatusLabel(status: string): string {
  if (!status) return 'Unknown';
  const normalizedStatus = status.toLowerCase();
  if (normalizedStatus === 'voidedpayout') return 'Voided Payout';
  if (normalizedStatus === 'list') return 'Listed';
  if (normalizedStatus === 'list_nonvl') return 'Listed (Non-VL)';
  if (normalizedStatus === 'delistinprogress_nonvl') return 'Delist Requested (Non-VL)';
  if (normalizedStatus === 'delistinprogress' || normalizedStatus === 'delistrequested') return 'Delist Requested';
  if (normalizedStatus === 'listingrequestcancelled') return 'Listing Request Cancelled';
  if (normalizedStatus === 'closed') return 'Listing Request Expired';
  if (normalizedStatus === 'settled') return 'Settled';
  if (normalizedStatus === 'settlementinprogress') return 'Settlement In Progress';
  if (normalizedStatus === 'return_nonvl') return 'Returned (Non-VL)';
  if (normalizedStatus === 'return') return 'Returned';
  if (normalizedStatus === 'tobeverified') return 'To Be Verified';
  if (normalizedStatus === 'verified_nonvl') return 'Verified (Non-VL)';
  if (normalizedStatus === 'verified') return 'Verified';
  if (normalizedStatus === 'invalid') return 'Invalid';
  if (normalizedStatus === 'sold') return 'Sold';
  if (normalizedStatus === 'underreview') return 'Under Review';
  if (normalizedStatus === 'paidmanuallyviaqa') return 'Paid Manually via QA';
  if (normalizedStatus === 'qadeniedpayout') return 'QA Denied Payout';
  if (normalizedStatus === 'unsold_nonvl') return 'Unsold (Non-VL)';
  if (normalizedStatus === 'unsold') return 'Unsold';
  if (normalizedStatus === 'delist_nonvl') return 'Delisted (Non-VL)';
  if (normalizedStatus === 'delist') return 'Delisted';
  if (normalizedStatus === 'deleted') return 'Deleted';
  return status;
};

  /** Sold tab: surface payout state (matches list fail-to-transfer / DB view intent). */
  /** Sold tab: surface payout state (matches list fail-to-transfer / DB view intent). */
  const getManageTicketsStatusPresentation = (ticket: Ticket): { label: string; iconStatus: string } => {
    const placementStatus = (ticket.tp_status || '').trim();
    if ((placementStatus || '').toLowerCase() !== 'sold') {
      return { label: getStatusLabel(placementStatus), iconStatus: placementStatus };
    }
    if (ticket.tp_payout_status === 'Voided_Payout') {
      return { label: 'Voided Payout', iconStatus: 'sold' };
    }
    const pay = ticket.tp_payment_status;
    if (pay === 'Success') {
      return { label: 'Settled', iconStatus: 'settled' };
    }
    if (pay === 'Inprogress') {
      return { label: 'Settlement In Progress', iconStatus: 'settlementinprogress' };
    }
    if (pay === 'Failed') {
      return { label: 'Failed', iconStatus: 'failed' };
    }
    if (pay == null || pay === '') {
      const payout = ticket.tp_payout_status;
      if (payout && payout !== 'Unvoided_Payout') {
        return { label: String(payout).replace(/_/g, ' '), iconStatus: 'sold' };
      }
      return { label: 'Sold', iconStatus: 'sold' };
    }
    return { label: pay, iconStatus: 'sold' };
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (
    dateString: string,
    timeZone?: string | null,
    dateTimeZone?: string | null,
    eTime?: string | null,
  ): string => {
    if (!dateString && !dateTimeZone) return 'N/A';
    const formatted = formatDbEventDate({
      e_date: dateString,
      e_time_zone: timeZone,
      e_date_time_zone: dateTimeZone,
      e_time: eTime,
    }, 'short');
    return formatted === 'TBD' ? 'N/A' : formatted;
  };

  const formatTime = (
    dateString: string,
    timeZone?: string | null,
    dateTimeZone?: string | null,
    eTime?: string | null,
  ): string => {
    if (!dateString && !dateTimeZone && !eTime) return '';
    return formatDbEventTime({
      e_date: dateString,
      e_time_zone: timeZone,
      e_date_time_zone: dateTimeZone,
      e_time: eTime,
    }, 'short');
  };

  const hasActiveFilters = (): boolean => {
    return filterStatus !== getDefaultStatus() ||
      (!hideSuperUserClientToolbar && (clientType !== 'All' || clientSearch !== '')) ||
      selectedLeague !== '' ||
      eventVenueSearch !== '' ||
      startDate !== '' ||
      endDate !== '' ||
      cardFilter !== null ||
      onHoldFilter ||
      pidStatusFilter !== 'all' ||
      vlShareFilter !== 'all' ||
      vlSupportFilter !== 'all' ||
      vlSupportOwner !== 'company' ||
      lastPricedFilter.length > 0 ||
      lastPricedFrom !== '' ||
      lastPricedTo !== '' ||
      lastPricedCustomFilter !== null
  };

  type ManageTicketTableRow =
    | { kind: 'section'; sectionKey: string; label: string; count: number }
    | { kind: 'group'; groupKey: string; groupLabel: string; rowTab: string; summary: { listingId: string; seatLabel: string; seatMode?: 'odd' | 'even' | null; totalQuantity: number; status: string; eventName: string; section: string; row: string }; tickets: Ticket[] }
    | { kind: 'ticket'; ticket: Ticket; rowTab: string };

  const groupingBoundaryVariables = useMemo(() => {
    const boundarySourceBuckets = isEventGroupedView
      ? [
          eventGroupedByStatus.pending,
          eventGroupedByStatus.listed,
          eventGroupedByStatus.sold,
          eventGroupedByStatus.delisted,
          eventGroupedByStatus.deleted,
        ]
      : [filteredTickets];
    const countsByIdentity = new Map<string, number>();
    boundarySourceBuckets.forEach((bucket) => {
      const countsInBucket = new Map<string, number>();
      bucket.forEach((ticket) => {
        const identity = getEventGroupKey(ticket);
        if (identity) countsInBucket.set(identity, (countsInBucket.get(identity) ?? 0) + 1);
      });
      countsInBucket.forEach((count, identity) => {
        if (count > 1) countsByIdentity.set(identity, count);
      });
    });
    const groupedIdentities = new Set(countsByIdentity.keys());
    if (!groupedIdentities.size) return null;

    const groupedTickets = boundarySourceBuckets
      .flat()
      .filter((ticket) => {
        const identity = getEventGroupKey(ticket);
        return identity !== null && groupedIdentities.has(identity);
      });
    const listingIds = Array.from(new Set(
      groupedTickets
        .map((ticket) => String(ticket.tp_vl_listing_id ?? '').trim())
        .filter((id) => id && !/^qa-/i.test(id) && !/^(null|undefined)$/i.test(id)),
    ));
    const ticketIds = Array.from(new Set(
      groupedTickets
        .map((ticket) => String(ticket.t_id ?? '').trim())
        .filter((id) => id && !/^(null|undefined)$/i.test(id)),
    ));
    return listingIds.length || ticketIds.length ? { listingIds, ticketIds } : null;
  }, [filteredTickets, isEventGroupedView, eventGroupedByStatus]);
  const groupingBoundaryKey = groupingBoundaryVariables
    ? JSON.stringify({
        listingIds: [...groupingBoundaryVariables.listingIds].sort(),
        ticketIds: [...groupingBoundaryVariables.ticketIds].sort(),
      })
    : '';
  const groupingBoundariesReady = groupingBoundaryKey === loadedGroupingBoundaryKey;
  const groupingBoundaryVariablesRef = useRef(groupingBoundaryVariables);
  groupingBoundaryVariablesRef.current = groupingBoundaryVariables;
  const fetchGroupingBoundariesRef = useRef(fetchTicketGroupingBoundaryTickets);
  fetchGroupingBoundariesRef.current = fetchTicketGroupingBoundaryTickets;

  useEffect(() => {
    const requestId = ++ticketGroupingBoundaryRequestRef.current;
    if (!groupingBoundaryKey) {
      setTicketGroupingBoundaryTickets((prev) => (prev.length === 0 ? prev : []));
      setLoadedGroupingBoundaryKey((prev) => (prev === '' ? prev : ''));
      return;
    }

    const variables = groupingBoundaryVariablesRef.current;
    if (!variables) return;
    fetchGroupingBoundariesRef.current({ variables })
      .then(({ data }) => {
        if (ticketGroupingBoundaryRequestRef.current !== requestId) return;
        setTicketGroupingBoundaryTickets(data?.ticket_placement ?? []);
        setLoadedGroupingBoundaryKey(groupingBoundaryKey);
      })
      .catch((error: unknown) => {
        if (ticketGroupingBoundaryRequestRef.current !== requestId) return;
        setTicketGroupingBoundaryTickets([]);
        setLoadedGroupingBoundaryKey(groupingBoundaryKey);
        showError(getErrorMessage(error, 'Unable to refresh ticket grouping'));
      });

    return () => {
      if (ticketGroupingBoundaryRequestRef.current === requestId) {
        ticketGroupingBoundaryRequestRef.current += 1;
      }
    };
  }, [groupingBoundaryKey]);

  // Build all display rows first, then paginate by group/event rows (not seats).
  // One event/listing with many seats still counts as 1 row toward "Rows per page".
  const allManageTicketTableRows: ManageTicketTableRow[] = useMemo(() => {
    // Seat-boundary splits change row positions. Wait until that list matches
    // the current tickets so the first paint is the final group order.
    if (groupingBoundaryKey && !groupingBoundariesReady) return [];
    if (isEventGroupedView) {
      const ticketsForRows = activeTab === 'Delisted'
        ? sortDelistedTabTickets(filteredTickets)
        : sortManageTickets(filteredTickets);
      return buildManageTicketDisplayGroups(
        ticketsForRows,
        activeTab,
        ticketGroupingBoundaryTickets.length ? ticketGroupingBoundaryTickets : undefined,
      ) as ManageTicketTableRow[];
    }
    const ticketsForRows = activeTab === 'Delisted'
      ? sortDelistedTabTickets(filteredTickets)
      : directSeatPagination
        ? filteredTickets
        : sortManageTickets(filteredTickets);
    return buildManageTicketDisplayGroups(
      ticketsForRows,
      activeTab,
      ticketGroupingBoundaryTickets,
    );
  }, [
    isEventGroupedView,
    filteredTickets,
    activeTab,
    ticketGroupingBoundaryTickets,
    directSeatPagination,
    groupingBoundaryKey,
    groupingBoundariesReady,
  ]);

const filteredFlaggedTickets = useMemo(() => {
    if (activeTab !== 'Listed') {
      return allManageTicketTableRows;
    }

    const range = getLastPricedRange(lastPricedFrom, lastPricedTo);
    const matchesRow = (row: ManageTicketTableRow): boolean => {
      const tickets = row.kind === 'group' ? row.tickets : row.kind === 'ticket' ? [row.ticket] : [];
      if (!tickets.length) return true;

      return tickets.some((ticket) =>
        matchesLastPricedRange(ticket, range) &&
        (!lastPricedCustomFilter || matchesLastPricedRange(ticket, lastPricedCustomFilter.range)) &&
        (!lastPricedFilter.length || matchesLastPricedBucketFilter(ticket, lastPricedFilter))
      );
    };

    if (!lastPricedFilter.length && !range && !lastPricedCustomFilter) return allManageTicketTableRows;
    return allManageTicketTableRows.filter((row) => {
      if (row.kind === 'section') return true;
      return matchesRow(row);
    });
  }, [
    activeTab,
    allManageTicketTableRows,
    lastPricedFilter,
    lastPricedFrom,
    lastPricedTo,
    lastPricedCustomFilter,
  ]);
  const showPriceColumn =
    isEventGroupedView ||
    activeTab === 'Listed' ||
    activeTab === 'Sold';
  const visibleTableSort = tableSort && (
    isManageTicketTableSortEnabled(activeTab, isMailParserErrorView) &&
    (tableSort.column !== 'price' || showPriceColumn) &&
    (tableSort.column !== 'lastPriced' || activeTab === 'Listed')
  )
    ? tableSort
    : null;
  const sortedManageTicketRows = useMemo(
    () => sortManageTicketDisplayRows(filteredFlaggedTickets, directSeatPagination ? null : visibleTableSort),
    [filteredFlaggedTickets, visibleTableSort, directSeatPagination],
  );
  const handleTableSort = (column: ManageTicketSortColumn) => {
    if (!isManageTicketTableSortEnabled(activeTab, isMailParserErrorView)) return;
    if (
      serverPaginationActive &&
      !serverGroupClientPaging &&
      (effectiveTab === 'Listed' || effectiveTab === 'Sold')
    ) {
      sortRequestPendingKeyRef.current = sortRequestContextKey;
      setSortRequestPendingKey(sortRequestContextKey);
    }
    setTableSort((current) => ({
      column,
      direction: current?.column === column && current.direction === 'asc' ? 'desc' : 'asc',
    }));
    setPageOffset(0);
  };
  const showDelistReturnEmailColumn =
    activeTab === 'Delisted' || activeTab === 'Listing Request Cancelled';
  const showListedFinancialColumns =
    isSuperUser && (activeTab === 'Listed' || activeTab === 'Sold');
  const hideSuperUserMarketplaceColumns =
    isSuperUser && (activeTab === 'Listed' || activeTab === 'Sold');
  const showMarketplaceColumn = !hideSuperUserMarketplaceColumns && (activeTab === 'Listed' || activeTab === 'Sold');
  const showSharedColumn = !isSuperUser && activeTab === 'Listed';
  const hideSoldClientColumns = hideSuperUserClientColumns;
  const manageTicketColumnUserId = String(
    globalState?.profile?.id || localStorage.getItem('adminUserId') || 'anonymous',
  );
  const [savedColumnOrders, setSavedColumnOrders] = useState<Record<string, ManageTicketColumnId[]>>({});
  const [columnDropTarget, setColumnDropTarget] = useState<ManageTicketColumnId | null>(null);
  const columnDragMovedRef = useRef(false);
  useEffect(() => {
    const stored = readManageTicketColumnOrder(manageTicketColumnUserId, activeTab);
    setSavedColumnOrders((current) => (
      stored ? { ...current, [activeTab]: stored } : current
    ));
  }, [manageTicketColumnUserId, activeTab]);
  const visibleManageTicketColumns = useMemo(() => {
    const columns: ManageTicketColumnId[] = ['event', 'section', 'row', 'seats', 'status'];
    if (showMarketplaceColumn) columns.push('marketplaces');
    if (showSharedColumn) columns.push('vlShare');
    if (showPriceColumn) columns.push('price');
    if (showListedFinancialColumns) columns.push('cost', 'potentialPayout', 'difference');
    if (activeTab === 'Listed') columns.push('lastPriced');
    if (!hideSoldClientColumns) columns.push('clientName');
    if (showDelistReturnEmailColumn) columns.push('returnEmail');
    if (!hideSoldClientColumns) columns.push('clientType');
    return columns;
  }, [
    showMarketplaceColumn,
    showSharedColumn,
    showPriceColumn,
    showListedFinancialColumns,
    activeTab,
    hideSoldClientColumns,
    showDelistReturnEmailColumn,
  ]);
  const orderedManageTicketColumns = useMemo(
    () => visibleManageTicketColumnOrder(savedColumnOrders[activeTab], visibleManageTicketColumns),
    [savedColumnOrders, activeTab, visibleManageTicketColumns],
  );
  const eventColumnIsSticky = orderedManageTicketColumns[0] === 'event';
  const reorderManageTicketColumns = (fromId: ManageTicketColumnId, toId: ManageTicketColumnId) => {
    setSavedColumnOrders((current) => {
      const next = reorderVisibleManageTicketColumns(
        current[activeTab] ?? MANAGE_TICKET_COLUMN_IDS,
        orderedManageTicketColumns,
        fromId,
        toId,
      );
      writeManageTicketColumnOrder(manageTicketColumnUserId, activeTab, next);
      return { ...current, [activeTab]: next };
    });
  };
  const columnDragProps = (columnId: ManageTicketColumnId) => ({
    draggable: true,
    title: 'Drag to reorder',
    onDragStart: (event: React.DragEvent<HTMLTableCellElement>) => {
      columnDragMovedRef.current = true;
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', columnId);
    },
    onDragOver: (event: React.DragEvent<HTMLTableCellElement>) => {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      setColumnDropTarget(columnId);
    },
    onDrop: (event: React.DragEvent<HTMLTableCellElement>) => {
      event.preventDefault();
      const fromId = event.dataTransfer.getData('text/plain');
      setColumnDropTarget(null);
      if (isManageTicketColumnId(fromId) && fromId !== columnId) {
        reorderManageTicketColumns(fromId, columnId);
      }
    },
    onDragEnd: () => {
      setColumnDropTarget(null);
      window.setTimeout(() => {
        columnDragMovedRef.current = false;
      }, 0);
    },
  });
  const renderSortableHeader = (label: string, column: ManageTicketSortColumn, className: string, columnId: ManageTicketColumnId) => {
    const dragClass = columnDropTarget === columnId ? ` ${styles.columnDropTarget}` : '';
    if (!isManageTicketTableSortEnabled(activeTab, isMailParserErrorView)) {
      return (
        <th className={`${className}${dragClass}`} {...columnDragProps(columnId)}>
          {label}
        </th>
      );
    }
    const isActive = visibleTableSort?.column === column;
    const activeDirection = isActive ? visibleTableSort?.direction : null;
    return (
      <th
        className={`${className}${dragClass}`}
        aria-sort={activeDirection ? (activeDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
        {...columnDragProps(columnId)}
      >
        <button
          type="button"
          className={styles.sortableHeader}
          onClick={() => {
            if (columnDragMovedRef.current) {
              columnDragMovedRef.current = false;
              return;
            }
            handleTableSort(column);
          }}
          aria-label={`Sort by ${label}${activeDirection ? `, ${activeDirection === 'asc' ? 'ascending' : 'descending'}` : ''}`}
        >
          <span>{label}</span>
          <span className={styles.sortArrows} aria-hidden="true">
            <span className={`${styles.sortArrow} ${isActive && activeDirection !== 'asc' ? styles.sortArrowIdle : ''}`}>▲</span>
            <span className={`${styles.sortArrow} ${isActive && activeDirection !== 'desc' ? styles.sortArrowIdle : ''}`}>▼</span>
          </span>
        </button>
      </th>
    );
  };
  const renderPlainHeader = (label: string, className: string, columnId: ManageTicketColumnId) => (
    <th className={`${className}${columnDropTarget === columnId ? ` ${styles.columnDropTarget}` : ''}`} {...columnDragProps(columnId)}>
      {label}
    </th>
  );
  const renderManageTicketColumnHeader = (columnId: ManageTicketColumnId) => {
    switch (columnId) {
      case 'event':
        return renderSortableHeader(
          'Event',
          'event',
          `${styles.thEvent}${eventColumnIsSticky ? ` ${styles.stickyEvent}` : ''}`,
          'event',
        );
      case 'section':
        return renderSortableHeader('Section', 'section', styles.thSection, 'section');
      case 'row':
        return renderSortableHeader('Row', 'row', styles.thRow, 'row');
      case 'seats':
        return renderPlainHeader('Seats', styles.thSeat, 'seats');
      case 'status':
        return renderPlainHeader('Status', styles.thStatus, 'status');
      case 'marketplaces':
        return renderPlainHeader('Marketplaces', styles.thMarketplace, 'marketplaces');
      case 'vlShare':
        return renderPlainHeader('VL Share', styles.thShared, 'vlShare');
      case 'price':
        return renderSortableHeader('Price', 'price', styles.thPrice, 'price');
      case 'cost':
        return renderPlainHeader('Cost', styles.thPrice, 'cost');
      case 'potentialPayout':
        return renderPlainHeader('Potential Payout', styles.thPrice, 'potentialPayout');
      case 'difference':
        return renderPlainHeader('Difference', styles.thPrice, 'difference');
      case 'lastPriced':
        return renderSortableHeader('Last Priced', 'lastPriced', styles.thLastPriced, 'lastPriced');
      case 'clientName':
        return renderPlainHeader('Client Name', styles.thClient, 'clientName');
      case 'returnEmail':
        return renderPlainHeader('Return Email', styles.thReturnEmail, 'returnEmail');
      case 'clientType':
        return renderPlainHeader('Client Type', styles.thClientType, 'clientType');
      default:
        return null;
    }
  };
  const renderOrderedManageTicketCells = (
    cells: Partial<Record<ManageTicketColumnId, React.ReactNode>>,
  ) => orderedManageTicketColumns.map((columnId) => (
    <React.Fragment key={columnId}>{cells[columnId]}</React.Fragment>
  ));
  const manageTicketTableRows: ManageTicketTableRow[] = useMemo(() => {
    const rows = sortedManageTicketRows;
    if (isEventGroupedView) {
      if (!isSuperUser) return rows;
      return rows.filter((row) =>
        row.kind === 'section'
          ? row.sectionKey !== 'deleted'
          : row.rowTab !== 'Deleted',
      );
    }
    // Multi-window server path keeps every group in the loaded seat window.
    // Slice the current page inside that window; the next seat fetch starts
    // only after those groups are exhausted.
    if (directSeatPagination) {
      return rows;
    }
    if (serverPaginationActive && !serverGroupClientPaging) {
      const requestedWindow = getGroupedSeatWindowIndex(pageOffset, groupsPerFullWindow);
      const sortingWithPreviousWindow = isManageTicketSortRequestPending(
        sortRequestPendingKey,
        sortRequestContextKey,
      );
      if (
        groupsPerFullWindow &&
        requestedWindow !== loadedSeatWindowIndex &&
        !sortingWithPreviousWindow
      ) return [];
      const localOffset = sortingWithPreviousWindow
        ? 0
        : getGroupedWindowLocalOffset(pageOffset, groupsPerFullWindow);
      return rows.slice(localOffset, localOffset + pageSize);
    }
    return rows.slice(pageOffset, pageOffset + pageSize);
  }, [
    isSuperUser,
    isEventGroupedView,
    serverPaginationActive,
    directSeatPagination,
    serverGroupClientPaging,
    sortedManageTicketRows,
    pageOffset,
    pageSize,
    groupsPerFullWindow,
    loadedSeatWindowIndex,
    sortRequestPendingKey,
    sortRequestContextKey,
  ]);

  const vlSupportTpIds = useMemo(() => {
    if (activeTab !== 'Listed' && activeTab !== 'Verification') return [] as string[];
    const ids = new Set<string>();
    for (const row of manageTicketTableRows) {
      if (row.kind === 'group') row.tickets.forEach((t) => t.tp_id && ids.add(t.tp_id));
      else if (row.kind !== 'section' && row.ticket?.tp_id) ids.add(row.ticket.tp_id);
    }
    return Array.from(ids).sort();
  }, [activeTab, manageTicketTableRows]);
  const vlSupportTpIdsKey = vlSupportTpIds.join(',');

  // "Change seat numbers" (odd / even): trims a card to odd-only or even-only seats before it is on Victory Live.
  const seatModeDialog = useSeatModeDialog({
    onChanged: () => void handleRefresh(),
    onSuccess: (message) => success(message),
    onError: (message) => showError(message),
  });
  const openSeatModeDialog = (tickets: Ticket[]) =>
    seatModeDialog.open({
      tpIds: tickets.map((t) => t.tp_id),
      seats: tickets.map((t) => Number(t.tp_seat_no)),
      currentMode: getGroupSeatMode(tickets),
      eventName: tickets[0]?.e_name,
    });
  /** Menu item for a card: To Be Verified / Verified only. Listed cards explain why it is off. */
  const renderChangeSeatNumbersItem = (tickets: Ticket[]) => {
    if (isSuperUser || !['Verification', 'Listed'].includes(activeTab)) return null;
    const editable = tickets.every((t) => t.tp_status === 'ToBeVerified' || t.tp_status === 'Verified');
    const listed = tickets.some((t) => t.tp_status === 'List');
    if (!editable && !listed) return null;
    return (
      <button
        className={styles.actionMenuItem}
        disabled={!editable}
        title={editable ? 'Make these odd-only or even-only seats' : 'Already on Victory Live. Delist these tickets before changing seat numbers.'}
        onClick={(e) => {
          e.stopPropagation();
          setOpenActionMenu(null);
          openSeatModeDialog(tickets);
        }}
      >
        <Edit size={14} /> Odd/Even seat numbers
      </button>
    );
  };

  useEffect(() => {
    if (!vlSupportTpIds.length || !vlSupportAvailable) return;
    let cancelled = false;
    (async () => {
      try {
        const next: Record<string, VlSupportRequest[]> = {};
        for (let i = 0; i < vlSupportTpIds.length; i += 400) {
          const { data } = await apolloClient.query({
            query: GET_VL_SUPPORT_REQUESTS_BY_TP_IDS,
            variables: { tpIds: vlSupportTpIds.slice(i, i + 400) },
            fetchPolicy: 'network-only',
          });
          for (const row of data?.vl_support_request || []) {
            (next[row.tp_id] ||= []).push(row);
          }
        }
        if (!cancelled) setVlSupportByTpId(next);
      } catch (err) {
        console.warn('[ManageTickets] Victory Live support requests unavailable on this Hasura', err);
        if (!cancelled) {
          setVlSupportAvailable(false);
          setVlSupportFilter('all');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vlSupportTpIdsKey, vlSupportAvailable, vlSupportReloadToken, apolloClient]);


  const currentPageTickets = useMemo(
    () => getManageTicketDisplayTickets(manageTicketTableRows),
    [manageTicketTableRows],
  );

  const groupedFetchSize = getGroupedPageSeatFetchSize(pageSize);
  const loadedWindowGroups = allManageTicketTableRows.filter((row) => row.kind !== 'section').length;
  const loadedWindowSeats = getManageTicketDisplayTickets(allManageTicketTableRows).length;
  const seatWindowIndex = getGroupedSeatWindowIndex(pageOffset, groupsPerFullWindow);
  // One 400-seat fetch is paged as groups. The footer total is that group count,
  // not the seat aggregate, once the loaded window is the last one.
  const pageItemCount = directSeatPagination
    ? currentPageTickets.length
    : manageTicketTableRows.length;
  const multiWindowPaging = serverPaginationActive && !serverGroupClientPaging && !directSeatPagination;
  const seatTotalForPages = Math.max(totalCount, 0);
  const moreSeatWindows =
    multiWindowPaging &&
    loadedWindowSeats >= groupedFetchSize &&
    seatTotalForPages > (seatWindowIndex + 1) * groupedFetchSize;
  const knownGroupedTotal = multiWindowPaging
    ? resolveLoadedGroupedRowTotal({
        seatTotal: seatTotalForPages,
        loadedSeats: loadedWindowSeats,
        loadedGroups: loadedWindowGroups,
        fetchSize: groupedFetchSize,
        groupsPerFullWindow,
        windowIndex: seatWindowIndex,
      })
    : null;
  const groupsThroughLoadedWindow =
    seatWindowIndex * (groupsPerFullWindow && groupsPerFullWindow > 0 ? groupsPerFullWindow : loadedWindowGroups) +
    loadedWindowGroups;
  const groupedRowCount = multiWindowPaging
    ? loadedWindowGroups
    : allManageTicketTableRows.filter((row) => row.kind !== 'section').length;
  const effectiveTotalCount = directSeatPagination
    ? Math.max(totalCount, 0)
    : isEventGroupedView
    ? groupedRowCount
    : serverGroupClientPaging || !serverPaginationActive
      ? groupedRowCount
      : (knownGroupedTotal ?? Math.max(
          groupsThroughLoadedWindow,
          pageItemCount > 0 ? pageOffset + pageItemCount : 0,
        ));
  const currentPage = effectiveTotalCount === 0 ? 0 : Math.floor(pageOffset / pageSize) + 1;
  const totalPages = Math.max(
    1,
    Math.ceil(effectiveTotalCount / pageSize) || 1,
    moreSeatWindows ? currentPage + 1 : 1,
  );
  const nextGroupedPageDisabled = directSeatPagination
    ? pageOffset + pageSize >= effectiveTotalCount
    : !hasAnotherGroupedPage({
        pageOffset,
        displayedRows: pageItemCount,
        loadedGroups: groupedRowCount,
        groupsPerFullWindow: multiWindowPaging ? groupsPerFullWindow : null,
        moreSeatWindows,
      });
  const startIndex =
    effectiveTotalCount === 0 || pageItemCount === 0
      ? 0
      : pageOffset + 1;
  const endIndex =
    effectiveTotalCount === 0 || pageItemCount === 0
      ? 0
      : Math.min(pageOffset + pageItemCount, effectiveTotalCount);
  const handleNextPage = () => {
    if (directSeatPagination) {
      handlePageChange(pageOffset + pageSize);
      return;
    }
    handlePageChange(getNextGroupedPageOffset({
      pageOffset,
      pageSize,
      displayedRows: manageTicketTableRows.length,
      groupsPerFullWindow: serverPaginationActive && !serverGroupClientPaging ? groupsPerFullWindow : null,
    }));
  };

  const paginationUnitLabel = directSeatPagination ? 'tickets' : 'groups';
  const resultsRangeLabel =
    effectiveTotalCount === 0
      ? `Showing 0 of 0 ${paginationUnitLabel}`
      : `Showing ${startIndex} to ${endIndex} of ${effectiveTotalCount} ${paginationUnitLabel}`;

  useEffect(() => {
    if (!serverPaginationActive || serverGroupClientPaging) return;
    if (loadedWindowSeats < groupedFetchSize || loadedWindowGroups <= 0) return;
    if (seatWindowIndex !== 0) return;
    setGroupsPerFullWindow((prev) => (prev === loadedWindowGroups ? prev : loadedWindowGroups));
  }, [
    serverPaginationActive,
    serverGroupClientPaging,
    loadedWindowSeats,
    loadedWindowGroups,
    groupedFetchSize,
    seatWindowIndex,
  ]);

  const showExpandedCommission = activeTab === 'Sold';

  const tableColSpan =
    9 +
    (showPriceColumn ? 1 : 0) +
    (activeTab === 'Listed' ? 1 : 0) +
    (showDelistReturnEmailColumn ? 1 : 0) +
    (showMarketplaceColumn ? 1 : 0) +
    (showSharedColumn ? 1 : 0) -
    (hideSoldClientColumns ? 2 : 0) +
    (showListedFinancialColumns ? 3 : 0);

  const saveListedTicketCost = async (ticket: Ticket) => {
    if (!showListedFinancialColumns) return;
    const draft = listedCostDrafts[ticket.tp_id] ?? String(listedTicketCosts[ticket.tp_id] ?? '');
    const cost = draft.trim() === '' ? null : Number(draft);
    if (cost !== null && (!Number.isFinite(cost) || cost < 0)) {
      showError('Enter a valid non-negative ticket cost.');
      return;
    }
    setSavingListedCostId(ticket.tp_id);
    try {
      const { data } = await updateListedTicketCost({
        variables: { tp_id: ticket.tp_id, tp_cost: cost },
      });
      const updated = data?.update_ticket_placement?.returning?.[0];
      if (!updated) {
        throw new Error(`The ticket cost was not updated. Refresh the ${activeTab} tab and try again.`);
      }
      setListedTicketCosts((previous) => ({ ...previous, [ticket.tp_id]: updated.tp_cost }));
      setListedCostDrafts((previous) => {
        const next = { ...previous };
        delete next[ticket.tp_id];
        return next;
      });
      success('Ticket cost saved.');
    } catch (error: unknown) {
      showError(getErrorMessage(error, 'Unable to save ticket cost'));
    } finally {
      setSavingListedCostId(null);
    }
  };

  const getListedPotentialPayout = (ticket: Ticket) => {
    const listPrice = Number(
      activeTab === 'Sold' ? ticket.tp_sold_amount ?? ticket.tp_list_price : ticket.tp_list_price,
    ) || 0;
    const fee = listedTicketFees[ticket.tp_id]?.fee ?? Number.NaN;
    const pricingMode = String(ticket.u_pricing_mode || listedTicketFees[ticket.tp_id]?.pricingMode || '')
      .trim()
      .toLowerCase()
      .replace(/[\s_]+/g, '-');
    const feePercentage = pricingMode === 'self-pricing'
      ? Number.isFinite(fee) && fee > 0 && fee !== 20 ? fee : 15
      : Number.isFinite(fee) && fee > 0 ? fee : 20;
    return listPrice * (100 - feePercentage) / 100;
  };
  const groupingBoundariesPending = Boolean(groupingBoundaryKey) && !groupingBoundariesReady;
  // Apollo can finish before rows are applied. Keep the same spinner up through
  // that gap and the boundary fetch so the table does not appear, hide, and appear again.
  const awaitingTicketApply =
    !isEventGroupedView &&
    !isMailParserTpLookupView &&
    Boolean(data) &&
    !queryError &&
    !hasAppliedActiveTabDataRef.current;
  const ticketsTableLoading = (isEventGroupedView
    ? eventGroupedLoading
    : isMailParserTpLookupView
    ? mailParserErrorLoading
    : shouldShowManageTicketQueryLoader(
        loading,
        Boolean(data),
        isManageTicketSortRequestPending(sortRequestPendingKey, sortRequestContextKey),
      ) || isLoading) || groupingBoundariesPending || awaitingTicketApply;
  const isStatusOperationInProgress =
    statusTableBusyCount > 0 ||
    bulkStatusChangeLoading ||
    bulkSoldLoading ||
    bulkListLoading ||
    statusChangeLoading ||
    listedModalLoading;
  const statusOperationMessage =
    statusTableBusyCount > 0 ? statusTableBusyMessage : 'Updating ticket status…';

  const isTicketRow = (row: ManageTicketTableRow): row is Extract<ManageTicketTableRow, { kind: 'ticket' }> =>
    row.kind === 'ticket';

  const visibleTableTickets = useMemo(() => getManageTicketDisplayTickets(manageTicketTableRows), [manageTicketTableRows]);
  const visibleTicketIds = useMemo(() => visibleTableTickets.map((ticket) => ticket.tp_id), [visibleTableTickets]);

  useEffect(() => {
    if (bulkSelectedTicketIds.size === 0) return;
    const visibleSet = new Set(visibleTicketIds);
    const nextSelection = new Set(Array.from(bulkSelectedTicketIds).filter(id => visibleSet.has(id)));
    if (nextSelection.size !== bulkSelectedTicketIds.size) {
      setBulkSelectedTicketIds(nextSelection);
    }
  }, [visibleTicketIds, bulkSelectedTicketIds]);

  const selectedTicketsList = useMemo(() => {
    return visibleTableTickets.filter((ticket) => bulkSelectedTicketIds.has(ticket.tp_id));
  }, [visibleTableTickets, bulkSelectedTicketIds]);

  const selectedSeatTicketsList = useMemo(() => {
    return filteredTickets.filter((ticket) => selectedTpIds.includes(ticket.tp_id));
  }, [filteredTickets, selectedTpIds]);

  const statusFilterButtonLabel = useMemo(() => {
    if (
      filterStatus === 'All Verification' ||
      filterStatus === 'All Listed' ||
      filterStatus === 'All Sold' ||
      filterStatus === 'All Delisted' ||
      filterStatus === 'All Listing Request Cancelled' ||
      filterStatus === 'All Expired' ||
      filterStatus === 'All Unsold'
    ) {
      return 'Filter Status';
    }
    return filterStatusOptions.find(opt => opt.value === filterStatus)?.label || 'Filter Status';
  }, [filterStatus, filterStatusOptions]);

  const handleUserClick = (userId: string) => {
    navigate(`/user/${userId}?tab=tickets`);
  };

  const getPriceConfirmationChanges = (): Array<{ currentPrice: unknown; proposedPrice: number }> => {
    if (priceConfirmationFlow === 'single' && selectedTicketForPrice) {
      return [{
        currentPrice: selectedTicketForPrice.tp_list_price,
        proposedPrice: Number(parseFloat(newPrice)) || 0,
      }];
    }
    if (priceConfirmationFlow === 'listed' && listedModalTicket) {
      return [{
        currentPrice: listedModalTicket.tp_list_price,
        proposedPrice: Number(parseFloat(listedPrice)) || 0,
      }];
    }
    if (
      priceConfirmationFlow === 'bulkPrice' ||
      priceConfirmationFlow === 'bulkList' ||
      priceConfirmationFlow === 'bulkStatus'
    ) {
      const prices = priceConfirmationFlow === 'bulkPrice'
        ? bulkPriceIndividual
        : priceConfirmationFlow === 'bulkList'
          ? bulkListedPrices
          : bulkStatusListingPrices;
      return bulkSelectedTickets.map((ticket) => ({
        currentPrice: ticket.tp_list_price,
        proposedPrice: Number(parseFloat(String(prices[ticket.tp_id] || ''))) || 0,
      }));
    }
    return [];
  };

  return (
    <div className={isDarkMode ? styles.dark : undefined}>
      {(isStatusOperationInProgress || vlProductionMapLoading) && (
        <div className={styles.vlBusyBannerFixed} role="status" aria-live="polite">
          <div className={styles.vlBusySpinner} />
          <div className={styles.vlBusyBannerText}>
            <strong>
              {vlProductionMapLoading && !isStatusOperationInProgress
                ? 'Submitting to Victory Live…'
                : statusOperationMessage}
            </strong>
            <span>Please wait — this stays at the top until it finishes.</span>
          </div>
        </div>
      )}
      {/* --- Listed Modal UI --- */}
      {showListedModal && listedModalTicket && (() => {
        const currentSt = listedModalTicket.tp_status || '';
        const getCtBadgeClass = (st: string) => {
          const s = (st || '').toLowerCase().replace(/\s/g, '');
          if (s === 'verified') return styles.ctStatusBadgeVerified;
          if (s === 'list' || s === 'listed') return styles.ctStatusBadgeListed;
          if (s === 'sold') return styles.ctStatusBadgeSold;
          if (s === 'tobeverified') return styles.ctStatusBadgeToBeVerified;
          if (s === 'invalid') return styles.ctStatusBadgeInvalid;
          return styles.ctStatusBadgeDefault;
        };
        return (
          <div className={styles.modalOverlay}>
            <div className={styles.ctModalContent} onClick={e => e.stopPropagation()}>
              <div className={styles.ctModalHeader}>
                <div className={styles.ctModalTitleRow}>
                  <RefreshCcw className={styles.ctRotateIcon} size={16} />
                  <span className={styles.ctModalTitle}>Change Ticket Status</span>
                </div>
                <button className={styles.ctModalClose} onClick={() => setShowListedModal(false)}>×</button>
              </div>
              <div className={styles.ctModalBody}>
                <div className={styles.ctEventName}>{listedModalTicket.e_name}</div>
                <div className={styles.ctEventDetails}>
                  Section {listedModalTicket.tp_section || 'N/A'} • Row {listedModalTicket.tp_row || 'N/A'} • Seats {listedModalTicket.tp_seat_no || 'N/A'}
                </div>
                <div className={styles.ctEventDate}>
                  {formatDate(listedModalTicket.e_date, listedModalTicket.e_time_zone)}{listedModalTicket.e_date ? ` • ${formatTime(listedModalTicket.e_date, listedModalTicket.e_time_zone)}` : ''}
                </div>

                <div className={styles.ctCurrentStatusRow}>
                  <span className={styles.ctCurrentStatusLabel}>Current Status:</span>
                  <span className={`${styles.ctStatusBadge} ${getCtBadgeClass(currentSt)}`}>
                    {getStatusIcon(currentSt)} {getStatusLabel(currentSt)}
                  </span>
                </div>

                <div className={styles.ctNewStatusTitle}>Select New Status:</div>
                <div className={styles.ctStatusOptions}>
                  <button className={`${styles.ctStatusOption} ${styles.ctStatusOptionSelected}`}>
                    Listed (Non-VL)
                  </button>
                </div>

                <div className={styles.ctTransitionVisual}>
                  <span className={`${styles.ctStatusBadge} ${getCtBadgeClass(currentSt)}`}>
                    {getStatusIcon(currentSt)} {getStatusLabel(currentSt)}
                  </span>
                  <ArrowRight size={16} className={styles.ctTransArrow} />
                  <span className={`${styles.ctStatusBadge} ${styles.ctStatusBadgeListed}`}>
                    {getStatusIcon('List')} Listed (Non-VL)
                  </span>
                </div>

                <div className={styles.ctPriceLabel}>Listing Price (required):</div>
                <div className={styles.ctPriceInputWrapper}>
                  <span className={styles.ctPriceDollar}>$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    className={styles.ctPriceInput}
                    value={listedPrice}
                    onChange={e => setListedPrice(sanitizePriceInput(e.target.value))}
                    placeholder="Enter listing price"
                  />
                </div>
              </div>
              <div className={styles.ctModalFooter}>
                <button className={styles.ctBackBtn} onClick={() => setShowListedModal(false)}>Back</button>
                <button
                  className={`${styles.ctConfirmBtn} ${(!listedPrice || listedModalLoading) ? styles.ctConfirmBtnDisabled : ''}`}
                  disabled={!listedPrice || listedModalLoading}
                  onClick={() => { void handleListedModalConfirm(); }}
                >
                  {listedModalLoading ? 'Saving...' : 'Confirm Change'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* --- Sold Modal UI (aligned with List Tickets → Sold Details / markAsSold) --- */}
      {showSoldModal && soldModalTicket && (() => {
        const currentSt = soldModalTicket.tp_status || '';
        const getCtBadgeClass = (st: string) => {
          const s = (st || '').toLowerCase().replace(/\s/g, '');
          if (s === 'verified') return styles.ctStatusBadgeVerified;
          if (s === 'list' || s === 'listed') return styles.ctStatusBadgeListed;
          if (s === 'sold') return styles.ctStatusBadgeSold;
          if (s === 'tobeverified') return styles.ctStatusBadgeToBeVerified;
          if (s === 'invalid') return styles.ctStatusBadgeInvalid;
          return styles.ctStatusBadgeDefault;
        };
        const listPriceNum = Number(soldModalTicket.tp_list_price) || 0;
        const selectedMp = marketplacesList.find(m => m.id === soldMarketplaceId);
        const logitixParsed = parseFloat(String(soldLogitixAmount || '').trim());
        const hasLogitixInput = !isNaN(logitixParsed) && logitixParsed > 0;
        const expectedFromLogitix =
          selectedMp && listPriceNum
            ? Math.round(listPriceNum * (100 - selectedMp.vl_fee_percentage)) / 100
            : null;
        const quickPct =
          selectedMp && soldFeeConfigReady
            ? effectiveSoldQuickCutPercent(
                selectedMp.vl_fee_percentage,
                soldFeeMode,
                soldSuperClientPercent,
              )
            : 0;
        const quickCutDollars =
          selectedMp && listPriceNum && soldFeeConfigReady
            ? Number(roundToFixed(listPriceNum * (quickPct / 100), 2))
            : 0;
        const receivedForSettlement =
          hasLogitixInput ? logitixParsed : expectedFromLogitix != null ? expectedFromLogitix : null;
        const usingEnteredLogitix = hasLogitixInput;
        const settlementAmount =
          receivedForSettlement != null &&
          selectedMp &&
          soldFeeConfigReady &&
          quickCutDollars != null
            ? Math.round((receivedForSettlement - quickCutDollars) * 100) / 100
            : null;
        const soldModalLoading = statusChangeLoading || markAsSoldLoading;
        const confirmDisabled =
          !soldFeeConfigReady ||
          soldModalLoading ||
          !soldMarketplaceId ||
          !String(soldLogitixAmount || '').trim() ||
          settlementAmount == null ||
          settlementAmount <= 0;
        return (
          <div className={styles.modalOverlay}>
            <div className={styles.ctModalContent} onClick={e => e.stopPropagation()}>
              <div className={styles.ctModalHeader}>
                <div className={styles.ctModalTitleRow}>
                  <RefreshCcw className={styles.ctRotateIcon} size={16} />
                  <span className={styles.ctModalTitle}>Change Ticket Status</span>
                </div>
                <button type="button" className={styles.ctModalClose} onClick={closeSoldModal}>×</button>
              </div>
              <div className={styles.ctModalBody}>
                <div className={styles.ctEventName}>{soldModalTicket.e_name}</div>
                <div className={styles.ctEventDetails}>
                  Section {soldModalTicket.tp_section || 'N/A'} • Row {soldModalTicket.tp_row || 'N/A'} • Seats {soldModalTicket.tp_seat_no || 'N/A'}
                </div>
                <div className={styles.ctEventDate}>
                  {formatDate(soldModalTicket.e_date, soldModalTicket.e_time_zone)}{soldModalTicket.e_date ? ` • ${formatTime(soldModalTicket.e_date, soldModalTicket.e_time_zone)}` : ''}
                </div>

                <div className={styles.ctCurrentStatusRow} style={{ marginTop: 8 }}>
                  <span className={styles.ctCurrentStatusLabel}>List price:</span>
                  <b>${formatNumberWithCommas(listPriceNum)}</b>
                </div>

                <div className={styles.ctCurrentStatusRow}>
                  <span className={styles.ctCurrentStatusLabel}>Current Status:</span>
                  <span className={`${styles.ctStatusBadge} ${getCtBadgeClass(currentSt)}`}>
                    {getStatusIcon(currentSt)} {getStatusLabel(currentSt)}
                  </span>
                </div>

                <div className={styles.ctNewStatusTitle}>Select New Status:</div>
                <div className={styles.ctStatusOptions}>
                  <button type="button" className={`${styles.ctStatusOption} ${styles.ctStatusOptionSelected}`}>
                    Sold
                  </button>
                </div>

                {!soldFeeConfigReady ? (
                  <div style={{ margin: '12px 0', fontSize: 14 }}>Loading fee settings…</div>
                ) : null}

                <div style={{ marginBottom: 16 }}>
                  <div className={styles.ctPriceLabel} style={{ marginBottom: 8 }}>Marketplace:</div>
                  <select
                    className={styles.ctMarketplaceSelect}
                    value={soldMarketplaceId}
                    disabled={!soldFeeConfigReady || marketplacesList.length === 0}
                    onChange={e => setSoldMarketplaceId(e.target.value)}
                  >
                    <option value="">
                      {marketplacesList.length === 0 ? 'No active marketplaces available' : 'Choose a marketplace...'}
                    </option>
                    {marketplacesList.map(mp => (
                      <option key={mp.id} value={mp.id}>{mp.name} ({mp.vl_fee_percentage}%)</option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <div className={styles.ctPriceLabel}>Amount received from Logitix</div>
                 
                  <div className={styles.cttPriceInputWrapper}>
                    <span className={styles.ctPriceDollar}>$</span>
                    <input
                    readOnly
                      type="text"
                      inputMode="decimal"
                      className={styles.ctPriceInput}
                     
                      value={soldLogitixAmount}
                     
                    />
                  </div>
                </div>

                <div className={styles.ctTransitionVisual}>
                  <span className={`${styles.ctStatusBadge} ${getCtBadgeClass(currentSt)}`}>
                    {getStatusIcon(currentSt)} {getStatusLabel(currentSt)}
                  </span>
                  <ArrowRight size={16} className={styles.ctTransArrow} />
                  <span className={`${styles.ctStatusBadge} ${styles.ctStatusBadgeSold}`}>
                    {getStatusIcon('Sold')} Sold
                  </span>
                </div>

                {selectedMp && soldFeeConfigReady ? (
                  <div className={styles.ctFeeBreakdown}>
                    <div><b>Sold details (preview)</b></div>
                    <div>Marketplace: {selectedMp.name} ({selectedMp.vl_fee_percentage}%)</div>
                    <div>
                      QuickAsyst fee{selectedMp ? ` (${quickPct}% of list)` : ''}:{' '}
                      <span style={{ color: '#b91c1c' }}>
                        {quickCutDollars != null ? `-$${formatNumberWithCommas(quickCutDollars)}` : '—'}
                      </span>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <span className={styles.ctCurrentStatusLabel}>Amount settled to user</span>
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 700 }}>
                      {settlementAmount != null ? `$${formatNumberWithCommas(settlementAmount)}` : '—'}
                    </div>
                    {receivedForSettlement != null && quickCutDollars != null && settlementAmount != null ? (
                      <div style={{ fontSize: 12, marginTop: 6 }}>
                        ${formatNumberWithCommas(receivedForSettlement)}{' '}
                        {usingEnteredLogitix ? 'received' : 'expected'} − $
                        {formatNumberWithCommas(quickCutDollars)} QuickAsyst fee
                        {usingEnteredLogitix ? '' : ' (preview)'}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <div className={styles.ctModalFooter}>
                <button type="button" className={styles.ctBackBtn} onClick={closeSoldModal}>Back</button>
                <button
                  type="button"
                  className={`${styles.ctConfirmBtn} ${confirmDisabled ? styles.ctConfirmBtnDisabled : ''}`}
                  disabled={confirmDisabled}
                  onClick={handleSoldModalConfirm}
                >
                  {soldModalLoading ? 'Saving…' : 'Confirm Change'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {seatModeDialog.dialog}
      {vlSupportBulk && (
        <div className={styles.modalOverlay} onClick={() => setVlSupportBulk(null)}>
          <div
            className={styles.modalContent}
            onClick={(event) => event.stopPropagation()}
            style={{ maxWidth: 760, width: '95%', maxHeight: '90vh', overflowY: 'auto', padding: 0 }}
          >
            <VlSupportPanel
              tpIds={vlSupportBulk.tpIds}
              bulk={vlSupportBulk.bulk}
              trackingAvailable={vlSupportAvailable}
              ownerInitials={myVlSupportInitials}
              onClose={() => setVlSupportBulk(null)}
              onError={() => undefined}
              onSent={(result) => {
                if (result.redirectedForNonProd) {
                  info(`Test environment: email went to ${result.sentTo ?? 'the QA inbox'}, not Victory Live.`);
                } else {
                  success(`Email sent to ${result.sentTo ?? 'Victory Live'}`);
                }
                if (result.warning) info(result.warning);
                setVlSupportBulk(null);
                clearSelectionState();
                setVlSupportReloadToken((n) => n + 1);
                if (vlSupportFilter !== 'all') void handleRefresh();
              }}
            />
          </div>
        </div>
      )}

      {/* Main Component UI - Exact match to first image */}
      <div className={`${styles.container} ${isDarkMode ? styles.dark : ""}`}>
        <div className={styles.mainContent}>
          {/* Header Section */}
          <div className={styles.headerSection}>
            <div className={styles.headerLeft}>
              <p className={styles.subTitle}>All Tickets</p>
              <h1 className={styles.mainTitle}>Manage Tickets</h1>
            </div>
            <div className={styles.headerRight}>
              <button className={styles.iconButton} onClick={handleRefresh} title="Refresh">
                <RefreshCcw size={16} />
              </button>
              <button className={styles.iconButton} onClick={() => setIsDarkMode(!isDarkMode)} title="Toggle theme">
                {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
              </button>
            </div>
          </div>

          {/* Tracking Issues Cards (admin / sales rep ops — not shown for self-pricing super user) */}
          {!isSuperUser && (
          <div className={styles.trackingBar} aria-label="Tracking issues">
            <button
              type="button"
              className={`${styles.trackingChip} ${styles.trackingChipRed}`}
              title="Listing Request Expired: never transferred, event started"
              onClick={() => handleOpenCardDetailModal("LISTING_EXPIRED")}
            >
              <Clock size={14} />
              <span className={styles.trackingChipLabel}>Listing Request Expired</span>
              <span className={styles.trackingChipCount}>{trackingCounts.listingExpired}</span>
              <span className={styles.trackingChipHint}>Never transferred, event started</span>
              <ExternalLink size={12} />
            </button>
            <button
              type="button"
              className={`${styles.trackingChip} ${styles.trackingChipOrange}`}
              title="Fail to Transfer: 48+ hours in To Be Verified"
              onClick={() => handleOpenCardDetailModal("FAILED_TRANSFER")}
            >
              <AlertCircle size={14} />
              <span className={styles.trackingChipLabel}>Fail to Transfer</span>
              <span className={styles.trackingChipCount}>{trackingCounts.failedTransfer}</span>
              <span className={styles.trackingChipHint}>48+ hours in To Be Verified</span>
              <ExternalLink size={12} />
            </button>
          </div>
          )}

          {/* Tabs, filters and selection bar — pinned on Listed so they stay visible while scrolling */}
          <div ref={stickyToolbarRef} className={pinListedToolbar ? styles.stickyToolbar : undefined}>
          {/* Tabs + Filter Row — combined */}
          <div className={styles.tabsAndFilterRow}>
            <div className={styles.tabsGroup}>
              {!isSuperUser && (
              <button
                className={`${styles.statusTabBtn} ${activeTab === 'Verification' ? styles.activeTab : ''}`}
                onClick={() => handleTabChange('Verification')}
              >
                Verification ({tabCounts.Verification})
              </button>
              )}
              <button
                className={`${styles.statusTabBtn} ${activeTab === 'Listed' ? styles.activeTab : ''}`}
                onClick={() => handleTabChange('Listed')}
              >
                Listed ({tabCounts.Listed})
              </button>
              <button
                className={`${styles.statusTabBtn} ${activeTab === 'Sold' ? styles.activeTab : ''}`}
                onClick={() => handleTabChange('Sold')}
              >
                Sold ({tabCounts.Sold})
              </button>
              <button
                className={`${styles.statusTabBtn} ${activeTab === 'Delisted' ? styles.activeTab : ''}`}
                onClick={() => handleTabChange('Delisted')}
              >
                Delisted ({tabCounts.Delisted})
              </button>
              {!isSuperUser && (
              <button
                className={`${styles.statusTabBtn} ${activeTab === 'Listing Request Cancelled' ? styles.activeTab : ''}`}
                onClick={() => handleTabChange('Listing Request Cancelled')}
              >
                Listing Request Cancelled ({tabCounts['Listing Request Cancelled']})
              </button>
              )}
              <button
                className={`${styles.statusTabBtn} ${activeTab === 'Unsold' ? styles.activeTab : ''}`}
                onClick={() => handleTabChange('Unsold')}
              >
                Unsold ({tabCounts.Unsold})
              </button>
              {!isSuperUser && (
              <button
                className={`${styles.statusTabBtn} ${activeTab === 'Listing Expired' ? styles.activeTab : ''}`}
                onClick={() => handleTabChange('Listing Expired')}
              >
                Listing Expired ({isMarketingUser ? (tabCounts['Listing Expired'] ?? 0) : trackingCounts.listingExpired})
              </button>
              )}
              {!isSuperUser && <button
                className={`${styles.statusTabBtn} ${activeTab === 'Deleted' ? styles.activeTab : ''}`}
                onClick={() => handleTabChange('Deleted')}
              >
                Deleted ({tabCounts.Deleted})
              </button>}
            </div>

            <div className={styles.filterGroup}>
              <div className={styles.filterDropdownWrapper}>
                <button
                  className={styles.filterStatusBtn}
                  type="button"
                  onClick={() => {
                    setShowFilterDropdown(!showFilterDropdown);
                    setShowClientDropdown(false);
                    setShowBarcodeFilterDropdown(false);
                  }}
                >
                  <Filter size={13} style={{ marginRight: 4 }} /> 
                  {statusFilterButtonLabel}
                </button>
                {showFilterDropdown && (
                  <div className={styles.filterDropdown}>
                    {filterStatusOptions.map(option => (
                      <div
                        key={option.value}
                        className={`${styles.filterDropdownItem} ${filterStatus === option.value ? styles.filterDropdownItemActive : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFilterStatus(option.value);
                          setShowFilterDropdown(false);
                          setPageOffset(0);
                        }}
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {shouldShowBarcodeStatus(activeTab, isSuperUser) && !isSelfPricingListedView && (
              <div className={styles.filterDropdownWrapper}>
                <button
                  className={styles.filterStatusBtn}
                  type="button"
                  onClick={() => {
                    setShowBarcodeFilterDropdown(!showBarcodeFilterDropdown);
                    setShowFilterDropdown(false);
                    setShowClientDropdown(false);
                    setShowStripeAccountFilterDropdown(false);
                  }}
                >
                  <ScanBarcode size={13} />
                  {renderBarcodeFilterIcon(
                    BARCODE_ATTACHED_FILTER_OPTIONS.find((option) => option.value === barcodeFilter)?.icon,
                  )}
                  {barcodeFilterButtonLabel(barcodeFilter)}
                </button>
                {showBarcodeFilterDropdown && (
                  <div className={styles.filterDropdown}>
                    {BARCODE_ATTACHED_FILTER_OPTIONS.map((option) => (
                      <div
                        key={option.value}
                        className={`${styles.filterDropdownItem} ${barcodeFilter === option.value ? styles.filterDropdownItemActive : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setBarcodeFilter(option.value);
                          setShowBarcodeFilterDropdown(false);
                          setPageOffset(0);
                        }}
                      >
                        <span className={styles.filterDropdownItemInner}>
                          {option.icon ? renderBarcodeFilterIcon(option.icon, 14) : <ScanBarcode size={14} />}
                          {option.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              )}
              {activeTab === 'Sold' && !isSuperUserSoldView && (
                <div className={styles.filterDropdownWrapper}>
                  <button
                    className={styles.filterStatusBtn}
                    type="button"
                    onClick={() => {
                      setShowStripeAccountFilterDropdown(!showStripeAccountFilterDropdown);
                      setShowFilterDropdown(false);
                      setShowClientDropdown(false);
                      setShowBarcodeFilterDropdown(false);
                    }}
                  >
                    <Filter size={13} style={{ marginRight: 4 }} />
                    {getStripeAccountFilterButtonLabel(stripeAccountFilter)}
                  </button>
                  {showStripeAccountFilterDropdown && (
                    <div className={styles.filterDropdown}>
                      {STRIPE_ACCOUNT_FILTER_OPTIONS.map((option) => (
                        <div
                          key={option.value}
                          className={`${styles.filterDropdownItem} ${stripeAccountFilter === option.value ? styles.filterDropdownItemActive : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setStripeAccountFilter(option.value);
                            setShowStripeAccountFilterDropdown(false);
                            setPageOffset(0);
                          }}
                        >
                          {option.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'Listed' && (
                <>
                  <div className={styles.filterDropdownWrapper}>
                    <button
                      className={`${styles.filterStatusBtn} ${vlShareFilter !== 'all' ? styles.filterDropdownItemActive : ''}`}
                      type="button"
                      onClick={() => {
                        setShowVlShareFilterDropdown((prev) => !prev);
                        setShowVlSupportFilterDropdown(false);
                        setShowPidStatusFilterDropdown(false);
                        setShowPricingMethodFilterDropdown(false);
                        setShowLastPricedFilterDropdown(false);
                        setShowClientDropdown(false);
                        setShowFilterDropdown(false);
                        setShowBarcodeFilterDropdown(false);
                      }}
                    >
                      <Share2 size={13} style={{ marginRight: 4 }} />
                      {vlShareFilter === 'all'
                        ? 'Share'
                        : vlShareFilter === 'shared'
                          ? 'Shared'
                          : 'Unshared'}
                    </button>
                    {showVlShareFilterDropdown && (
                      <div className={styles.filterDropdown}>
                        {[
                          { value: 'all' as const, label: 'All' },
                          { value: 'shared' as const, label: 'Shared' },
                          { value: 'unshared' as const, label: 'Unshared' },
                        ].map((option) => (
                          <div
                            key={option.value}
                            className={`${styles.filterDropdownItem} ${vlShareFilter === option.value ? styles.filterDropdownItemActive : ''}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              setVlShareFilter(option.value);
                              setShowVlShareFilterDropdown(false);
                              setPageOffset(0);
                            }}
                          >
                            {option.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {vlSupportAvailable && (
                  <div className={styles.filterDropdownWrapper}>
                    <button
                      className={`${styles.filterStatusBtn} ${vlSupportFilter !== 'all' ? styles.filterDropdownItemActive : ''}`}
                      type="button"
                      onClick={() => {
                        setShowVlSupportFilterDropdown((prev) => !prev);
                        setShowVlShareFilterDropdown(false);
                        setShowPidStatusFilterDropdown(false);
                        setShowPricingMethodFilterDropdown(false);
                        setShowLastPricedFilterDropdown(false);
                        setShowClientDropdown(false);
                        setShowFilterDropdown(false);
                        setShowBarcodeFilterDropdown(false);
                      }}
                    >
                      <Mail size={13} style={{ marginRight: 4 }} />
                      {vlSupportFilter === 'all'
                        ? 'VL Support'
                        : vlSupportFilter === 'any'
                          ? 'Any Support Ticket'
                          : vlSupportFilter === 'contacted'
                            ? 'Open Support Ticket'
                            : vlSupportFilter === 'completed'
                              ? 'Completed Support Ticket'
                              : 'Not Contacted'}
                    </button>
                    {showVlSupportFilterDropdown && (
                      <div className={styles.filterDropdown}>
                        {[
                          { value: 'all' as const, label: 'All tickets' },
                          { value: 'any' as const, label: 'Any support ticket' },
                          { value: 'contacted' as const, label: 'Open support ticket' },
                          { value: 'completed' as const, label: 'Completed support ticket' },
                          { value: 'none' as const, label: 'Not contacted' },
                        ].map((option) => (
                          <div
                            key={option.value}
                            className={`${styles.filterDropdownItem} ${vlSupportFilter === option.value ? styles.filterDropdownItemActive : ''}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              setVlSupportFilter(option.value);
                              setShowVlSupportFilterDropdown(false);
                              setPageOffset(0);
                            }}
                          >
                            {option.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  )}

                  {vlSupportAvailable && (
                  <div className={styles.filterDropdownWrapper}>
                    <button
                      className={`${styles.filterStatusBtn} ${vlSupportOwner !== 'company' ? styles.filterDropdownItemActive : ''}`}
                      type="button"
                      onClick={() => {
                        setShowVlSupportOwnerDropdown((prev) => !prev);
                        setShowVlSupportFilterDropdown(false);
                        setShowVlShareFilterDropdown(false);
                        setShowPidStatusFilterDropdown(false);
                        setShowPricingMethodFilterDropdown(false);
                        setShowLastPricedFilterDropdown(false);
                        setShowClientDropdown(false);
                        setShowFilterDropdown(false);
                        setShowBarcodeFilterDropdown(false);
                      }}
                    >
                      <Users size={13} style={{ marginRight: 4 }} />
                      {vlSupportOwner === 'company'
                        ? 'Support Owner'
                        : vlSupportOwner === 'mine'
                          ? `Mine${myVlSupportInitials ? ` (${myVlSupportInitials})` : ''}`
                          : vlSupportOwner}
                    </button>
                    {showVlSupportOwnerDropdown && (
                      <div className={styles.filterDropdown}>
                        {[
                          { value: 'company', label: 'Company (everyone)' },
                          { value: 'mine', label: `My tickets${myVlSupportInitials ? ` (${myVlSupportInitials})` : ''}` },
                          ...VL_SUPPORT_OWNERS.map((initials) => ({ value: initials as string, label: initials })),
                        ].map((option) => (
                          <div
                            key={option.value}
                            className={`${styles.filterDropdownItem} ${vlSupportOwner === option.value ? styles.filterDropdownItemActive : ''}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              setVlSupportOwner(option.value);
                              setShowVlSupportOwnerDropdown(false);
                              setPageOffset(0);
                            }}
                          >
                            {option.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  )}

                  {!isSelfPricingListedView && (
                  <div className={styles.filterDropdownWrapper}>
                    <button
                      className={`${styles.filterStatusBtn} ${pidStatusFilter !== 'all' ? styles.filterDropdownItemActive : ''}`}
                      type="button"
                      onClick={() => {
                        setShowPidStatusFilterDropdown((prev) => !prev);
                        setShowVlShareFilterDropdown(false);
                        setShowPricingMethodFilterDropdown(false);
                        setShowLastPricedFilterDropdown(false);
                        setShowClientDropdown(false);
                        setShowFilterDropdown(false);
                        setShowBarcodeFilterDropdown(false);
                      }}
                    >
                      <Filter size={13} style={{ marginRight: 4 }} />
                      {pidStatusFilter === 'all'
                        ? 'PID Status'
                        : `PID Status: ${pidStatusFilter === 'available' ? 'Available' : 'Not Available'}`}
                    </button>
                    {showPidStatusFilterDropdown && (
                      <div className={styles.filterDropdown}>
                        {[
                          { value: 'all' as const, label: 'All' },
                          { value: 'available' as const, label: 'Available' },
                          { value: 'not_available' as const, label: 'Not Available' },
                        ].map((option) => (
                          <div
                            key={option.value}
                            className={`${styles.filterDropdownItem} ${pidStatusFilter === option.value ? styles.filterDropdownItemActive : ''}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              setPidStatusFilter(option.value);
                              setShowPidStatusFilterDropdown(false);
                              setPageOffset(0);
                            }}
                          >
                            {option.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  )}

                  {!isSelfPricingListedView && (
                  <div className={styles.filterDropdownWrapper}>
                    <button
                      className={`${styles.filterStatusBtn} ${onHoldFilter ? styles.onHoldFilterBtnActive : ''} ${styles.onHoldFilterBtn}`}
                      type="button"
                      onClick={() => {
                        setOnHoldFilter((prev) => !prev);
                        setPageOffset(0);
                        setShowLastPricedFilterDropdown(false);
                        setShowClientDropdown(false);
                        setShowFilterDropdown(false);
                        setShowBarcodeFilterDropdown(false);
                      }}
                    >
                      On Hold
                      <span className={styles.onHoldCountBadge} aria-label={`${onHoldCount} tickets on hold`}>
                        {onHoldCount}
                      </span>
                    </button>
                  </div>
                  )}

                  {!isSelfPricingListedView && <div className={styles.filterDropdownWrapper}>
                    <button
                      className={`${styles.filterStatusBtn} ${pricingMethodFilter !== 'all' ? styles.filterDropdownItemActive : ''}`}
                      type="button"
                      onClick={() => {
                        setShowPricingMethodFilterDropdown((prev) => !prev);
                        setShowClientDropdown(false);
                        setShowFilterDropdown(false);
                        setShowBarcodeFilterDropdown(false);
                        setShowLastPricedFilterDropdown(false);
                      }}
                    >
                      <DollarSign size={13} style={{ marginRight: 4 }} />
                      {pricingMethodFilter === 'all'
                        ? 'Pricing Method'
                        : pricingMethodFilter === 'MANUAL'
                          ? 'Manual'
                          : pricingMethodFilter === 'NONE'
                            ? 'Nothing'
                            : 'Follow the Market'}
                    </button>
                    {showPricingMethodFilterDropdown && (
                      <div className={styles.filterDropdown}>
                        {[
                          { value: 'all' as const, label: 'All Pricing Methods' },
                          { value: 'MANUAL' as const, label: 'Manual' },
                          { value: 'FOLLOW_MARKET' as const, label: 'Follow the Market' },
                          { value: 'NONE' as const, label: 'Nothing' },
                        ].map((option) => (
                          <div
                            key={option.value}
                            className={`${styles.filterDropdownItem} ${pricingMethodFilter === option.value ? styles.filterDropdownItemActive : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setPricingMethodFilter(option.value);
                              setShowPricingMethodFilterDropdown(false);
                              setPageOffset(0);
                            }}
                          >
                            {option.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>}

                  <div className={styles.filterDropdownWrapper}>
                    <button
                      className={`${styles.filterStatusBtn} ${
                        lastPricedFilter.length || lastPricedFrom || lastPricedTo || lastPricedCustomFilter
                          ? styles.filterDropdownItemActive
                          : ''
                      }`}
                      type="button"
                      onClick={() => {
                        setShowLastPricedFilterDropdown((prev) => !prev);
                        setShowClientDropdown(false);
                        setShowFilterDropdown(false);
                        setShowBarcodeFilterDropdown(false);
                      }}
                    >
                      <Filter size={13} style={{ marginRight: 4 }} />
                      {lastPricedFilterButtonLabel}
                      {(lastPricedFilter.length || lastPricedFrom || lastPricedTo || lastPricedCustomFilter) && (
                        <span className={styles.lastPricedFilterBadge}>
                          {lastPricedFilter.length + (lastPricedFrom || lastPricedTo ? 1 : 0) + (lastPricedCustomFilter ? 1 : 0)}
                        </span>
                      )}
                    </button>
                    {showLastPricedFilterDropdown && (
                      <div className={`${styles.filterDropdown} ${styles.lastPricedDropdown}`}>
                        {LAST_PRICED_BUCKET_OPTIONS.map((option) => {
                          const checked = lastPricedFilter.includes(option.label);
                          return (
                            <div
                              key={option.value}
                              className={`${styles.filterDropdownItem} ${checked ? styles.filterDropdownItemActive : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setLastPricedFilter((prev) => {
                                  const next = prev.includes(option.label)
                                    ? prev.filter((value) => value !== option.label)
                                    : [...prev, option.label];
                                  setPageOffset(0);
                                  return next;
                                });
                              }}
                            >
                              <span>{option.label}</span>
                            </div>
                          );
                        })}
                        <div
                          className={`${styles.filterDropdownItem} ${
                            showLastPricedRangePicker || lastPricedFrom || lastPricedTo
                              ? styles.filterDropdownItemActive
                              : ''
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowLastPricedRangePicker((prev) => !prev);
                          }}
                        >
                          <span className={styles.lastPricedRangeOptionLabel}>
                            <Calendar size={13} />
                            Date & Time Range
                          </span>
                        </div>
                        {(showLastPricedRangePicker || lastPricedFrom || lastPricedTo) && (
                          <div className={styles.lastPricedRange}>
                            <label className={styles.lastPricedRangeField}>
                              <span>From Date & Time</span>
                              <input
                                type="datetime-local"
                                value={lastPricedFrom}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleLastPricedRangeChange('from', e.target.value);
                                }}
                              />
                            </label>
                            <label className={styles.lastPricedRangeField}>
                              <span>To Date & Time</span>
                              <input
                                type="datetime-local"
                                value={lastPricedTo}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleLastPricedRangeChange('to', e.target.value);
                                }}
                              />
                            </label>
                            {lastPricedRangeError && (
                              <div className={styles.lastPricedRangeError}>{lastPricedRangeError}</div>
                            )}
                          </div>
                        )}
                        <div className={styles.lastPricedCustomFilter}>
                          <label className={styles.lastPricedCustomUnit}>
                            <select
                              value={lastPricedCustomWindow}
                              aria-label="Last Priced window"
                              onChange={(event) => {
                                event.stopPropagation();
                                setLastPricedCustomWindow(event.target.value as 'within' | 'outside');
                                setLastPricedCustomError('');
                              }}
                            >
                              <option value="within">Within</option>
                              <option value="outside">Outside</option>
                            </select>
                          </label>
                          <label className={styles.lastPricedCustomInput}>
                            <span>last</span>
                            <input
                              type="number"
                              min="0.01"
                              step="any"
                              inputMode="decimal"
                              value={lastPricedCustomValue}
                              aria-label="Last Priced age"
                              aria-invalid={Boolean(lastPricedCustomError)}
                              aria-describedby={lastPricedCustomError ? 'last-priced-custom-error' : undefined}
                              onChange={(event) => {
                                const value = event.target.value;
                                if (/^\d*\.?\d*$/.test(value)) {
                                  setLastPricedCustomValue(value);
                                  setLastPricedCustomError('');
                                }
                              }}
                            />
                          </label>
                          <label className={styles.lastPricedCustomUnit}>
                            <select
                              value={lastPricedCustomUnit}
                              aria-label="Last Priced time unit"
                              onChange={(event) => {
                                setLastPricedCustomUnit(event.target.value as 'Hours' | 'Minutes' | 'Days');
                                setLastPricedCustomError('');
                              }}
                            >
                              <option value="Hours">Hours</option>
                              <option value="Minutes">Minutes</option>
                              <option value="Days">Days</option>
                            </select>
                          </label>
                          <button
                            className={styles.lastPricedCustomApply}
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleLastPricedCustomFilter();
                            }}
                          >
                            Filter
                          </button>
                          {lastPricedCustomError && (
                            <div
                              className={styles.lastPricedRangeError}
                              id="last-priced-custom-error"
                              role="alert"
                            >
                              {lastPricedCustomError}
                            </div>
                          )}
                        </div>
                        <div
                          className={styles.lastPricedFilterClear}
                          onClick={(e) => {
                            e.stopPropagation();
                            setLastPricedFilter([]);
                            setLastPricedFrom('');
                            setLastPricedTo('');
                            setShowLastPricedRangePicker(false);
                            clearLastPricedCustomFilter();
                            setPageOffset(0);
                            setShowLastPricedFilterDropdown(false);
                          }}
                        >
                          Clear
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {!isSelfPricingListedView && !hideSuperUserClientToolbar && <div className={styles.filterDropdownWrapper}>
                <button
                  className={styles.filterStatusBtn}
                  type="button"
                  onClick={() => {
                    setShowClientDropdown(!showClientDropdown);
                    setShowFilterDropdown(false);
                    setShowBarcodeFilterDropdown(false);
                  }}
                >
                  {clientType === 'All'
                    ? 'Client Type'
                    : `${clientType} (${totalCount})`}
                </button>
                {showClientDropdown && (
                  <div className={styles.filterDropdown}>
                    {['All', 'Self Pricing', 'Concierge'].map(ct => {
                      const count = clientTypeCounts[ct as keyof typeof clientTypeCounts] ?? 0;
                      const countLabel = clientTypeCountsLoading && lastClientTypeCountsQueryKeyRef.current !== ticketsQueryKey
                        ? '...'
                        : count;
                      return (
                        <div
                          key={ct}
                          className={`${styles.filterDropdownItem} ${clientType === ct ? styles.filterDropdownItemActive : ''}`}
                          onClick={(e) => { e.stopPropagation(); setClientType(ct); setShowClientDropdown(false); }}
                        >
                          <span>{ct}</span>
                          <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#6b7280' }}>
                            ({countLabel})
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>}

              {/* --- NEW EVENT DATE FILTERS SECTION --- */}
              <div className={styles.dateFilterWrapper}>
                <button
                  className={`${styles.dateFilterBtn} ${datePreset === 'today' ? styles.activeDateFilter : ''}`}
                  onClick={() => applyDatePreset('today')}
                >
                  Today
                </button>
                <button
                  className={`${styles.dateFilterBtn} ${datePreset === 'tomorrow' ? styles.activeDateFilter : ''}`}
                  onClick={() => applyDatePreset('tomorrow')}
                >
                  Tomorrow
                </button>
                <button
                  className={`${styles.dateFilterBtn} ${datePreset === 'next7days' ? styles.activeDateFilter : ''}`}
                  onClick={() => applyDatePreset('next7days')}
                >
                  Next 7 days
                </button>
                <button
                  className={styles.dateFilterBtn}
                  onClick={() => {
                    setCustomRangeStart(startDate || getTodayStr());
                    setCustomRangeEnd(endDate || getTodayStr());
                    setShowCustomRangeModal(true);
                  }}
                >
                  <Calendar size={14} style={{ marginRight: 4 }} />
                  Custom
                </button>
                {getActiveDateFilterLabel() && (
                  <span className={styles.activeDatePill}>
                    {getActiveDateFilterLabel()}
                    <X
                      size={14}
                      style={{ marginLeft: 6, cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        clearDateFilter();
                      }}
                    />
                  </span>
                )}
              </div>

              <div className={styles.searchEventWrapper}>
                <SearchIcon size={15} className={styles.searchIconSmall} />
                <input
                  type="text"
                  placeholder="Search event, team, venue..."
                  className={styles.searchInputEvent}
                  value={debouncedEventVenueSearch}
                  onChange={(e) => setDebouncedEventVenueSearch(e.target.value)}
                />
              </div>

              {!isSelfPricingListedView && !hideSuperUserClientToolbar && <div className={styles.searchClientWrapper}>
                <SearchIcon size={15} className={styles.searchIconSmall} />
                <input
                  type="text"
                  placeholder="Search customer name or email..."
                  className={styles.searchInputEvent}
                  value={debouncedClientSearch}
                  onChange={(e) => setDebouncedClientSearch(e.target.value)}
                />
              </div>}

              <button className={styles.exportButton} onClick={handleExport}>
                <Download size={16} /> Export CSV
              </button>
            </div>
          </div>

          {/* Results Info */}
          <div className={styles.resultsInfo}>
            <span>
              {resultsRangeLabel}
            </span>
            {hasActiveFilters() && (
              <button className={styles.clearFiltersBtn} onClick={clearAllFilters}>
                Clear All Filters
              </button>
            )}
          </div>

          {/* Expanded-seat bulk actions */}
          {showBulkActions && bulkSelectedTicketIds.size === 0 && (() => {
            const selectedStatuses: string[] = selectedSeatTicketsList.map((ticket) => normalizeStatusValue(ticket.tp_status || ''));
            const hasSelectedTickets = selectedSeatTicketsList.length > 0;
            const hasSoldSelected = selectedStatuses.includes('Sold');
            const canMarkSelectedSold =
              hasSelectedTickets && selectedStatuses.every((status) => status === 'List' || status === 'Unsold');
            const canDelistSelected =
              hasSelectedTickets && selectedStatuses.every((status) => status === 'List');
            const canChangeSelectedPrice =
              hasSelectedTickets && selectedStatuses.every((status) => status === 'List');

            return (
              <div className={styles.bulkActionsBar}>
                <div className={styles.bulkActionsLeft}>
                  <CheckSquare size={16} />
                  <span>{selectedTpIds.length} ticket(s) selected</span>
                  <button className={styles.clearSelectionBtn} onClick={() => {
                    setSelectedSeats({});
                    setSelectAllSeats(false);
                    setShowBulkActions(false);
                    setSelectedTpIds([]);
                    setBulkSelectedTicketIds(new Set());
                  }}>
                    Clear
                  </button>
                </div>
                <div className={styles.bulkActionsRight}>
                  {canMarkSelectedSold && (
                    <button className={styles.bulkMarkSold} onClick={() => handleBulkAction('markSold')}>
                      <CheckCircle size={14} /> Mark as Sold
                    </button>
                  )}
                  {hasSelectedTickets && !hasSoldSelected && (
                    !isSuperUserListedStatusReadOnly(isSuperUser, activeTab) && <button className={styles.bulkChangePrice} onClick={() => openBulkStatusModal(selectedTpIds)}>
                      <CheckCircle size={14} /> Bulk Status
                    </button>
                  )}
                  {canDelistSelected && (
                    <button className={styles.bulkDelist} onClick={() => handleBulkAction('delist')}>
                      <Package size={14} /> Delist
                    </button>
                  )}
                  {canChangeSelectedPrice && activeTab === 'Listed' && (
                    <button className={styles.bulkChangePrice} onClick={() => handleBulkAction('changePrice')}>
                      <DollarSign size={14} /> Change Price
                    </button>
                  )}
                </div>
              </div>
            );
          })()}
          </div>

          {/* Loading State */}
          {ticketsTableLoading && (
            <div className={styles.loadingState}>
              <div className={styles.spinner}></div>
              <p>Loading tickets...</p>
            </div>
          )}

          {/* Error State */}
          {queryError && !isEventGroupedView && !ticketsTableLoading && (
            <div className={styles.errorState}>
              <AlertCircle size={48} />
              <h3>Error Loading Tickets</h3>
              <p>{queryError.message}</p>
              <button onClick={handleRefresh} className={styles.retryBtn}>Retry</button>
            </div>
          )}

          {/* Tickets Table */}
          {!ticketsTableLoading && !(queryError && !isEventGroupedView) && (
            <div className={styles.tableBusyWrap}>
              {isStatusOperationInProgress && (
                <div className={styles.tableBusyOverlay} role="status" aria-live="polite">
                  <div className={styles.tableBusyOverlayInner}>
                    <div className={styles.spinner} />
                    <p>{statusOperationMessage}</p>
                  </div>
                </div>
              )}
            <>
              {/* Bulk selection actions bar */}
              {bulkSelectedTicketIds.size > 0 && (() => {
                const bulkSelectedStatuses = selectedTicketsList.map((ticket) =>
                  normalizeStatusValue(ticket.tp_status || ''),
                );
                const vlProgress = getVlProgressForTickets(selectedTicketsList);
                const allHaveVlIds = selectedTicketsList.every((t) =>
                  Boolean(t.tp_vl_listing_id || t.tp_vl_item_id),
                );
                const allToBeVerifiedOrVerified =
                  selectedTicketsList.length > 0 &&
                  bulkSelectedStatuses.every((s) => s === 'Verified' || s === 'ToBeVerified');
                const allListed =
                  selectedTicketsList.length > 0 &&
                  bulkSelectedStatuses.every((s) => s === 'List');
                const canVlProgress =
                  selectedTicketsList.length > 0 &&
                  vlProgress != null &&
                  activeTab === 'Verification';
                // Sync can attach seats that exist on VL but never got listing IDs saved.
                const canVlSyncOnVerification =
                  activeTab === 'Verification' && allToBeVerifiedOrVerified;
                const canVlSyncOnListed = activeTab === 'Listed' && allListed;
                const canVlBarcodes =
                  (canVlSyncOnVerification || canVlSyncOnListed) && allHaveVlIds;
                // Listed: barcode crawl / group / split still need saved VL ids.
                const canVlManageOnListed =
                  activeTab === 'Listed' && allListed && allHaveVlIds;
                const allVerified =
                  selectedTicketsList.length > 0 &&
                  bulkSelectedStatuses.every((s) => s === 'Verified');
                const allHaveMappedPid = selectedTicketsList.every((ticket) => {
                  const pid = String(
                    getTicketVlProductionId(ticket, vlProductionIdByEventId) || '',
                  ).trim();
                  return Boolean(pid && /^\d+$/.test(pid));
                });
                const canRemapProduction =
                  activeTab === 'Verification' && allVerified && allHaveMappedPid;
                const canAddPid =
                  (canVlSyncOnVerification || canVlSyncOnListed) &&
                  selectedTicketsList.length > 0;

                return (
                <div
                  className={styles.bulkActionsBar}
                  style={pinListedToolbar ? { position: 'sticky', top: stickyToolbarHeight, zIndex: 20 } : undefined}
                >
                  <div className={styles.bulkActionsLeft}>
                    <div><strong>{bulkSelectedTicketIds.size}</strong> ticket{bulkSelectedTicketIds.size > 1 ? 's' : ''} selected</div>
                    {selectedTicketsList.length > 0 && (
                      <div style={{ marginLeft: 8 }}>
                        Event: {selectedTicketsList[0].e_name || 'N/A'} • Section: {selectedTicketsList[0].tp_section || 'N/A'} • Row: {selectedTicketsList[0].tp_row || 'N/A'}
                      </div>
                    )}
                    <button className={styles.clearSelectionBtn} onClick={() => { clearSelectionState(); }}>Clear Selection</button>
                  </div>
               <div className={styles.bulkActionsRight}>
  {canVlProgress && vlProgress && (
    <button
      className={styles.bulkMarkSold}
      onClick={() => void handleVlProgressAction()}
      disabled={statusTableBusyCount > 0}
      title={vlProgress.title}
    >
      {vlProgress.step === 'submit' && <ExternalLink size={14} />}
      {vlProgress.step === 'share' && <Share2 size={14} />}
      {' '}
      {vlProgress.label}
    </button>
  )}
  {canRemapProduction && (
    <button
      className={styles.bulkChangePrice}
      onClick={() => void handleOpenProductionSync(selectedTicketsList)}
      disabled={statusTableBusyCount > 0}
      title="Overwrite the mapped Victory Live Production ID, then resubmit inventory"
    >
      <MapPinned size={14} /> Remap PID
    </button>
  )}
  {!isSuperUser && canAddPid && (
    <button
      className={styles.bulkChangePrice}
      onClick={() => void handleOpenAddPid(selectedTicketsList)}
      disabled={statusTableBusyCount > 0}
      title="Save Production ID for the whole ticket group in QuickAsyst only (does not update Victory Live)"
    >
      <MapPinned size={14} /> Add PID
    </button>
  )}
  {!isSuperUser && (canVlSyncOnVerification || canVlSyncOnListed) && (
    <button
      className={styles.bulkChangePrice}
      onClick={() => openSyncFromVlScopeModal()}
      disabled={statusTableBusyCount > 0}
      title="Choose what to sync from Victory Live (merge/split, barcode, price, notes, …)"
    >
      <RefreshCcw size={14} /> Sync from VL
    </button>
  )}
  {canVlSyncOnVerification && canVlBarcodes && (
    <>
      <button
        className={styles.bulkChangePrice}
        onClick={() => void handleSyncBarcodes()}
        disabled={statusTableBusyCount > 0 || barcodeSyncSummary?.phase === 'crawling' || barcodeSyncSummary?.phase === 'verifying'}
        title="Start barcode crawl, wait for completion, then verify selected seats"
      >
        <ScanBarcode size={14} /> Sync Barcodes
      </button>
      
    </>
  )}
  {canVlManageOnListed && (
    <>
      <button
        className={styles.bulkChangePrice}
        onClick={() => void handleSyncBarcodes()}
        disabled={statusTableBusyCount > 0 || barcodeSyncSummary?.phase === 'crawling' || barcodeSyncSummary?.phase === 'verifying'}
        title="Start barcode crawl, wait for completion, then verify selected seats"
      >
        <ScanBarcode size={14} /> Sync Barcodes
      </button>
      
    </>
  )}
  {!isSuperUser && activeTab === 'Listed' && allListed && (() => {
    const scope = bulkSupportScope(selectedTicketsList);
    return (
      <button
        className={styles.bulkChangePrice}
        onClick={() => openVlSupportBulk(selectedTicketsList)}
        disabled={!scope.ok}
        title={scope.ok ? 'Email Victory Live once about every selected listing' : scope.reason}
      >
        <Mail size={14} /> Contact Victory Live
      </button>
    );
  })()}
  {!isSuperUser && activeTab === 'Listed' && vlSupportAvailable && openSupportTicketIdsIn(selectedTicketsList).length > 0 && (
    <button
      className={styles.bulkChangePrice}
      onClick={() => void handleVlSupportBulkComplete(selectedTicketsList)}
      disabled={vlSupportBulkCompleting}
      title="Mark the open Victory Live support tickets on the selected listings as complete"
    >
      <CheckCircle size={14} /> {vlSupportBulkCompleting ? 'Completing…' : 'Mark support complete'}
    </button>
  )}
  {!isSuperUser && activeTab === 'Listed' && allListed && (
    <>
      <button
        className={styles.bulkChangePrice}
        onClick={() => void handleRefreshConciergePrices()}
        disabled={statusTableBusyCount > 0 || conciergePriceLoading}
        title="Pull current Victory Live list prices (Follow the Market / listing price) and show up/down changes"
      >
        <RefreshCcw size={14} /> Refresh VL Prices
      </button>
      <button className={styles.bulkChangePrice} onClick={() => openBulkPriceModal()}>
        <DollarSign size={14} /> Change Price
      </button>
    </>
  )}
  {!isSuperUserListedStatusReadOnly(isSuperUser, activeTab) && <button className={styles.bulkChangePrice} onClick={() => openBulkStatusModal()}>
    Bulk Update Status
  </button>}
</div>
                </div>
                );
              })()}
              <div className={styles.tableContainer}>
                <table className={styles.ticketsTable}>
                  <thead>
                    <tr>
                      <th className={`${styles.checkboxColumn} ${styles.stickyCheckbox}`}>
                        <input
                          type="checkbox"
                          className={styles.bulkSelectCheckbox}
                          checked={visibleTicketIds.length > 0 && visibleTicketIds.every(id => bulkSelectedTicketIds.has(id))}
                          onChange={(e) => handleBulkSelectAllPage(e.target.checked)}
                        />
                      </th>
                      {orderedManageTicketColumns.map((columnId) => (
                        <React.Fragment key={columnId}>
                          {renderManageTicketColumnHeader(columnId)}
                        </React.Fragment>
                      ))}
                      <th className={styles.thActions}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {manageTicketTableRows.length > 0 ? (
                      manageTicketTableRows.map((row) => {
                        if (row.kind === 'section') {
                          return (
                            <tr key={`event-section-${row.sectionKey}`} className={styles.eventGroupHeaderRow}>
                              <td colSpan={tableColSpan}>
                                <span className={styles.eventGroupHeaderLabel}>{row.label}</span>
                                <span className={styles.eventGroupHeaderCount}>({row.count})</span>
                              </td>
                            </tr>
                          );
                        }

                        if (row.kind === 'group') {
                          const isExpanded = expandedGroupKeys.includes(row.groupKey);
                          const groupTicketIds = row.tickets.map((ticket) => ticket.tp_id);
                          const representativeTicket = row.tickets[0];
                          const hideListedSelfPricingActions = isSuperUser && row.rowTab === 'Listed';
                          const hideSoldTicketDetailsActions = hidesSuperUserTicketDetails(isSuperUser, row.rowTab);
                          const hideSuperUserDelistedActions = isSuperUserDelistedTab(isSuperUser, row.rowTab);
                          const groupSeats = row.tickets.flatMap((ticket) => generateSeats(ticket));
                          const groupSeatIds = groupSeats.map((seat) => seat.id);
                          const isGroupSeatFullySelected = groupSeatIds.length > 0 && groupSeatIds.every((seatId) => selectedSeats[seatId]);
                          const isGroupSeatPartiallySelected = groupSeatIds.some((seatId) => selectedSeats[seatId]) && !isGroupSeatFullySelected;
                          const rowKey = `group-${row.groupKey}`;
                          return (
                            <React.Fragment key={rowKey}>
                              <tr
                                className={[
                                  styles.ticketRow,
                                  styles.groupedRow,
                                  openStatusDropdown === representativeTicket.tp_id ? styles.statusMenuOpen : '',
                                ].filter(Boolean).join(' ')}
                                onClick={() => toggleGroupRow(row.groupKey)}
                              >
                                <td className={`${styles.checkboxCell} ${styles.stickyCheckbox}`} onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="checkbox"
                                    className={styles.ticketCheckbox}
                                    checked={isGroupSeatFullySelected}
                                    ref={(element) => {
                                      if (element) {
                                        element.indeterminate = isGroupSeatPartiallySelected;
                                      }
                                    }}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleSelectSeatsByIds(groupSeatIds, e.target.checked);
                                    }}
                                  />
                                </td>
                                {(() => {
                                  const columnCells: Partial<Record<ManageTicketColumnId, React.ReactNode>> = {
                                  event: renderManageTicketsEventCell({
                                  sticky: eventColumnIsSticky,
                                  ticket: representativeTicket,
                                  eventName: row.summary.eventName,
                                  datePrefix: (
                                    <ChevronRight
                                      size={12}
                                      className={isExpanded ? styles.groupChevronExpanded : styles.groupChevronCollapsed}
                                    />
                                  ),
                                  formatDate,
                                  formatTime,
                                  productionIdByEventId: vlProductionIdByEventId,
                                  marketplaceLinksByProductionId,
                                  showBarcodeStatusIcon: shouldShowBarcodeStatus(row.rowTab, isSuperUser),
                                  hidePidBarcodeDetails: hidesSuperUserEventCardDetails(isSuperUser, row.rowTab),
                                  groupTickets: row.tickets,
                                  onBarcodeStatusSelect: (tickets, status) => {
                                    void handleBarcodeStatusUpdate(tickets, status);
                                  },
                                  extraContent: renderVlSupportControl(rowKey, row.tickets),
                                }),
                                section: <td className={styles.tdSection}>{row.summary.section || 'N/A'}</td>,
                                row: <td className={styles.tdRow}>{row.summary.row || 'N/A'}</td>,
                                seats: (
                                  <td className={styles.tdSeat}>
                                    <SeatModeBadge mode={row.summary.seatMode} />
                                    <div>{row.summary.seatLabel}</div>
                                  </td>
                                ),
                                status: <td className={styles.tdStatus}>
                                  {!isSuperUserListedStatusReadOnly(isSuperUser, row.rowTab) && !hideSuperUserDelistedActions && (
                                    <WrongPidRetryButton
                                      wrongPid={row.tickets.some((ticket) => ticket.tp_wrong_pid === true)}
                                      status={representativeTicket.tp_status}
                                      disabled={isStatusOperationInProgress}
                                      className={styles.wrongPidRetryBtn}
                                      onResubmit={() => void openWrongPidResubmit(row.tickets)}
                                      onRelist={() => void openWrongPidRelist(row.tickets)}
                                    />
                                  )}
                                  {(() => {
                                    const status = (representativeTicket.tp_status || '').toLowerCase();
                                    const pres = getManageTicketsStatusPresentation(representativeTicket);
                                    if (isSuperUserListedStatusReadOnly(isSuperUser, row.rowTab)) {
                                      return (
                                        <span className={`${styles.statusBadge} ${getStatusColor(pres.iconStatus)}`}>
                                          {getStatusIcon(pres.iconStatus)} {pres.label}
                                        </span>
                                      );
                                    }
                                    const paymentUiKey = (pres.iconStatus || '').toLowerCase();
                                    if (!canOverrideSettlementStatus && (paymentUiKey === 'settled' || paymentUiKey === 'settlementinprogress')) {
                                      return (
                                        <span className={`${styles.statusBadge} ${getStatusColor(pres.iconStatus)}`}>
                                          {getStatusIcon(pres.iconStatus)} {pres.label}
                                        </span>
                                      );
                                    }
                                    if (status === 'return' || status === 'closed' || status === 'unsold' || status === 'invalid') {
                                      return (
                                        <span className={`${styles.statusBadge} ${getStatusColor(pres.iconStatus)}`}>
                                          {getStatusIcon(pres.iconStatus)} {pres.label}
                                        </span>
                                      );
                                    }
                                    const transitions = getStatusTransitions(getTicketTransitionStatus(representativeTicket));
                                    if (!transitions.length) {
                                      return (
                                        <span className={`${styles.statusBadge} ${getStatusColor(pres.iconStatus)}`}>
                                          {getStatusIcon(pres.iconStatus)} {pres.label}
                                        </span>
                                      );
                                    }
                                    return (
                                      <div
                                        className={[
                                          styles.statusDropdownWrapper,
                                          openStatusDropdown === representativeTicket.tp_id ? styles.statusDropdownOpen : '',
                                        ].filter(Boolean).join(' ')}
                                      >
                                        <button
                                          className={`${styles.statusBadge} ${getStatusColor(pres.iconStatus)} ${styles.statusDropdownBtn}`}
                                          disabled={isStatusOperationInProgress}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (isStatusOperationInProgress) return;
                                            setOpenStatusDropdown(openStatusDropdown === representativeTicket.tp_id ? null : representativeTicket.tp_id);
                                          }}
                                        >
                                          {getStatusIcon(pres.iconStatus)} {pres.label} <ChevronDown size={12} style={{ marginLeft: 4 }} />
                                        </button>
                                        {openStatusDropdown === representativeTicket.tp_id && (
                                          <div className={styles.statusDropdownList}>
                                            {transitions.map(opt => (
                                              <div
                                                key={opt.value}
                                                className={styles.statusDropdownListItem}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setOpenStatusDropdown(null);
                                                  const selectedIds = Array.from(bulkSelectedTicketIds);
                                                  const groupTpIds = row.tickets.map((t) => t.tp_id);
                                                  const selectedInGroup = selectedIds.filter((id) =>
                                                    groupTpIds.includes(id),
                                                  );
                                                  // No seats selected → change status for the whole group
                                                  // (was only updating representativeTicket = 1 seat).
                                                  const ticketsForUpdate =
                                                    selectedInGroup.length > 0
                                                      ? row.tickets.filter((t) =>
                                                          selectedInGroup.includes(t.tp_id),
                                                        )
                                                      : row.tickets;
                                                  if (ticketsForUpdate.length > 1) {
                                                    void applyMultiTicketStatusChangeFromUi(
                                                      ticketsForUpdate,
                                                      opt.value,
                                                    );
                                                    return;
                                                  }
                                                  handleStatusChangeConfirm(
                                                    ticketsForUpdate[0] || representativeTicket,
                                                    opt.value,
                                                  );
                                                }}
                                              >
                                                {getStatusIcon(opt.value)} {opt.label}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </td>,
                                marketplaces: showMarketplaceColumn ? renderListedMarketplaceCell(row.tickets) : null,
                                vlShare: showSharedColumn ? renderListedSharedCell(row.tickets) : null,
                                price: showPriceColumn ? (() => {
                                  const soldAmount = Number(representativeTicket.tp_sold_amount ?? 0);
                                  const hasSoldAmount = Number.isFinite(soldAmount) && soldAmount > 0;
                                  const listAmount = Number(representativeTicket.tp_list_price ?? 0);
                                  const hasListPrice = Number.isFinite(listAmount) && listAmount > 0;
                                  const display = hasSoldAmount
                                    ? formatCurrency(representativeTicket.tp_sold_amount ?? 0)
                                    : hasListPrice
                                      ? formatCurrency(representativeTicket.tp_list_price ?? 0)
                                      : '—';
                                  const canChangePrice =
                                    row.rowTab === 'Listed' &&
                                    (getResolvedClientType(representativeTicket) === 'self-pricing' ||
                                      getResolvedClientType(representativeTicket) === 'concierge');
                                  return (
                                    <td
                                      className={styles.tdPrice}
                                      onClick={canChangePrice ? (e) => e.stopPropagation() : undefined}
                                    >
                                      {canChangePrice ? (
                                        <button
                                          type="button"
                                          className={styles.clickablePrice}
                                          title="Change Price"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleAction('Change Price', representativeTicket, e, {
                                              tpIds: row.tickets.map((t) => t.tp_id).filter(Boolean),
                                            });
                                          }}
                                        >
                                          {renderPricingMethodLabel(representativeTicket.tp_pricing_method, representativeTicket)}
                                          <Pencil size={13} aria-hidden />
                                          {display}
                                        </button>
                                      ) : (
                                          <span className={styles.priceDisplayWithIcon}>
                                            {renderPricingMethodLabel(representativeTicket.tp_pricing_method, representativeTicket)}
                                            {display}
                                          </span>
                                      )}
                                    </td>
                                  );
                                })() : null,
                                cost: showListedFinancialColumns ? (() => {
                                  const cost = listedTicketCosts[representativeTicket.tp_id];
                                  return (
                                      <td className={styles.tdPrice}>
                                        <div className={styles.costEditor}>
                                          <span aria-hidden="true">$</span>
                                          <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            aria-label={`Cost for ${representativeTicket.e_name || 'ticket'}`}
                                            value={listedCostDrafts[representativeTicket.tp_id] ?? (cost == null ? '' : String(cost))}
                                            onClick={(event) => event.stopPropagation()}
                                            onChange={(event) => setListedCostDrafts((previous) => ({
                                              ...previous,
                                              [representativeTicket.tp_id]: event.target.value,
                                            }))}
                                          />
                                          <button
                                            type="button"
                                            className={styles.costSaveButton}
                                            aria-label="Save cost"
                                            title="Save cost"
                                            disabled={savingListedCostId === representativeTicket.tp_id}
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              void saveListedTicketCost(representativeTicket);
                                            }}
                                          >
                                            Save
                                          </button>
                                        </div>
                                      </td>
                                  );
                                })() : null,
                                potentialPayout: showListedFinancialColumns ? (
                                  <td className={styles.tdPrice}>{formatCurrency(getListedPotentialPayout(representativeTicket))}</td>
                                ) : null,
                                difference: showListedFinancialColumns ? (() => {
                                  const cost = listedTicketCosts[representativeTicket.tp_id];
                                  const payout = getListedPotentialPayout(representativeTicket);
                                  const difference = cost == null ? null : payout - cost;
                                  return (
                                    <td className={styles.tdPrice}>
                                      {difference == null ? '—' : (
                                        <span className={difference >= 0 ? styles.costProfit : styles.costLoss}>
                                          {difference >= 0 ? '+' : '−'}{formatCurrency(Math.abs(difference))}
                                          <small>{difference >= 0 ? 'Profit' : 'Loss'}</small>
                                        </span>
                                      )}
                                    </td>
                                  );
                                })() : null,
                                lastPriced: activeTab === 'Listed' ? (() => {
                                  const pricePending = row.tickets.some((ticket) => priceUpdatePendingIds.has(ticket.tp_id));
                                  const badge = getLastPricedBadge(representativeTicket);
                                  const lastPricedValue = getLastPricedDisplayValue(representativeTicket);
                                  return (
                                    <td className={styles.tdLastPriced}>
                                      <div className={styles.lastPricedCell}>
                                        {pricePending ? (
                                          <span className={styles.lastPricedSpinner} role="status" aria-label="Updating price" />
                                        ) : (
                                          <span className={styles.lastPricedDate} title={describeLastPricedBucket(representativeTicket)}>
                                            {lastPricedValue}
                                          </span>
                                        )}
                                        {!pricePending && badge ? (
                                          <span
                                            className={`${styles.stalenessBadge} ${styles[badge.className]}`}
                                            title={badge.title}
                                            aria-label={badge.ariaLabel}
                                          >
                                            {badge.label}
                                          </span>
                                        ) : null}
                                      </div>
                                    </td>
                                  );
                                })() : null,
                                clientName: !hideSoldClientColumns ? (
                                  <td className={styles.tdClient}>
                                    <div
                                      className={styles.clientName}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleUserClick(representativeTicket.u_id);
                                      }}
                                    >
                                      {representativeTicket.u_full_name || `${representativeTicket.u_first_name || ''} ${representativeTicket.u_last_name || ''}`.trim() || 'N/A'}
                                    </div>
                                    <div className={styles.clientEmail}>{representativeTicket.u_email_id || 'N/A'}</div>
                                  </td>
                                ) : null,
                                returnEmail: showDelistReturnEmailColumn ? (
                                  <td className={styles.tdReturnEmail}>
                                    <div className={styles.clientEmail}>{representativeTicket.tp_delist_requested_email?.trim() || '—'}</div>
                                  </td>
                                ) : null,
                                clientType: !hideSoldClientColumns ? (
                                  <td className={styles.tdClientType}>
                                    <span
                                      className={
                                        styles.clientTypeBadge + ' ' +
                                        (getResolvedClientType(representativeTicket) === 'self-pricing'
                                          ? styles.selfPricingBadge
                                          : getResolvedClientType(representativeTicket) === 'concierge'
                                          ? styles.conciergeBadge
                                          : '')
                                      }
                                    >
                                      {getResolvedClientType(representativeTicket) === 'self-pricing'
                                        ? 'Self Pricing'
                                        : getResolvedClientType(representativeTicket) === 'concierge'
                                        ? 'Concierge'
                                        : 'N/A'}
                                    </span>
                                  </td>
                                ) : null,
                                  };
                                  return renderOrderedManageTicketCells(columnCells);
                                })()}
                                <td className={styles.tdActions}>
                                  <div className={styles.actionMenuWrapper}>
                                    <button className={styles.actionMenuBtn} onClick={(e) => toggleActionMenu(representativeTicket.tp_id, e)}>
                                      <MoreVertical size={16} />
                                    </button>
                                    {openActionMenu === representativeTicket.tp_id && (
                                      <div className={styles.actionMenuList}>
                                        {(() => {
                                          const groupStatuses = [...new Set(row.tickets.map((ticket) => normalizeStatusValue(ticket.tp_status || '')))];
                                          const showWrongPid =
                                            !hideSuperUserDelistedActions &&
                                            !isSuperUserListedStatusReadOnly(isSuperUser, row.rowTab) &&
                                            groupStatuses.length === 1 &&
                                            (groupStatuses[0] === 'Verified' || groupStatuses[0] === 'List');
                                          if (!showWrongPid) return null;
                                          return (
                                            <button
                                              className={styles.actionMenuItem}
                                              title="Remove this ticket group from the wrong Victory Live production"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setOpenActionMenu(null);
                                                setWrongPidConfirm({
                                                  tpIds: row.tickets.map((ticket) => ticket.tp_id).filter(Boolean),
                                                  tickets: row.tickets,
                                                });
                                              }}
                                            >
                                              <X size={14} /> Wrong PID
                                            </button>
                                          );
                                        })()}
                                        {!hideListedSelfPricingActions && !hideSoldTicketDetailsActions && <button className={styles.actionMenuItem} onClick={(e) => handleAction('View Details', representativeTicket, e)}><Eye size={14} /> View Details</button>}
                                        {!hideListedSelfPricingActions && !hideSoldTicketDetailsActions && (representativeTicket.tp_status !== 'Return' && row.rowTab !== 'Deleted') && (
                                          <button className={styles.actionMenuItem} onClick={(e) => handleAction('Change Status', representativeTicket, e)}><CheckCircle size={14} /> Change Status</button>
                                        )}
                                        {renderChangeSeatNumbersItem(row.tickets)}
                                        {!hideListedSelfPricingActions && row.rowTab === 'Listed' && (
                                          <button
                                            className={styles.actionMenuItem}
                                            onClick={(e) =>
                                              handleAction('Pricing Method Status', representativeTicket, e, {
                                                tpIds: row.tickets.map((t) => t.tp_id).filter(Boolean),
                                              })
                                            }
                                          >
                                            <DollarSign size={14} /> Pricing Method Status
                                          </button>
                                        )}
                                        {!hideListedSelfPricingActions && !hideSoldTicketDetailsActions && <button
                                          className={styles.actionMenuItem}
                                          onClick={(e) =>
                                            handleAction('Edit Details', representativeTicket, e, {
                                              tpIds: row.tickets.map((t) => t.tp_id).filter(Boolean),
                                            })
                                          }
                                        >
                                          <Edit size={14} /> Edit Details
                                        </button>}
                                        {row.rowTab === 'Listed' && (getResolvedClientType(representativeTicket) === 'self-pricing' || getResolvedClientType(representativeTicket) === 'concierge') && (
                                          <button
                                            className={styles.actionMenuItem}
                                            onClick={(e) =>
                                              handleAction('Change Price', representativeTicket, e, {
                                                tpIds: row.tickets.map((t) => t.tp_id).filter(Boolean),
                                              })
                                            }
                                          >
                                            <DollarSign size={14} /> Change Price
                                          </button>
                                        )}
                                        <button
                                          className={styles.actionMenuItem}
                                          onClick={(e) =>
                                            handleAction('Price Change Logs', representativeTicket, e, {
                                              tpIds: row.tickets.map((t) => t.tp_id).filter(Boolean),
                                            })
                                          }
                                        >
                                          <History size={14} /> Price Change Logs
                                        </button>
                                        {row.rowTab !== 'Listed' && representativeTicket.tp_status === 'List' && (
                                          <button className={styles.actionMenuItem} onClick={(e) => handleAction('Delist', representativeTicket, e)}><Package size={14} /> Delist</button>
                                        )}
                                        {!hideListedSelfPricingActions && renderVlRowActionItems(row.tickets, row.rowTab)}
                                        {(() => {
                                          const status = normalizeStatusValue(representativeTicket.tp_status || '');
                                          const allowedStatuses = ['Verified', 'List', 'Delist', 'ToBeVerified'];
                                          if (row.rowTab !== 'Listed' && !hideSuperUserDelistedActions && allowedStatuses.includes(status)) {
                                            return (
                                              <button className={styles.actionMenuItem} onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedTicketForDelete(representativeTicket);
                                                setShowDeleteConfirmModal(true);
                                                setOpenActionMenu(null);
                                              }}><X size={14} /> Delete</button>
                                            );
                                          }
                                          return null;
                                        })()}
                                        {!hideListedSelfPricingActions && !hideSoldTicketDetailsActions && <button className={styles.actionMenuItem} onClick={(e) => handleAction('View Client Profile', representativeTicket, e)}><FileText size={14} /> View Client Profile</button>}
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                              {renderVlSupportPanel(
                                rowKey,
                                row.tickets,
                                representativeTicket,
                                row.summary.eventName,
                                row.summary.section,
                                row.summary.row,
                                row.summary.seatLabel,
                              )}
                              {isExpanded && (
                                <tr className={styles.expandedRow}>
                                  <td colSpan={tableColSpan}>
                                    <div className={styles.expandedContent}>
                                      <div className={styles.expandedMeta}>
                                        <span>
                                          <span className={styles.expandedMetaLabel}>Venue:</span>{' '}
                                          {representativeTicket.e_address || 'N/A'}
                                        </span>
                                        <span className={styles.expandedMetaDivider}>|</span>
                                        <span>
                                          <span className={styles.expandedMetaLabel}>League:</span>{' '}
                                          {representativeTicket.l_name || 'N/A'}
                                        </span>
                                        <span className={styles.expandedMetaDivider}>|</span>
                                        <span>
                                          <span className={styles.expandedMetaLabel}>Email:</span>{' '}
                                          {representativeTicket.u_email_id || 'N/A'}
                                        </span>
                                        {!hidesSuperUserTicketDetails(isSuperUser, row.rowTab) &&
                                          getDisplayedTicketVlProductionId(representativeTicket, vlProductionIdByEventId) && (
                                          <>
                                            <span className={styles.expandedMetaDivider}>|</span>
                                            <span>
                                              <span className={styles.expandedMetaLabel}>Production ID:</span>{' '}
                                              {getDisplayedTicketVlProductionId(representativeTicket, vlProductionIdByEventId)}
                                            </span>
                                          </>
                                        )}
                                        {showExpandedCommission && (
                                          <>
                                            <span className={styles.expandedMetaDivider}>|</span>
                                            <span>
                                              <span className={styles.expandedMetaLabel}>Commission:</span>{' '}
                                              {getSalesRepCommission(representativeTicket) > 0
                                                ? formatCurrency(getSalesRepCommission(representativeTicket))
                                                : '—'}{' '}
                                              {canEditSoldCommission && (
                                              <button
                                                type="button"
                                                className={styles.commissionEditButton}
                                                onClick={(event) => {
                                                  event.stopPropagation();
                                                  openCommissionModal(representativeTicket);
                                                }}
                                                title="Edit commission for this line"
                                                aria-label="Edit commission for this line"
                                              >
                                                <Pencil size={13} aria-hidden />
                                              </button>
                                              )}
                                            </span>
                                        
                                            <span className={styles.expandedMetaDivider}>|</span>
                                            <span>
                                              <span className={styles.expandedMetaLabel}>Marketplace:</span>{' '}
                                              {getTicketMarketplaceName(representativeTicket) || '—'}
                                            </span>
                                          </>
                                        )}
                                      </div>
                                      <div className={styles.expandedHeader}>
                                        <div>
                                          <h4>
                                            Grouped Seats{' '}
                                            <span className={styles.seatCountLabel}>({groupSeats.length} seats)</span>
                                          </h4>
                                          {shouldShowBarcodeStatus(row.rowTab, isSuperUser) && !getTicketsBarcodeStatus(row.tickets).allAttached && (
                                            <p className={styles.barcodeMissingSummary}>
                                              {getBarcodeMissingSummary(row.tickets)}
                                            </p>
                                          )}
                                        </div>
                                        <div className={styles.selectAllContainer}>
                                          <label className={styles.selectAllLabel} onClick={(e) => e.stopPropagation()}>
                                            <input
                                              type="checkbox"
                                              className={styles.selectAllCheckbox}
                                              checked={groupSeats.every((seat) => selectedSeats[seat.id])}
                                              ref={(element) => {
                                                if (element) {
                                                  element.indeterminate =
                                                    groupSeats.some((seat) => selectedSeats[seat.id]) &&
                                                    !groupSeats.every((seat) => selectedSeats[seat.id]);
                                                }
                                              }}
                                              onChange={(e) => {
                                                e.stopPropagation();
                                                handleSelectSeatsByIds(
                                                  groupSeats.map((seat) => seat.id),
                                                  e.target.checked,
                                                );
                                              }}
                                            />
                                            <span>{groupSeats.every((seat) => selectedSeats[seat.id]) ? 'Deselect All' : 'Select All'}</span>
                                          </label>
                                        </div>
                                      </div>
                                      <div className={styles.seatsGrid}>
                                        {groupSeats.map((seat) => {
                                          const seatTicket = row.tickets.find((candidate) => candidate.tp_id === seat.ticketId) ?? representativeTicket;
                                          return (
                                            <div
                                              key={seat.id}
                                              className={`${styles.seatCard} ${selectedSeats[seat.id] ? styles.selectedSeat : ''}`}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleSelectSeat(seat.id);
                                              }}
                                            >
                                              <div className={styles.seatCardHeader}>
                                                <div className={styles.seatSelection}>
                                                  {selectedSeats[seat.id] ? (
                                                    <CheckSquare size={14} className={styles.checkedIcon} />
                                                  ) : (
                                                    <Square size={14} className={styles.uncheckedIcon} />
                                                  )}
                                                  <span className={styles.seatNumber}>Seat {seat.number}</span>
                                                </div>
                                                <div className={styles.seatCardHeaderActions}>
                                                  {renderSeatCardStatus(seatTicket, seat.id, row.rowTab)}
                                                </div>
                                              </div>
                                              <div className={styles.seatDetails}>
                                                <span>Sec {seat.section} • Row {seat.row}</span>
                                                <span className={styles.seatPrice}>{formatCurrency(Number(seat.price) || 0)}</span>
                                              </div>
                                              {shouldShowBarcodeStatus(row.rowTab, isSuperUser) && renderSeatBarcodeStatus(seatTicket)}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        }

                        const ticket = row.ticket;
                        const rowTab = row.rowTab;
                        const hideListedSelfPricingActions = isSuperUser && rowTab === 'Listed';
                        const hideSoldTicketDetailsActions = hidesSuperUserTicketDetails(isSuperUser, rowTab);
                        const hideSuperUserDelistedActions = isSuperUserDelistedTab(isSuperUser, rowTab);

                        return (
                        <React.Fragment key={ticket.tp_id}>
                          <tr
                            className={[
                              styles.ticketRow,
                              (ticket.tp_status || '').toLowerCase() === 'listing expired' ? styles.listingExpiredRow : '',
                              openStatusDropdown === ticket.tp_id ? styles.statusMenuOpen : '',
                            ].filter(Boolean).join(' ')}
                            onClick={() => toggleRow(ticket.tp_id)}
                          >
                             <td className={`${styles.checkboxCell} ${styles.stickyCheckbox}`} onClick={(e) => e.stopPropagation()}>
    <input
      type="checkbox"
      className={styles.ticketCheckbox}
      checked={bulkSelectedTicketIds.has(ticket.tp_id)}
      onChange={(e) => handleBulkSelectTicket(ticket.tp_id, e.target.checked)}
    />
  </td>
                            {(() => {
                              const columnCells: Partial<Record<ManageTicketColumnId, React.ReactNode>> = {
                              event: renderManageTicketsEventCell({
                              sticky: eventColumnIsSticky,
                              ticket,
                              eventName: ticket.e_name,
                              formatDate,
                              formatTime,
                              productionIdByEventId: vlProductionIdByEventId,
                              marketplaceLinksByProductionId,
                              showBarcodeStatusIcon: shouldShowBarcodeStatus(rowTab, isSuperUser),
                              hidePidBarcodeDetails: hidesSuperUserEventCardDetails(isSuperUser, rowTab),
                              groupTickets: [ticket],
                              onBarcodeStatusSelect: (tickets, status) => {
                                void handleBarcodeStatusUpdate(tickets, status);
                              },
                              extraContent: renderVlSupportControl(ticket.tp_id, [ticket]),
                            }),
                            section: <td className={styles.tdSection}>{ticket.tp_section || 'N/A'}</td>,
                            row: <td className={styles.tdRow}>{ticket.tp_row || 'N/A'}</td>,
                            seats: (
                              <td className={styles.tdSeat}>
                                <SeatModeBadge mode={ticket.tp_seat_mode} />
                                <div>{ticket.tp_seat_no || 'N/A'}</div>
                              </td>
                            ),
                            status: <td className={styles.tdStatus}>
  {!isSuperUserListedStatusReadOnly(isSuperUser, rowTab) && !hideSuperUserDelistedActions && (
    <WrongPidRetryButton
      wrongPid={ticket.tp_wrong_pid === true}
      status={ticket.tp_status}
      disabled={isStatusOperationInProgress}
      className={styles.wrongPidRetryBtn}
      onResubmit={() => void openWrongPidResubmit([ticket])}
      onRelist={() => void openWrongPidRelist([ticket])}
    />
  )}
  {(() => {
    const status = (ticket.tp_status || '').toLowerCase();
    const pres = getManageTicketsStatusPresentation(ticket);
    if (isSuperUserListedStatusReadOnly(isSuperUser, rowTab)) {
      return (
        <span className={`${styles.statusBadge} ${getStatusColor(pres.iconStatus)}`}>
          {getStatusIcon(pres.iconStatus)}
          {pres.label}
        </span>
      );
    }
    const paymentUiKey = (pres.iconStatus || '').toLowerCase();
    // Payment-driven Sold labels stay read-only unless an admin is applying a manual override.
    if (!canOverrideSettlementStatus && (paymentUiKey === 'settled' || paymentUiKey === 'settlementinprogress')) {
      return (
        <span className={`${styles.statusBadge} ${getStatusColor(pres.iconStatus)}`}>
          {getStatusIcon(pres.iconStatus)}
          {pres.label}
        </span>
      );
    }
    // For terminal statuses, just show badge without dropdown
    if (status === 'return' || status === 'closed' || status === 'unsold' || status === 'invalid') {
      return (
        <span className={`${styles.statusBadge} ${getStatusColor(pres.iconStatus)}`}>
          {getStatusIcon(pres.iconStatus)}
          {pres.label}
        </span>
      );
    }
    const transitions = getTicketStatusTransitions(ticket);
    if (!transitions.length) {
      return (
        <span className={`${styles.statusBadge} ${getStatusColor(pres.iconStatus)}`}>
          {getStatusIcon(pres.iconStatus)}
          {pres.label}
        </span>
      );
    }
    return (
      <div
        className={[
          styles.statusDropdownWrapper,
          openStatusDropdown === ticket.tp_id ? styles.statusDropdownOpen : '',
        ].filter(Boolean).join(' ')}
      >
        <button
          className={`${styles.statusBadge} ${getStatusColor(pres.iconStatus)} ${styles.statusDropdownBtn}`}
          disabled={isStatusOperationInProgress}
          onClick={(e) => {
            e.stopPropagation();
            if (isStatusOperationInProgress) return;
            setOpenStatusDropdown(openStatusDropdown === ticket.tp_id ? null : ticket.tp_id);
          }}
        >
          {getStatusIcon(pres.iconStatus)}
          {pres.label}
          <ChevronDown size={12} style={{ marginLeft: 4 }} />
        </button>
        {openStatusDropdown === ticket.tp_id && (
          <div className={styles.statusDropdownList}>
            {transitions.map(opt => (
              <div
                key={opt.value}
                className={styles.statusDropdownListItem}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenStatusDropdown(null);
                  handleStatusChangeConfirm(ticket, opt.value);
                }}
              >
                {getStatusIcon(opt.value)}
                {opt.label}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  })()}
</td>,
                            marketplaces: showMarketplaceColumn ? renderListedMarketplaceCell([ticket]) : null,
                            vlShare: showSharedColumn ? renderListedSharedCell([ticket]) : null,
                            price: showPriceColumn ? (() => {
                              const soldAmount = Number(ticket.tp_sold_amount ?? 0);
                              const hasSoldAmount =
                                Number.isFinite(soldAmount) &&
                                soldAmount > 0;
                              const listAmount = Number(ticket.tp_list_price ?? 0);
                              const hasListPrice = Number.isFinite(listAmount) && listAmount > 0;
                              const display =
                                hasSoldAmount
                                  ? formatCurrency(ticket.tp_sold_amount ?? 0)
                                  : hasListPrice
                                    ? formatCurrency(ticket.tp_list_price ?? 0)
                                    : '—';
                              const canChangePrice =
                                rowTab === 'Listed' &&
                                (getResolvedClientType(ticket) === 'self-pricing' ||
                                  getResolvedClientType(ticket) === 'concierge');
                              return (
                                <td
                                  className={styles.tdPrice}
                                  onClick={canChangePrice ? (e) => e.stopPropagation() : undefined}
                                >
                                  {canChangePrice ? (
                                    <button
                                      type="button"
                                      className={styles.clickablePrice}
                                      title="Change Price"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleAction('Change Price', ticket, e);
                                      }}
                                    >
                                      {renderPricingMethodLabel(ticket.tp_pricing_method, ticket)}
                                      <Pencil size={13} aria-hidden />
                                      {display}
                                    </button>
                                  ) : (
                                      <span className={styles.priceDisplayWithIcon}>
                                        {renderPricingMethodLabel(ticket.tp_pricing_method, ticket)}
                                        {display}
                                      </span>
                                  )}
                                </td>
                              );
                            })() : null,
                            cost: showListedFinancialColumns ? (() => {
                              const cost = listedTicketCosts[ticket.tp_id];
                              return (
                                  <td className={styles.tdPrice}>
                                    <div className={styles.costEditor}>
                                      <span aria-hidden="true">$</span>
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        aria-label={`Cost for ${ticket.e_name || 'ticket'}`}
                                        value={listedCostDrafts[ticket.tp_id] ?? (cost == null ? '' : String(cost))}
                                        onClick={(event) => event.stopPropagation()}
                                        onChange={(event) => setListedCostDrafts((previous) => ({
                                          ...previous,
                                          [ticket.tp_id]: event.target.value,
                                        }))}
                                      />
                                      <button
                                        type="button"
                                        className={styles.costSaveButton}
                                        aria-label="Save cost"
                                        title="Save cost"
                                        disabled={savingListedCostId === ticket.tp_id}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          void saveListedTicketCost(ticket);
                                        }}
                                      >
                                        Save
                                      </button>
                                    </div>
                                  </td>
                              );
                            })() : null,
                            potentialPayout: showListedFinancialColumns ? (
                              <td className={styles.tdPrice}>{formatCurrency(getListedPotentialPayout(ticket))}</td>
                            ) : null,
                            difference: showListedFinancialColumns ? (() => {
                              const cost = listedTicketCosts[ticket.tp_id];
                              const payout = getListedPotentialPayout(ticket);
                              const difference = cost == null ? null : payout - cost;
                              return (
                                <td className={styles.tdPrice}>
                                  {difference == null ? '—' : (
                                    <span className={difference >= 0 ? styles.costProfit : styles.costLoss}>
                                      {difference >= 0 ? '+' : '−'}{formatCurrency(Math.abs(difference))}
                                      <small>{difference >= 0 ? 'Profit' : 'Loss'}</small>
                                    </span>
                                  )}
                                </td>
                              );
                            })() : null,
                            lastPriced: activeTab === 'Listed' ? (() => {
                              const pricePending = priceUpdatePendingIds.has(ticket.tp_id);
                              const badge = getLastPricedBadge(ticket);
                              const lastPricedValue = getLastPricedDisplayValue(ticket);
                              return (
                                <td className={styles.tdLastPriced}>
                                  <div className={styles.lastPricedCell}>
                                    {pricePending ? (
                                      <span className={styles.lastPricedSpinner} role="status" aria-label="Updating price" />
                                    ) : (
                                      <span className={styles.lastPricedDate} title={describeLastPricedBucket(ticket)}>
                                        {lastPricedValue}
                                      </span>
                                    )}
                                    {!pricePending && badge ? (
                                      <span
                                        className={`${styles.stalenessBadge} ${styles[badge.className]}`}
                                        title={badge.title}
                                        aria-label={badge.ariaLabel}
                                      >
                                        {badge.label}
                                      </span>
                                    ) : null}
                                  </div>
                                </td>
                              );
                            })() : null,
                            clientName: !hideSoldClientColumns ? (
                              <td className={styles.tdClient}>
                                <div
                                  className={styles.clientName}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleUserClick(ticket.u_id);
                                  }}
                                >
                                  {ticket.u_full_name ||
                                    `${ticket.u_first_name || ''} ${ticket.u_last_name || ''}`.trim() ||
                                    'N/A'}
                                </div>
                                <div className={styles.clientEmail}>{ticket.u_email_id || 'N/A'}</div>
                              </td>
                            ) : null,
                            returnEmail: showDelistReturnEmailColumn ? (
                              <td className={styles.tdReturnEmail}>
                                <div className={styles.clientEmail}>
                                  {ticket.tp_delist_requested_email?.trim() || '—'}
                                </div>
                              </td>
                            ) : null,
                            clientType: !hideSoldClientColumns ? (
                              <td className={styles.tdClientType}>
                                <span
                                  className={
                                    styles.clientTypeBadge + ' ' +
                                    (getResolvedClientType(ticket) === 'self-pricing'
                                      ? styles.selfPricingBadge
                                      : getResolvedClientType(ticket) === 'concierge'
                                      ? styles.conciergeBadge
                                      : '')
                                  }
                                >
                                  {getResolvedClientType(ticket) === 'self-pricing'
                                    ? 'Self Pricing'
                                    : getResolvedClientType(ticket) === 'concierge'
                                    ? 'Concierge'
                                    : 'N/A'}
                                </span>
                              </td>
                            ) : null,
                              };
                              return renderOrderedManageTicketCells(columnCells);
                            })()}
                            <td className={styles.tdActions}>
                              <div className={styles.actionMenuWrapper}>
                                <button
                                  className={styles.actionMenuBtn}
                                  onClick={(e) => toggleActionMenu(ticket.tp_id, e)}
                                >
                                  <MoreVertical size={16} />
                                </button>
                                {openActionMenu === ticket.tp_id && (
                                  <div className={styles.actionMenuList}>
                                    {(() => {
                                      const status = normalizeStatusValue(ticket.tp_status || '');
                                      const showWrongPid =
                                        !hideSuperUserDelistedActions &&
                                        !isSuperUserListedStatusReadOnly(isSuperUser, rowTab) &&
                                        (status === 'Verified' || status === 'List');
                                      if (!showWrongPid) return null;
                                      return (
                                        <button
                                          className={styles.actionMenuItem}
                                          title="Remove this ticket group from the wrong Victory Live production"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setOpenActionMenu(null);
                                            setWrongPidConfirm({ tpIds: [ticket.tp_id], tickets: [ticket] });
                                          }}
                                        >
                                          <X size={14} /> Wrong PID
                                        </button>
                                      );
                                    })()}
                                    {!hideListedSelfPricingActions && !hideSoldTicketDetailsActions && <button className={styles.actionMenuItem} onClick={(e) => handleAction('View Details', ticket, e)}>
                                      <Eye size={14} /> View Details
                                    </button>}
                                {!hideListedSelfPricingActions && !hideSoldTicketDetailsActions && (ticket.tp_status !== 'Return' && rowTab !== 'Deleted') && (
  <button className={styles.actionMenuItem} onClick={(e) => handleAction('Change Status', ticket, e)}>
    <CheckCircle size={14} /> Change Status
  </button>
)}
                                    {renderChangeSeatNumbersItem([ticket])}
                                    {!hideListedSelfPricingActions && rowTab === 'Listed' && (
                                      <button className={styles.actionMenuItem} onClick={(e) => handleAction('Pricing Method Status', ticket, e)}>
                                        <DollarSign size={14} /> Pricing Method Status
                                      </button>
                                    )}
                                    {!hideListedSelfPricingActions && !hideSoldTicketDetailsActions && <button className={styles.actionMenuItem} onClick={(e) => handleAction('Edit Details', ticket, e)}>
                                      <Edit size={14} /> Edit Details
                                    </button>}
                                    {rowTab === 'Listed' &&
                                      (getResolvedClientType(ticket) === 'self-pricing' ||
                                        getResolvedClientType(ticket) === 'concierge') && (
                                        <button
                                          className={styles.actionMenuItem}
                                          onClick={(e) => handleAction('Change Price', ticket, e)}
                                        >
                                          <DollarSign size={14} /> Change Price
                                        </button>
                                      )}
                                    <button
                                      className={styles.actionMenuItem}
                                      onClick={(e) => handleAction('Price Change Logs', ticket, e)}
                                    >
                                      <History size={14} /> Price Change Logs
                                    </button>
                                    {rowTab !== 'Listed' && ticket.tp_status === 'List' && (
                                      <button className={styles.actionMenuItem} onClick={(e) => handleAction('Delist', ticket, e)}>
                                        <Package size={14} /> Delist
                                      </button>
                                    )}
                                    {!hideListedSelfPricingActions && renderVlRowActionItems([ticket], rowTab)}
                                    {(() => {
                                      const status = normalizeStatusValue(ticket.tp_status || '');
                                      const allowedStatuses = ['Verified', 'List', 'Delist', 'ToBeVerified'];
                                      if (rowTab !== 'Listed' && !hideSuperUserDelistedActions && allowedStatuses.includes(status)) {
                                        return (
                                          <button className={styles.actionMenuItem} onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedTicketForDelete(ticket);
                                            setShowDeleteConfirmModal(true);
                                            setOpenActionMenu(null);
                                          }}>
                                            <X size={14} /> Delete
                                          </button>
                                        );
                                      }
                                      return null;
                                    })()}
                                    {!hideListedSelfPricingActions && !hideSoldTicketDetailsActions && <button className={styles.actionMenuItem} onClick={(e) => handleAction('View Client Profile', ticket, e)}>
                                      <FileText size={14} /> View Client Profile
                                    </button>}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                          {renderVlSupportPanel(
                            ticket.tp_id,
                            [ticket],
                            ticket,
                            ticket.e_name,
                            ticket.tp_section || 'N/A',
                            ticket.tp_row || 'N/A',
                            String(ticket.tp_seat_no || 'N/A'),
                          )}
                          {/* Expanded Row for Seats */}
                          {expandedRows.includes(ticket.tp_id) && (
                            <tr className={styles.expandedRow}>
                              <td colSpan={tableColSpan}>
                                <div className={styles.expandedContent}>
                                  {/* Venue / League / Email meta row */}
                                  <div className={styles.expandedMeta}>
                                    <span>
                                      <span className={styles.expandedMetaLabel}>Venue:</span>{' '}
                                      {ticket.e_address || 'N/A'}
                                    </span>
                                    <span className={styles.expandedMetaDivider}>|</span>
                                    <span>
                                      <span className={styles.expandedMetaLabel}>League:</span>{' '}
                                      {ticket.l_name || 'N/A'}
                                    </span>
                                    <span className={styles.expandedMetaDivider}>|</span>
                                    <span>
                                      <span className={styles.expandedMetaLabel}>Email:</span>{' '}
                                      {ticket.u_email_id || (ticket as any).u_original_email || 'N/A'}
                                    </span>
                                    {!hideSoldTicketDetailsActions && getDisplayedTicketVlProductionId(ticket, vlProductionIdByEventId) && (
                                      <>
                                        <span className={styles.expandedMetaDivider}>|</span>
                                        <span>
                                          <span className={styles.expandedMetaLabel}>Production ID:</span>{' '}
                                          {getDisplayedTicketVlProductionId(ticket, vlProductionIdByEventId)}
                                        </span>
                                      </>
                                    )}
                                    {showExpandedCommission && (
                                      <>
                                        <span className={styles.expandedMetaDivider}>|</span>
                                        <span>
                                          <span className={styles.expandedMetaLabel}>Commission:</span>{' '}
                                          {getSalesRepCommission(ticket) > 0
                                            ? formatCurrency(getSalesRepCommission(ticket))
                                            : '—'}{' '}
                                          {canEditSoldCommission && (
                                          <button
                                            type="button"
                                            className={styles.commissionEditButton}
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              openCommissionModal(ticket);
                                            }}
                                            title="Edit commission for this line"
                                            aria-label="Edit commission for this line"
                                          >
                                            <Pencil size={13} aria-hidden />
                                          </button>
                                          )}
                                        </span>
                                        <span className={styles.expandedMetaDivider}>|</span>
                                        <span>
                                          <span className={styles.expandedMetaLabel}>Marketplace:</span>{' '}
                                          {getTicketMarketplaceName(ticket) || '—'}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                  <div className={styles.expandedHeader}>
                                    <div>
                                      <h4>
                                        Individual Seats{' '}
                                        <span className={styles.seatCountLabel}>({generateSeats(ticket).length} seats)</span>
                                      </h4>
                                      {shouldShowBarcodeStatus(rowTab, isSuperUser) && !ticketHasBarcodeAttached(ticket) && (
                                        <p className={styles.barcodeMissingSummary}>
                                          {getBarcodeMissingSummary([ticket])}
                                        </p>
                                      )}
                                    </div>
                                    <div className={styles.selectAllContainer}>
                                      <label className={styles.selectAllLabel} onClick={(e) => e.stopPropagation()}>
                                        <input
                                          type="checkbox"
                                          checked={generateSeats(ticket).every((seat) => selectedSeats[seat.id])}
                                          ref={(element) => {
                                            if (element) {
                                              const seatIds = generateSeats(ticket).map((seat) => seat.id);
                                              element.indeterminate =
                                                seatIds.some((seatId) => selectedSeats[seatId]) &&
                                                !seatIds.every((seatId) => selectedSeats[seatId]);
                                            }
                                          }}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            handleSelectSeatsByIds(
                                              generateSeats(ticket).map((seat) => seat.id),
                                              e.target.checked,
                                            );
                                          }}
                                        />
                                        <span>{generateSeats(ticket).every((seat) => selectedSeats[seat.id]) ? 'Deselect All' : 'Select All'}</span>
                                      </label>
                                    </div>
                                  </div>
                                  <div className={styles.seatsGrid}>
                                    {generateSeats(ticket).map((seat) => (
                                      <div
                                        key={seat.id}
                                        className={`${styles.seatCard} ${selectedSeats[seat.id] ? styles.selectedSeat : ''}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSelectSeat(seat.id);
                                        }}
                                      >
                                        <div className={styles.seatCardHeader}>
                                          <div className={styles.seatSelection}>
                                            {selectedSeats[seat.id] ? 
                                              <CheckSquare size={14} className={styles.checkedIcon} /> : 
                                              <Square size={14} className={styles.uncheckedIcon} />
                                            }
                                            <span className={styles.seatNumber}>Seat {seat.number}</span>
                                          </div>
                                          <div className={styles.seatCardHeaderActions}>
                                            {renderSeatCardStatus(ticket, seat.id, rowTab)}
                                          </div>
                                        </div>
                                        <div className={styles.seatDetails}>
                                          <span>Sec {seat.section} • Row {seat.row}</span>
                                            <span className={styles.seatPrice}>
                                              {renderPricingMethodLabel(ticket.tp_pricing_method, ticket)}
                                              {formatCurrency(Number(seat.price) || 0)}
                                            </span>
                                        </div>
                                        {shouldShowBarcodeStatus(rowTab, isSuperUser) && renderSeatBarcodeStatus(ticket)}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={tableColSpan} className={styles.noResults}>
                          {isEventGroupedView
                            ? 'No tickets found for this event'
                            : 'No tickets found matching the selected filters'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
{/* Concierge VL price changes */}
{showConciergePriceModal && (
  <div
    className={styles.modalOverlay}
  >
    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
      <div className={styles.modalHeader}>
        <h3>Concierge Price Updates</h3>
        <button
          type="button"
          className={styles.modalClose}
          onClick={() => setShowConciergePriceModal(false)}
          disabled={conciergePriceLoading}
        >
          <X size={18} />
        </button>
      </div>
      <div className={styles.modalBody}>
        <p style={{ marginTop: 0 }}>This run (from Victory Live)</p>
        <div style={{ maxHeight: 240, overflowY: 'auto', marginBottom: 16 }}>
          {(conciergePriceResults.filter(
            (r) =>
              r.status === 'up' ||
              r.status === 'down' ||
              r.status === 'failed' ||
              isVlUnknownStatus(r.status),
          ).length
            ? conciergePriceResults.filter(
                (r) =>
                  r.status === 'up' ||
                  r.status === 'down' ||
                  r.status === 'failed' ||
                  isVlUnknownStatus(r.status),
              )
            : conciergePriceResults
          ).map((row) => (
            <div
              key={row.tpId}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 0',
                borderBottom: '1px solid rgba(0,0,0,0.06)',
                fontSize: 13,
              }}
            >
              {row.direction === 'up' ? (
                <TrendingUp size={16} color="#16a34a" />
              ) : row.direction === 'down' ? (
                <TrendingDown size={16} color="#dc2626" />
              ) : (
                <RefreshCcw size={14} />
              )}
              <div style={{ flex: 1 }}>
                <div>
                  {row.eventName || 'Event'} • Sec {row.section || 'N/A'} • Row {row.row || 'N/A'} • Seat{' '}
                  {row.seat ?? 'N/A'}
                </div>
                <div style={{ opacity: 0.75 }}>
                  {row.oldPrice != null ? `$${Number(row.oldPrice).toFixed(2)}` : '—'}
                  {' → '}
                  {row.newPrice != null ? `$${Number(row.newPrice).toFixed(2)}` : '—'}
                  {row.message ? ` · ${row.message}` : ''}
                </div>
              </div>
            </div>
          ))}
          {!conciergePriceResults.length && (
            <div style={{ opacity: 0.7, padding: '8px 0' }}>No results for this run.</div>
          )}
        </div>
        <p style={{ marginBottom: 8 }}>
          All price changes ({conciergeRecentChanges.length})
        </p>
        <div style={{ maxHeight: 280, overflowY: 'auto' }}>
          {conciergeRecentChanges.length === 0 ? (
            <div style={{ opacity: 0.7, padding: '8px 0' }}>No price change history for these seats yet.</div>
          ) : (
            conciergeRecentChanges.map((row) => (
              <div
                key={row.lpId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 0',
                  borderBottom: '1px solid rgba(0,0,0,0.06)',
                  fontSize: 13,
                }}
              >
                {row.direction === 'up' ? (
                  <TrendingUp size={16} color="#16a34a" />
                ) : row.direction === 'down' ? (
                  <TrendingDown size={16} color="#dc2626" />
                ) : (
                  <RefreshCcw size={14} />
                )}
                <div style={{ flex: 1 }}>
                  <div>
                    {row.eventName || 'Event'} • Sec {row.section || 'N/A'} • Row {row.row || 'N/A'} • Seat{' '}
                    {row.seat ?? 'N/A'}
                  </div>
                  <div style={{ opacity: 0.75 }}>
                    {row.oldPrice != null ? `$${Number(row.oldPrice).toFixed(2)}` : '—'}
                    {' → '}
                    {row.newPrice != null ? `$${Number(row.newPrice).toFixed(2)}` : '—'}
                    {row.editType ? ` · ${row.editType}` : ''}
                    {row.createdAt
                      ? ` · ${new Date(row.createdAt).toLocaleString()}`
                      : ''}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      <div className={styles.modalFooter}>
        <button
          type="button"
          className={styles.modalConfirm}
          onClick={() => setShowConciergePriceModal(false)}
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}
{/* Split on Victory Live — same idea as VL Apply Split quantity */}
{showVlSplitModal && (
  <div className={styles.modalOverlay}>
    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
      <div className={styles.modalHeader}>
        <h3>Split Ticket Group on VL</h3>
        <button
          type="button"
          className={styles.modalClose}
          onClick={() => setShowVlSplitModal(false)}
          disabled={vlSplitLoading}
        >
          <X size={18} />
        </button>
      </div>
      <div className={styles.modalBody}>
        <p>
          Group has <strong>{vlSplitGroupSize}</strong> seats. Enter how many to split off
          (from the <strong>last / highest</strong> seat numbers — same as Victory Live).
          Listing details (price, share, marketplaces, notes) are preserved.
        </p>
        <label style={{ display: 'block', marginTop: 12, marginBottom: 6 }}>Quantity</label>
        <input
          type="number"
          min={1}
          max={Math.max(1, vlSplitGroupSize - 1)}
          value={vlSplitQuantity}
          disabled={vlSplitLoading}
          className={styles.vlShareInput || undefined}
          style={{ width: 120, padding: '8px 10px' }}
          onChange={(e) => {
            const next = e.target.value;
            setVlSplitQuantity(next);
            const qty = Number(next);
            if (!Number.isFinite(qty) || qty < 1) {
              setVlSplitSeatPreview('');
              return;
            }
            const sorted = vlSplitTpIds
              .map((tpId) => filteredTickets.find((ticket) => ticket.tp_id === tpId))
              .filter((ticket): ticket is Ticket => Boolean(ticket))
              .sort((a, b) => Number(a.tp_seat_no) - Number(b.tp_seat_no));
            setVlSplitSeatPreview(
              sorted
                .slice(-Math.floor(qty))
                .map((t) => String(t.tp_seat_no ?? '').trim())
                .filter(Boolean)
                .join(', '),
            );
          }}
        />
        {vlSplitSeatPreview ? (
          <p style={{ marginTop: 10 }}>
            Will move seats <strong>{vlSplitSeatPreview}</strong> into a new VL group.
            Remaining seats stay in the original group.
          </p>
        ) : null}
        {vlSplitLoading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
            <div className={styles.spinner} />
            Splitting on Victory Live…
          </div>
        )}
      </div>
      <div className={styles.modalFooter}>
        <button
          className={styles.modalCancel}
          onClick={() => setShowVlSplitModal(false)}
          disabled={vlSplitLoading}
        >
          Cancel
        </button>
        <button
          className={styles.modalConfirm}
          onClick={handleConfirmVlSplit}
          disabled={vlSplitLoading}
        >
          {vlSplitLoading ? 'Applying…' : 'Apply Split'}
        </button>
      </div>
    </div>
  </div>
)}
{/* Share on Victory Live marketplaces */}
{showVlShareModal && (() => {
  const vlShareSelectedTickets = Array.from(bulkSelectedTicketIds)
    .map((tpId) => filteredTickets.find((ticket) => ticket.tp_id === tpId))
    .filter((ticket): ticket is Ticket => Boolean(ticket));
  const vlShareUseCommonPrice =
    vlShareSelectedTickets.length > 1 &&
    vlShareSelectedTickets.every(
      (ticket) =>
        Number(ticket.tp_list_price) === Number(vlShareSelectedTickets[0]?.tp_list_price),
    );
  const vlShareCommonTpId = vlShareSelectedTickets[0]?.tp_id || '';
  const vlShareMarketplaceIds = vlShareSelectedTickets[0]
    ? getTicketMarketplaceIds(
        vlShareSelectedTickets[0],
        marketplaceLinksByProductionId,
        vlProductionIdByEventId,
      )
    : { stubhubId: null, seatgeekId: null };
  const vlSharePriceMarketplaceLinks = (
    <EventMarketplaceDeepLinks
      stubhubId={vlShareMarketplaceIds.stubhubId}
      seatgeekId={vlShareMarketplaceIds.seatgeekId}
      eventName={vlShareSelectedTickets[0]?.e_name}
      eventDate={formatVenueEventDateYmd(
        vlShareSelectedTickets[0]?.e_date,
        vlShareSelectedTickets[0]?.e_time_zone,
        vlShareSelectedTickets[0]?.e_date_time_zone,
        vlShareSelectedTickets[0]?.e_time,
      )}
      className={styles.marketplaceDeepLinksInline}
      linkClassName={styles.marketplaceDeepLink}
      disabledClassName={styles.marketplaceDeepLinkDisabled}
      stubhubClassName={styles.marketplaceDeepLinkStubhub}
      seatgeekClassName={styles.marketplaceDeepLinkSeatgeek}
    />
  );

  return (
  <div className={styles.modalOverlay}>
    <div
      className={`${styles.modalContent} ${styles.vlShareModal}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className={styles.modalHeader}>
        <h3>Share on Victory Live</h3>
        <button
          type="button"
          className={styles.modalClose}
          onClick={() => {
            wrongPidFlowRef.current = null;
            setShowVlShareModal(false);
          }}
          disabled={vlShareLoading}
        >
          <X size={18} />
        </button>
      </div>
      <div className={styles.modalBody}>
        <p className={styles.vlShareHint}>
          Select marketplaces, edit section/row/seat/price/notes for {bulkSelectedTicketIds.size} seat(s).
          Updates Victory Live and QuickAsyst; QA status becomes Listed on success.
        </p>
        {vlShareSelectedTickets[0] && (
          <div className={styles.vlShareEventSummary}>
            <div className={styles.eventName}>
              {vlShareSelectedTickets[0].e_name || 'Selected tickets'}
            </div>
          </div>
        )}

        {vlShareLoading && (
          <div className={styles.vlShareLoadingRow}>
            <div className={styles.spinner} />
            Sharing to Victory Live…
          </div>
        )}

        <div className={styles.vlShareSection}>
          <label className={styles.vlShareLabel}>Section / Row / Seat</label>
          {vlShareSelectedTickets.length === 1 ? (
            <div className={styles.vlShareFieldStack}>
              <div className={styles.vlShareSeatGrid}>
                <input
                  type="text"
                  className={styles.vlShareInput}
                  value={vlShareSections[vlShareSelectedTickets[0].tp_id] ?? ''}
                  disabled={vlShareLoading}
                  onChange={(e) =>
                    setVlShareSections((prev) => ({
                      ...prev,
                      [vlShareSelectedTickets[0].tp_id]: e.target.value,
                    }))
                  }
                  placeholder="Section"
                />
                <input
                  type="text"
                  className={styles.vlShareInput}
                  value={vlShareRows[vlShareSelectedTickets[0].tp_id] ?? ''}
                  disabled={vlShareLoading}
                  onChange={(e) =>
                    setVlShareRows((prev) => ({
                      ...prev,
                      [vlShareSelectedTickets[0].tp_id]: e.target.value,
                    }))
                  }
                  placeholder="Row"
                />
                <input
                  type="text"
                  className={styles.vlShareInput}
                  value={vlShareSeats[vlShareSelectedTickets[0].tp_id] ?? ''}
                  disabled={vlShareLoading}
                  onChange={(e) =>
                    setVlShareSeats((prev) => ({
                      ...prev,
                      [vlShareSelectedTickets[0].tp_id]: e.target.value,
                    }))
                  }
                  placeholder="Seat"
                />
              </div>
              <div>
                <label className={styles.vlShareLabel}>
                  List price
                  {vlSharePriceMarketplaceLinks}
                </label>
                <div className={styles.vlSharePriceWrap}>
                  <input
                    type="text"
                    className={styles.vlSharePriceInput}
                    inputMode="decimal"
                    value={vlSharePrices[vlShareSelectedTickets[0].tp_id] ?? ''}
                    disabled={vlShareLoading}
                    onChange={(e) =>
                      setVlSharePrices((prev) => ({
                        ...prev,
                        [vlShareSelectedTickets[0].tp_id]: sanitizePriceInput(e.target.value),
                      }))
                    }
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
          ) : vlShareUseCommonPrice &&
            vlShareSelectedTickets.every(
              (t) =>
                String(t.tp_section || '') === String(vlShareSelectedTickets[0]?.tp_section || '') &&
                String(t.tp_row || '') === String(vlShareSelectedTickets[0]?.tp_row || ''),
            ) ? (
            <div className={styles.vlShareFieldStack}>
              <div className={styles.vlShareSeatGridTwo}>
                <input
                  type="text"
                  className={styles.vlShareInput}
                  value={vlShareSections[vlShareCommonTpId] ?? ''}
                  disabled={vlShareLoading}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    setVlShareSections((prev) => {
                      const next = { ...prev };
                      for (const ticket of vlShareSelectedTickets) next[ticket.tp_id] = nextValue;
                      return next;
                    });
                  }}
                  placeholder="Section for all"
                />
                <input
                  type="text"
                  className={styles.vlShareInput}
                  value={vlShareRows[vlShareCommonTpId] ?? ''}
                  disabled={vlShareLoading}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    setVlShareRows((prev) => {
                      const next = { ...prev };
                      for (const ticket of vlShareSelectedTickets) next[ticket.tp_id] = nextValue;
                      return next;
                    });
                  }}
                  placeholder="Row for all"
                />
              </div>
              <div className={styles.vlShareSeatList}>
                {vlShareSelectedTickets.map((ticket) => (
                  <div key={ticket.tp_id} className={styles.vlShareSeatRow}>
                    <span className={styles.vlShareSeatRowLabel}>Seat</span>
                    <input
                      type="text"
                      className={styles.vlShareInput}
                      value={vlShareSeats[ticket.tp_id] ?? ''}
                      disabled={vlShareLoading}
                      onChange={(e) =>
                        setVlShareSeats((prev) => ({ ...prev, [ticket.tp_id]: e.target.value }))
                      }
                      placeholder="Seat"
                    />
                  </div>
                ))}
              </div>
              <div>
                <label className={styles.vlShareLabel}>
                  List price
                  {vlSharePriceMarketplaceLinks}
                </label>
                <div className={styles.vlSharePriceWrap}>
                  <input
                    type="text"
                    className={styles.vlSharePriceInput}
                    inputMode="decimal"
                    value={vlSharePrices[vlShareCommonTpId] ?? ''}
                    disabled={vlShareLoading}
                    onChange={(e) => {
                      const nextValue = sanitizePriceInput(e.target.value);
                      setVlSharePrices((prev) => {
                        const next = { ...prev };
                        for (const ticket of vlShareSelectedTickets) next[ticket.tp_id] = nextValue;
                        return next;
                      });
                    }}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.vlShareTicketList}>
              {vlShareSelectedTickets.map((ticket) => (
                <div key={ticket.tp_id} className={styles.vlShareTicketBlock}>
                  <div className={styles.vlShareSeatGrid}>
                    <input
                      type="text"
                      className={styles.vlShareInput}
                      value={vlShareSections[ticket.tp_id] ?? ''}
                      disabled={vlShareLoading}
                      onChange={(e) =>
                        setVlShareSections((prev) => ({ ...prev, [ticket.tp_id]: e.target.value }))
                      }
                      placeholder="Section"
                    />
                    <input
                      type="text"
                      className={styles.vlShareInput}
                      value={vlShareRows[ticket.tp_id] ?? ''}
                      disabled={vlShareLoading}
                      onChange={(e) =>
                        setVlShareRows((prev) => ({ ...prev, [ticket.tp_id]: e.target.value }))
                      }
                      placeholder="Row"
                    />
                    <input
                      type="text"
                      className={styles.vlShareInput}
                      value={vlShareSeats[ticket.tp_id] ?? ''}
                      disabled={vlShareLoading}
                      onChange={(e) =>
                        setVlShareSeats((prev) => ({ ...prev, [ticket.tp_id]: e.target.value }))
                      }
                      placeholder="Seat"
                    />
                  </div>
                  <div>
                    <label className={styles.vlShareLabel}>
                      List price
                      {vlSharePriceMarketplaceLinks}
                    </label>
                    <div className={styles.vlSharePriceWrap}>
                    <input
                      type="text"
                      className={styles.vlSharePriceInput}
                      inputMode="decimal"
                      value={vlSharePrices[ticket.tp_id] ?? ''}
                      disabled={vlShareLoading}
                      onChange={(e) =>
                        setVlSharePrices((prev) => ({
                          ...prev,
                          [ticket.tp_id]: sanitizePriceInput(e.target.value),
                        }))
                      }
                      placeholder="0.00"
                    />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {!Array.from(bulkSelectedTicketIds).every((id) => {
          const ticket =
            filteredTickets.find((row) => row.tp_id === id) ||
            statusFilterSourceTickets.find((row) => row.tp_id === id);
          return ticket ? getResolvedClientType(ticket) === 'self-pricing' : false;
        }) && (
        <div className={styles.vlShareSection}>
          <label className={styles.vlShareLabel}>Pricing method</label>
          <div className={styles.pricingMethodOptions} role="radiogroup" aria-label="Pricing method">
            <label className={`${styles.pricingMethodOption} ${vlSharePricingMethod === 'MANUAL' ? styles.pricingMethodOptionSelected : ''}`}>
              <input type="radio" name="vl-pricing-method" value="MANUAL" checked={vlSharePricingMethod === 'MANUAL'} disabled={vlShareLoading} onChange={() => setVlSharePricingMethod('MANUAL')} />
              <span>Manually Priced</span>
              <span
                className={styles.manualPriceBadge}
                title="Manually Priced"
                aria-label="Manually Priced"
              >
                <DollarSign className={styles.pricingMethodPriceIcon} size={13} aria-hidden="true" />
              </span>
            </label>
            <label className={`${styles.pricingMethodOption} ${vlSharePricingMethod === 'FOLLOW_MARKET' ? styles.pricingMethodOptionSelected : ''}`}>
              <input type="radio" name="vl-pricing-method" value="FOLLOW_MARKET" checked={vlSharePricingMethod === 'FOLLOW_MARKET'} disabled={vlShareLoading} onChange={() => setVlSharePricingMethod('FOLLOW_MARKET')} />
              <span>Follow the Market — Active</span>
              <span
                className={styles.followMarketPriceBadge}
                title="Follow Market"
                aria-label="Follow Market"
              >
                <TrendingUp className={styles.pricingMethodPriceIcon} size={13} aria-hidden="true" />
              </span>
            </label>
            <label className={`${styles.pricingMethodOption} ${vlSharePricingMethod === 'NONE' ? styles.pricingMethodOptionSelected : ''}`}>
              <input type="radio" name="vl-pricing-method" value="NONE" checked={vlSharePricingMethod === 'NONE'} disabled={vlShareLoading} onChange={() => setVlSharePricingMethod('NONE')} />
              <span>Nothing</span>
            </label>
          </div>
        </div>
        )}

        <div className={styles.vlShareSection}>
          <label className={styles.vlShareLabel}>Internal notes</label>
          {isParkingPassNoteGroup(vlShareSelectedTickets.map((ticket) => vlShareRows[ticket.tp_id] ?? ticket.tp_row)) ? (
            <textarea
              className={styles.vlShareTextarea}
              value={vlShareInternalNotes}
              disabled={vlShareLoading}
              rows={3}
              onChange={(e) => {
                vlShareNotesEditedRef.current = true;
                setVlShareInternalNoteQuery(e.target.value);
                setVlShareInternalNotes(e.target.value);
              }}
            />
          ) : (
          <ListingNoteCombobox
            value={vlShareInternalNoteQuery}
            options={vlShareInternalNoteChoices}
            sentValue={vlShareInternalNotes}
            disabled={vlShareLoading}
            placeholder="Search or enter internal notes"
            onChange={(value) => {
              vlShareNotesEditedRef.current = true;
              const draft = composeListingNoteDraft(value, vlShareInternalNoteChoices, vlShareListingCustom, 'internal');
              setVlShareInternalNoteQuery(draft.query);
              setVlShareInternalNotes(draft.sent);
            }}
          />
          )}
        </div>

        <div className={styles.vlShareSection}>
          <label className={styles.vlShareLabel}>External notes</label>
          {isParkingPassNoteGroup(vlShareSelectedTickets.map((ticket) => vlShareRows[ticket.tp_id] ?? ticket.tp_row)) ? (
            <textarea
              className={styles.vlShareTextarea}
              value={vlShareExternalNotes}
              disabled={vlShareLoading}
              rows={3}
              onChange={(e) => {
                vlShareNotesEditedRef.current = true;
                setVlShareExternalNoteQuery(e.target.value);
                setVlShareExternalNotes(e.target.value);
              }}
            />
          ) : (
          <ListingNoteCombobox
            value={vlShareExternalNoteQuery}
            options={vlShareExternalNoteChoices}
            sentValue={vlShareExternalNotes}
            disabled={vlShareLoading}
            placeholder="Search or enter external notes"
            onChange={(value) => {
              vlShareNotesEditedRef.current = true;
              const draft = composeListingNoteDraft(value, vlShareExternalNoteChoices, vlShareListingCustom, 'external');
              setVlShareExternalNoteQuery(draft.query);
              setVlShareExternalNotes(draft.sent);
            }}
          />
          )}
        </div>

        <div className={styles.vlShareSection}>
          <label className={styles.vlShareLabel}>QA notes</label>
          <textarea
            className={styles.vlShareTextarea}
            value={vlShareQaNotes}
            disabled={vlShareLoading}
            onChange={(e) => setVlShareQaNotes(e.target.value)}
            placeholder="QA notes (saved in QuickAsyst only, not sent to Victory Live)"
            rows={2}
          />
        </div>

        <div className={styles.vlShareMarketplaceActions}>
          <button
            type="button"
            className={styles.modalCancel}
            onClick={() => setVlEnabledMarketplaces(VL_MARKETPLACE_OPTIONS.map((m) => m.id))}
            disabled={vlShareLoading}
          >
            Select all
          </button>
          <button
            type="button"
            className={styles.modalCancel}
            onClick={() => setVlEnabledMarketplaces([])}
            disabled={vlShareLoading}
          >
            Clear all
          </button>
        </div>

        <div className={styles.vlShareMarketplaceGrid}>
          {VL_MARKETPLACE_OPTIONS.map((m) => {
            const checked = vlEnabledMarketplaces.includes(m.id);
            return (
              <label key={m.id} className={styles.vlShareMarketplaceOption}>
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={vlShareLoading}
                  onChange={() => {
                    setVlEnabledMarketplaces((prev) =>
                      checked ? prev.filter((id) => id !== m.id) : [...prev, m.id],
                    );
                  }}
                />
                <MarketplaceBrandMark id={m.id} />
                {m.label}
              </label>
            );
          })}
        </div>
      </div>
      <div className={styles.modalFooter}>
        <button
          type="button"
          className={styles.modalCancel}
          onClick={() => {
            wrongPidFlowRef.current = null;
            setShowVlShareModal(false);
          }}
          disabled={vlShareLoading}
        >
          Cancel
        </button>
        <button
          type="button"
          className={styles.modalConfirm}
          onClick={handleShareVlListings}
          disabled={vlShareLoading || vlEnabledMarketplaces.length === 0}
        >
          {vlShareLoading ? 'Sharing…' : 'Share & List'}
        </button>
      </div>
    </div>
  </div>
  );
})()}
{pendingSeatNotesSave && (
  <div className={styles.modalOverlay}>
    <div className={styles.modalContent} onClick={(event) => event.stopPropagation()}>
      <div className={styles.modalHeader}>
        <h3>Save Profile Notes</h3>
      </div>
      <div className={styles.modalBody}>
        <p>
          Save these notes to this Team + seating location on the user's profile for future listing requests?
        </p>
      </div>
      <div className={styles.modalFooter}>
        <button
          type="button"
          className={styles.modalCancel}
          onClick={() => void handleSeatNotesSaveChoice(false)}
          disabled={seatNotesSaveLoading}
        >
          No
        </button>
        <button
          type="button"
          className={styles.modalConfirm}
          onClick={() => void handleSeatNotesSaveChoice(true)}
          disabled={seatNotesSaveLoading}
        >
          {seatNotesSaveLoading ? 'Saving…' : 'Yes'}
        </button>
      </div>
    </div>
  </div>
)}
{wrongPidConfirm && (
  <div className={styles.modalOverlay}>
    <div className={styles.modalContent} onClick={(event) => event.stopPropagation()}>
      <div className={styles.modalHeader}>
        <h3>Wrong PID</h3>
      </div>
      <div className={styles.modalBody}>
        <p>Do you want to delete this wrong PID ticket group?</p>
      </div>
      <div className={styles.modalFooter}>
        <button
          type="button"
          className={styles.modalCancel}
          disabled={wrongPidLoading}
          onClick={() => setWrongPidConfirm(null)}
        >
          No
        </button>
        <button
          type="button"
          className={styles.modalConfirm}
          disabled={wrongPidLoading}
          onClick={() => void confirmWrongPid()}
        >
          {wrongPidLoading ? 'Removing…' : 'Yes'}
        </button>
      </div>
    </div>
  </div>
)}
{/* Step 3 PID confirmation / legacy failed-submit remap */}
{showVlProductionMapModal && vlProductionMismatch && (
  <div
    className={styles.modalOverlay}
  >
    <div
      className={`${styles.modalContent} ${styles.vlProductionMapModal}`}
      onClick={(e) => e.stopPropagation()}
    >
      {vlProductionMapLoading && (
        <div className={styles.vlModalBusyOverlay} role="status" aria-live="polite">
          <div className={styles.spinner} />
          <p>
            {statusTableBusyCount > 0
              ? statusTableBusyMessage
              : vlProductionModalMode === 'add-pid'
                ? 'Saving Production ID…'
                : 'Submitting to Victory Live…'}
          </p>
        </div>
      )}
      <div className={styles.modalHeader}>
        <h3>Search Events for Production IDs</h3>
        <button
          type="button"
          className={styles.modalClose}
          disabled={vlProductionMapLoading || vlProductionSearchLoading}
          onClick={() => {
            wrongPidFlowRef.current = null;
            setShowVlProductionMapModal(false);
          }}
        >
          ×
        </button>
      </div>
      <div className={styles.modalBody}>
        <p className={styles.vlShareHint}>
          {vlProductionModalMode === 'add-pid'
            ? 'Add or overwrite the Production ID for '
            : vlProductionModalMode === 'confirm'
              ? 'Select the Production ID for '
              : 'Overwrite the Victory Live production for '}
          <strong>{vlProductionMismatch.eventName}</strong> @ {vlProductionMismatch.venue} on{' '}
          {vlProductionMismatch.eventDate}.{' '}
          {vlProductionModalMode === 'add-pid'
            ? 'Saves to QuickAsyst only — ticket status stays the same and Victory Live is not updated.'
            : vlProductionModalMode === 'confirm'
              ? 'Submit to VL and Mark Verified saves the PID, creates Victory Live inventory, and moves tickets to Verified in one step.'
              : 'This overwrites the PID, submits inventory to Victory Live, and moves tickets to Verified.'}
        </p>
        {/* Enter PID tab hidden for now — search is the only visible option.
        <button
          type="button"
          role="tab"
          aria-selected={vlAddPidTab === 'enter'}
          className={`${styles.vlAddPidTab} ${vlAddPidTab === 'enter' ? styles.vlAddPidTabActive : ''}`}
          disabled={vlProductionMapLoading}
          onClick={() => setVlAddPidTab('enter')}
        >
          Enter PID
        </button>
        */}
        {(vlProductionModalMode !== 'add-pid' || vlAddPidTab === 'search') && (
          <>
            <div className={styles.vlProductionSearchRow}>
              <input
                className={styles.vlProductionSearchInput}
                value={vlProductionSearchQuery}
                disabled={vlProductionMapLoading || vlProductionSearchLoading}
                onChange={(e) => setVlProductionSearchQuery(e.target.value)}
                placeholder="Event name (e.g. New York Yankees)"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearchVlProductions();
                  }
                }}
              />
              <button
                type="button"
                className={styles.modalConfirm}
                onClick={handleSearchVlProductions}
                disabled={vlProductionMapLoading || vlProductionSearchLoading}
              >
                {vlProductionSearchLoading ? 'Searching…' : 'Search'}
              </button>
            </div>
            <div className={styles.vlProductionTableWrap}>
              <table className={styles.vlProductionTable}>
                <thead>
                  <tr>
                    <th />
                    <th>Production ID</th>
                    <th>Event Name</th>
                    <th>Venue</th>
                    <th>Date</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {vlProductionSearchResults.length === 0 ? (
                    <tr>
                      <td colSpan={6}>No productions found — adjust the search or escalate</td>
                    </tr>
                  ) : (
                    vlProductionSearchResults.map((row) => (
                      <tr
                        key={row.productionId}
                        className={
                          vlSelectedProductionId === row.productionId
                            ? styles.vlProductionRowSelected
                            : undefined
                        }
                        onClick={() => setVlSelectedProductionId(row.productionId)}
                      >
                        <td>
                          <input
                            type="radio"
                            name="vlProductionPick"
                            checked={vlSelectedProductionId === row.productionId}
                            onChange={() => setVlSelectedProductionId(row.productionId)}
                            disabled={vlProductionMapLoading}
                          />
                        </td>
                        <td>
                          <div className={styles.vlProductionIdCell}>
                            <span>{row.productionId}</span>
                            {row.isSuggested ? (
                              <span className={styles.suggestedPidPill}>Suggested</span>
                            ) : null}
                          </div>
                        </td>
                        <td>{displayVlEventName(row.eventName) || '—'}</td>
                        <td>{row.venueName || '—'}</td>
                        <td>{formatVlProductionDate(row.eventDate)}</td>
                        <td>{formatVlProductionTime(row.eventDate, row.eventTime)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
        {vlProductionModalMode === 'add-pid' && vlAddPidTab === 'enter' && (
          <div className={styles.vlEnterPidPanel}>
            <label className={styles.vlEnterPidLabel} htmlFor="vl-enter-production-id">
              Production ID
            </label>
            <div className={styles.vlProductionSearchRow}>
              <input
                id="vl-enter-production-id"
                className={styles.vlProductionSearchInput}
                value={vlEnteredProductionId}
                disabled={vlProductionMapLoading}
                inputMode="numeric"
                autoComplete="off"
                placeholder="Enter Production ID (numbers only)"
                onChange={(e) => setVlEnteredProductionId(e.target.value.replace(/[^\d]/g, ''))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void handleMapVlProductionAndRetry();
                  }
                }}
              />
            </div>
            <p className={styles.vlEnterPidHint}>
              Saves this Production ID to QuickAsyst only. Ticket status stays the same.
            </p>
          </div>
        )}
      </div>
      <div className={styles.modalFooter}>
        <button
          type="button"
          className={styles.modalCancel}
          onClick={() => {
            wrongPidFlowRef.current = null;
            setShowVlProductionMapModal(false);
          }}
          disabled={vlProductionMapLoading}
        >
          Cancel
        </button>
        <button
          type="button"
          className={styles.modalConfirm}
          onClick={handleMapVlProductionAndRetry}
          disabled={
            vlProductionMapLoading ||
            (vlProductionModalMode === 'add-pid' && vlAddPidTab === 'enter'
              ? !/^\d+$/.test(vlEnteredProductionId.trim())
              : !vlSelectedProductionId)
          }
        >
          {vlProductionMapLoading
            ? vlProductionModalMode === 'add-pid'
              ? 'Saving…'
              : 'Submitting to VL…'
            : vlProductionModalMode === 'add-pid'
              ? vlAddPidTab === 'enter'
                ? 'Add PID'
                : 'Save PID to QuickAsyst'
              : vlProductionModalMode === 'confirm'
                ? 'Submit to VL and Mark Verified'
                : 'Overwrite, Submit to VL & Mark Verified'}
        </button>
      </div>
    </div>
  </div>
)}
{/* Bulk Status Change Modal */}
{showBulkStatusModal && bulkSelectedTickets.length > 0 && (() => {
  const isBulkListTransition =
    normalizeStatusValue(bulkNewStatus) === 'List' ||
    normalizeStatusValue(bulkNewStatus) === 'List_NonVL';
  const isBulkSoldTransition = normalizeStatusValue(bulkNewStatus) === 'Sold';
  const bulkUndoToListTickets = bulkSelectedTickets.filter(ticket => {
    const status = normalizeStatusValue(ticket.tp_status || '');
    return status === 'Delist' || status === 'DelistInProgress' || status === 'DelistRequested';
  });
  const bulkPublishToListTickets = bulkSelectedTickets.filter(
    ticket => normalizeStatusValue(ticket.tp_status || '') === 'Verified',
  );
  const isBulkListUndoOnly =
    isBulkListTransition && bulkUndoToListTickets.length === bulkSelectedTickets.length;
  const isBulkListPublishOnly =
    isBulkListTransition && bulkPublishToListTickets.length === bulkSelectedTickets.length;
  const bulkDeletedToListTickets = bulkSelectedTickets.filter(
    ticket => normalizeStatusValue(ticket.tp_status || '') === 'Deleted',
  );
  const isBulkListDeletedOnly =
    isBulkListTransition &&
    bulkDeletedToListTickets.length === bulkSelectedTickets.length;
  const resolvedBulkListPrices = isBulkListTransition
    ? resolveBulkListingPriceByTpId(bulkSelectedTickets)
    : bulkStatusListingPrices;
  const bulkListPricesValid = isBulkListTransition
    ? areAllBulkStatusListingPricesValid(bulkSelectedTickets, resolvedBulkListPrices)
    : true;
  const bulkSoldReady = isBulkSoldTransitionReady(
    bulkSelectedTickets,
    bulkSoldLogitixAmounts,
    bulkSoldMarketplaceId,
    bulkSoldFeeConfigReady,
    marketplacesList,
    bulkSoldFeeConfigByTicketId,
  );
  const bulkSelectedMp = marketplacesList.find(m => m.id === bulkSoldMarketplaceId);
  const bulkSeatLabels = bulkSelectedTickets
    .map(t => t.tp_seat_no)
    .filter(seat => seat != null && String(seat).trim() !== '')
    .join(', ');
  const bulkConfirmDisabled =
    !bulkNewStatus ||
    bulkStatusChangeLoading ||
    bulkSoldLoading ||
    (isBulkListTransition && (!isBulkListUndoOnly && !isBulkListPublishOnly && !isBulkListDeletedOnly || !bulkListPricesValid)) ||
    (isBulkSoldTransition && !bulkSoldReady);

  return (
  <div className={styles.modalOverlay}>
    <div
      className={styles.ctModalContent}
      onClick={(e) => e.stopPropagation()}
      style={{ maxWidth: isBulkListTransition || isBulkSoldTransition ? '720px' : '600px' }}
    >
      <div className={styles.ctModalHeader}>
        <div className={styles.ctModalTitleRow}>
          <RefreshCcw className={styles.ctRotateIcon} size={16} />
          <span className={styles.ctModalTitle}>
            {isBulkSoldTransition ? 'Change Ticket Status' : `Bulk Status Change (${bulkSelectedTickets.length} tickets)`}
          </span>
        </div>
        <button className={styles.ctModalClose} onClick={closeBulkStatusModal}>×</button>
      </div>
      <div className={styles.ctModalBody}>
        {/* Show warning if tickets are from different groups */}
        {(() => {
          const validation = canBulkUpdateTickets(bulkSelectedTickets.map(t => t.tp_id));
          if (!validation.allowed && validation.reason) {
            return (
              <div className={styles.bulkActionWarning}>
                <AlertCircle size={16} />
                <span>{validation.reason}</span>
              </div>
            );
          }
          return null;
        })()}
        
        <div className={styles.ctEventName}>
          {bulkSelectedTickets[0]?.e_name}
          {bulkSelectedTickets.some(t => t.e_id !== bulkSelectedTickets[0]?.e_id) && ' (Multiple Events)'}
        </div>
        <div className={styles.ctEventDetails}>
          {isBulkSoldTransition ? (
            <>
              Section {bulkSelectedTickets[0]?.tp_section || 'N/A'} • Row {bulkSelectedTickets[0]?.tp_row || 'N/A'}
              {bulkSeatLabels ? ` • Seats ${bulkSeatLabels}` : ''}
            </>
          ) : (
            <>
              Section: {bulkSelectedTickets[0]?.tp_section || 'N/A'} • Row: {bulkSelectedTickets[0]?.tp_row || 'N/A'}
              {bulkSelectedTickets.some(t => t.tp_section !== bulkSelectedTickets[0]?.tp_section ||
                                           t.tp_row !== bulkSelectedTickets[0]?.tp_row) &&
                ' (Multiple sections/rows - bulk update not allowed)'}
            </>
          )}
        </div>
        {isBulkSoldTransition && bulkSelectedTickets[0]?.e_date ? (
          <div className={styles.ctEventDate}>
            {formatDate(bulkSelectedTickets[0].e_date, bulkSelectedTickets[0].e_time_zone)}
            {bulkSelectedTickets[0].e_date ? ` • ${formatTime(bulkSelectedTickets[0].e_date, bulkSelectedTickets[0].e_time_zone)}` : ''}
          </div>
        ) : null}

        {isBulkSoldTransition ? (
          <div className={styles.ctCurrentStatusRow} style={{ marginTop: 8 }}>
            <span className={styles.ctCurrentStatusLabel}>Tickets selected:</span>
            <b>{bulkSelectedTickets.length}</b>
          </div>
        ) : null}

        <div className={styles.ctCurrentStatusRow}>
          <span className={styles.ctCurrentStatusLabel}>
            {isBulkSoldTransition ? 'Current Status:' : 'Current Statuses:'}
          </span>
          {isBulkSoldTransition ? (
              // Show the actual current status for the first selected ticket (helps when tickets are Unsold)
              <span className={`${styles.ctStatusBadge} ${getCtBadgeClassForStatus(bulkSelectedTickets[0]?.tp_status || '')}`}>
                {getStatusIcon(bulkSelectedTickets[0]?.tp_status || '')} {getStatusLabel(bulkSelectedTickets[0]?.tp_status || '')}
              </span>
            ) : (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {Array.from(new Set(bulkSelectedTickets.map(t => getStatusLabel(t.tp_status || '')))).map(status => (
                <span key={status} className={`${styles.ctStatusBadge} ${getCtBadgeClassForStatus(status)}`}>
                  {getStatusIcon(status)} {status}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className={styles.ctNewStatusTitle}>Select New Status:</div>
        <div className={styles.ctStatusOptions}>
          {getBulkStatusTransitions(bulkSelectedTickets).map((st) => (
            <button
              key={st.value}
              className={`${styles.ctStatusOption} ${bulkNewStatus === st.value ? styles.ctStatusOptionSelected : ''}`}
              onClick={() => handleBulkNewStatusSelect(st.value)}
            >
              {st.label}
            </button>
          ))}
        </div>

        {isBulkSoldTransition ? (
          <>
            {!bulkSoldFeeConfigReady ? (
              <div style={{ margin: '12px 0', fontSize: 14 }}>Loading fee settings…</div>
            ) : null}

            <div style={{ marginBottom: 16 }}>
              <div className={styles.ctPriceLabel} style={{ marginBottom: 8 }}>Marketplace:</div>
              <select
                className={styles.ctMarketplaceSelect}
                value={bulkSoldMarketplaceId}
                disabled={!bulkSoldFeeConfigReady || marketplacesList.length === 0}
                onChange={e => setBulkSoldMarketplaceId(e.target.value)}
              >
                <option value="">
                  {marketplacesList.length === 0 ? 'No active marketplaces available' : 'Choose a marketplace...'}
                </option>
                {marketplacesList.map(mp => (
                  <option key={mp.id} value={mp.id}>{mp.name} ({mp.vl_fee_percentage}%)</option>
                ))}
              </select>
            </div>

            {/* <div className={styles.ctPriceLabel} style={{ marginTop: 4 }}>Apply same Logitix amount to all (optional)</div>
            <div style={{ fontSize: 12, marginBottom: 6 }}>
              Defaults to list price minus marketplace fee for each ticket.
            </div>
            <div className={styles.ctPriceInputWrapper} style={{ marginBottom: 12 }}>
              <span className={styles.ctPriceDollar}>$</span>
              <input
                type="text"
                inputMode="decimal"
                className={styles.ctPriceInput}
                value={bulkSoldCommonLogitixAmount}
                onChange={e => setBulkSoldCommonLogitixAmount(sanitizePriceInput(e.target.value))}
                placeholder="Enter amount"
                disabled={!bulkSoldFeeConfigReady || !bulkSoldMarketplaceId}
              />
              <button
                type="button"
                className={styles.ctConfirmBtn}
              
                onClick={handleApplyBulkSoldAmount}
                disabled={!bulkSoldFeeConfigReady || !bulkSoldMarketplaceId}
              >
                Apply to all
              </button>
            </div> */}

            <div className={styles.ctTransitionVisual}>
              {/* Show the actual current status for bulk selection (helpful for Unsold -> Sold) */}
              <span className={`${styles.ctStatusBadge} ${getCtBadgeClassForStatus(bulkSelectedTickets[0]?.tp_status || '')}`}>
                {getStatusIcon(bulkSelectedTickets[0]?.tp_status || '')} {getStatusLabel(bulkSelectedTickets[0]?.tp_status || '')}
              </span>
              <ArrowRight size={16} className={styles.ctTransArrow} />
              <span className={`${styles.ctStatusBadge} ${styles.ctStatusBadgeSold}`}>
                {getStatusIcon('Sold')} Sold
              </span>
            </div>
          </>
        ) : bulkNewStatus ? (
          <div className={styles.ctTransitionVisual}>
            <span className={styles.ctStatusBadge}>
              {bulkSelectedTickets.length} Ticket(s)
            </span>
            <ArrowRight size={16} className={styles.ctTransArrow} />
            <span className={`${styles.ctStatusBadge} ${getCtBadgeClassForStatus(bulkNewStatus)}`}>
              {getStatusIcon(bulkNewStatus)} {getStatusLabel(bulkNewStatus)}
            </span>
          </div>
        ) : null}

        {isBulkListTransition && isBulkListUndoOnly ? (
          <div style={{ marginTop: 12, fontSize: 13, color: '#4b5563' }}>
            Undo delist and return these tickets to the <strong>Listed</strong> tab. Enter a listing price for each seat (required if current price is $0).
          </div>
        ) : null}

        {isBulkListTransition && (
          <>
            <div className={styles.ctPriceLabel} style={{ marginTop: 12 }}>Apply same list price to all (optional)</div>
            <div className={styles.ctPriceInputWrapper} style={{ marginBottom: 12 }}>
              <span className={styles.ctPriceDollar}>$</span>
              <input
                type="text"
                inputMode="decimal"
                className={styles.ctPriceInput}
                value={bulkStatusListingCommonPrice}
                onChange={e => setBulkStatusListingCommonPrice(sanitizePriceInput(e.target.value))}
                placeholder="Enter price"
              />
              <button
                type="button"
                className={styles.ctConfirmBtn}
                style={{ marginLeft: 12, minWidth: 120 }}
                onClick={handleApplyBulkStatusListingPrice}
              >
                Apply to all
              </button>
            </div>
          </>
        )}
        
        {/* Preview of affected tickets */}
        <div style={{ marginTop: 16, maxHeight: isBulkListTransition || isBulkSoldTransition ? '360px' : '200px', overflowY: 'auto', borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>
            {isBulkListTransition
              ? 'Listing price per ticket (required):'
              : isBulkSoldTransition
                ? 'Amount received from Logitix per ticket:'
                : 'Affected tickets:'}
          </div>
          {(isBulkListTransition || isBulkSoldTransition ? bulkSelectedTickets : bulkSelectedTickets.slice(0, 5)).map(ticket => {
            const soldAmount = Number(ticket.tp_sold_amount ?? 0);
            const hasSoldAmount = Number.isFinite(soldAmount) && soldAmount > 0;
            const currentPrice = hasSoldAmount
              ? soldAmount
              : ticket.tp_list_price != null
                ? Number(ticket.tp_list_price)
                : null;

            if (isBulkSoldTransition) {
              const listPriceNum = Number(ticket.tp_list_price) || 0;
              const ticketFeeConfig = getBulkTicketSoldFeeConfig(ticket.tp_id, bulkSoldFeeConfigByTicketId);
              const quickPct = bulkSelectedMp
                ? effectiveSoldQuickCutPercent(
                    bulkSelectedMp.vl_fee_percentage,
                    ticketFeeConfig.feeMode,
                    ticketFeeConfig.superClientPercent,
                  )
                : 0;
              const quickCutDollars = bulkSelectedMp && bulkSoldFeeConfigReady
                ? Number(roundToFixed(listPriceNum * (quickPct / 100), 2))
                : 0;
              const expectedFromLogitix = bulkSelectedMp
                ? Math.round(listPriceNum * (100 - bulkSelectedMp.vl_fee_percentage)) / 100
                : null;
              const logitixParsed = parseFloat(String(bulkSoldLogitixAmounts[ticket.tp_id] || '').trim());
              const hasLogitixInput = !isNaN(logitixParsed) && logitixParsed > 0;
              const receivedForSettlement =
                hasLogitixInput ? logitixParsed : expectedFromLogitix != null ? expectedFromLogitix : null;
              const usingEnteredLogitix = hasLogitixInput;
              const settlementAmount =
                receivedForSettlement != null && bulkSelectedMp && bulkSoldFeeConfigReady
                  ? Math.round((receivedForSettlement - quickCutDollars) * 100) / 100
                  : null;

              const clientFeeLabel =
                ticketFeeConfig.feeMode === 'super' && ticketFeeConfig.superClientPercent != null
                  ? `${ticketFeeConfig.superClientPercent}% client fee`
                  : `${DEFAULT_CONCIERGE_PERCENT}% client fee (default)`;

              return (
                <div key={ticket.tp_id} style={{ padding: '12px 0', borderBottom: '1px solid #e5e7eb' }}>
                  <div className={styles.ctEventDetails} style={{ marginBottom: 8 }}>
                    Seat {ticket.tp_seat_no ?? 'N/A'} • Section {ticket.tp_section || 'N/A'} • Row {ticket.tp_row || 'N/A'}
                    {ticket.u_full_name ? ` • ${ticket.u_full_name}` : ''}
                  </div>
                  <div className={styles.ctCurrentStatusRow} style={{ marginTop: 0, marginBottom: 8 }}>
                    <span className={styles.ctCurrentStatusLabel}>List price:</span>
                    <b>${formatNumberWithCommas(listPriceNum)}</b>
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>{clientFeeLabel}</div>
                  <div className={styles.ctPriceLabel}>Amount received from Logitix</div>
                  <div className={styles.cttPriceInputWrapper} style={{ marginBottom: 8 }}>
                    <span className={styles.ctPriceDollar}>$</span>
                    <input
                    readOnly
                      type="text"
                      inputMode="decimal"
                      className={styles.ctPriceInput}
                     
                      value={bulkSoldLogitixAmounts[ticket.tp_id] ?? ''}
                    
                    />
                  </div>
                  {bulkSelectedMp && bulkSoldFeeConfigReady ? (
                    <div className={styles.ctFeeBreakdown}>
                      <div><b>Sold details (preview)</b></div>
                      <div>Marketplace: {bulkSelectedMp.name} ({bulkSelectedMp.vl_fee_percentage}%)</div>
                      <div>
                        QuickAsyst fee ({quickPct}% of list):{' '}
                        <span style={{ color: '#b91c1c' }}>
                          {quickCutDollars != null ? `-$${formatNumberWithCommas(quickCutDollars)}` : '—'}
                        </span>
                      </div>
                      <div style={{ marginTop: 8 }}>
                        <span className={styles.ctCurrentStatusLabel}>Amount settled to user</span>
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 700 }}>
                        {settlementAmount != null ? `$${formatNumberWithCommas(settlementAmount)}` : '—'}
                      </div>
                      {receivedForSettlement != null && quickCutDollars != null && settlementAmount != null ? (
                        <div style={{ fontSize: 12, marginTop: 6 }}>
                          ${formatNumberWithCommas(receivedForSettlement)}{' '}
                          {usingEnteredLogitix ? 'received' : 'expected'} − $
                          {formatNumberWithCommas(quickCutDollars)} QuickAsyst fee
                          {usingEnteredLogitix ? '' : ' (preview)'}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            }

            if (isBulkListTransition) {
              return (
                <div
                  key={ticket.tp_id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1.2fr 1fr',
                    gap: 10,
                    alignItems: 'center',
                    padding: '10px 0',
                    borderBottom: '1px solid #e5e7eb',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>Seat {ticket.tp_seat_no ?? 'N/A'}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      {getStatusLabel(ticket.tp_status || '')}
                      {currentPrice != null && <> • Current: ${currentPrice}</>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span className={styles.ctPriceDollar}>$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className={styles.ctPriceInput}
                      value={bulkStatusListingPrices[ticket.tp_id] ?? ''}
                      onChange={e =>
                        setBulkStatusListingPrices({
                          ...bulkStatusListingPrices,
                          [ticket.tp_id]: sanitizePriceInput(e.target.value),
                        })
                      }
                      placeholder="Price"
                    />
                  </div>
                </div>
              );
            }

            return (
              <div key={ticket.tp_id} style={{ fontSize: 12, padding: '4px 0', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span>Seat {ticket.tp_seat_no}</span>
                <span style={{ color: '#6b7280', textAlign: 'right' }}>
                  {getStatusLabel(ticket.tp_status || '')}
                  {currentPrice != null && <> • {formatCurrency(currentPrice)}</>}
                </span>
              </div>
            );
          })}
          {!isBulkListTransition && !isBulkSoldTransition && bulkSelectedTickets.length > 5 && (
            <div style={{ fontSize: 12, color: '#6b7280', paddingTop: 4 }}>
              ... and {bulkSelectedTickets.length - 5} more
            </div>
          )}
        </div>
      </div>
      <div className={styles.ctModalFooter}>
        <button className={styles.ctBackBtn} onClick={closeBulkStatusModal}>
          {isBulkSoldTransition ? 'Back' : 'Cancel'}
        </button>
        <button
          className={`${styles.ctConfirmBtn} ${bulkConfirmDisabled ? styles.ctConfirmBtnDisabled : ''}`}
          disabled={bulkConfirmDisabled}
          onClick={() => { void handleBulkStatusChange(); }}
        >
          {bulkStatusChangeLoading || bulkSoldLoading
            ? 'Saving…'
            : isBulkSoldTransition
              ? `Confirm ${bulkSelectedTickets.length} Ticket(s)`
              : `Update ${bulkSelectedTickets.length} Ticket(s)`}
        </button>
      </div>
    </div>
  </div>
  );
})()}

{showBulkListModal && bulkSelectedTickets.length > 0 && (
  <div className={styles.modalOverlay}>
    <div className={styles.ctModalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
      <div className={styles.ctModalHeader}>
        <div className={styles.ctModalTitleRow}>
          <RefreshCcw className={styles.ctRotateIcon} size={16} />
          <span className={styles.ctModalTitle}>Bulk List Tickets</span>
        </div>
        <button className={styles.ctModalClose} onClick={closeBulkListModal}>×</button>
      </div>
      <div className={styles.ctModalBody}>
        <div className={styles.ctEventName}>{bulkSelectedTickets[0]?.e_name || 'Selected Tickets'}</div>
        <div className={styles.ctEventDetails}>
          {bulkSelectedTickets.length} ticket(s) selected • Section {bulkSelectedTickets[0]?.tp_section || 'N/A'} • Row {bulkSelectedTickets[0]?.tp_row || 'N/A'}
        </div>

        <div className={styles.ctPriceLabel} style={{ marginTop: 12 }}>Apply same list price to all selected tickets</div>
        <div className={styles.ctPriceInputWrapper} style={{ marginBottom: 12 }}>
          <span className={styles.ctPriceDollar}>$</span>
          <input
            type="text"
            inputMode="decimal"
            className={styles.ctPriceInput}
            value={bulkListCommonPrice}
            onChange={e => setBulkListCommonPrice(sanitizePriceInput(e.target.value))}
            placeholder="Enter price"
          />
          <button className={styles.ctConfirmBtn} style={{ marginLeft: 12, minWidth: 120 }} onClick={handleApplyBulkListPrice}>
            Apply to all
          </button>
        </div>

        <div style={{ marginTop: 16 }}>
          <div className={styles.ctPriceLabel}>Ticket details & prices</div>
          <div style={{ marginTop: 8, display: 'grid', gap: 10 }}>
            {bulkSelectedTickets.map(ticket => (
              <div key={ticket.tp_id} style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 10, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #e5e7eb' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{ticket.e_name || 'Ticket'}</div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>
                    Seat {ticket.tp_seat_no || 'N/A'} • Section {ticket.tp_section || 'N/A'} • Row {ticket.tp_row || 'N/A'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span className={styles.ctPriceDollar}>$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    className={styles.ctPriceInput}
                    value={bulkListedPrices[ticket.tp_id] ?? ''}
                    onChange={e => setBulkListedPrices({ ...bulkListedPrices, [ticket.tp_id]: sanitizePriceInput(e.target.value) })}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={styles.ctModalFooter}>
        <button className={styles.ctBackBtn} onClick={closeBulkListModal}>Back</button>
        <button
          className={`${styles.ctConfirmBtn} ${bulkListLoading ? styles.ctConfirmBtnDisabled : ''}`}
          disabled={bulkListLoading}
          onClick={() => { void handleBulkListConfirm(); }}
        >
          {bulkListLoading ? 'Saving...' : `Confirm ${bulkSelectedTickets.length} Tickets`}
        </button>
      </div>
    </div>
  </div>
)}

{showBulkSoldModal && bulkSelectedTickets.length > 0 && (
  <div className={styles.modalOverlay}>
    <div className={styles.ctModalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '720px' }}>
      <div className={styles.ctModalHeader}>
        <div className={styles.ctModalTitleRow}>
          <RefreshCcw className={styles.ctRotateIcon} size={16} />
          <span className={styles.ctModalTitle}>Bulk Mark as Sold</span>
        </div>
        <button className={styles.ctModalClose} onClick={closeBulkSoldModal}>×</button>
      </div>
      <div className={styles.ctModalBody}>
        <div className={styles.ctEventName}>{bulkSelectedTickets[0]?.e_name || 'Selected Tickets'}</div>
        <div className={styles.ctEventDetails}>
          {bulkSelectedTickets.length} ticket(s) selected • Section {bulkSelectedTickets[0]?.tp_section || 'N/A'} • Row {bulkSelectedTickets[0]?.tp_row || 'N/A'}
        </div>

        <div className={styles.ctPriceLabel} style={{ marginTop: 12 }}>Marketplace</div>
        <select
          className={styles.ctMarketplaceSelect}
          value={bulkSoldMarketplaceId}
          onChange={e => setBulkSoldMarketplaceId(e.target.value)}
          disabled={marketplacesLoading || (!marketplacesList.length && !marketplacesLoading)}
        >
          {marketplacesLoading ? (
            <option value="">Loading marketplaces...</option>
          ) : marketplacesError ? (
            <option value="">Unable to load marketplaces</option>
          ) : marketplacesList.length === 0 ? (
            <option value="">No active marketplaces available</option>
          ) : (
            <option value="">Choose a marketplace...</option>
          )}
          {marketplacesList.map(mp => (
            <option key={mp.id} value={mp.id}>{mp.name} ({mp.vl_fee_percentage}%)</option>
          ))}
        </select>

        {/* <div className={styles.ctPriceLabel} style={{ marginTop: 16 }}>Apply same Logitix amount to all selected tickets</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
          <div className={styles.ctPriceInputWrapper} style={{ flex: 1 }}>
            <span className={styles.ctPriceDollar}>$</span>
            <input
              type="text"
              inputMode="decimal"
              className={styles.ctPriceInput}
              value={bulkSoldCommonLogitixAmount}
              onChange={e => setBulkSoldCommonLogitixAmount(sanitizePriceInput(e.target.value))}
              placeholder="Enter amount"
            />
          </div>
          <button className={styles.ctConfirmBtn} style={{ minWidth: 120 }} onClick={handleApplyBulkSoldAmount}>
            Apply to all
          </button>
        </div> */}
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
          Defaults to list price minus marketplace fee for each ticket if left blank.
        </div>

        <div style={{ marginTop: 16 }}>
          <div className={styles.ctPriceLabel}>Ticket amounts</div>
          <div style={{ marginTop: 8, display: 'grid', gap: 10 }}>
            {bulkSelectedTickets.map(ticket => {
              const listPrice = Number(ticket.tp_list_price) || 0;
              const selectedMp = marketplacesList.find(m => m.id === bulkSoldMarketplaceId);
              const ticketFeeConfig = getBulkTicketSoldFeeConfig(ticket.tp_id, bulkSoldFeeConfigByTicketId);
              const quickPct = effectiveSoldQuickCutPercent(
                selectedMp?.vl_fee_percentage || 0,
                ticketFeeConfig.feeMode,
                ticketFeeConfig.superClientPercent,
              );
              const quickCutDollars = Number(roundToFixed(listPrice * (quickPct / 100), 2));
              const expectedLogitix = selectedMp ? Math.round(listPrice * (100 - selectedMp.vl_fee_percentage)) / 100 : 0;
              const rawLogitixValue = bulkSoldLogitixAmounts[ticket.tp_id];
              const logitixValue = rawLogitixValue !== undefined && rawLogitixValue !== null && String(rawLogitixValue).trim() !== ''
                ? Number(parseFloat(String(rawLogitixValue).trim()))
                : expectedLogitix;
              const settlement = Number.isFinite(logitixValue)
                ? Math.round((logitixValue - quickCutDollars) * 100) / 100
                : 0;
              return (
                <div key={ticket.tp_id} style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr', gap: 10, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #e5e7eb' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{ticket.e_name || 'Ticket'}</div>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>
                      Seat {ticket.tp_seat_no || 'N/A'} • Section {ticket.tp_section || 'N/A'} • Row {ticket.tp_row || 'N/A'}
                    </div>
                    <div style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>
                      List price: ${formatNumberWithCommas(listPrice)}
                    </div>
                    <div style={{ fontSize: 13, color: '#374151' }}>
                      Marketplace fee: {selectedMp ? `${selectedMp.vl_fee_percentage}%` : '—'} • QuickAsyst fee: ${formatNumberWithCommas(quickCutDollars)}
                    </div>
                  </div>
                  <div className={styles.ctPriceInputWrapper} style={{ minWidth: 140 }}>
                    <span className={styles.ctPriceDollar}>$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className={styles.ctPriceInput}
                      value={bulkSoldLogitixAmounts[ticket.tp_id] ?? ''}
                      onChange={e => setBulkSoldLogitixAmounts({ ...bulkSoldLogitixAmounts, [ticket.tp_id]: sanitizePriceInput(e.target.value) })}
                      disabled={!bulkSoldMarketplaceId}
                      placeholder={selectedMp ? String(expectedLogitix) : 'Enter amount'}
                    />
                  </div>
                  <div style={{ fontSize: 13, color: '#1f2937', textAlign: 'right' }}>
                    Settled: ${Number.isNaN(settlement) ? '0.00' : settlement.toFixed(2)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className={styles.ctModalFooter}>
        <button className={styles.ctBackBtn} onClick={closeBulkSoldModal}>Back</button>
        <button
          className={`${styles.ctConfirmBtn} ${bulkSoldLoading ? styles.ctConfirmBtnDisabled : ''}`}
          disabled={bulkSoldLoading || !bulkSoldMarketplaceId}
          onClick={handleBulkSoldConfirm}
        >
          {bulkSoldLoading ? 'Saving...' : `Confirm ${bulkSelectedTickets.length} Tickets`}
        </button>
      </div>
    </div>
  </div>
)}

{/* Bulk Price Change Modal */}
{showBulkPriceModal && bulkSelectedTickets.length > 0 && (
  <div className={styles.modalOverlay}>
    <div className={styles.ctModalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '750px' }}>
      <div className={styles.ctModalHeader}>
        <div className={styles.ctModalTitleRow}>
          <DollarSign className={styles.ctRotateIcon} size={20} />
          <span className={styles.ctModalTitle}>Bulk Change Price</span>
        </div>
        <button className={styles.ctModalClose} disabled={bulkPriceLoading} onClick={closeBulkPriceModal}>×</button>
      </div>
      <div className={styles.ctModalBody}>
        {bulkPriceLoading && (
          <div className={styles.modalInlineBusy} role="status" aria-live="polite">
            <div className={styles.spinner} />
            <span>Updating list prices on Victory Live — please wait…</span>
          </div>
        )}
        <div className={styles.ctEventName}>{bulkSelectedTickets[0]?.e_name || 'Selected Tickets'}</div>
        <div className={styles.ctEventDetails}>
          {bulkSelectedTickets.length} ticket(s) selected • Section {bulkSelectedTickets[0]?.tp_section || 'N/A'} • Row {bulkSelectedTickets[0]?.tp_row || 'N/A'}
        </div>

        <div className={styles.ctPriceLabel} style={{ marginTop: 16, marginBottom: 8 }}>Apply same price to all selected tickets (optional)</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
          <div className={styles.ctPriceInputWrapper} style={{ flex: 1 }}>
            <span className={styles.ctPriceDollar}>$</span>
            <input
              type="text"
              inputMode="decimal"
              className={styles.ctPriceInput}
              value={bulkPriceCommon}
              onChange={e => setBulkPriceCommon(sanitizePriceInput(e.target.value))}
              placeholder="Enter price"
            />
          </div>
          <button 
            className={styles.ctConfirmBtn} 
            style={{ minWidth: 140 }}
            onClick={handleApplyCommonBulkPrice}
          >
            <span>Apply to all</span>
          </button>
        </div>

        <div style={{ borderTop: '2px solid #e5e7eb', paddingTop: 16, marginTop: 16 }}>
          <div className={styles.ctPriceLabel} style={{ marginBottom: 12 }}>Individual ticket prices</div>
          <div className={styles.bulkPriceScrollContainer} style={{ maxHeight: '400px', overflowY: 'auto', display: 'grid', gap: 12 }}>
            {bulkSelectedTickets.map((ticket) => {
              const currentPrice = ticket.tp_list_price != null ? Number(ticket.tp_list_price) : null;
              return (
                <div 
                  key={ticket.tp_id} 
                  className={styles.bulkPriceTicketCard}
                >
                  <div className={styles.bulkPriceTicketInfo}>
                    <div className={styles.bulkPriceTicketName}>Seat {ticket.tp_seat_no || 'N/A'}</div>
                    <div className={styles.bulkPriceTicketSubtext}>
                      {ticket.u_full_name || 'N/A'}
                    </div>
                    <div className={styles.bulkPriceTicketCurrentPrice}>
                      Current price: ${currentPrice?.toFixed(2) || '0.00'}
                    </div>
                  </div>
                  <div className={styles.ctPriceInputWrapper}>
                    <span className={styles.ctPriceDollar}>$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className={styles.ctPriceInput}
                      value={bulkPriceIndividual[ticket.tp_id] ?? ''}
                      onChange={e =>
                        setBulkPriceIndividual({
                          ...bulkPriceIndividual,
                          [ticket.tp_id]: sanitizePriceInput(e.target.value),
                        })
                      }
                      placeholder="0"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className={styles.ctModalFooter}>
        <button className={styles.ctBackBtn} disabled={bulkPriceLoading} onClick={closeBulkPriceModal}>Cancel</button>
        <button
          className={`${styles.ctConfirmBtn} ${bulkPriceLoading ? styles.ctConfirmBtnDisabled : ''}`}
          disabled={bulkPriceLoading}
          onClick={() => { void handleBulkPriceConfirm(); }}
        >
          {bulkPriceLoading ? 'Saving…' : `Update ${bulkSelectedTickets.length} Ticket${bulkSelectedTickets.length > 1 ? 's' : ''}`}
        </button>
      </div>
    </div>
  </div>
)}

{/* Bulk Delist Email Modal */}
{showBulkDelistEmailModal && (
  <div className={styles.modalOverlay}>
    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
      <div className={styles.modalHeader}>
        <div>
          <h3>Bulk Delist Request</h3>
          <p>Please provide the email address to return the tickets to</p>
        </div>
        <button className={styles.modalClose} onClick={() => setShowBulkDelistEmailModal(false)}><X size={18} /></button>
      </div>
      <div className={styles.modalBody}>
        <div className={styles.delistCount}>Delisting <strong>{pendingBulkDelistTpIds.length}</strong> ticket(s)</div>
        <div className={styles.editField}>
          <label>Email</label>
          <input type="email" placeholder="name@company.com" {...registerDelistEmail('email', { required: 'Email is required', pattern: { value: emailRegexPattern, message: 'Enter a valid email' } })} />
          {delistEmailErrors.email && <small className={styles.errorText}>{delistEmailErrors.email.message as string}</small>}
        </div>
        <div className={styles.editField}>
          <label>Confirm Email</label>
          <input type="email" placeholder="name@company.com" {...registerDelistEmail('confirmEmail', { required: 'Confirm email is required', pattern: { value: emailRegexPattern, message: 'Enter a valid email' }, validate: (val) => watchDelistEmail('email') === val || 'Emails do not match' })} />
          {delistEmailErrors.confirmEmail && <small className={styles.errorText}>{delistEmailErrors.confirmEmail.message as string}</small>}
        </div>
      </div>
      <div className={styles.modalFooter}>
        <button className={styles.modalCancel} onClick={() => setShowBulkDelistEmailModal(false)} disabled={statusTableBusyCount > 0}>Cancel</button>
        <button className={styles.modalConfirm} onClick={handleDelistEmailSubmit(onBulkDelistEmailSubmit)} disabled={statusTableBusyCount > 0}>
          {statusTableBusyCount > 0 ? 'Submitting…' : 'Submit Delist Request'}
        </button>
      </div>
    </div>
  </div>
)}
{showSyncFromVlScopeModal && (
  <SyncFromVlScopeModal
    open={showSyncFromVlScopeModal}
    confirming={syncFromVlConfirming}
    seatCount={syncFromVlScopeTpIds.length}
    onClose={() => {
      if (syncFromVlConfirming) return;
      setShowSyncFromVlScopeModal(false);
    }}
    onConfirm={(scope) => void handleSyncVlGroups(syncFromVlScopeTpIds, scope)}
  />
)}
{showBarcodeSyncConfirmModal && (
  <BarcodeSyncConfirmModal
    open={showBarcodeSyncConfirmModal}
    loadingRecommend={barcodeSyncRecommendLoading}
    confirming={barcodeSyncConfirming}
    recommendation={barcodeSyncRecommendation}
    error={barcodeSyncRecommendError}
    title="Sync Barcodes"
    onClose={() => {
      if (
        barcodeSyncConfirming ||
        barcodeSyncSummary?.phase === 'crawling' ||
        barcodeSyncSummary?.phase === 'verifying'
      ) return;
      setShowBarcodeSyncConfirmModal(false);
    }}
    onConfirm={confirmBarcodeSync}
  />
)}
<CheckBarcodesModal
  open={showCheckBarcodesModal}
  loading={checkBarcodesLoading}
  error={checkBarcodesError}
  result={checkBarcodesResult}
  onClose={() => {
    if (
      checkBarcodesLoading ||
      barcodeSyncSummary?.phase === 'crawling' ||
      barcodeSyncSummary?.phase === 'verifying'
    ) return;
    setShowCheckBarcodesModal(false);
  }}
/>
{showBarcodeSyncSummaryModal && barcodeSyncSummary && (
  <div className={styles.modalOverlay}>
    <div className={styles.modalContent} style={{ maxWidth: '560px' }}>
      <div className={styles.modalHeader}>
        <div>
          <h3>Barcode Sync Summary</h3>
          <p>
            {barcodeSyncSummary.phase === 'crawling' && 'Crawling in progress…'}
            {barcodeSyncSummary.phase === 'verifying' && 'Crawl complete. Verifying selected seats…'}
            {barcodeSyncSummary.phase === 'success' && 'Success: all selected seats have matching barcodes.'}
            {barcodeSyncSummary.phase === 'failure' && 'Failure: one or more seats are missing/mismatched.'}
          </p>
        </div>
        <button
          className={styles.modalClose}
          onClick={() => setShowBarcodeSyncSummaryModal(false)}
          disabled={barcodeSyncSummary.phase === 'crawling' || barcodeSyncSummary.phase === 'verifying'}
        >
          <X size={18} />
        </button>
      </div>
      <div className={styles.modalBody}>
        {barcodeSyncSummary.crawlMessage && (
          <div style={{ marginBottom: 10, fontSize: 13, color: '#6b7280' }}>
            {barcodeSyncSummary.crawlMessage}
          </div>
        )}
        <div className={styles.editField} style={{ gap: 8 }}>
          {(barcodeSyncSummary.crawlResults || []).map((row) => (
            <div key={row.accountId}>
              <strong>{row.accountId}</strong>
              {row.username ? ` - ${row.username}` : ''}
              {row.marketplaceName ? ` (${row.marketplaceName})` : ''}
              {': '}
              {row.status === 'processing'
                ? 'Processing'
                : row.status === 'completed'
                  ? 'Completed'
                  : row.status === 'failed'
                    ? `Failed${row.message ? ` - ${row.message}` : ''}`
                    : 'Pending'}
              {row.crawlStatus ? ` (${row.crawlStatus})` : ''}
            </div>
          ))}
        </div>
        {barcodeSyncSummary.verificationMessage && (
          <div style={{ marginTop: 10, fontSize: 13, color: '#6b7280' }}>
            {barcodeSyncSummary.verificationMessage}
          </div>
        )}
        {barcodeSyncSummary.failures?.length ? (
          <div style={{ marginTop: 10, fontSize: 13, color: '#991b1b' }}>
            {barcodeSyncSummary.failures.slice(0, 12).map((row) => (
              <div key={row}>- {row}</div>
            ))}
            {barcodeSyncSummary.failures.length > 12
              ? <div>...and {barcodeSyncSummary.failures.length - 12} more</div>
              : null}
          </div>
        ) : null}
      </div>
      <div className={styles.modalFooter}>
        <button
          className={styles.modalConfirm}
          onClick={() => setShowBarcodeSyncSummaryModal(false)}
          disabled={barcodeSyncSummary.phase === 'crawling' || barcodeSyncSummary.phase === 'verifying'}
        >
          {barcodeSyncSummary.phase === 'success' ? 'Done' : 'Close'}
        </button>
      </div>
    </div>
  </div>
)}
              {/* Pagination — show whenever there are rows (even a single page) */}
              {effectiveTotalCount > 0 && !isEventGroupedView && (
                <div className={styles.pagination}>
                  <div className={styles.paginationLeft}>
                    <span>{resultsRangeLabel}</span>
                    <div className={styles.pageSizeSelector}>
                      <label>{directSeatPagination ? 'Tickets per page:' : 'Groups per page:'}</label>
                      <select value={pageSize} onChange={(e) => handlePageSizeChange(Number(e.target.value))}>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>
                  <div className={styles.paginationRight}>
                    <button onClick={() => handlePageChange(pageOffset - pageSize)} disabled={pageOffset === 0}>
                      <ChevronLeft size={16} /> Previous
                    </button>
                    <span>Page {currentPage} of {totalPages}</span>
                    <button onClick={handleNextPage} disabled={nextGroupedPageDisabled}>
                      Next <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
            </div>
          )}
        </div>
      </div>

      {/* Custom Range Modal */}
      {showCustomRangeModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className={styles.modalHeader}>
              <h3><Calendar size={16} style={{ marginRight: 8 }} /> Select Date Range</h3>
              <button className={styles.modalClose} onClick={() => setShowCustomRangeModal(false)}><X size={18} /></button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.editField}>
                <label>Start Date</label>
                <input
                  type="date"
                  value={customRangeStart}
                  onChange={(e) => setCustomRangeStart(e.target.value)}
                />
              </div>
              <div className={styles.editField}>
                <label>End Date</label>
                <input
                  type="date"
                  value={customRangeEnd}
                  onChange={(e) => setCustomRangeEnd(e.target.value)}
                />
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.modalCancel} onClick={() => setShowCustomRangeModal(false)}>Cancel</button>
              <button className={styles.modalConfirm} onClick={applyCustomRange}>Apply Range</button>
            </div>
          </div>
        </div>
      )}

      {/* Price Change Modal */}
      {showPriceModal && selectedTicketForPrice && (() => {
        const priceModalTickets = selectedTpIds
          .map(
            (id) =>
              filteredTickets.find((t) => t.tp_id === id) ??
              statusFilterSourceTickets.find((t) => t.tp_id === id) ??
              (selectedTicketForPrice.tp_id === id ? selectedTicketForPrice : null),
          )
          .filter((t): t is Ticket => Boolean(t));
        const seatsForLabel = (priceModalTickets.length ? priceModalTickets : [selectedTicketForPrice])
          .map((t) => t.tp_seat_no)
          .filter((s) => s !== null && s !== undefined && String(s).trim() !== '')
          .map((s) => String(s).trim());
        const uniqueSeats = [...new Set(seatsForLabel)].sort((a, b) => {
          const na = Number(a);
          const nb = Number(b);
          if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
          return a.localeCompare(b);
        });
        const seatDisplay = uniqueSeats.length ? uniqueSeats.join(', ') : 'N/A';
        return (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Confirm Price Change</h3>
              <button className={styles.modalClose} disabled={priceLoading} onClick={() => setShowPriceModal(false)}><X size={18} /></button>
            </div>
            <div className={styles.modalBody}>
              {priceLoading && (
                <div className={styles.modalInlineBusy} role="status" aria-live="polite">
                  <div className={styles.spinner} />
                  <span>Updating list price on Victory Live — please wait…</span>
                </div>
              )}
              <div className={styles.modalEventInfo}>
                <div className={styles.modalEventName}>{selectedTicketForPrice.e_name}</div>
                <div className={styles.modalEventDetails}>
                  Section {selectedTicketForPrice.tp_section || 'N/A'} • Row {selectedTicketForPrice.tp_row || 'N/A'} • Seats {seatDisplay}
                </div>
              </div>
              <div className={styles.priceCompare}>
                <div className={styles.currentPrice}>
                  <div>Current Price</div>
                  <div>${selectedTicketForPrice.tp_list_price || 0}</div>
                </div>
                <div className={styles.newPriceInput}>
                  <div>New Price</div>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={newPrice}
                    onChange={(e) => setNewPrice(sanitizePriceInput(e.target.value))}
                    placeholder="0"
                  />
                </div>
              </div>
              <div className={styles.selectedCountInfo}>
                <CheckSquare size={12} /> {(priceModalTickets.length || selectedTpIds.length || 1)} ticket(s) selected
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.modalCancel} onClick={() => setShowPriceModal(false)} disabled={priceLoading}>Cancel</button>
              <button
                className={styles.modalConfirm}
                onClick={() => { void handlePriceChangeConfirm(); }}
                disabled={priceLoading || !newPrice}
              >
                {priceLoading ? 'Saving…' : 'Confirm Price Change'}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Price Change Logs Modal */}
      {showPriceLogsModal && selectedTicketForPriceLogs && (
        <div
 className={styles.modalOverlay}
 >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '960px', width: '95%' }}
          >
            <div className={styles.modalHeader}>
              <h3><History size={18} /> Price Change Logs</h3>
              <button
                className={styles.modalClose}
                onClick={() => {
                  setShowPriceLogsModal(false);
                  setSelectedTicketForPriceLogs(null);
                  setPriceLogsTpIds([]);
                }}
              >
                <X size={18} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalEventInfo} style={{ marginBottom: 12 }}>
                <div className={styles.modalEventName}>{selectedTicketForPriceLogs.e_name || 'N/A'}</div>
                <div>
                  {[
                    selectedTicketForPriceLogs.tp_section && `Sec ${selectedTicketForPriceLogs.tp_section}`,
                    selectedTicketForPriceLogs.tp_row && `Row ${selectedTicketForPriceLogs.tp_row}`,
                    selectedTicketForPriceLogs.tp_seat_no != null && `Seat ${selectedTicketForPriceLogs.tp_seat_no}`,
                  ]
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </div>
              </div>
              <PriceChangeLogsPanel
                tpIds={priceLogsTpIds}
                compact
                hideFilters
                hideClientColumns
              />
            </div>
            <div className={styles.modalFooter}>
              <button
                className={styles.modalCancel}
                onClick={() => {
                  setShowPriceLogsModal(false);
                  setSelectedTicketForPriceLogs(null);
                  setPriceLogsTpIds([]);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {showViewModal && selectedTicketForView && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className={styles.modalHeader}>
              <h3><Eye size={18} /> Ticket Details</h3>
              <button className={styles.modalClose} onClick={() => setShowViewModal(false)}><X size={18} /></button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.viewDetailsHero}>
                <h4>{selectedTicketForView.e_name || 'N/A'}</h4>
                <p><MapPinned size={12} /> {selectedTicketForView.e_address || 'N/A'}</p>
                <p><Calendar size={12} /> {formatDate(selectedTicketForView.e_date, selectedTicketForView.e_time_zone)} • {formatTime(selectedTicketForView.e_date, selectedTicketForView.e_time_zone)}</p>
              </div>
              <div className={styles.viewDetailsGrid}>
                <div className={styles.detailSection}>
                  <h5>Ticket Details</h5>
                  <div><span>Section</span><span>{selectedTicketForView.tp_section || 'N/A'}</span></div>
                  <div><span>Row</span><span>{selectedTicketForView.tp_row || 'N/A'}</span></div>
                  <div><span>Seat Numbers</span><span>{selectedTicketForView.tp_seat_no || 'N/A'}</span></div>
                  <div><span>Status</span><span className={styles.statusBadge}>{getStatusLabel(selectedTicketForView.tp_status || '')}</span></div>
                  <div><span>List Price</span><span>{formatCurrency(selectedTicketForView.tp_list_price || 0)}</span></div>
                </div>
                <div className={styles.detailSection}>
                  <h5>Client Details</h5>
                  <div><span>Client Name</span><span>{getClientDisplayName(selectedTicketForView)}</span></div>
                  <div><span>Client Email</span><span>{selectedTicketForView.u_email_id || 'N/A'}</span></div>
                  {selectedTicketForView.tp_delist_requested_email?.trim() && (
                    <div><span>Return Email</span><span>{selectedTicketForView.tp_delist_requested_email.trim()}</span></div>
                  )}
                  <div><span>Client Type</span><span>{getResolvedClientType(selectedTicketForView) === 'self-pricing'
                    ? 'Self Pricing'
                    : getResolvedClientType(selectedTicketForView) === 'concierge'
                    ? 'Concierge'
                    : 'N/A'}</span></div>
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.modalConfirm} onClick={() => {
                setShowViewModal(false);
                if (selectedTicketForView.u_id) navigate(`/user/${selectedTicketForView.u_id}?tab=tickets`);
              }}>View Full Profile</button>
              <button className={styles.modalCancel} onClick={() => setShowViewModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Ticket Details Modal */}
      {showEditModal && selectedTicketForEdit && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3><Edit size={18} /> Edit Ticket Details</h3>
              <button
                type="button"
                className={styles.modalClose}
                onClick={closeEditModal}
                disabled={editDetailsLoading}
              >
                <X size={18} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalEventInfo}>
                <div className={styles.modalEventName}>{selectedTicketForEdit.e_name}</div>
                <div>{formatDate(selectedTicketForEdit.e_date, selectedTicketForEdit.e_time_zone)} • {formatTime(selectedTicketForEdit.e_date, selectedTicketForEdit.e_time_zone)}</div>
                {editDetailsTickets.length > 1 && (
                  <div style={{ marginTop: 6, fontSize: 13, color: '#64748b' }}>
                    Updating {editDetailsTickets.length} seats in this listing on Victory Live and QuickAsyst. Section, row, and notes apply to all seats.
                  </div>
                )}
              </div>
              {editDetailsLoading && (
                <div className={styles.loadingState} style={{ padding: '8px 0 12px', minHeight: 'auto' }}>
                  <div className={styles.spinner} />
                  <p style={{ margin: 0 }}>Saving to Victory Live and QuickAsyst…</p>
                </div>
              )}
              <div className={styles.editField}>
                <label>Section</label>
                <input
                  type="text"
                  value={editFormData.section}
                  disabled={editDetailsLoading}
                  onChange={e => setEditFormData({...editFormData, section: e.target.value})}
                />
              </div>
              <div className={styles.editField}>
                <label>Row</label>
                <input
                  type="text"
                  value={editFormData.row}
                  disabled={editDetailsLoading}
                  onChange={(e) => {
                    const row = e.target.value;
                    setEditFormData((current) => ({
                      ...current,
                      row,
                      ...(isParkingPassRow(row)
                        ? { internalNotes: PARKING_INTERNAL_NOTE, externalNotes: PARKING_EXTERNAL_NOTE }
                        : {}),
                    }));
                  }}
                />
              </div>
    {['Verified', 'ToBeVerified'].includes(selectedTicketForEdit.tp_status || '') && (
  <div style={{ display: 'flex', gap: '16px', width: '100%' }}>
    <div className={styles.editField} style={{ flex: 1 }}>
      <label>Start Seat</label>
      <input
        type="number"
        placeholder="e.g. 802"
        value={editFormData.seatStart}
        disabled={editDetailsLoading}
        onChange={e => setEditFormData({ ...editFormData, seatStart: e.target.value })}
      />
    </div>
    <div className={styles.editField} style={{ flex: 1 }}>
      <label>End Seat</label>
      <input
        type="number"
        placeholder="e.g. 803"
        value={editFormData.seatEnd}
        disabled={editDetailsLoading}
        onChange={e => setEditFormData({ ...editFormData, seatEnd: e.target.value })}
      />
    </div>
  </div>
)}
              <div className={styles.editField}>
                <label>Internal Note</label>
                {isParkingPassRow(editFormData.row) ? (
                  <textarea
                    rows={3}
                    value={editFormData.internalNotes}
                    disabled={editDetailsLoading}
                    onChange={(e) => {
                      editDetailsNotesEditedRef.current = true;
                      setEditFormData((current) => ({ ...current, internalNotes: e.target.value }));
                    }}
                  />
                ) : (
                <ListingNoteCombobox
                  value={editFormData.internalNotes}
                  options={editInternalNoteChoices}
                  disabled={editDetailsLoading}
                  placeholder="Search or enter internal notes"
                  onChange={(value) => {
                    editDetailsNotesEditedRef.current = true;
                    const draft = composeListingNoteDraft(value, editInternalNoteChoices, '', 'internal');
                    setEditFormData((current) => ({ ...current, internalNotes: draft.query }));
                  }}
                />
                )}
              </div>
              <div className={styles.editField}>
                <label>External Note</label>
                {isParkingPassRow(editFormData.row) ? (
                  <textarea
                    rows={3}
                    value={editFormData.externalNotes}
                    disabled={editDetailsLoading}
                    onChange={(e) => {
                      editDetailsNotesEditedRef.current = true;
                      setEditFormData((current) => ({ ...current, externalNotes: e.target.value }));
                    }}
                  />
                ) : (
                <ListingNoteCombobox
                  value={editFormData.externalNotes}
                  options={editExternalNoteChoices}
                  disabled={editDetailsLoading}
                  placeholder="Search or enter external notes"
                  onChange={(value) => {
                    editDetailsNotesEditedRef.current = true;
                    const draft = composeListingNoteDraft(value, editExternalNoteChoices, '', 'external');
                    setEditFormData((current) => ({ ...current, externalNotes: draft.query }));
                  }}
                />
                )}
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button
                type="button"
                className={styles.modalCancel}
                onClick={closeEditModal}
                disabled={editDetailsLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`${styles.modalConfirm} ${editDetailsLoading ? styles.ctConfirmBtnDisabled : ''}`}
                onClick={handleEditDetailsConfirm}
                disabled={editDetailsLoading}
              >
                {editDetailsLoading ? 'Saving…' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {canEditSoldCommission && showCommissionModal && selectedTicketForCommission && (
        <div className={styles.modalOverlay} onClick={closeCommissionModal}>
          <div className={styles.modalContent} onClick={(event) => event.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className={styles.modalHeader}>
              <h3><DollarSign size={18} /> Edit Line Commission</h3>
              <button type="button" className={styles.modalClose} onClick={closeCommissionModal} disabled={commissionOverrideLoading}>
                <X size={18} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalEventInfo}>
                <div className={styles.modalEventName}>{selectedTicketForCommission.e_name || 'Ticket line'}</div>
                <div>
                  {selectedTicketForCommission.tp_section || 'N/A'} · {selectedTicketForCommission.tp_row || 'N/A'} · Seat {selectedTicketForCommission.tp_seat_no || 'N/A'}
                </div>
              </div>
              <div className={styles.editField}>
                <label htmlFor="commission-override-input">Commission amount</label>
                <input
                  id="commission-override-input"
                  type="text"
                  inputMode="decimal"
                  value={commissionOverrideInput}
                  onChange={(event) => setCommissionOverrideInput(event.target.value)}
                  disabled={commissionOverrideLoading}
                  placeholder="0.00"
                  autoFocus
                />
                <small>Enter a non-negative amount with up to 2 decimal places.</small>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalCancel} onClick={closeCommissionModal} disabled={commissionOverrideLoading}>
                Cancel
              </button>
              <button type="button" className={styles.modalConfirm} onClick={handleCommissionOverrideSave} disabled={commissionOverrideLoading}>
                {commissionOverrideLoading ? 'Saving…' : 'Save Commission'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Method Status Modal */}
      {showPricingMethodModal && selectedTicketForPricingMethod && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3><DollarSign size={18} /> Pricing Method Status</h3>
              <button className={styles.modalClose} onClick={closePricingMethodModal} disabled={pricingMethodLoading}><X size={18} /></button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalEventInfo}>
                <div className={styles.modalEventName}>{selectedTicketForPricingMethod.e_name}</div>
                <div className={styles.modalEventDetails}>
                  {selectedTicketForPricingMethod.tp_section || 'N/A'} • {selectedTicketForPricingMethod.tp_row || 'N/A'} • {selectedTicketForPricingMethod.tp_seat_no || 'N/A'}
                </div>
              </div>

            <div className={styles.editField}>
  <label>Pricing method</label>
  <div className={styles.pricingMethodOptions} role="radiogroup" aria-label="Pricing Method">
    {getResolvedClientType(selectedTicketForPricingMethod) !== 'self-pricing' && (
      <>
    <label className={`${styles.pricingMethodOption} ${pricingMethodFormData === 'MANUAL' ? styles.pricingMethodOptionSelected : ''}`}>
      <input
        type="radio"
        name="pricing-method-status"
        value="MANUAL"
        checked={pricingMethodFormData === 'MANUAL'}
        disabled={pricingMethodLoading}
        onChange={() => setPricingMethodFormData('MANUAL')}
      />
     
      <span>Manually Priced</span> <span
        className={styles.manualPriceBadge}
        title="Manually Priced"
        aria-label="Manually Priced"
      >
        <DollarSign className={styles.pricingMethodPriceIcon} size={13} aria-hidden="true" />
      </span>
    </label>
    <label className={`${styles.pricingMethodOption} ${pricingMethodFormData === 'FOLLOW_MARKET' ? styles.pricingMethodOptionSelected : ''}`}>
      <input
        type="radio"
        name="pricing-method-status"
        value="FOLLOW_MARKET"
        checked={pricingMethodFormData === 'FOLLOW_MARKET'}
        disabled={pricingMethodLoading}
        onChange={() => setPricingMethodFormData('FOLLOW_MARKET')}
      />
    
      <span>Follow Market Tool</span>  <span
        className={styles.followMarketPriceBadge}
        title="Follow Market"
        aria-label="Follow Market"
      >
        <TrendingUp className={styles.pricingMethodPriceIcon} size={13} aria-hidden="true" />
      </span>
    </label>
      </>
    )}
    <label className={`${styles.pricingMethodOption} ${pricingMethodFormData === 'NONE' ? styles.pricingMethodOptionSelected : ''}`}>
      <input
        type="radio"
        name="pricing-method-status"
        value="NONE"
        checked={pricingMethodFormData === 'NONE'}
        disabled={pricingMethodLoading}
        onChange={() => setPricingMethodFormData('NONE')}
      />
      <span>Nothing</span>
    </label>
  </div>
  {getResolvedClientType(selectedTicketForPricingMethod) === 'self-pricing' && (
    <p style={{ marginTop: 8, fontSize: 13, opacity: 0.8 }}>
      Self Pricing tickets do not use Manual or Follow the Market.
    </p>
  )}
</div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.modalCancel} onClick={closePricingMethodModal} disabled={pricingMethodLoading}>Cancel</button>
              <button className={styles.modalConfirm} onClick={handlePricingMethodStatusConfirm} disabled={pricingMethodLoading}>
                {pricingMethodLoading ? 'Saving…' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Status Modal */}
      {showStatusModal && selectedTicketForStatus && (() => {
        const currentSt = getTicketTransitionStatus(selectedTicketForStatus);
        const pres = getManageTicketsStatusPresentation(selectedTicketForStatus);
        const currentLabel = pres.label;
        const selectedLabel = newStatus ? getStatusLabel(newStatus) : '';
        const getCtBadgeClass = (st: string) => {
          const s = (st || '').toLowerCase().replace(/\s/g, '');
          if (s === 'verified') return styles.ctStatusBadgeVerified;
          if (s === 'list' || s === 'listed') return styles.ctStatusBadgeListed;
          if (s === 'sold') return styles.ctStatusBadgeSold;
          if (s === 'tobeverified') return styles.ctStatusBadgeToBeVerified;
          if (s === 'invalid') return styles.ctStatusBadgeInvalid;
          return styles.ctStatusBadgeDefault;
        };
        const ctBadgeClassForCurrent = () => {
          const ik = (pres.iconStatus || '').toLowerCase().replace(/\s/g, '');
          if (ik === 'settled') return styles.ctStatusBadgeVerified;
          if (ik === 'settlementinprogress') return styles.ctStatusBadgeListed;
          if (ik === 'failed') return styles.ctStatusBadgeInvalid;
          return getCtBadgeClass(currentSt);
        };
        return (
          <div className={styles.modalOverlay}>
            <div className={styles.ctModalContent} onClick={(e) => e.stopPropagation()}>
              <div className={styles.ctModalHeader}>
                <div className={styles.ctModalTitleRow}>
                  <RefreshCcw className={styles.ctRotateIcon} size={16} />
                  <span className={styles.ctModalTitle}>Change Ticket Status</span>
                </div>
                <button className={styles.ctModalClose} onClick={closeStatusModal}>×</button>
              </div>
              <div className={styles.ctModalBody}>
                <div className={styles.ctEventName}>{selectedTicketForStatus.e_name}</div>
                <div className={styles.ctEventDetails}>
                  Section {selectedTicketForStatus.tp_section || 'N/A'} • Row {selectedTicketForStatus.tp_row || 'N/A'} • Seats {selectedTicketForStatus.tp_seat_no || 'N/A'}
                </div>
                <div className={styles.ctEventDate}>
                  {formatDate(selectedTicketForStatus.e_date, selectedTicketForStatus.e_time_zone)}{selectedTicketForStatus.e_date ? ` • ${formatTime(selectedTicketForStatus.e_date, selectedTicketForStatus.e_time_zone)}` : ''}
                </div>

                <div className={styles.ctCurrentStatusRow}>
                  <span className={styles.ctCurrentStatusLabel}>Current Status:</span>
                  <span className={`${styles.ctStatusBadge} ${ctBadgeClassForCurrent()}`}>
                    {getStatusIcon(pres.iconStatus)} {currentLabel}
                  </span>
                </div>

                <div className={styles.ctNewStatusTitle}>Select New Status:</div>
                <div className={styles.ctStatusOptions}>
                  {getTicketStatusTransitions(selectedTicketForStatus).map((st) => {
                    const isDelistOpt = st.value === 'DelistInProgress' || st.value === 'DelistRequested';
                    const isSelected = newStatus === st.value;
                    let btnClass = styles.ctStatusOption;
                    if (isSelected) {
                      btnClass = isDelistOpt
                        ? `${styles.ctStatusOption} ${styles.ctStatusOptionOrangeSelected}`
                        : `${styles.ctStatusOption} ${styles.ctStatusOptionSelected}`;
                    } else if (isDelistOpt) {
                      btnClass = `${styles.ctStatusOption} ${styles.ctStatusOptionOrange}`;
                    }
                    return (
                      <button key={st.value} className={btnClass} onClick={() => setNewStatus(st.value)}>
                        {st.label}
                      </button>
                    );
                  })}
                </div>

                {newStatus && (
                  <div className={styles.ctTransitionVisual}>
                    <span className={`${styles.ctStatusBadge} ${ctBadgeClassForCurrent()}`}>
                      {getStatusIcon(pres.iconStatus)} {currentLabel}
                    </span>
                    <ArrowRight size={16} className={styles.ctTransArrow} />
                    <span className={`${styles.ctStatusBadge} ${getCtBadgeClass(newStatus)}`}>
                      {getStatusIcon(newStatus)} {selectedLabel}
                    </span>
                  </div>
                )}
              </div>
              <div className={styles.ctModalFooter}>
                <button className={styles.ctBackBtn} onClick={closeStatusModal}>Back</button>
                <button
                  className={`${styles.ctConfirmBtn} ${(!newStatus || statusChangeLoading) ? styles.ctConfirmBtnDisabled : ''}`}
                  disabled={!newStatus || statusChangeLoading}
                  onClick={() => {
                    if (selectedTicketForStatus && newStatus) {
                      handleStatusChangeConfirm(selectedTicketForStatus, newStatus);
                    }
                  }}
                >
                  {statusChangeLoading ? 'Saving...' : 'Confirm Change'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Delist Email Modal */}
      {showDelistEmailModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <h3>Delist Request</h3>
                <p>Please provide the email address to return the tickets to</p>
              </div>
              <button className={styles.modalClose} onClick={closeDelistEmailModal}><X size={18} /></button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.delistCount}>Delisting <strong>{pendingDelistTpIds.length}</strong> ticket(s)</div>
              <div className={styles.editField}>
                <label>Email</label>
                <input type="email" placeholder="name@company.com" {...registerDelistEmail('email', { required: 'Email is required', pattern: { value: emailRegexPattern, message: 'Enter a valid email' } })} />
                {delistEmailErrors.email && <small className={styles.errorText}>{delistEmailErrors.email.message as string}</small>}
              </div>
              <div className={styles.editField}>
                <label>Confirm Email</label>
                <input type="email" placeholder="name@company.com" {...registerDelistEmail('confirmEmail', { required: 'Confirm email is required', pattern: { value: emailRegexPattern, message: 'Enter a valid email' }, validate: (val) => watchDelistEmail('email') === val || 'Emails do not match' })} />
                {delistEmailErrors.confirmEmail && <small className={styles.errorText}>{delistEmailErrors.confirmEmail.message as string}</small>}
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.modalCancel} onClick={closeDelistEmailModal} disabled={statusTableBusyCount > 0}>Cancel</button>
              <button className={styles.modalConfirm} onClick={handleDelistEmailSubmit(onDelistEmailSubmit)} disabled={statusTableBusyCount > 0}>
                {statusTableBusyCount > 0 ? 'Submitting…' : 'Submit Delist Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Listed tab: confirm share / unshare (Shared column toggle) */}
      {listedShareConfirm && listedShareConfirm.tpIds.length > 0 && (
        <ListedShareConfirmModal
          targetShared={listedShareConfirm.targetShared}
          ticketCount={listedShareConfirm.tpIds.length}
          loading={listedSharedToggleLoading}
          onCancel={cancelListedShareConfirm}
          onConfirm={confirmListedShareToggle}
        />
      )}

      {/* Listed tab: confirm unshare + delist */}
      {showListedDelistConfirm && listedDelistTpIds.length > 0 && (
        <ListedDelistConfirmModal
          ticketCount={listedDelistTpIds.length}
          enableReturnEmailSelection
          qaUserEmail={authenticatedProfileEmail}
          storedReturnEmail={listedDelistStoredEmail}
          loading={statusChangeLoading}
          progress={listedDelistProgress}
          onCancel={cancelListedDelistConfirm}
          onConfirm={(requestedEmailId) => confirmListedDelist(requestedEmailId)}
          onRetryStep={(step, requestedEmailId) => void confirmListedDelist(requestedEmailId, step)}
        />
      )}

      {showSelfPricingPriceConfirm && (
        <SelfPricingPriceConfirmModal
          ticketCount={selfPricingPriceConfirmCount}
          summary={selfPricingPriceConfirmSummary}
          loading={priceLoading || bulkPriceLoading}
          onCancel={cancelSelfPricingPriceConfirm}
          onConfirm={confirmSelfPricingPriceChange}
        />
      )}

      {showNonVlStatusConfirm && (
        <NonVlStatusConfirmModal
          summary={nonVlStatusConfirmSummary}
          loading={statusChangeLoading || bulkStatusChangeLoading}
          onCancel={cancelNonVlStatusConfirm}
          onConfirm={confirmNonVlStatusChange}
        />
      )}

      {/* Listed tab: edit marketplace selection (stay Listed) */}
      {showListedMarketplaceEdit && listedMarketplaceEditTpIds.length > 0 && (
        <div
 className={styles.modalOverlay}
 >
          <div
            className={`${styles.modalContent} ${styles.vlShareModal}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h3>Edit marketplace selection</h3>
              <button
                type="button"
                className={styles.modalClose}
                disabled={listedMarketplaceEditLoading}
                onClick={() => setShowListedMarketplaceEdit(false)}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.vlShareHint}>
                Choose which marketplaces this listing is shared to
              </p>
              <div className={styles.vlShareMarketplaceActions}>
                <button
                  type="button"
                  className={styles.modalCancel}
                  onClick={() =>
                    setListedEditMarketplaces(VL_MARKETPLACE_OPTIONS.map((m) => m.id))
                  }
                  disabled={listedMarketplaceEditLoading}
                >
                  Select all
                </button>
                <button
                  type="button"
                  className={styles.modalCancel}
                  onClick={() => setListedEditMarketplaces([])}
                  disabled={listedMarketplaceEditLoading}
                >
                  Clear all
                </button>
              </div>
              <div className={styles.vlShareMarketplaceGrid}>
                {VL_MARKETPLACE_OPTIONS.map((m) => {
                  const checked = listedEditMarketplaces.includes(m.id);
                  return (
                    <label key={m.id} className={styles.vlShareMarketplaceOption}>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={listedMarketplaceEditLoading}
                        onChange={() => {
                          setListedEditMarketplaces((prev) =>
                            checked ? prev.filter((id) => id !== m.id) : [...prev, m.id],
                          );
                        }}
                      />
                      <MarketplaceBrandMark id={m.id} />
                      {m.label}
                    </label>
                  );
                })}
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button
                type="button"
                className={styles.modalCancel}
                onClick={() => setShowListedMarketplaceEdit(false)}
                disabled={listedMarketplaceEditLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.modalConfirm}
                onClick={() => void handleUpdateListedMarketplaces()}
                disabled={listedMarketplaceEditLoading || listedEditMarketplaces.length === 0}
              >
                {listedMarketplaceEditLoading ? 'Saving…' : 'Save marketplaces'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && selectedTicketForDelete && (
        <div className={styles.modalOverlay}>
          <div className={styles.ctModalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.ctModalHeader}>
              <div className={styles.ctModalTitleRow}>
                <AlertCircle className={styles.deleteIconWarning} size={20} />
                <span className={styles.ctModalTitle}>Delete Ticket</span>
              </div>
              <button className={styles.ctModalClose} onClick={() => setShowDeleteConfirmModal(false)}>×</button>
            </div>
            <div className={styles.ctModalBody}>
              <div className={styles.ctEventName}>{selectedTicketForDelete.e_name}</div>
              <div className={styles.ctEventDetails}>
                Section {selectedTicketForDelete.tp_section || 'N/A'} • Row {selectedTicketForDelete.tp_row || 'N/A'} • Seats {selectedTicketForDelete.tp_seat_no || 'N/A'}
              </div>
              <div className={styles.ctEventDate}>
                {formatDate(selectedTicketForDelete.e_date, selectedTicketForDelete.e_time_zone)}{selectedTicketForDelete.e_date ? ` • ${formatTime(selectedTicketForDelete.e_date, selectedTicketForDelete.e_time_zone)}` : ''}
              </div>

              <div className={styles.ctCurrentStatusRow}>
                <span className={styles.ctCurrentStatusLabel}>Current Status:</span>
                <span className={`${styles.statusBadge} ${getStatusColor(selectedTicketForDelete.tp_status)}`}>
                  {getStatusIcon(selectedTicketForDelete.tp_status)} {getStatusLabel(selectedTicketForDelete.tp_status)}
                </span>
              </div>

              <div className={styles.deleteWarningMessage}>
                <AlertCircle size={16} />
                <span>Are you sure you want to delete this ticket? This action will remove the ticket from active views and cannot be undone.</span>
              </div>
            </div>
            <div className={styles.ctModalFooter}>
              <button className={styles.ctBackBtn} onClick={() => setShowDeleteConfirmModal(false)} disabled={deleteLoading}>Cancel</button>
              <button
                className={`${styles.ctConfirmBtn} ${deleteLoading ? styles.ctConfirmBtnDisabled : ''}`}
                disabled={deleteLoading}
                onClick={handleDeleteConfirm}
              >
                {deleteLoading ? 'Deleting...' : 'Delete Ticket'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Card Detail Modal for Tracking Issues */}
      {showCardDetailModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '80vh' }}>
            <div className={styles.modalHeader}>
              <h3>
                {showCardDetailModal === 'LISTING_EXPIRED' 
                  ? `Listing Request Expired Tickets (${cardDetailTickets.length})`
                  : `Fail to Transfer Tickets (${cardDetailTickets.length})`}
              </h3>
              <button className={styles.modalClose} onClick={() => setShowCardDetailModal(null)}><X size={18} /></button>
            </div>
            <div className={styles.modalBody} style={{ overflowX: 'auto' }}>
              {cardDetailLoading ? (
                <div className={styles.loadingState}><div className={styles.spinner}></div><p>Loading tickets...</p></div>
              ) : cardDetailTickets.length > 0 ? (
                <table className={styles.modalTable}>
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>User</th>
                      <th>Section</th>
                      <th>Row</th>
                      <th>Seat</th>
                      <th>Status</th>
                      <th>Price</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cardDetailTickets.map((ticket, idx) => (
                      <tr key={ticket.tp_id || idx}>
                        <td><div className={styles.eventName}>{ticket.e_name || 'N/A'}</div></td>
                        <td>
                          {ticket.u_full_name ||
                            `${ticket.u_first_name || ''} ${ticket.u_last_name || ''}`.trim() ||
                            'N/A'}
                        </td>
                        <td>{ticket.tp_section || 'N/A'}</td>
                        <td>{ticket.tp_row || 'N/A'}</td>
                        <td>{ticket.tp_seat_no || 'N/A'}</td>
                        <td>
                          {showCardDetailModal === 'FAILED_TRANSFER' ? (
                            (() => {
                              const transitions = getTicketStatusTransitions(ticket);
                              const modalStatusDropdownKey = `failed-${ticket.tp_id}`;
                              if (!transitions.length) {
                                return <span className={`${styles.statusBadge} ${getStatusColor(ticket.tp_status)}`}>{getStatusLabel(ticket.tp_status || '')}</span>;
                              }
                              return (
                                <div className={styles.statusDropdownWrapper}>
                                  <button
                                    className={`${styles.statusBadge} ${getStatusColor(ticket.tp_status)} ${styles.statusDropdownBtn}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenStatusDropdown(openStatusDropdown === modalStatusDropdownKey ? null : modalStatusDropdownKey);
                                    }}
                                  >
                                    {getStatusIcon(ticket.tp_status || '')}
                                    {getStatusLabel(ticket.tp_status || '')}
                                    <ChevronDown size={12} style={{ marginLeft: 4 }} />
                                  </button>
                                  {openStatusDropdown === modalStatusDropdownKey && (
                                    <div className={styles.statusDropdownList}>
                                      {transitions.map((opt) => (
                                        <div
                                          key={opt.value}
                                          className={styles.statusDropdownListItem}
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            setOpenStatusDropdown(null);
                                            if (opt.value === 'Sold' && normalizeStatusValue(ticket.tp_status || '') === 'List') {
                                              await openSoldModalForTicket(ticket);
                                            } else {
                                              await handleStatusChangeConfirm(ticket, opt.value);
                                            }
                                          }}
                                        >
                                          {getStatusIcon(opt.value)}
                                          {opt.label}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })()
                          ) : (
                            <span className={styles.statusBadge}>{getStatusLabel(ticket.tp_status || '')}</span>
                          )}
                        </td>
                        <td>{formatCurrency(ticket.tp_list_price ?? 0)}</td>
                        <td>{formatDate(ticket.e_date, ticket.e_time_zone)}</td>
                        <td>
                          <button className={styles.viewUserBtn} onClick={() => handleUserClick(ticket.u_id)}>View User</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className={styles.noResults}>No tickets found</div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.modalCancel} onClick={() => setShowCardDetailModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {priceConfirmationFlow && (
        <div className={styles.modalOverlay}>
          <div className={`${styles.modalContent} ${styles.priceChangeModal}`} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3><span className={styles.priceChangeIcon}><AlertCircle size={19} /></span> Price Change Warning</h3>
              <button className={styles.modalClose} onClick={cancelPriceChangeConfirmation}>
                <X size={18} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.priceChangeWarning}>
                <strong>Large price change</strong>
                <span>{getPriceChangeWarningNote(getPriceConfirmationChanges())}</span>
              </div>

              {priceConfirmationFlow === 'single' && selectedTicketForPrice && (
                <div className={`${styles.modalEventInfo} ${styles.priceChangeDetails}`}>
                  <div className={styles.modalEventName}>{selectedTicketForPrice.e_name}</div>
                  <div className={styles.modalEventDetails}>
                    Section {selectedTicketForPrice.tp_section || 'N/A'} • Row {selectedTicketForPrice.tp_row || 'N/A'} • Seat {selectedTicketForPrice.tp_seat_no || 'N/A'}
                  </div>
                  <div className={styles.priceRow}><span>Current price</span><strong>{formatCurrency(Number(selectedTicketForPrice.tp_list_price) || 0)}</strong></div>
                  <div className={`${styles.priceRow} ${styles.newPriceRow}`}><span>New price</span><strong>{formatCurrency(Number(parseFloat(newPrice)) || 0)}</strong></div>
                </div>
              )}

              {priceConfirmationFlow === 'listed' && listedModalTicket && (
                <div className={`${styles.modalEventInfo} ${styles.priceChangeDetails}`}>
                  <div className={styles.modalEventName}>{listedModalTicket.e_name}</div>
                  <div className={styles.modalEventDetails}>
                    Section {listedModalTicket.tp_section || 'N/A'} • Row {listedModalTicket.tp_row || 'N/A'} • Seat {listedModalTicket.tp_seat_no || 'N/A'}
                  </div>
                  <div className={styles.priceRow}><span>Current price</span><strong>{formatCurrency(Number(listedModalTicket.tp_list_price) || 0)}</strong></div>
                  <div className={`${styles.priceRow} ${styles.newPriceRow}`}><span>New price</span><strong>{formatCurrency(Number(parseFloat(listedPrice)) || 0)}</strong></div>
                </div>
              )}

              {(priceConfirmationFlow === 'bulkPrice' || priceConfirmationFlow === 'bulkList' || priceConfirmationFlow === 'bulkStatus') && (
                <table className={styles.priceChangeTable}>
                  <thead>
                    <tr>
                      <th>Seat #</th>
                      <th>Current Price</th>
                      <th>New Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkSelectedTickets.map((ticket) => {
                      const prices = priceConfirmationFlow === 'bulkPrice'
                        ? bulkPriceIndividual
                        : priceConfirmationFlow === 'bulkList'
                          ? bulkListedPrices
                          : bulkStatusListingPrices;
                      return (
                        <tr key={ticket.tp_id}>
                          <td>{ticket.tp_seat_no || 'N/A'}</td>
                          <td>{formatCurrency(Number(ticket.tp_list_price) || 0)}</td>
                          <td>{formatCurrency(Number(parseFloat(String(prices[ticket.tp_id] || ''))) || 0)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.modalCancel} onClick={cancelPriceChangeConfirmation}>Cancel</button>
              <button className={styles.modalConfirm} onClick={() => { void confirmPriceChange(); }}>
                Proceed with change
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageTickets;